import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Affix,
  AutoComplete,
  Badge,
  Button,
  Card,
  DatePicker,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Progress,
  Select,
  Space,
  Spin,
  Tag,
  Tabs,
  Upload,
} from 'antd';
import {
  BellOutlined,
  CameraOutlined,
  InboxOutlined,
  RightOutlined,
  DownOutlined,
  CheckCircleTwoTone,
  MessageOutlined,
  PlusOutlined,
  EditOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { API_BASE_URL } from '../config';
import { confirmDialog } from '../utils/confirm';
import { readStoredApiKey, scopedKey, getActiveAccountId } from '../utils/accountSession';
import useRealtimeSocket from '../hooks/useRealtimeSocket';
import { useAuth } from '../contexts/AuthContext';
import {
  applyScrollableWheel,
  computeFloatingSummaryMaxHeight,
  getSummaryLineItemCount,
  normalizeProductCodeKey,
  reconcileSelectionMap,
} from './orderXV2Utils';
import {
  createOrderGenieRequest,
  updateOrderGenieItems,
  updateOrderGenieCart,
  updateOrderGenieSummary,
  deleteOrderGenieRequest,
  getOrderGenieProcessStatus,
  getOrderGenieProcessStatusBulk,
  getOrderGenieQueueMetrics,
  getOrderGenieRequest,
  getOrderGenieRunDetails,
  getOrderGenieSettings,
  matchOrderGenieRequestItem,
  listOrderGenieQuotes,
  listOrderGenieRequests,
  listOrderGenieRequestUsers,
  searchOrderGenieErpCustomers,
  searchOrderGenieErpShipToAddresses,
  getOrderGenieErpPricesBulk,
  listOrderGenieQueue,
  processOrderGenieRequestAsync,
  replayOrderGenieQueueRequest,
  probeOrderGenieRequest,
  getReadinessDiagnoseRequest,
  previewOrderGenieFile,
  previewOrderGenieStoredFile,
  stopOrderGenieProcess,
  listOrderGenieUsageEvents,
  updateOrderGenieRequestPriority,
  getOrderGenieSetupSecrets,
  saveOrderGenieSetupSecrets,
} from '../services/orderXApi';
import OrderGenieV3ProductSearch from './OrderXV3ProductSearch';
import OrderGenieSetupSecretsDrawer from '../components/OrderXSetupSecrets/OrderXSetupSecretsDrawer';

const { Dragger } = Upload;
const { RangePicker } = DatePicker;

const buildDefaultName = (userName = '') => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const normalizedUser = String(userName || '').trim();
  return `${normalizedUser || 'Request'} ${date} ${time}`;
};

const isSuccessStatus = (value) => {
  const normalized = String(value || '').toLowerCase();
  return normalized === 'completed' || normalized === 'matched';
};

const isTerminalStatus = (value) => {
  const normalized = String(value || '').toLowerCase();
  return normalized === 'completed'
    || normalized === 'matched'
    || normalized === 'failed'
    || normalized === 'cancelled';
};

const hasTerminalReviewSignal = (status) => {
  const statusValue = String(status?.request_status || status?.status || '').toLowerCase();
  const stepValue = String(status?.request_step || status?.step || '').toLowerCase();
  const messageValue = String(status?.message || '').toLowerCase();
  const isActivelyProcessing =
    statusValue === 'running'
    || statusValue === 'queued'
    || ['reading', 'parsing', 'processing', 'matching', 'ai_rerank', 'rerank', 'reranking', 'queued'].includes(stepValue);
  if (isActivelyProcessing) return false;
  return isTerminalStatus(statusValue)
    || ['completed', 'matched', 'failed', 'cancelled', 'results', 'review'].includes(stepValue)
    || messageValue.includes('ready for review');
};

const getStatusValueText = (value) => {
  if (value && typeof value === 'object') {
    return String(value.status || value.step || '').trim();
  }
  return String(value || '').trim();
};

const getStatusMessageText = (value) => {
  if (value && typeof value === 'object') {
    return String(value.message || value.detail || value.status || value.step || '').trim();
  }
  return String(value || '').trim();
};

const normalizeStatusPayload = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const nestedStatus = source.status && typeof source.status === 'object' ? source.status : {};
  const merged = { ...nestedStatus, ...source };
  const normalizedStatus = getStatusValueText(merged.status);
  const normalizedStep = getStatusValueText(merged.step || merged.status);
  const normalizedRequestStatus = getStatusValueText(merged.request_status || normalizedStatus);
  const normalizedRequestStep = getStatusValueText(merged.request_step || merged.step || normalizedRequestStatus);
  return {
    ...merged,
    status: normalizedStatus,
    step: normalizedStep,
    request_status: normalizedRequestStatus,
    request_step: normalizedRequestStep,
    row_rerun_status: getStatusValueText(merged.row_rerun_status),
    row_rerun_step: getStatusValueText(merged.row_rerun_step),
    message: getStatusMessageText(merged.message || merged.detail || merged.status),
    detail: getStatusMessageText(merged.detail),
  };
};

const STREAM_PANEL_HIDDEN_STEPS = new Set([
  'parsed',
  'matching',
  'ai_rerank',
  'rerank',
  'reranking',
  'completed',
  'matched',
  'failed',
  'cancelled',
]);

const OrderGenieV2 = ({ variant = 'v2' }) => {
  const { account, getAuthHeaders } = useAuth();
  const isV3 = String(variant || '').toLowerCase() === 'v3';
  const [userId, setUserId] = useState('');
  const [userEditorOpen, setUserEditorOpen] = useState(false);
  const [userIdDraft, setUserIdDraft] = useState('');
  const [customerIdAsInErp, setCustomerIdAsInErp] = useState('');
  const [customerIdAsInErpDraft, setCustomerIdAsInErpDraft] = useState('');
  const [erpCustomerOptions, setErpCustomerOptions] = useState([]);
  const [erpCustomerLoading, setErpCustomerLoading] = useState(false);
  const erpCustomerSearchTimerRef = useRef(null);
  const erpCustomerSearchSeqRef = useRef(0);
  const [shipToIdAsInErp, setShipToIdAsInErp] = useState('');
  const [shipToIdAsInErpDraft, setShipToIdAsInErpDraft] = useState('');
  const [shipToOptions, setShipToOptions] = useState([]);
  const [shipToLoading, setShipToLoading] = useState(false);
  const [erpPriceByProductCode, setErpPriceByProductCode] = useState({});
  const erpPriceInFlightRef = useRef(new Set());
  // Codes already looked up (including empty/no-price). Prevents refetch loops when
  // /prices/bulk returns {"prices":{}}.
  const erpPriceAttemptedRef = useRef(new Set());
  // Fetch prices for whichever product codes aren't already cached/in-flight, in one
  // bulk call. Callers pass only the codes actually visible on screen so we never
  // fetch prices for options the user hasn't asked to see yet.
  const loadErpPricesForCodes = useCallback((codes) => {
    const missing = Array.from(new Set(
      (codes || []).map((code) => String(code || '').trim()).filter(Boolean)
    )).filter((code) => (
      !(code in erpPriceByProductCode)
      && !erpPriceInFlightRef.current.has(code)
      && !erpPriceAttemptedRef.current.has(code)
    ));
    if (!missing.length) return;
    missing.forEach((code) => erpPriceInFlightRef.current.add(code));
    getOrderGenieErpPricesBulk(missing)
      .then((resp) => {
        const prices = resp?.prices && typeof resp.prices === 'object' ? resp.prices : {};
        // Empty {"prices":{}} must not setState — that recreates loadErpPricesForCodes
        // while codes stay "missing" and causes an infinite refetch loop.
        if (!Object.keys(prices).length) return;
        setErpPriceByProductCode((prev) => ({ ...prev, ...prices }));
      })
      .catch(() => {
        // ignore — the price line just stays hidden for these codes
      })
      .finally(() => {
        missing.forEach((code) => {
          erpPriceInFlightRef.current.delete(code);
          erpPriceAttemptedRef.current.add(code);
        });
      });
  }, [erpPriceByProductCode]);
  const [requestName, setRequestName] = useState(buildDefaultName());
  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [overlayActive, setOverlayActive] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [overlayDetail, setOverlayDetail] = useState('');
  const [quoteReadyNotice, setQuoteReadyNotice] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [realtimePanelOpen, setRealtimePanelOpen] = useState(false);
  const [realtimeUnreadCount, setRealtimeUnreadCount] = useState(0);
  const [realtimeFeed, setRealtimeFeed] = useState([]);
  const readingPageRef = useRef(1);
  const readingTotalRef = useRef(1);
  const pendingAutoOpenRequestIdRef = useRef(null);
  const [step, setStep] = useState('upload');
  const [items, setItems] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedByItem, setSelectedByItem] = useState({});
  const [qtyByItem, setQtyByItem] = useState({});
  const [productImageBaseUrl, setProductImageBaseUrl] = useState('');
  const [sourceFiles, setSourceFiles] = useState([]);
  const [uploadPreviewTable, setUploadPreviewTable] = useState([]);
  const [uploadPreviewText, setUploadPreviewText] = useState('');
  const [uploadPreviewHtml, setUploadPreviewHtml] = useState('');
  const [uploadSheetNames, setUploadSheetNames] = useState([]);
  const [uploadSelectedSheet, setUploadSelectedSheet] = useState('');
  const [uploadSheetCheckLoading, setUploadSheetCheckLoading] = useState(false);
  const [uploadSheetCheckReady, setUploadSheetCheckReady] = useState(false);
  const [storedPreviewByPath, setStoredPreviewByPath] = useState({});
  const [previewModalFile, setPreviewModalFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [userIdRequiredError, setUserIdRequiredError] = useState('');
  const [fileRequiredError, setFileRequiredError] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingItems, setEditingItems] = useState([]);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [editingUserId, setEditingUserId] = useState('');
  const [editingSaving, setEditingSaving] = useState(false);
  const [editingHighlightKey, setEditingHighlightKey] = useState('');
  const [editDrawerValidationError, setEditDrawerValidationError] = useState('');
  const [editDrawerInitialSnapshot, setEditDrawerInitialSnapshot] = useState('');
  const editingRowRefs = useRef({});
  const [statusUserId, setStatusUserId] = useState('all');
  const [statusUserIdDraft, setStatusUserIdDraft] = useState('all');
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusRequests, setStatusRequests] = useState([]);
  const [statusQuotes, setStatusQuotes] = useState([]);
  const [requestStatusById, setRequestStatusById] = useState({});
  const [configuredParallelMatchThreads, setConfiguredParallelMatchThreads] = useState(1);
  const [statusUserOptions, setStatusUserOptions] = useState([]);
  const [statusUserLoading, setStatusUserLoading] = useState(false);
  const [statusUserSearch, setStatusUserSearch] = useState('');
  const [statusUserDropdownOpen, setStatusUserDropdownOpen] = useState(false);
  const [statusPage, setStatusPage] = useState(1);
  const [statusPageSize, setStatusPageSize] = useState(10);
  const [statusTotal, setStatusTotal] = useState(0);
  const [statusDateRange, setStatusDateRange] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageEvents, setUsageEvents] = useState([]);
  const [usageDateRange, setUsageDateRange] = useState(null);
  const [usageError, setUsageError] = useState('');
  const [queuePanelItems, setQueuePanelItems] = useState([]);
  const [queuePanelTotal, setQueuePanelTotal] = useState(0);
  const [queueMetrics, setQueueMetrics] = useState(null);
  const [queuePanelLoading, setQueuePanelLoading] = useState(false);
  const [queuePrioritySavingByRequest, setQueuePrioritySavingByRequest] = useState({});
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);
  const [setupSecretsOpen, setSetupSecretsOpen] = useState(false);
  const [setupSecretsMissing, setSetupSecretsMissing] = useState([]);
  const [setupSecretsSaving, setSetupSecretsSaving] = useState(false);
  const [setupSecretsError, setSetupSecretsError] = useState('');
  const [queueDeadLetterItems, setQueueDeadLetterItems] = useState([]);
  const [queueReplayLoadingByRequest, setQueueReplayLoadingByRequest] = useState({});
  const [queueMetricsTrend, setQueueMetricsTrend] = useState([]);
  const [queueAdminUsers, setQueueAdminUsers] = useState([]);
  const [queuePriorityEnabled, setQueuePriorityEnabled] = useState(true);
  const [expandedProgressByRequest, setExpandedProgressByRequest] = useState({});
  const [v3ProcessDetailsLoadingByRequest, setV3ProcessDetailsLoadingByRequest] = useState({});
  const [reviewHydrationLoadingByRequest, setReviewHydrationLoadingByRequest] = useState({});
  const [v3ProcessDetailsModalOpen, setV3ProcessDetailsModalOpen] = useState(false);
  const [v3ProcessDetailsModalRequest, setV3ProcessDetailsModalRequest] = useState(null);
  const [v3ProcessDetailsModalStatus, setV3ProcessDetailsModalStatus] = useState(null);
  const [probeModalOpen, setProbeModalOpen] = useState(false);
  const [probeModalLoading, setProbeModalLoading] = useState(false);
  const [probeModalResumeLoading, setProbeModalResumeLoading] = useState(false);
  const [probeModalRequest, setProbeModalRequest] = useState(null);
  const [probeModalData, setProbeModalData] = useState(null);
  const [probeModalDiagnose, setProbeModalDiagnose] = useState(null);
  const [probeModalError, setProbeModalError] = useState('');
  const [progressClockMs, setProgressClockMs] = useState(Date.now());
  const [quoteTabs, setQuoteTabs] = useState([]);
  const [v3ReviewOpenedByRequest, setV3ReviewOpenedByRequest] = useState({});
  const [requestSummaryById, setRequestSummaryById] = useState({});
  const [quoteSelections, setQuoteSelections] = useState({});
  const [quoteQtys, setQuoteQtys] = useState({});
  const [reviewMatchFilterByRequest, setReviewMatchFilterByRequest] = useState({});
  const [activeProcesses, setActiveProcesses] = useState([]);
  const [processRunTypeById, setProcessRunTypeById] = useState({});
  const [processStatus, setProcessStatus] = useState(null);
  const [processMetaById, setProcessMetaById] = useState({});
  const processMetaRef = useRef({});
  const statusUserIdRef = useRef('all');
  const statusListLoadInFlightRef = useRef(false);
  const statusListLoadPendingRef = useRef(null);
  const floatingSummaryCardRef = useRef(null);
  const floatingMatchFilterRef = useRef(null);
  const cameraInputRef = useRef(null);
  const pasteInputRef = useRef(null);
  const statusUserSelectRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const aiStreamPanelRef = useRef(null);
  const aiStreamTargetRef = useRef('');
  const aiStreamRenderedRef = useRef('');
  const aiStreamTimerRef = useRef(null);
  const aiStreamDoneRef = useRef(false);
  const aiStreamIngressRateRef = useRef(0);
  const aiStreamIngressSampleRef = useRef({ len: 0, at: 0 });
  const aiStreamRequestRef = useRef('');
  const aiStreamFinalizedRef = useRef(false);
  const fullHydrationInFlightRef = useRef({});
  const rerunKickoffAtRef = useRef({});
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const workflowFallbackNotified = useRef(new Set());
  const [expandedMatchKeys, setExpandedMatchKeys] = useState({});
  const [expandedDebugKeys, setExpandedDebugKeys] = useState({});
  const [expandedRowsByKey, setExpandedRowsByKey] = useState({});
  const [manualSearchByRow, setManualSearchByRow] = useState({});
  const [manualSearchLoadingByRow, setManualSearchLoadingByRow] = useState({});
  const [manualSearchErrorByRow, setManualSearchErrorByRow] = useState({});
  const [manualSearchMetaByRow, setManualSearchMetaByRow] = useState({});
  const [manualSearchResultsByRow, setManualSearchResultsByRow] = useState({});
  const [manualSearchViewByRow, setManualSearchViewByRow] = useState({});
  const [, setManualSearchQueryModeByRow] = useState({});
  const [, setManualSearchSelectedByRow] = useState({});
  const [manualSearchApplyReasonByRow, setManualSearchApplyReasonByRow] = useState({});
  const [manualReplaceModalOpen, setManualReplaceModalOpen] = useState(false);
  const [manualReplaceReasonDraft, setManualReplaceReasonDraft] = useState('');
  const [manualReplaceReasonError, setManualReplaceReasonError] = useState('');
  const queueActorUserId = useMemo(
    () => String(userId || statusUserId || '').trim().toLowerCase(),
    [userId, statusUserId]
  );
  const isQueueAdmin = useMemo(() => {
    return queueAdminUsers.length > 0
      && Boolean(queueActorUserId)
      && queueAdminUsers.includes(queueActorUserId);
  }, [queueActorUserId, queueAdminUsers]);
  const [manualReplacePending, setManualReplacePending] = useState(null);
  const [manualSearchCategoryByRow, setManualSearchCategoryByRow] = useState({});
  const [manualSearchSnapshotByRow, setManualSearchSnapshotByRow] = useState({});
  const [advancedSearchTipsOpen, setAdvancedSearchTipsOpen] = useState(false);
  const [rowRerunLoadingByRow, setRowRerunLoadingByRow] = useState({});
  const [rowRerunStatusByRow, setRowRerunStatusByRow] = useState({});
  const [brokenImageUrls, setBrokenImageUrls] = useState({});
  const processStaleNotified = useRef(new Set());
  const completionNotified = useRef(new Set());
  const lastRealtimeNotificationAtRef = useRef(0);
  const realtimePanelOpenRef = useRef(false);
  const [runDetailsOpen, setRunDetailsOpen] = useState(false);
  const [runDetailsLoading, setRunDetailsLoading] = useState(false);
  const [runDetailsData, setRunDetailsData] = useState(null);
  const [aiStreamRenderedText, setAiStreamRenderedText] = useState('');
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );
  const [floatingSummaryMaxHeight, setFloatingSummaryMaxHeight] = useState(null);
  const [floatingMatchFilterHeight, setFloatingMatchFilterHeight] = useState(0);
  const tenantUserStorageKey = useMemo(() => {
    const accountId = String(account?.id ?? '').trim();
    if (accountId) return `order_genie_v2_user_id_${accountId}`;
    const apiKey = typeof window !== 'undefined' ? String(readStoredApiKey() || '').trim() : '';
    if (apiKey) return `order_genie_v2_user_id_${apiKey.slice(0, 16)}`;
    return 'order_genie_v2_user_id_default';
  }, [account?.id]);
  const tenantCustomerErpStorageKey = useMemo(() => {
    const accountId = String(account?.id ?? '').trim();
    if (accountId) return `order_genie_v2_customer_erp_id_${accountId}`;
    const apiKey = typeof window !== 'undefined' ? String(readStoredApiKey() || '').trim() : '';
    if (apiKey) return `order_genie_v2_customer_erp_id_${apiKey.slice(0, 16)}`;
    return 'order_genie_v2_customer_erp_id_default';
  }, [account?.id]);
  const customerErpFallbackStorageKey = 'order_genie_v2_customer_erp_id_global';
  const tenantShipToStorageKey = useMemo(() => {
    const accountId = String(account?.id ?? '').trim();
    if (accountId) return `order_genie_v2_shipto_id_${accountId}`;
    const apiKey = typeof window !== 'undefined' ? String(readStoredApiKey() || '').trim() : '';
    if (apiKey) return `order_genie_v2_shipto_id_${apiKey.slice(0, 16)}`;
    return 'order_genie_v2_shipto_id_default';
  }, [account?.id]);
  const shipToFallbackStorageKey = 'order_genie_v2_shipto_id_global';
  const statusPageSizeStorageKey = useMemo(() => {
    const accountId = String(account?.id ?? '').trim();
    if (accountId) return `order_genie_v2_status_page_size_${accountId}`;
    const apiKey = typeof window !== 'undefined' ? String(readStoredApiKey() || '').trim() : '';
    if (apiKey) return `order_genie_v2_status_page_size_${apiKey.slice(0, 16)}`;
    return 'order_genie_v2_status_page_size_default';
  }, [account?.id]);

  const PROCESS_POLL_STALE_MS = 20000;
  const PROCESS_START_TIMEOUT_MS = 15000;

  // Deep-link: read ?openRequest=<id> on mount and auto-open that request
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const reqId = params.get('openRequest');
    if (!reqId) return;
    window.history.replaceState({}, '', window.location.pathname);
    // Force 'all' so email_inbox-sourced requests are visible in the list
    setStatusUserId('all');
    setStatusUserIdDraft('all');
    // Switch to Request List tab so loadStatusRequests fires and populates statusRequests
    setActiveTab('status');
    pendingAutoOpenRequestIdRef.current = reqId;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Once statusRequests loads, auto-open the pending request (from deep-link)
  useEffect(() => {
    const targetId = pendingAutoOpenRequestIdRef.current;
    if (!targetId || !statusRequests.length) return;
    const req = statusRequests.find((r) => String(r.id) === targetId);
    if (!req) return;
    pendingAutoOpenRequestIdRef.current = null;
    const tabKey = `quote-${req.id}`;
    setQuoteTabs((prev) => {
      if (prev.some((t) => String(t.id) === String(req.id))) return prev;
      return [
        ...prev,
        {
          id: req.id,
          title: `Request: ${req.request_name || req.id}`,
          items: [],
          matches: [],
          sourceFiles: [],
          requestSummary: {},
          userId: req.user_id || 'all',
          statusSnapshot: { status: req.status, step: req.status },
        },
      ];
    });
    setRequestId(req.id);
    setProcessStatus({ status: req.status, step: req.status });
    setStep('results');
    setActiveTab(tabKey);
  }, [statusRequests]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    processMetaRef.current = processMetaById;
  }, [processMetaById]);

  useEffect(() => {
    statusUserIdRef.current = String(statusUserId || '').trim();
  }, [statusUserId]);

  useEffect(() => {
    realtimePanelOpenRef.current = realtimePanelOpen;
  }, [realtimePanelOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = String(localStorage.getItem(tenantUserStorageKey) || '').trim();
      if (!saved) return;
      setUserId(saved);
      setStatusUserId((prev) => {
        const next = prev && prev !== 'all' ? prev : saved;
        setStatusUserIdDraft(next);
        return next;
      });
      setRequestName((prev) => {
        const current = String(prev || '').trim();
        if (!current || current === buildDefaultName()) {
          return buildDefaultName(saved);
        }
        return prev;
      });
    } catch (err) {
      // Ignore storage read failures.
    }
  }, [tenantUserStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = String(
        localStorage.getItem(tenantCustomerErpStorageKey)
        || localStorage.getItem(customerErpFallbackStorageKey)
        || ''
      ).trim();
      if (!saved) return;
      setCustomerIdAsInErp(saved);
    } catch (err) {
      // Ignore storage read failures.
    }
  }, [customerErpFallbackStorageKey, tenantCustomerErpStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = String(
        localStorage.getItem(tenantShipToStorageKey)
        || localStorage.getItem(shipToFallbackStorageKey)
        || ''
      ).trim();
      if (!saved) return;
      setShipToIdAsInErp(saved);
    } catch (err) {
      // Ignore storage read failures.
    }
  }, [shipToFallbackStorageKey, tenantShipToStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = Number(localStorage.getItem(statusPageSizeStorageKey) || 10);
      const allowedSizes = new Set([10, 20, 30, 40, 50]);
      if (allowedSizes.has(saved)) {
        setStatusPageSize(saved);
      }
    } catch (err) {
      // Ignore storage read failures.
    }
  }, [statusPageSizeStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const normalized = String(userId || '').trim();
      if (normalized) {
        localStorage.setItem(tenantUserStorageKey, normalized);
      } else {
        localStorage.removeItem(tenantUserStorageKey);
      }
    } catch (err) {
      // Ignore storage write failures.
    }
  }, [tenantUserStorageKey, userId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const normalized = String(customerIdAsInErp || '').trim();
      if (normalized) {
        localStorage.setItem(tenantCustomerErpStorageKey, normalized);
        localStorage.setItem(customerErpFallbackStorageKey, normalized);
      } else {
        localStorage.removeItem(tenantCustomerErpStorageKey);
        localStorage.removeItem(customerErpFallbackStorageKey);
      }
    } catch (err) {
      // Ignore storage write failures.
    }
  }, [customerErpFallbackStorageKey, customerIdAsInErp, tenantCustomerErpStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const normalized = String(shipToIdAsInErp || '').trim();
      if (normalized) {
        localStorage.setItem(tenantShipToStorageKey, normalized);
        localStorage.setItem(shipToFallbackStorageKey, normalized);
      } else {
        localStorage.removeItem(tenantShipToStorageKey);
        localStorage.removeItem(shipToFallbackStorageKey);
      }
    } catch (err) {
      // Ignore storage write failures.
    }
  }, [shipToFallbackStorageKey, shipToIdAsInErp, tenantShipToStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(statusPageSizeStorageKey, String(statusPageSize || 10));
    } catch (err) {
      // Ignore storage write failures.
    }
  }, [statusPageSize, statusPageSizeStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const editDrawerWidth = useMemo(() => {
    if (viewportWidth < 576) return viewportWidth;
    if (viewportWidth < 992) return Math.round(viewportWidth * 0.92);
    return Math.min(720, Math.round(viewportWidth * 0.75));
  }, [viewportWidth]);

  const getAffixTarget = useCallback(() => {
    if (typeof document === 'undefined') return window;
    const mainScroller = document.querySelector('main.flex-grow-1');
    return mainScroller || window;
  }, []);

  const recalcFloatingSummaryMaxHeight = useCallback(() => {
    if (typeof window === 'undefined') return;
    const target = getAffixTarget();
    const cardEl = floatingSummaryCardRef.current;
    const targetRect =
      target && target !== window && typeof target.getBoundingClientRect === 'function'
        ? target.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight, height: window.innerHeight };
    const cardRect =
      cardEl && typeof cardEl.getBoundingClientRect === 'function'
        ? cardEl.getBoundingClientRect()
        : null;
    const cardTop = cardRect ? cardRect.top : targetRect.top + 84;
    const nextMaxHeight = computeFloatingSummaryMaxHeight({
      targetBottom: targetRect.bottom,
      cardTop,
      bottomGap: 8,
      minHeight: 280,
    });
    setFloatingSummaryMaxHeight(nextMaxHeight);
  }, [getAffixTarget]);

  const handleFloatingSummaryWheel = useCallback((event) => {
    const rootEl = event?.currentTarget;
    if (!rootEl) return;
    const el =
      (typeof rootEl.querySelector === 'function' &&
        rootEl.querySelector('.ant-card-body')) ||
      rootEl;
    const { consumed, nextScrollTop } = applyScrollableWheel(el, event.deltaY);
    if (!consumed) return;
    if (event.cancelable) {
      event.preventDefault();
    }
    if (typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    el.scrollTop = nextScrollTop;
  }, []);

  const recalcFloatingMatchFilterHeight = useCallback(() => {
    if (typeof window === 'undefined') return;
    const panelEl = floatingMatchFilterRef.current;
    if (!panelEl || typeof panelEl.getBoundingClientRect !== 'function') {
      setFloatingMatchFilterHeight(0);
      return;
    }
    const nextHeight = Math.max(0, Math.ceil(panelEl.getBoundingClientRect().height) + 24);
    setFloatingMatchFilterHeight((prev) => (Math.abs(prev - nextHeight) <= 2 ? prev : nextHeight));
  }, []);

  const previewFile = useMemo(() => (files && files.length ? files[files.length - 1] : null), [files]);
  const previewFileBlob = previewFile?.originFileObj || previewFile;
  const previewUrl = useMemo(() => {
    if (!previewFileBlob) return '';
    try {
      return URL.createObjectURL(previewFileBlob);
    } catch (err) {
      return '';
    }
  }, [previewFileBlob]);
  const previewType = previewFileBlob?.type || previewFile?.type || '';
  const isTabularPreviewFile = useCallback((contentType = '', filename = '') => {
    const mime = String(contentType || '').toLowerCase();
    const name = String(filename || '').toLowerCase();
    return mime.includes('csv')
      || mime.includes('tab-separated-values')
      || mime.includes('spreadsheet')
      || mime.includes('excel')
      || name.endsWith('.csv')
      || name.endsWith('.tsv')
      || name.endsWith('.xlsx')
      || name.endsWith('.xls');
  }, []);

  const isPlainTextPreviewFile = useCallback((contentType = '', filename = '') => {
    const mime = String(contentType || '').toLowerCase();
    const name = String(filename || '').toLowerCase();
    const isTabularText = mime.includes('csv')
      || mime.includes('tab-separated-values')
      || name.endsWith('.csv')
      || name.endsWith('.tsv');
    return (mime.startsWith('text/') && !isTabularText)
      || name.endsWith('.txt')
      || name.endsWith('.text')
      || name.startsWith('pasted-text-');
  }, []);

  const normalizeHtmlAsText = useCallback((html) => {
    return String(html || '')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
      .replace(/<\/?[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const sanitizeRichHtml = useCallback((html) => {
    return String(html || '')
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
      .replace(/<(iframe|object|embed|link|meta|base)[^>]*>/gi, '')
      .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
      .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
      .replace(/javascript:/gi, '');
  }, []);

  const readBlobAsText = useCallback(async (blob) => {
    if (!blob) return '';
    if (typeof blob.text === 'function') {
      try {
        return await blob.text();
      } catch (_) {
        // Fallback below.
      }
    }
    return await new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('Failed to read text'));
        reader.readAsText(blob);
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    const filename = String(previewFile?.name || previewFileBlob?.name || '').trim();
    const keepLastPreviewWhileProcessing =
      (Array.isArray(sourceFiles) && sourceFiles.length > 0)
      || (Array.isArray(activeProcesses) && activeProcesses.length > 0)
      || String(activeTab || '').startsWith('quote-')
      || Boolean(requestId);
    if (!previewFileBlob) {
      if (keepLastPreviewWhileProcessing) {
        return;
      }
      setUploadSheetCheckLoading(false);
      setUploadSheetCheckReady(false);
      setUploadPreviewTable([]);
      setUploadPreviewText('');
      setUploadPreviewHtml('');
      setUploadSheetNames([]);
      setUploadSelectedSheet('');
      return;
    }
    if (isPlainTextPreviewFile(previewType, filename)) {
      setUploadSheetCheckLoading(false);
      setUploadSheetCheckReady(false);
      let cancelled = false;
      (async () => {
        try {
          const rawText = await readBlobAsText(previewFileBlob);
          if (cancelled) return;
          setUploadPreviewTable([]);
          setUploadPreviewText(String(rawText || '').trim());
          setUploadPreviewHtml('');
          setUploadSheetNames([]);
          setUploadSelectedSheet('');
        } catch (_) {
          if (cancelled) return;
          setUploadPreviewTable([]);
          setUploadPreviewText('');
          setUploadPreviewHtml('');
          setUploadSheetNames([]);
          setUploadSelectedSheet('');
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (!isTabularPreviewFile(previewType, filename)) {
      setUploadSheetCheckLoading(false);
      setUploadSheetCheckReady(false);
      setUploadPreviewTable([]);
      setUploadPreviewText('');
      setUploadPreviewHtml('');
      setUploadSheetNames([]);
      setUploadSelectedSheet('');
      return;
    }
    const isExcelTabular = filename.endsWith('.xlsx') || filename.endsWith('.xls');
    setUploadSheetCheckLoading(isExcelTabular);
    setUploadSheetCheckReady(!isExcelTabular);
    let cancelled = false;
    (async () => {
      try {
        const data = await previewOrderGenieFile(previewFileBlob, {
          selectedSheet: uploadSelectedSheet,
        });
        if (cancelled) return;
        setUploadPreviewTable(Array.isArray(data?.preview_table) ? data.preview_table : []);
        setUploadPreviewText(String(data?.preview_text || '').trim());
        setUploadPreviewHtml('');
        const sheetNames = Array.isArray(data?.sheet_names)
          ? data.sheet_names.map((name) => String(name || '').trim()).filter(Boolean)
          : [];
        setUploadSheetNames(sheetNames);
        if (sheetNames.length <= 1) {
          const nextSingleSheet = sheetNames.length === 1 ? sheetNames[0] : '';
          if (uploadSelectedSheet !== nextSingleSheet) setUploadSelectedSheet(nextSingleSheet);
        } else if (uploadSelectedSheet && !sheetNames.includes(uploadSelectedSheet)) {
          setUploadSelectedSheet('');
        }
        setUploadSheetCheckLoading(false);
        setUploadSheetCheckReady(true);
      } catch (error) {
        if (cancelled) return;
        setUploadPreviewTable([]);
        setUploadPreviewText('');
        setUploadPreviewHtml('');
        setUploadSheetNames([]);
        setUploadSelectedSheet('');
        setUploadSheetCheckLoading(false);
        setUploadSheetCheckReady(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeProcesses,
    activeTab,
    isPlainTextPreviewFile,
    isTabularPreviewFile,
    previewFile,
    previewFileBlob,
    previewType,
    readBlobAsText,
    requestId,
    sourceFiles,
    uploadSelectedSheet,
  ]);

  useEffect(() => {
    const docs = Array.isArray(sourceFiles) ? sourceFiles : [];
    const latest = docs.length ? docs[docs.length - 1] : null;
    if (!latest) return;
    const storagePath = String(latest?.storage_path || latest?.path || '').trim();
    const filename = String(latest?.filename || latest?.name || '').trim();
    const contentType = String(latest?.content_type || latest?.type || '').trim();
    if (!storagePath || !filename || !isTabularPreviewFile(contentType, filename)) return;
    if (storedPreviewByPath[storagePath]?.loading || storedPreviewByPath[storagePath]?.loaded) return;
    setStoredPreviewByPath((prev) => ({ ...prev, [storagePath]: { loading: true, loaded: false } }));
    let cancelled = false;
    (async () => {
      try {
        const data = await previewOrderGenieStoredFile({
          storage_path: storagePath,
          filename,
          content_type: contentType,
          selected_sheet: String(latest?.selected_sheet || '').trim() || undefined,
        });
        if (cancelled) return;
        setStoredPreviewByPath((prev) => ({
          ...prev,
          [storagePath]: {
            loading: false,
            loaded: true,
            table: Array.isArray(data?.preview_table) ? data.preview_table : [],
            text: String(data?.preview_text || '').trim(),
          },
        }));
      } catch (error) {
        if (cancelled) return;
        setStoredPreviewByPath((prev) => ({
          ...prev,
          [storagePath]: { loading: false, loaded: true, table: [], text: '' },
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceFiles, isTabularPreviewFile, storedPreviewByPath]);

  useEffect(() => {
    if (!quoteReadyNotice) return;
    const timer = setTimeout(() => setQuoteReadyNotice(null), 8000);
    return () => clearTimeout(timer);
  }, [quoteReadyNotice]);

  const resolveProductImageUrl = useCallback(
    (value) => {
      if (!value) return '';
      if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('blob:') || value.startsWith('data:')) {
        return value;
      }
      const base = (productImageBaseUrl || '').trim();
      if (!base) return value;
      const baseClean = base.endsWith('/') ? base.slice(0, -1) : base;
      const pathClean = value.startsWith('/') ? value.slice(1) : value;
      return `${baseClean}/${pathClean}`;
    },
    [productImageBaseUrl]
  );

  const markImageUrlBroken = useCallback((value) => {
    const resolved = String(resolveProductImageUrl(value) || '').trim();
    if (!resolved) return;
    setBrokenImageUrls((prev) => {
      if (prev[resolved]) return prev;
      return { ...prev, [resolved]: true };
    });
  }, [resolveProductImageUrl]);

  const isImageUrlBroken = useCallback((value) => {
    const resolved = String(resolveProductImageUrl(value) || '').trim();
    if (!resolved) return false;
    return !!brokenImageUrls[resolved];
  }, [brokenImageUrls, resolveProductImageUrl]);

  useEffect(() => {
    const loadFallback = async () => {
      try {
        const headers = getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, { headers });
        const data = await resp.json().catch(() => ({}));
        if (resp.ok) {
          setProductImageBaseUrl(data?.config?.product_image_fallback_base_url || '');
        }
      } catch (err) {
        // ignore
      }
    };
    loadFallback();
  }, [getAuthHeaders]);

  useEffect(() => {
    const loadConfiguredWorkers = async () => {
      try {
        const settingsResp = await getOrderGenieSettings();
        const settings = settingsResp?.settings || {};
        const rawValue = Number(settings?.parallel_match_threads);
        const normalizedValue = Number.isFinite(rawValue) ? Math.max(1, Math.min(4, rawValue)) : 1;
        setConfiguredParallelMatchThreads(normalizedValue);
        const rawQueueAdmins = settings?.queue_admin_users;
        const normalizedQueueAdmins = Array.isArray(rawQueueAdmins)
          ? rawQueueAdmins.map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
          : [];
        setQueueAdminUsers(Array.from(new Set(normalizedQueueAdmins)));
        setQueuePriorityEnabled(Boolean(settings?.queue_priority_enabled ?? true));
      } catch (err) {
        setConfiguredParallelMatchThreads(1);
        setQueueAdminUsers([]);
        setQueuePriorityEnabled(true);
      }
    };
    loadConfiguredWorkers();
  }, []);

  useEffect(() => {
    if (!statusUserId && userId) {
      setStatusUserId(userId);
      setStatusUserIdDraft(userId);
    }
  }, [statusUserId, userId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    recalcFloatingSummaryMaxHeight();
    const onResize = () => recalcFloatingSummaryMaxHeight();
    const target = getAffixTarget();
    const onScroll = () => recalcFloatingSummaryMaxHeight();
    window.addEventListener('resize', onResize);
    let attachedToWindow = false;
    if (target && target !== window && typeof target.addEventListener === 'function') {
      target.addEventListener('scroll', onScroll, { passive: true });
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
      attachedToWindow = true;
    }
    return () => {
      window.removeEventListener('resize', onResize);
      if (target && target !== window && typeof target.removeEventListener === 'function') {
        target.removeEventListener('scroll', onScroll);
      } else if (attachedToWindow) {
        window.removeEventListener('scroll', onScroll);
      }
    };
  }, [getAffixTarget, recalcFloatingSummaryMaxHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const shouldReflowSummary =
      (activeTab === 'new' && step === 'results') || String(activeTab || '').startsWith('quote-');
    if (!shouldReflowSummary) return undefined;
    const emitResize = () => window.dispatchEvent(new Event('resize'));
    const t1 = window.setTimeout(emitResize, 0);
    const t2 = window.setTimeout(emitResize, 120);
    const t3 = window.setTimeout(() => recalcFloatingSummaryMaxHeight(), 160);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [activeTab, step, quoteTabs.length, recalcFloatingSummaryMaxHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    recalcFloatingMatchFilterHeight();
    const onResize = () => recalcFloatingMatchFilterHeight();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && floatingMatchFilterRef.current) {
      observer = new ResizeObserver(() => recalcFloatingMatchFilterHeight());
      observer.observe(floatingMatchFilterRef.current);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (observer) observer.disconnect();
    };
  }, [recalcFloatingMatchFilterHeight, activeTab, quoteTabs.length]);

  useEffect(() => {
    const hasRunningCurrentProcess = (
      String(processStatus?.status || '').toLowerCase() === 'running'
      || ['reading', 'parsing', 'processing', 'matching', 'ai_rerank'].includes(
        String(processStatus?.step || '').toLowerCase()
      )
    );
    const shouldTick = activeProcesses.length > 0 || hasRunningCurrentProcess;
    if (!shouldTick) return undefined;
    const timer = window.setInterval(() => {
      setProgressClockMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [activeProcesses.length, processStatus?.status, processStatus?.step]);

  useEffect(() => {
    const panel = aiStreamPanelRef.current;
    if (!panel) return;
    panel.scrollTop = panel.scrollHeight;
  }, [aiStreamRenderedText]);

  useEffect(() => {
    const requestKey = String(requestId || '');
    if (aiStreamRequestRef.current !== requestKey) {
      aiStreamRequestRef.current = requestKey;
      aiStreamFinalizedRef.current = false;
      aiStreamIngressRateRef.current = 0;
      aiStreamIngressSampleRef.current = { len: 0, at: Date.now() };
      aiStreamTargetRef.current = '';
      aiStreamRenderedRef.current = '';
      setAiStreamRenderedText('');
      if (aiStreamTimerRef.current) {
        clearInterval(aiStreamTimerRef.current);
        aiStreamTimerRef.current = null;
      }
    }

    const targetText = String(processStatus?.ai_stream_markdown || '');
    const stepValue = String(processStatus?.step || '').toLowerCase();
    const streamPhaseClosed = STREAM_PANEL_HIDDEN_STEPS.has(stepValue);
    const isDone = Boolean(processStatus?.ai_stream_done) || streamPhaseClosed;

    const mergeStreamText = (currentText, incomingText) => {
      const current = String(currentText || '');
      const incoming = String(incomingText || '');
      const norm = (value) => String(value || '').replace(/\s+/g, ' ').trim();

      if (aiStreamFinalizedRef.current) {
        if (!incoming) return current;
        if (incoming.startsWith(current)) return incoming;
        if (current.startsWith(incoming)) return current;
        if (current.includes(incoming)) return current;
        if (norm(current) === norm(incoming) || norm(current).includes(norm(incoming))) return current;
        return current;
      }

      if (!current) return incoming;
      if (!incoming) return current;
      if (incoming.startsWith(current)) return incoming;
      if (current.startsWith(incoming)) return current;
      if (current.includes(incoming)) return current;
      if (norm(current) === norm(incoming)) return current;
      const sharedPrefix = Math.min(32, current.length, incoming.length);
      if (sharedPrefix >= 12 && current.slice(0, sharedPrefix) === incoming.slice(0, sharedPrefix)) {
        return incoming.length >= current.length ? incoming : current;
      }

      const maxOverlap = Math.min(current.length, incoming.length);
      for (let overlap = maxOverlap; overlap >= 8; overlap -= 1) {
        if (current.slice(current.length - overlap) === incoming.slice(0, overlap)) {
          return current + incoming.slice(overlap);
        }
      }
      return incoming;
    };

    const mergedTarget = mergeStreamText(aiStreamRenderedRef.current, targetText);
    aiStreamTargetRef.current = mergedTarget;
    aiStreamDoneRef.current = isDone;
    aiStreamFinalizedRef.current = Boolean(isDone && mergedTarget);
    {
      const now = Date.now();
      const prev = aiStreamIngressSampleRef.current || { len: 0, at: now };
      const prevLen = Number(prev.len || 0);
      const prevAt = Number(prev.at || 0);
      const deltaLen = Math.max(0, mergedTarget.length - prevLen);
      const deltaMs = Math.max(1, now - prevAt);
      // Smooth chars/sec so typewriter keeps up with incoming stream speed.
      const instantRate = (deltaLen * 1000) / deltaMs;
      aiStreamIngressRateRef.current = (aiStreamIngressRateRef.current * 0.7) + (instantRate * 0.3);
      aiStreamIngressSampleRef.current = { len: mergedTarget.length, at: now };
    }

    if (!mergedTarget) {
      aiStreamRenderedRef.current = '';
      setAiStreamRenderedText('');
      aiStreamFinalizedRef.current = false;
      if (aiStreamTimerRef.current) {
        clearInterval(aiStreamTimerRef.current);
        aiStreamTimerRef.current = null;
      }
      return undefined;
    }

    if (!mergedTarget.startsWith(aiStreamRenderedRef.current)) {
      aiStreamRenderedRef.current = mergedTarget;
      setAiStreamRenderedText(mergedTarget);
    }

    if (aiStreamFinalizedRef.current) {
      aiStreamRenderedRef.current = mergedTarget;
      setAiStreamRenderedText(mergedTarget);
      if (aiStreamTimerRef.current) {
        clearInterval(aiStreamTimerRef.current);
        aiStreamTimerRef.current = null;
      }
      return undefined;
    }

    if (aiStreamTimerRef.current) return undefined;

    aiStreamTimerRef.current = setInterval(() => {
      const target = aiStreamTargetRef.current;
      const current = aiStreamRenderedRef.current;
      if (current.length >= target.length) {
        if (aiStreamDoneRef.current && aiStreamTimerRef.current) {
          clearInterval(aiStreamTimerRef.current);
          aiStreamTimerRef.current = null;
        }
        return;
      }
      const remaining = target.length - current.length;
      if (aiStreamDoneRef.current) {
        aiStreamRenderedRef.current = target;
        setAiStreamRenderedText(target);
        if (aiStreamTimerRef.current) {
          clearInterval(aiStreamTimerRef.current);
          aiStreamTimerRef.current = null;
        }
        return;
      }
      const ingressRate = Math.max(0, Number(aiStreamIngressRateRef.current || 0)); // chars / sec
      const ingressPerTick = Math.ceil((ingressRate * 42) / 1000);
      const backlogStep =
        remaining > 2200 ? Math.ceil(remaining / 28)
          : remaining > 1200 ? Math.ceil(remaining / 40)
            : remaining > 500 ? Math.ceil(remaining / 70)
              : remaining > 180 ? Math.ceil(remaining / 110)
                : 1;
      const stepSize = Math.max(1, Math.min(64, Math.max(ingressPerTick + 1, backlogStep)));
      const next = target.slice(0, current.length + stepSize);
      aiStreamRenderedRef.current = next;
      setAiStreamRenderedText(next);
    }, 42);
    return undefined;
  }, [processStatus?.ai_stream_markdown, processStatus?.ai_stream_done, processStatus?.step, requestId]);

  useEffect(() => () => {
    if (aiStreamTimerRef.current) {
      clearInterval(aiStreamTimerRef.current);
      aiStreamTimerRef.current = null;
    }
  }, []);

  const loadStatusUsers = useCallback(async () => {
    setStatusUserLoading(true);
    try {
      const resp = await listOrderGenieRequestUsers();
      const items = Array.isArray(resp?.items) ? resp.items : [];
      setStatusUserOptions(items);
    } catch (err) {
      // ignore
    } finally {
      setStatusUserLoading(false);
    }
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((note) => !note.read).length,
    [notifications]
  );

  function applyDefaultSelections(sourceItems, sourceMatches, setSelectionState) {
    setSelectionState((prev) => {
      const { next, changed } = reconcileSelectionMap(sourceItems, sourceMatches, prev);
      return changed ? next : prev;
    });
  }

  const mergeCartSelectionsIntoMatches = useCallback((sourceMatches = [], cartItems = []) => {
    const baseMatches = Array.isArray(sourceMatches) ? sourceMatches.map((entry) => ({ ...entry })) : [];
    const cartRows = Array.isArray(cartItems) ? cartItems : [];
    if (!cartRows.length) return baseMatches;

    const latestByItem = {};
    cartRows.forEach((row) => {
      const itemKey = String(row?.item_id || '').trim();
      if (!itemKey || latestByItem[itemKey]) return;
      latestByItem[itemKey] = row;
    });

    const grouped = {};
    baseMatches.forEach((entry) => {
      const itemKey = String(entry?.item_id ?? entry?.itemId ?? '').trim();
      if (!itemKey) return;
      grouped[itemKey] = grouped[itemKey] || [];
      grouped[itemKey].push(entry);
    });

    Object.entries(latestByItem).forEach(([itemKey, row]) => {
      const selectedCode = String(row?.product_code || '').trim();
      if (!selectedCode) return;
      let meta = row?.meta_json;
      if (typeof meta === 'string') {
        try {
          meta = JSON.parse(meta);
        } catch (err) {
          meta = {};
        }
      }
      if (!meta || typeof meta !== 'object' || Array.isArray(meta)) meta = {};
      const manualReason = String(meta?.manual_override_reason || '').trim();
      const manualMatchPayload =
        meta?.manual_match && typeof meta.manual_match === 'object' && !Array.isArray(meta.manual_match)
          ? meta.manual_match
          : {};
      const rowMatches = Array.isArray(grouped[itemKey]) ? grouped[itemKey] : [];
      const selectedCodeNorm = normalizeProductCodeKey(selectedCode);
      let updated = false;
      const nextRows = rowMatches.map((entry) => {
        const codeNorm = normalizeProductCodeKey(entry?.product_code);
        if (codeNorm !== selectedCodeNorm) return entry;
        updated = true;
        return {
          ...entry,
          rank: 1,
          reasoning: manualReason || String(entry?.reasoning || '').trim(),
          reason: 'Manual search replacement',
          manual_override: true,
          manual_override_reason: manualReason,
          ai_ranked: false,
          match: entry?.match || manualMatchPayload?.match || {},
        };
      });
      if (!updated) {
        nextRows.push({
          item_id: itemKey,
          product_code: selectedCode,
          score: Number(manualMatchPayload?.score || 0),
          rank: 1,
          reasoning: manualReason,
          reason: 'Manual search replacement',
          manual_override: true,
          manual_override_reason: manualReason,
          ai_ranked: false,
          match:
            (manualMatchPayload?.match && typeof manualMatchPayload.match === 'object')
              ? manualMatchPayload.match
              : {},
        });
      }
      grouped[itemKey] = nextRows
        .slice()
        .sort((left, right) => {
          const leftNorm = normalizeProductCodeKey(left?.product_code);
          const rightNorm = normalizeProductCodeKey(right?.product_code);
          const leftPinned = leftNorm === selectedCodeNorm ? 1 : 0;
          const rightPinned = rightNorm === selectedCodeNorm ? 1 : 0;
          if (leftPinned !== rightPinned) return rightPinned - leftPinned;
          const leftRank = Number(left?.rank || 0);
          const rightRank = Number(right?.rank || 0);
          if (Number.isFinite(leftRank) && Number.isFinite(rightRank) && leftRank !== rightRank) {
            return leftRank - rightRank;
          }
          return 0;
        })
        .map((entry, idx) => ({ ...entry, rank: idx + 1 }));
    });

    return Object.values(grouped).flat();
  }, []);

  const rerankDebugEvents = useMemo(() => {
    const lines = Array.isArray(runDetailsData?.ai_events) ? runDetailsData.ai_events : [];
    const requestByKey = {};
    const responseByKey = {};
    const parseJsonSuffix = (line, token) => {
      const idx = String(line || '').indexOf(token);
      if (idx < 0) return null;
      const suffix = String(line || '').slice(idx + token.length).trim();
      if (!suffix) return null;
      try {
        return JSON.parse(suffix);
      } catch (err) {
        return { raw: suffix };
      }
    };
    const pickKey = (payload, fallback) => {
      if (payload && typeof payload === 'object') {
        const engine = String(payload.engine || payload.agent || '').trim();
        const model = String(payload.model || '').trim();
        const url = String(payload.url || '').trim();
        const candidateCount = String(payload.candidate_count ?? '').trim();
        if (engine || model || url || candidateCount) {
          return `${engine}|${model}|${url}|${candidateCount}`;
        }
      }
      return fallback;
    };
    lines.forEach((line, idx) => {
      const requestPayload = parseJsonSuffix(line, 'request=');
      if (line.includes('ai_rank_request_debug') && requestPayload) {
        const key = pickKey(requestPayload, `idx-${idx}`);
        requestByKey[key] = requestPayload;
      }
      const responsePayload = parseJsonSuffix(line, 'response=');
      if (line.includes('ai_rank_response_debug') && responsePayload) {
        const key = pickKey(responsePayload, `idx-${idx}`);
        responseByKey[key] = responsePayload;
      }
    });
    const keys = Array.from(new Set([...Object.keys(requestByKey), ...Object.keys(responseByKey)]));
    return keys.map((key, idx) => {
      const req = requestByKey[key] || {};
      const res = responseByKey[key] || {};
      return {
        id: `${key}-${idx}`,
        engine: req.engine || res.engine || 'auto',
        model: req.model || res.model || 'auto',
        url: req.url || res.url || 'N/A',
        request: req,
        response: res,
      };
    });
  }, [runDetailsData]);

  useEffect(() => {
    setSelectedByItem((prev) => {
      const { next, changed } = reconcileSelectionMap(items, matches, prev);
      return changed ? next : prev;
    });
  }, [items, matches]);

  // Prefetch prices only for the top/selected match of each currently-visible line —
  // never the full candidate list — across both the single active request and every
  // open quote tab. Prices for the other options load lazily when a row is expanded.
  useEffect(() => {
    const itemMatchSources = [
      { items, matches },
      ...(quoteTabs || []).map((tab) => ({ items: tab.items || [], matches: tab.matches || [] })),
    ];
    const seenItemKeys = new Set();
    const codes = [];
    itemMatchSources.forEach(({ items: sourceItems, matches: sourceMatches }) => {
      const matchesByItemKey = {};
      (sourceMatches || []).forEach((m) => {
        const key = String(m?.item_id ?? '');
        if (!key) return;
        (matchesByItemKey[key] = matchesByItemKey[key] || []).push(m);
      });
      (sourceItems || []).forEach((item) => {
        const key = String(item?.id || '');
        if (!key || seenItemKeys.has(key)) return;
        seenItemKeys.add(key);
        const itemMatches = matchesByItemKey[key] || [];
        const code = selectedByItem[key] || itemMatches[0]?.product_code;
        if (code) codes.push(code);
      });
    });
    loadErpPricesForCodes(codes);
  }, [items, matches, quoteTabs, selectedByItem, loadErpPricesForCodes]);

  useEffect(() => {
    setQuoteSelections((prev) => {
      const next = { ...prev };
      let changed = false;
      (quoteTabs || []).forEach((tab) => {
        const tabId = String(tab?.id || '');
        if (!tabId) return;
        const tabItems = Array.isArray(tab?.items) ? tab.items : [];
        const tabMatches = Array.isArray(tab?.matches) ? tab.matches : [];
        const currentMap = prev[tabId] || {};
        const reconciled = reconcileSelectionMap(tabItems, tabMatches, currentMap);
        if (reconciled.changed) {
          next[tabId] = reconciled.next;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [quoteTabs]);

  const setOverlay = (message, detail = '') => {
    setOverlayActive(true);
    setOverlayMessage(message);
    setOverlayDetail(detail);
  };

  const overlayIsCompleted = useMemo(() => {
    const message = String(overlayMessage || '').toLowerCase();
    const detail = String(overlayDetail || '').toLowerCase();
    return (
      message.includes('completed: quote ready for review')
      || message.includes('completed: request ready for review')
      || message.includes('quote ready for review')
      || message.includes('request ready for review')
      || detail.includes('review and choose your matches')
    );
  }, [overlayDetail, overlayMessage]);
  const showCompletedTopBar = overlayActive && overlayIsCompleted;

  useEffect(() => {
    if (!overlayActive || !overlayIsCompleted) return undefined;
    const timer = setTimeout(() => setOverlayActive(false), 1800);
    return () => clearTimeout(timer);
  }, [overlayActive, overlayIsCompleted]);

  const formatTimestamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
  };

  const formatDebugValue = useCallback((value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'N/A';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (err) {
        return String(value);
      }
    }
    return String(value);
  }, []);

  const decodeHtmlEntities = useCallback((value) => {
    const text = String(value || '');
    if (!text) return '';
    if (typeof window === 'undefined') return text;
    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    } catch (err) {
      return text;
    }
  }, []);

  const formatDebugStep = useCallback((stepEntry) => {
    const stage = String(stepEntry?.stage || '').trim();
    const flowName = String(stepEntry?.flow_name || '').trim();
    const status = String(stepEntry?.status || '').trim();
    const decision = String(stepEntry?.decision || '').trim();

    const statusMap = {
      skipped_disabled: 'Skipped (route disabled)',
      skipped_condition: 'Skipped (condition not met)',
      skipped_no_flow: 'Skipped (no flow configured)',
      skipped_empty_query: 'Skipped (empty query)',
      no_match: 'No matches',
      matched: 'Matched',
    };
    const decisionMap = {
      continue: 'Continue to next step',
      stop: 'Stop workflow',
    };

    const compactValue = (value) => {
      if (value === null || value === undefined || value === '') return '';
      let text = '';
      if (Array.isArray(value)) {
        text = value.length ? value.join(', ') : '';
      } else if (typeof value === 'object') {
        try {
          text = JSON.stringify(value);
        } catch (err) {
          text = String(value);
        }
      } else {
        text = String(value);
      }
      return text.length > 180 ? `${text.slice(0, 177)}...` : text;
    };

    let title = 'Workflow step';
    if (stage === 'workflow_start') title = 'Workflow started';
    else if (stage === 'workflow_no_match') title = 'Workflow completed with no matches';
    else if (stage === 'workflow_not_configured') title = 'Workflow is not configured';
    else if (stage === 'workflow_result') title = 'Workflow result';
    else if (stage === 'workflow_plan') title = 'Workflow plan loaded';
    else if (stage) title = stage.replace(/_/g, ' ');

    const details = [];
    if (stepEntry?.at) details.push(`At: ${compactValue(stepEntry.at)}`);
    if (stepEntry?.route_name) details.push(`Route: ${compactValue(stepEntry.route_name)}`);
    if (flowName) details.push(`Flow: ${flowName}`);
    if (stepEntry?.query) details.push(`Query: ${compactValue(stepEntry.query)}`);
    if (typeof stepEntry?.matched_count === 'number') details.push(`Matched: ${stepEntry.matched_count}`);
    if (typeof stepEntry?.ranked_count === 'number') details.push(`Ranked: ${stepEntry.ranked_count}`);
    if (status) details.push(statusMap[status] || `Status: ${status}`);
    if (decision) details.push(decisionMap[decision] || `Decision: ${decision}`);

    const hiddenKeys = new Set([
      'stage',
      'at',
      'route_name',
      'flow_name',
      'query',
      'matched_count',
      'ranked_count',
      'status',
      'decision',
    ]);
    Object.entries(stepEntry || {}).forEach(([key, value]) => {
      if (hiddenKeys.has(key)) return;
      if (value === null || value === undefined || value === '') return;
      const label = key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
      const formatted = compactValue(value);
      if (!formatted) return;
      details.push(`${label.charAt(0).toUpperCase()}${label.slice(1)}: ${formatted}`);
    });

    return details.length ? `${title} - ${details.join(' | ')}` : title;
  }, []);

  const normalizeMatchAttributes = useCallback((rawAttributes) => {
    let parsed = rawAttributes;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (err) {
        return [];
      }
    }

    if (Array.isArray(parsed)) {
      if (!parsed.length) return [];
      const [first] = parsed;
      if (typeof first === 'string') {
        try {
          parsed = JSON.parse(first);
        } catch (err) {
          return [];
        }
      } else if (first && typeof first === 'object' && !Array.isArray(first)) {
        parsed = first;
      } else {
        return [];
      }
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return [];
    }

    return Object.entries(parsed).filter(([key, value]) => key && value !== null && value !== '');
  }, []);

  const getProjectSummary = useCallback((sourceSummary = null) => {
    const summary =
      sourceSummary && typeof sourceSummary === 'object' && !Array.isArray(sourceSummary)
        ? sourceSummary
        : {};
    return {
      projectName: String(summary.projectName || summary.project_name || summary.project || '').trim(),
      projectDescription: String(summary.projectDescription || summary.project_description || '').trim(),
      orderNotes: String(summary.orderNotes || summary.order_notes || '').trim(),
      shippingInstructions: String(summary.shippingInstructions || summary.shipping_instructions || '').trim(),
      poNumber: String(summary.poNumber || summary.po_number || summary.purchase_order_number || '').trim(),
      shipVia: String(summary.shipVia || summary.ship_via || '').trim(),
      shipToAddress: String(summary.shipToAddress || summary.ship_to_address || '').trim(),
      requiredDate: String(summary.requiredDate || summary.required_date || '').trim(),
    };
  }, []);

  const mergeRequestSummary = useCallback((baseSummary = null, nextSummary = null) => {
    const baseOk = baseSummary && typeof baseSummary === 'object' && !Array.isArray(baseSummary);
    const nextOk = nextSummary && typeof nextSummary === 'object' && !Array.isArray(nextSummary);
    if (!baseOk && !nextOk) return null;
    if (!baseOk) return nextSummary;
    if (!nextOk) return baseSummary;
    const merged = { ...baseSummary };
    Object.entries(nextSummary).forEach(([key, value]) => {
      const hasIncomingValue =
        value !== null && value !== undefined && (!(typeof value === 'string') || value.trim() !== '');
      if (hasIncomingValue || !(key in merged)) {
        merged[key] = value;
      }
    });
    return merged;
  }, []);

  const extractCustomerIdErpFromSummary = useCallback((summary = null) => {
    if (!summary || typeof summary !== 'object' || Array.isArray(summary)) return '';
    return String(
      summary.customer_id_erp
      || summary.customerIdErp
      || summary.customer_id
      || summary.customerId
      || ''
    ).trim();
  }, []);

  const getAiEnhancedDescription = useCallback((item) => {
    const parsed = item?.parsed_json;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return '';
    return String(
      parsed.aiEnhancedDesc || parsed.ai_enhanced_desc || parsed.enhancedDescription || ''
    ).trim();
  }, []);

  const buildRowSearchKey = useCallback((sourceRequestId, itemId) => {
    return `${String(sourceRequestId || 'new')}:${String(itemId || '')}`;
  }, []);

  const supportsAdvancedSyntax = useCallback((query) => {
    const text = String(query || '').trim();
    if (!text) return false;
    return /[*?()[\]{}:^~"]|(?:\bAND\b|\bOR\b|\bNOT\b)|\sTO\s/i.test(text);
  }, []);

  const normalizeRequestUserId = useCallback((...values) => {
    for (const value of values) {
      const normalized = String(value || '').trim();
      if (!normalized) continue;
      if (normalized.toLowerCase() === 'all') continue;
      return normalized;
    }
    return '';
  }, []);

  const runManualRowSearch = useCallback(
    async (sourceRequestId, item, options = {}) => {
      const itemKey = String(item?.id || '');
      if (!itemKey) return;
      const rowKey = buildRowSearchKey(sourceRequestId, itemKey);
      const fallbackQuery = String(
        item?.normalized_parts?.part_number ||
          item?.normalized_parts?.manufacturer_part_number ||
          item?.normalized_parts?.description ||
          ''
      ).trim();
      const query = String(manualSearchByRow[rowKey] || fallbackQuery || '').trim();
      if (!query) {
        setManualSearchErrorByRow((prev) => ({ ...prev, [rowKey]: 'Enter search text.' }));
        return;
      }
      const pageSize = 8;
      const prevMeta = manualSearchMetaByRow[rowKey] || {};
      const canAppend = options?.append === true && String(prevMeta?.query || '').trim() === query;
      const targetPage = canAppend ? Math.max(2, Number(prevMeta?.page || 1) + 1) : 1;

      setManualSearchErrorByRow((prev) => ({ ...prev, [rowKey]: '' }));
      setManualSearchLoadingByRow((prev) => ({ ...prev, [rowKey]: true }));
      try {
        const headers = getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/api/v1/orderx/search/products`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            query,
            query_type: 'Direct Product Search',
            query_is_escaped: supportsAdvancedSyntax(query),
            page: targetPage,
            page_size: pageSize,
            include_attribute_facets: false,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || 'Unable to search products');
        }
        const docs = Array.isArray(data?.docs) ? data.docs : [];
        const manualMatches = docs
          .map((doc, idx) => {
            const rawProductCode =
              doc?.productCode
              ?? doc?.product_code
              ?? doc?.productcode
              ?? doc?.code
              ?? '';
            const productCode = Array.isArray(rawProductCode)
              ? String(rawProductCode[0] || '').trim()
              : String(rawProductCode || '').trim();
            if (!productCode) return null;
            return {
              item_id: itemKey,
              product_code: productCode,
              score: Number(doc?.score || 0),
              rank: idx + 1,
              reasoning: 'Manual search result',
              match: {
                name: doc?.name || '',
                description: doc?.description || '',
                image_url: doc?.imageUrl || doc?.image_url || '',
                manufacturer_name: doc?.manufacturerName || doc?.manufacturer_name || '',
                manufacturer_part_number: doc?.manufacturerPartNumber || doc?.manufacturer_part_number || '',
                attributesJson: doc?.attributesJson || doc?.attributes_json || doc?.attributes || '',
              },
            };
          })
          .filter(Boolean);
        const totalResultsRaw = Number(data?.total ?? data?.numFound ?? docs.length);
        const totalResults = Number.isFinite(totalResultsRaw) ? Math.max(0, totalResultsRaw) : docs.length;
        const mergeUniqueByProductCode = (baseList = [], nextList = []) => {
          const seen = new Set(
            (baseList || [])
              .map((entry) => String(entry?.product_code || '').trim())
              .filter(Boolean)
          );
          const merged = [...(baseList || [])];
          (nextList || []).forEach((entry) => {
            const code = String(entry?.product_code || '').trim();
            if (!code || seen.has(code)) return;
            seen.add(code);
            merged.push(entry);
          });
          return merged;
        };
        const loadedSoFar = canAppend
          ? Math.min(totalResults || Number.MAX_SAFE_INTEGER, Number(prevMeta?.loaded || 0) + manualMatches.length)
          : manualMatches.length;
        setManualSearchMetaByRow((prev) => ({
          ...prev,
          [rowKey]: {
            query,
            page: targetPage,
            pageSize,
            queryType: 'default',
            loaded: loadedSoFar,
            total: totalResults,
            hasMore: loadedSoFar < totalResults,
          },
        }));

        const sourceRequestKey = String(sourceRequestId || '').trim();
        const isCurrentRequestRow = sourceRequestKey && sourceRequestKey === String(requestId || '');
        const existingRowMatches = (
          isCurrentRequestRow
            ? (Array.isArray(matches) ? matches : [])
            : (
              (quoteTabs || []).find((tab) => String(tab.id) === sourceRequestKey)?.matches || []
            )
        ).filter((entry) => String(entry.item_id ?? entry.itemId ?? '') === itemKey);
        const activeFilterForRequest = String(
          reviewMatchFilterByRequest[String(sourceRequestId || 'new')] || 'all'
        );
        const isBaseAiAnalyzed = existingRowMatches.some(
          (entry) => Boolean(entry?.ai_ranked) || Boolean(entry?.ai_analyzed)
        );
        const isBaseAiLowConfidence = existingRowMatches.some(
          (entry) => Boolean(entry?.ai_low_confidence)
            || (Boolean(entry?.ai_analyzed) && !Boolean(entry?.ai_ranked))
        );
        const baseCategory = isBaseAiAnalyzed && !isBaseAiLowConfidence
          ? 'ai_reranked'
          : isBaseAiLowConfidence
            ? 'ai_low_confidence'
          : existingRowMatches.length === 0
            ? 'no_matches'
            : existingRowMatches.length === 1
              ? 'direct'
              : 'all';
        const lockedCategory = ['direct', 'ai_reranked', 'ai_low_confidence', 'no_matches'].includes(activeFilterForRequest)
          ? activeFilterForRequest
          : baseCategory;
        setManualSearchCategoryByRow((prev) => ({ ...prev, [rowKey]: lockedCategory }));
        setManualSearchSnapshotByRow((prev) => {
          const selectedCode = isCurrentRequestRow
            ? String(selectedByItem?.[itemKey] || '')
            : String(quoteSelections?.[sourceRequestKey]?.[itemKey] || '');
          if (canAppend && Object.prototype.hasOwnProperty.call(prev, rowKey)) return prev;
          return {
            ...prev,
            [rowKey]: {
              matches: existingRowMatches,
              selectedCode,
            },
          };
        });

        setManualSearchResultsByRow((prev) => {
          const existing = Array.isArray(prev[rowKey]) ? prev[rowKey] : [];
          const combined = canAppend
            ? mergeUniqueByProductCode(existing, manualMatches)
            : manualMatches;
          return { ...prev, [rowKey]: combined };
        });
        setManualSearchViewByRow((prev) => ({ ...prev, [rowKey]: 'search' }));
        if (!canAppend) {
          setManualSearchSelectedByRow((prev) => ({ ...prev, [rowKey]: '' }));
        }

        if (!manualMatches.length && !canAppend) {
          setManualSearchErrorByRow((prev) => ({ ...prev, [rowKey]: 'No products found.' }));
        } else if (!manualMatches.length && canAppend) {
          setManualSearchErrorByRow((prev) => ({ ...prev, [rowKey]: 'No more products to load.' }));
        }
      } catch (err) {
        setManualSearchErrorByRow((prev) => ({ ...prev, [rowKey]: err.message || 'Search failed.' }));
      } finally {
        setManualSearchLoadingByRow((prev) => ({ ...prev, [rowKey]: false }));
      }
    },
    [
      buildRowSearchKey,
      getAuthHeaders,
      manualSearchByRow,
      manualSearchMetaByRow,
      matches,
      quoteTabs,
      quoteSelections,
      requestId,
      selectedByItem,
      reviewMatchFilterByRequest,
      supportsAdvancedSyntax,
    ]
  );

  const clearManualRowSearch = useCallback((sourceRequestId, item) => {
    const itemKey = String(item?.id || '');
    if (!itemKey) return;
    const rowKey = buildRowSearchKey(sourceRequestId, itemKey);

    setManualSearchByRow((prev) => ({ ...prev, [rowKey]: '' }));
    setManualSearchQueryModeByRow((prev) => ({ ...prev, [rowKey]: 'default' }));
    setManualSearchErrorByRow((prev) => ({ ...prev, [rowKey]: '' }));
    setManualSearchLoadingByRow((prev) => ({ ...prev, [rowKey]: false }));
    setManualSearchResultsByRow((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, rowKey)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setManualSearchViewByRow((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, rowKey)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setManualSearchSelectedByRow((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, rowKey)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setManualSearchMetaByRow((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setManualSearchCategoryByRow((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, rowKey)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, [buildRowSearchKey]);

  const applyManualSearchSelection = useCallback(async (payload, reasonText) => {
    const rowKey = String(payload?.rowKey || '').trim();
    const itemKey = String(payload?.itemKey || '').trim();
    const sourceRequestKey = String(payload?.sourceRequestId || '').trim();
    const sourceUserKey = String(payload?.sourceUserId || '').trim();
    const productCode = String(payload?.productCode || '').trim();
    const reason = String(reasonText || '').trim();
    if (!rowKey || !itemKey || !sourceRequestKey || !productCode || !reason) return false;
    const rowSnapshot = manualSearchSnapshotByRow[rowKey] || {};
    const candidates = Array.isArray(manualSearchResultsByRow[rowKey]) ? manualSearchResultsByRow[rowKey] : [];
    const selectedManualMatch = candidates.find(
      (entry) => normalizeProductCodeKey(entry?.product_code) === normalizeProductCodeKey(productCode)
    ) || (
      payload?.selectedMatchPayload
      && typeof payload.selectedMatchPayload === 'object'
      ? payload.selectedMatchPayload
      : null
    );
    if (!selectedManualMatch) return false;

    const upsertRowSelection = (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      const remaining = list.filter((entry) => String(entry.item_id ?? entry.itemId ?? '') !== itemKey);
      const rowEntries = list.filter((entry) => String(entry.item_id ?? entry.itemId ?? '') === itemKey);
      const targetKey = normalizeProductCodeKey(productCode);
      const existing = rowEntries.find((entry) => normalizeProductCodeKey(entry?.product_code) === targetKey);
      const appliedMatch = {
        ...(selectedManualMatch || {}),
        item_id: itemKey,
        product_code: productCode,
        rank: 1,
        score: selectedManualMatch?.score ?? existing?.score ?? 0,
        ai_ranked: false,
        reason: 'Manual search replacement',
        reasoning: reason,
        manual_override: true,
        manual_override_reason: reason,
      };
      const replaced = rowEntries.map((entry) => (
        normalizeProductCodeKey(entry?.product_code) === targetKey
          ? { ...entry, ...appliedMatch }
          : entry
      ));
      const nextRowEntriesRaw = existing ? replaced : [...rowEntries, appliedMatch];
      const nextRowEntries = nextRowEntriesRaw
        .slice()
        .sort((left, right) => {
          const leftKey = normalizeProductCodeKey(left?.product_code);
          const rightKey = normalizeProductCodeKey(right?.product_code);
          const leftIsApplied = leftKey === targetKey ? 1 : 0;
          const rightIsApplied = rightKey === targetKey ? 1 : 0;
          if (leftIsApplied !== rightIsApplied) return rightIsApplied - leftIsApplied;
          const leftRank = Number(left?.rank || 0);
          const rightRank = Number(right?.rank || 0);
          if (Number.isFinite(leftRank) && Number.isFinite(rightRank) && leftRank !== rightRank) {
            return leftRank - rightRank;
          }
          return 0;
        })
        .map((entry, idx) => ({ ...entry, rank: idx + 1 }));
      return [...remaining, ...nextRowEntries];
    };

    const nextSnapshotMatches = upsertRowSelection(rowSnapshot?.matches || []);

    setQuoteTabs((prev) =>
      prev.map((tab) => (
        String(tab.id) === sourceRequestKey
          ? { ...tab, matches: upsertRowSelection(tab.matches) }
          : tab
      ))
    );

    if (String(requestId || '') === sourceRequestKey) {
      setMatches((prev) => upsertRowSelection(prev));
    }

    setSelectedByItem((prev) => ({ ...prev, [itemKey]: productCode }));

    setQuoteSelections((prev) => ({
      ...prev,
      [sourceRequestKey]: {
        ...(prev[sourceRequestKey] || {}),
        [itemKey]: productCode,
      },
    }));
    setManualSearchSnapshotByRow((prev) => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        matches: nextSnapshotMatches,
        selectedCode: productCode,
      },
    }));
    setManualSearchSelectedByRow((prev) => ({ ...prev, [rowKey]: productCode }));
    setManualSearchApplyReasonByRow((prev) => ({ ...prev, [rowKey]: reason }));
    setManualSearchViewByRow((prev) => ({ ...prev, [rowKey]: 'base' }));
    setManualSearchErrorByRow((prev) => ({ ...prev, [rowKey]: '' }));
    try {
      if (sourceUserKey && sourceRequestKey) {
        await updateOrderGenieCart(sourceRequestKey, {
          user_id: sourceUserKey,
          action: 'add',
          items: [
            {
              item_id: itemKey,
              product_code: productCode,
              quantity: 1,
              meta: {
                manual_override: true,
                manual_override_reason: reason,
                manual_match: selectedManualMatch,
              },
            },
          ],
        });
      }
    } catch (err) {
      // Keep UI state; persistence warning is surfaced in row error area.
      setManualSearchErrorByRow((prev) => ({
        ...prev,
        [rowKey]: 'Selection updated locally, but failed to persist to server. Try again.',
      }));
    }
    return true;
  }, [manualSearchResultsByRow, manualSearchSnapshotByRow, requestId]);

  const runRowLevelRerunMatch = useCallback(
    async (sourceRequestId, sourceUserId, item) => {
      const itemKey = String(item?.id || '');
      if (!sourceRequestId || !sourceUserId || !itemKey) return;
      const rowKey = buildRowSearchKey(sourceRequestId, itemKey);
      const notify = (title, detail = '') => {
        setNotifications((prev) => [
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            title,
            detail,
            createdAt: new Date(),
            read: false,
          },
          ...prev,
        ]);
      };
      setRowRerunLoadingByRow((prev) => ({ ...prev, [rowKey]: true }));
      setRowRerunStatusByRow((prev) => ({
        ...prev,
        [rowKey]: {
          message: 'Row rerun started',
          detail: 'Streaming updates every 5 seconds',
          elapsed: 0,
        },
      }));
      let rerunCompleted = false;
      const pollRowProgress = async () => {
        let elapsedSeconds = 0;
        while (!rerunCompleted) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 5000));
          if (rerunCompleted) break;
          elapsedSeconds += 5;
          let statusMessage = 'Row rerun in progress';
          let statusDetail = `Working in background... ${elapsedSeconds}s`;
          try {
            // eslint-disable-next-line no-await-in-loop
            const statusResp = await getOrderGenieProcessStatus(sourceRequestId, sourceUserId);
            const status = statusResp?.status || null;
            if (status?.message) {
              statusMessage = status.message;
            }
            if (status?.detail) {
              statusDetail = `${status.detail} | ${elapsedSeconds}s elapsed`;
            }
            if (status && String(sourceRequestId) === String(requestId || '')) {
              setProcessStatus((prev) => ({
                ...(prev || {}),
                ...status,
              }));
              if (status.message) {
                setOverlay(status.message, status.detail || '');
              }
            }
          } catch (err) {
            // Keep local heartbeat updates even if status fetch temporarily fails.
          }
          const elapsedSnapshot = elapsedSeconds;
          setRowRerunStatusByRow((prev) => ({
            ...prev,
            [rowKey]: {
              message: statusMessage,
              detail: statusDetail,
              elapsed: elapsedSnapshot,
            },
          }));
        }
      };
      const progressPromise = pollRowProgress();
      try {
        const requestSummaryFromState =
          requestSummaryById[sourceRequestId]
          || (quoteTabs || []).find((tab) => String(tab.id) === String(sourceRequestId))?.requestSummary
          || null;
        const resolvedCustomerIdErp =
          extractCustomerIdErpFromSummary(requestSummaryFromState)
          || String(customerIdAsInErp || '').trim();
        const rerunResp = await matchOrderGenieRequestItem(sourceRequestId, {
          user_id: sourceUserId,
          item_id: Number(itemKey),
          finalize: false,
          customer_id_erp: resolvedCustomerIdErp || undefined,
        });
        const rerunMatches = (Array.isArray(rerunResp?.matches) ? rerunResp.matches : [])
          .map((entry) => ({
            ...entry,
            item_id: entry?.item_id ?? entry?.itemId ?? Number(itemKey),
          }));
        const requestFilterKey = String(sourceRequestId || 'new');
        const activeFilter = String(reviewMatchFilterByRequest[requestFilterKey] || 'all');
        if (rerunMatches.length > 0 && activeFilter === 'no_matches') {
          // If rerun found matches while user is in No Matches view, switch so results stay visible.
          setReviewMatchFilterByRequest((prev) => ({ ...prev, [requestFilterKey]: 'all' }));
        }
        const refreshed = await getOrderGenieRequest(sourceRequestId, sourceUserId);
        const nextItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
        const nextMatchesRaw = Array.isArray(refreshed?.matches) ? refreshed.matches : [];
        const nextMatches = rerunMatches.length > 0
          ? [
              ...nextMatchesRaw.filter(
                (entry) => String(entry.item_id ?? entry.itemId ?? '') !== itemKey
              ),
              ...rerunMatches,
            ]
          : nextMatchesRaw;
        const nextSourceFiles = Array.isArray(refreshed?.request?.source_files)
          ? refreshed.request.source_files
          : [];
        const nextSummary =
          refreshed?.request?.summary_json &&
          typeof refreshed.request.summary_json === 'object' &&
          !Array.isArray(refreshed.request.summary_json)
            ? refreshed.request.summary_json
            : {};

        if (String(sourceRequestId) === String(requestId || '')) {
          setItems(nextItems);
          setMatches(nextMatches);
          setSourceFiles(nextSourceFiles);
          setRequestSummaryById((prev) => ({ ...prev, [sourceRequestId]: nextSummary }));
          setQuoteTabs((prev) =>
            prev.map((tab) =>
              String(tab.id) === String(sourceRequestId)
                ? {
                    ...tab,
                    items: nextItems,
                    matches: nextMatches,
                    sourceFiles: nextSourceFiles,
                    requestSummary: nextSummary,
                  }
                : tab
            )
          );
          const rerunOptions = (nextMatches || []).filter(
            (entry) => String(entry.item_id ?? entry.itemId ?? '') === itemKey
          );
          if (rerunOptions.length > 0) {
            const topRerunCode = String(rerunOptions[0]?.product_code || '').trim();
            if (topRerunCode) {
              setSelectedByItem((prev) => ({ ...prev, [itemKey]: topRerunCode }));
            }
          }
          setSelectedByItem((prev) => {
            const current = { ...prev };
            let changed = false;
            const grouped = {};
            (nextMatches || []).forEach((entry) => {
              const key = String(entry.item_id ?? entry.itemId ?? '');
              if (!key) return;
              grouped[key] = grouped[key] || [];
              grouped[key].push(entry);
            });
            (nextItems || []).forEach((row) => {
              const key = String(row.id || '');
              if (!key || current[key]) return;
              const options = grouped[key] || [];
              if (options.length) {
                current[key] = options[0].product_code;
                changed = true;
              }
            });
            return changed ? current : prev;
          });
        } else {
          setQuoteTabs((prev) =>
            prev.map((tab) =>
              String(tab.id) === String(sourceRequestId)
                ? {
                    ...tab,
                    items: nextItems,
                    matches: nextMatches,
                    sourceFiles: nextSourceFiles,
                    requestSummary: nextSummary,
                  }
                : tab
            )
          );
          const rerunOptions = (nextMatches || []).filter(
            (entry) => String(entry.item_id ?? entry.itemId ?? '') === itemKey
          );
          if (rerunOptions.length > 0) {
            const topRerunCode = String(rerunOptions[0]?.product_code || '').trim();
            if (topRerunCode) {
              setQuoteSelections((prev) => ({
                ...prev,
                [sourceRequestId]: {
                  ...(prev[sourceRequestId] || {}),
                  [itemKey]: topRerunCode,
                },
              }));
            }
          }
          setQuoteSelections((prev) => {
            const current = prev[sourceRequestId] || {};
            let changed = false;
            const next = { ...prev };
            const grouped = {};
            (nextMatches || []).forEach((entry) => {
              const key = String(entry.item_id ?? entry.itemId ?? '');
              if (!key) return;
              grouped[key] = grouped[key] || [];
              grouped[key].push(entry);
            });
            (nextItems || []).forEach((row) => {
              const key = String(row.id || '');
              if (!key || current[key]) return;
              const options = grouped[key] || [];
              if (options.length) {
                current[key] = options[0].product_code;
                changed = true;
              }
            });
            if (!changed) return prev;
            next[sourceRequestId] = { ...current };
            return next;
          });
          setRequestSummaryById((prev) => ({ ...prev, [sourceRequestId]: nextSummary }));
        }
        notify('Row match rerun completed', `Line ${itemKey} has been refreshed.`);
        setRowRerunStatusByRow((prev) => ({
          ...prev,
          [rowKey]: {
            message: 'Row rerun completed',
            detail: `Line ${itemKey} has been refreshed.`,
            elapsed: prev?.[rowKey]?.elapsed || 0,
          },
        }));
        setManualSearchMetaByRow((prev) => {
          if (!prev[rowKey]) return prev;
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
      } catch (err) {
        notify('Row match rerun failed', err.message || 'Unable to rerun this row.');
        setRowRerunStatusByRow((prev) => ({
          ...prev,
          [rowKey]: {
            message: 'Row rerun failed',
            detail: err.message || 'Unable to rerun this row.',
            elapsed: prev?.[rowKey]?.elapsed || 0,
          },
        }));
      } finally {
        rerunCompleted = true;
        await progressPromise;
        setRowRerunLoadingByRow((prev) => ({ ...prev, [rowKey]: false }));
        setTimeout(() => {
          setRowRerunStatusByRow((prev) => {
            if (!prev[rowKey]) return prev;
            const next = { ...prev };
            delete next[rowKey];
            return next;
          });
        }, 6000);
      }
    },
    [
      buildRowSearchKey,
      customerIdAsInErp,
      extractCustomerIdErpFromSummary,
      quoteTabs,
      requestId,
      requestSummaryById,
      reviewMatchFilterByRequest,
    ]
  );

  const withTimeout = (promise, ms, message) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(message || 'Request timed out')), ms);
      promise
        .then((result) => {
          clearTimeout(timer);
          resolve(result);
        })
        .catch((err) => {
          clearTimeout(timer);
          reject(err);
        });
    });

  const buildProcessSummary = (status, meta = {}) => {
    const summary = [];
    const formatSec = (value) => {
      const sec = Number(value || 0);
      if (!Number.isFinite(sec) || sec <= 0) return '';
      if (sec < 60) return `${Math.round(sec)}s`;
      const mins = Math.floor(sec / 60);
      const rem = Math.round(sec % 60);
      return `${mins}m ${rem}s`;
    };
    const itemsCount =
      status?.line_items ||
      (Array.isArray(status?.items_preview) ? status.items_preview.length : 0) ||
      meta.itemsCount ||
      0;
    const matchesCount =
      status?.matches ||
      (status?.matches_preview ? Object.values(status.matches_preview).flat().length : 0) ||
      meta.matchesCount ||
      0;
    const rerankCount = Math.max(
      Number(status?.rerank_count || 0),
      Number(meta?.rerankCount || 0)
    );
    const rerankTotal = Math.max(
      Number(status?.rerank_total || 0),
      Number(meta?.rerankTotal || 0),
      rerankCount
    );
    const statusHasLowConfidenceCount = Object.prototype.hasOwnProperty.call(status || {}, 'ai_low_confidence_count');
    const lowConfidenceCountRaw = statusHasLowConfidenceCount
      ? Number(status?.ai_low_confidence_count || 0)
      : Number(meta?.aiLowConfidenceCount || 0);
    const lowConfidenceCount = Math.max(
      0,
      Math.min(lowConfidenceCountRaw, rerankTotal > 0 ? rerankTotal : lowConfidenceCountRaw)
    );
    if (status?.reading_total) {
      summary.push(`Reading ${status.reading_page || 1}/${status.reading_total}`);
    }
    if (itemsCount || ['parsed', 'matching', 'completed'].includes(status?.step)) {
      summary.push(`Extracted ${itemsCount} line items`);
    }
    if (matchesCount || ['matching', 'completed'].includes(status?.step)) {
      summary.push(`Matched ${matchesCount} results`);
    }
    if ((status?.request_step || status?.step) === 'ai_rerank' || rerankTotal > 0) {
      summary.push(`${isV3 ? 'AI analyzed' : 'AI reranked'} ${rerankCount}/${rerankTotal || 0} rows`);
    }
    if (lowConfidenceCount > 0) {
      summary.push(`AI low confidence rows: ${lowConfidenceCount}`);
    }
    const readingTime = formatSec(status?.timing_reading_sec || status?.step_timings_sec?.reading || 0);
    const parsingTime = formatSec(status?.timing_parsing_sec || status?.step_timings_sec?.parsing || 0);
    const matchingTime = formatSec(status?.timing_matching_sec || status?.step_timings_sec?.matching || 0);
    const rerankTime = formatSec(status?.timing_rerank_sec || status?.step_timings_sec?.rerank || 0);
    const totalTime = formatSec(status?.timing_total_sec || status?.step_timings_sec?.total || 0);
    if (readingTime) summary.push(`Reading time: ${readingTime}`);
    if (parsingTime) summary.push(`Extraction time: ${parsingTime}`);
    if (matchingTime) summary.push(`Matching time: ${matchingTime}`);
    if (rerankTime) summary.push(`${isV3 ? 'AI analysis time' : 'Rerank time'}: ${rerankTime}`);
    if (totalTime) summary.push(`Total time: ${totalTime}`);
    if (isSuccessStatus(status?.request_status || status?.status)) {
      summary.push('Completed');
    } else if ((status?.request_status || status?.status) === 'failed') {
      summary.push(`Failed${status.detail ? `: ${status.detail}` : ''}`);
    }
    if (!status && meta.lastUpdateAt) {
      summary.push(`Last update ${formatTimestamp(meta.lastUpdateAt)}`);
    }
    return summary;
  };

  const renderExtractionActivity = useCallback((active = true) => (
    <div className={`order-genie-v2-extract-anim ${active ? 'is-active' : ''}`}>
      <div className="order-genie-v2-extract-anim-lines">
        <div className="order-genie-v2-extract-line w-90" />
        <div className="order-genie-v2-extract-line w-70" />
        <div className="order-genie-v2-extract-line w-95" />
        <div className="order-genie-v2-extract-line w-60" />
      </div>
      <div className="small text-muted mt-2">
        Extracting content...
      </div>
    </div>
  ), []);

  const getUnifiedRequestStatus = useCallback((targetRequestId, fallback = {}) => {
    const requestKey = String(targetRequestId || '');
    if (!requestKey) return normalizeStatusPayload(fallback || {});
    const statusFromMap = normalizeStatusPayload(requestStatusById[requestKey] || {});
    const fallbackStatus = normalizeStatusPayload(fallback || {});
    const merged = {
      ...fallbackStatus,
      ...statusFromMap,
    };
    const isCurrentRequest = requestKey === String(requestId || '');
    if (isCurrentRequest && processStatus && typeof processStatus === 'object') {
      const normalizedProcessStatus = normalizeStatusPayload(processStatus);
      Object.entries(normalizedProcessStatus).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (typeof value === 'string' && value.trim() === '') return;
        merged[key] = value;
      });
    }
    return normalizeStatusPayload(merged);
  }, [processStatus, requestId, requestStatusById]);

  const getV3TabStatusText = useCallback((targetRequestId) => {
    const tabStatusSnapshot = (quoteTabs || []).find(
      (tab) => String(tab?.id || '') === String(targetRequestId || '')
    )?.statusSnapshot || {};
    const status = getUnifiedRequestStatus(targetRequestId, tabStatusSnapshot);
    const statusValue = String(status?.request_status || status?.status || '').toLowerCase();
    const stepValue = String(status?.request_step || status?.step || '').toLowerCase();
    const rowRerunStatus = String(status?.row_rerun_status || '').toLowerCase();
    const rowRerunStep = String(status?.row_rerun_step || '').toLowerCase();
    const itemCount = Number(
      status?.line_items
      || processMetaById[String(targetRequestId || '')]?.itemsCount
      || 0
    );
    if (statusValue === 'completed' || statusValue === 'matched') return 'Ready for review';
    if (
      rowRerunStatus === 'running'
      || (rowRerunStep.startsWith('row_rerun') && !['row_rerun_completed', 'row_rerun_failed'].includes(rowRerunStep))
    ) {
      return 'Updating row matches...';
    }
    if (statusValue === 'failed') return 'Failed';
    if (statusValue === 'cancelled') return 'Stopped';
    if (stepValue === 'ai_rerank' || stepValue === 'rerank' || stepValue === 'reranking') return 'AI analysis...';
    if (stepValue === 'matching') return 'Matching products...';
    if (stepValue === 'parsed') {
      return itemCount > 0 ? `Extraction completed (${itemCount} rows)` : 'Extraction completed';
    }
    if (stepValue === 'parsing' || stepValue === 'processing' || stepValue === 'reading') return 'Extracting document...';
    if (stepValue === 'queued') return 'Queued';
    const fallbackMessage = String(status?.message || '').trim();
    return fallbackMessage || 'Processing...';
  }, [getUnifiedRequestStatus, processMetaById, quoteTabs]);

  const getRequestListDetailText = useCallback((detailValue) => {
    const detail = String(detailValue || '').trim();
    if (!detail) return '';
    const normalized = detail.toLowerCase();
    if (normalized === 'recovered from database status.') return '';
    if (normalized === 'recovered from database terminal status.') return '';
    if (normalized === 'review and choose your matches') return '';
    return detail;
  }, []);

  const renderRequestProgressCard = (status, meta = {}, options = {}) => {
    const onReview = typeof options.onReview === 'function' ? options.onReview : null;
    const showReadyTitle = options.showReadyTitle !== false;
    const statusValue = String(status?.status || '').toLowerCase();
    const stepValue = String(status?.step || '').toLowerCase();
    const extractionRowsByPage = (
      status?.extraction_rows_by_page && typeof status.extraction_rows_by_page === 'object'
        ? status.extraction_rows_by_page
        : (meta?.extractionRowsByPage && typeof meta.extractionRowsByPage === 'object' ? meta.extractionRowsByPage : {})
    );
    const rowsByPageTotal = Object.values(extractionRowsByPage || {}).reduce(
      (sum, value) => sum + (Number(value) || 0),
      0
    );
    const lineItems = Math.max(
      0,
      Number(status?.line_items || 0),
      Number(meta?.itemsCount || 0),
      Number(rowsByPageTotal || 0)
    );
    const documentPages = Number(status?.document_page_total || meta?.documentPageTotal || status?.reading_total || 0);
    const extractionTotal = Number(status?.extraction_page_total || meta?.extractionPageTotal || documentPages || 1);
    const extractionCompletedRaw = Number(status?.extraction_page_completed || meta?.extractionPageCompleted || 0);
    const extractionCompleted = Math.max(0, Math.min(extractionTotal || 1, extractionCompletedRaw));
    const extractionComplete = Boolean(status?.extraction_complete || meta?.extractionComplete
      || ['parsed', 'matching', 'ai_rerank', 'completed', 'matched'].includes(stepValue));

    const matchedCount = Number(status?.matched_count || meta?.matchedCount || 0);
    const matchedTotal = Number(status?.matched_total || meta?.matchedTotal || lineItems || 0);
    const matchProgressTotal = matchedTotal > 0 ? matchedTotal : 1;
    const matchProgressValue = Math.max(0, Math.min(matchProgressTotal, matchedCount));
    const matchComplete = matchedTotal > 0
      ? matchProgressValue >= matchedTotal
      : ['ai_rerank', 'completed', 'matched'].includes(stepValue);

    const rerankCount = Math.max(
      Number(status?.rerank_count || 0),
      Number(meta?.rerankCount || 0)
    );
    const rerankTotal = Math.max(
      Number(status?.rerank_total || 0),
      Number(meta?.rerankTotal || 0),
      rerankCount
    );
    const statusHasLowConfidenceCount = Object.prototype.hasOwnProperty.call(status || {}, 'ai_low_confidence_count');
    const lowConfidenceCountRaw = statusHasLowConfidenceCount
      ? Number(status?.ai_low_confidence_count || 0)
      : Number(meta?.aiLowConfidenceCount || 0);
    const lowConfidenceCount = Math.max(
      0,
      Math.min(lowConfidenceCountRaw, rerankTotal > 0 ? rerankTotal : lowConfidenceCountRaw)
    );
    const queueStatus = String(status?.queue_status || '').toLowerCase();
    const queuePosition = Math.max(0, Number(status?.queue_position || 0));
    const queueDepth = Math.max(0, Number(status?.queue_depth || 0));
    const queueEtaSeconds = Math.max(
      0,
      Number(status?.queue_eta_seconds || status?.eta_seconds || 0)
    );
    const queuePriorityLabel = String(status?.queue_priority_label || '').toLowerCase();
    const rerankProgressTotal = rerankTotal > 0 ? rerankTotal : 1;
    const rerankProgressValue = Math.max(0, Math.min(rerankProgressTotal, rerankCount));
    const rerankComplete = rerankTotal > 0
      ? rerankProgressValue >= rerankTotal
      : ['completed', 'matched'].includes(stepValue);

    const percent = (value, total) => {
      const denom = Math.max(1, Number(total || 0));
      const safeValue = Math.max(0, Math.min(denom, Number(value || 0)));
      return Math.round((safeValue / denom) * 100);
    };
    const toSeconds = (value) => {
      const num = Number(value || 0);
      return Number.isFinite(num) ? Math.max(0, Math.round(num)) : 0;
    };
    const timingReadingSec = toSeconds(status?.timing_reading_sec || status?.step_timings_sec?.reading);
    const timingParsingSec = toSeconds(status?.timing_parsing_sec || status?.step_timings_sec?.parsing);
    const timingMatchingSec = toSeconds(status?.timing_matching_sec || status?.step_timings_sec?.matching);
    const timingRerankSec = toSeconds(status?.timing_rerank_sec || status?.step_timings_sec?.rerank);
    const timingTotalSec = toSeconds(status?.timing_total_sec || status?.step_timings_sec?.total);

    const heartbeatElapsedSec = toSeconds(status?.heartbeat_elapsed_seconds || meta?.heartbeatElapsedSeconds || 0);
    const statusUpdatedAtMs = Number(new Date(status?.updated_at || meta?.lastUpdateAt || 0).getTime() || 0);
    const localElapsedSinceUpdateSec = statusUpdatedAtMs > 0
      ? Math.max(0, (progressClockMs - statusUpdatedAtMs) / 1000)
      : 0;
    const parsingInProgress = !extractionComplete
      && ['parsing', 'processing', 'reading'].includes(stepValue)
      && extractionTotal > 0;
    const estimatedParsingElapsedSec = parsingInProgress
      ? Math.max(
        0,
        Math.max(
          timingParsingSec,
          (heartbeatElapsedSec > 0 ? Math.max(0, heartbeatElapsedSec - timingReadingSec) : 0) + localElapsedSinceUpdateSec
        )
      )
      : timingParsingSec;
    const avgPageSec = extractionCompleted > 0
      ? Math.max(2, estimatedParsingElapsedSec / Math.max(1, extractionCompleted))
      : 8;
    const inPageFractionRaw = parsingInProgress
      ? (
        extractionCompleted > 0
          ? Math.max(0, estimatedParsingElapsedSec - (avgPageSec * extractionCompleted)) / Math.max(1, avgPageSec)
          : estimatedParsingElapsedSec / Math.max(1, avgPageSec)
      )
      : 0;
    const inPageFraction = parsingInProgress
      ? Math.max(0.03, Math.min(0.97, inPageFractionRaw))
      : 0;
    const extractionDisplayValue = extractionComplete
      ? extractionTotal
      : Math.min(Math.max(0, extractionTotal - 0.01), extractionCompleted + inPageFraction);
    const extractionPercent = extractionComplete ? 100 : percent(extractionDisplayValue, extractionTotal || 1);
    const extractionDisplayText = (
      !extractionComplete && extractionDisplayValue > extractionCompleted + 0.02
    )
      ? `${extractionDisplayValue.toFixed(1)}/${extractionTotal || 1}`
      : `${extractionCompleted}/${extractionTotal || 1}`;
    const matchingPercent = matchComplete ? 100 : percent(matchProgressValue, matchProgressTotal);
    const rerankPercent = rerankComplete ? 100 : percent(rerankProgressValue, rerankProgressTotal);
    const isDone = isSuccessStatus(statusValue);
    const showReview = isDone && onReview;
    const rowsByPageEntries = Object.entries(extractionRowsByPage || {});
    const tabularExpectedRows = Math.max(0, Number(status?.tabular_expected_rows || 0));
    const tabularExtractedRows = Math.max(0, Number(status?.tabular_extracted_rows || 0));
    const tabularSkippedRows = Math.max(0, Number(status?.tabular_skipped_rows || 0));
    const tabularAiMappingUsed = Boolean(status?.tabular_ai_mapping_used);
    const tabularFallbackReason = String(status?.tabular_fallback_reason || '').trim();
    const rowAiEnrichmentDone = Math.max(0, Number(status?.row_ai_enrichment_done || 0));
    const rowAiEnrichmentTotal = Math.max(rowAiEnrichmentDone, Number(status?.row_ai_enrichment_total || 0));
    const rowAiEnrichmentFailed = Math.max(0, Number(status?.row_ai_enrichment_failed || 0));
    const formatDuration = (seconds) => {
      const sec = toSeconds(seconds);
      if (sec <= 0) return '0 secs';
      const mins = Math.floor(sec / 60);
      const rem = sec % 60;
      if (mins <= 0) return `${rem} secs`;
      return `${mins} min ${rem} secs`;
    };

    return (
      <Card size="small" className="border-0 shadow-sm order-genie-v2-progress-card-v2 mt-2">
        <div className="fw-semibold mb-2">
          {isDone && showReadyTitle
            ? `Process Details: Time taken ${formatDuration(timingTotalSec)}`
            : 'Progress details'}
        </div>
        <div className="small text-muted mb-2">
          Pages in request: {documentPages > 0 ? documentPages : '—'}
        </div>

        <div className="mb-2">
          <div className="d-flex justify-content-between small">
            <span>Data extraction</span>
            <span>{extractionComplete && lineItems > 0 ? `${lineItems} rows extracted` : extractionDisplayText}</span>
          </div>
          <Progress percent={extractionPercent} size="small" status={extractionComplete ? 'success' : 'active'} />
        </div>

        {extractionComplete && rowsByPageEntries.length > 0 && (
          <div className="small text-muted mb-2">
            {rowsByPageEntries.map(([key, value]) => `${key.replace('page_', 'Page ')}: ${Number(value || 0)} rows`).join(' • ')}
          </div>
        )}
        {extractionComplete && lineItems > 0 && (
          <div className="small text-muted mb-2">
            Extracted rows: {lineItems}
          </div>
        )}
        {(tabularExpectedRows > 0 || tabularExtractedRows > 0 || tabularSkippedRows > 0) && (
          <div className="small text-muted mb-2">
            Tabular completeness: {Math.max(tabularExtractedRows, lineItems)}/{Math.max(tabularExpectedRows, tabularExtractedRows, lineItems)} extracted
            {tabularSkippedRows > 0 ? `, ${tabularSkippedRows} skipped` : ''}
          </div>
        )}
        {(tabularAiMappingUsed || tabularFallbackReason) && (
          <div className="small text-muted mb-2">
            Column mapping: {tabularAiMappingUsed ? 'AI mapping used' : 'Deterministic mapping'}
            {tabularFallbackReason ? ` (${tabularFallbackReason.replace(/_/g, ' ')})` : ''}
          </div>
        )}
        {rowAiEnrichmentTotal > 0 && (
          <div className="small text-muted mb-2">
            Row enrichment: {rowAiEnrichmentDone}/{rowAiEnrichmentTotal}
            {rowAiEnrichmentFailed > 0 ? `, failed ${rowAiEnrichmentFailed}` : ''}
          </div>
        )}

        <div className="mb-2">
          <div className="d-flex justify-content-between small">
            <span>Product matching</span>
            <span>{matchProgressValue}/{matchProgressTotal}</span>
          </div>
          <Progress percent={matchingPercent} size="small" status={matchComplete ? 'success' : 'active'} />
        </div>

        <div className="mb-1">
          <div className="d-flex justify-content-between small">
            <span>AI Analysis (rows requiring AI)</span>
            <span>{rerankTotal > 0 ? `${rerankProgressValue}/${rerankProgressTotal} rows` : '0/0 rows'}</span>
          </div>
          <Progress percent={rerankPercent} size="small" status={rerankComplete ? 'success' : 'active'} />
          {lineItems > 0 && rerankTotal > 0 && rerankTotal < lineItems && (
            <div className="small text-muted mt-1">
              AI analysis runs only on rows with multiple match candidates ({rerankTotal} of {lineItems}).
            </div>
          )}
        </div>
        {lowConfidenceCount > 0 && (
          <div className="small text-warning mt-1 mb-2">
            AI Low Confidence: {lowConfidenceCount} row(s) need manual review.
          </div>
        )}
        {queueStatus === 'queued' && (
          <div className="small text-warning mt-1 mb-2">
            {`Queue: #${Math.max(1, queuePosition)} of ${Math.max(queueDepth, queuePosition || 1)}`}
            {queueEtaSeconds > 0 ? ` · ETA ~${Math.max(1, Math.round(queueEtaSeconds / 60))} min` : ''}
            {queuePriorityLabel ? ` · Priority ${queuePriorityLabel}` : ''}
          </div>
        )}

        <div className="small text-muted mt-2">
          <div>Reading: {formatDuration(timingReadingSec)}</div>
          <div>Extraction: {formatDuration(timingParsingSec)}</div>
          <div>Matching: {formatDuration(timingMatchingSec)}</div>
          <div>{isV3 ? 'AI analysis' : 'AI rerank'}: {formatDuration(timingRerankSec)}</div>
          <div>Total: {formatDuration(timingTotalSec)}</div>
        </div>

        {showReview && (
          <div className="mt-2">
            <Button type="primary" size="small" onClick={onReview}>Review</Button>
          </div>
        )}
      </Card>
    );
  };

  const pushNotification = useCallback((title, detail = '') => {
    setNotifications((prev) => [
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        title,
        detail,
        createdAt: new Date(),
        read: false,
      },
      ...prev,
    ]);
  }, []);

  const getItemNotesValue = useCallback((item) => {
    return String(
      item?.normalized_parts?.notes ||
        item?.normalized_parts?.note ||
        item?.normalized_parts?.remarks ||
        item?.parsed_json?.itemNotes ||
        item?.parsed_json?.item_notes ||
        item?.parsed_json?.remarks ||
        item?.parsed_json?.note ||
        ''
    ).trim();
  }, []);

  const buildCheckoutPayload = useCallback(
    (sourceItems, sourceMatches, sourceRequestId, sourceSummary, selectionState, qtyState) => {
      const groupedMatches = {};
      (sourceMatches || []).forEach((entry) => {
        const key = String(entry?.item_id ?? entry?.itemId ?? '');
        if (!key) return;
        groupedMatches[key] = groupedMatches[key] || [];
        groupedMatches[key].push(entry);
      });
      const summaryData = getProjectSummary(sourceSummary);
      const checkoutItems = [];
      (sourceItems || []).forEach((item) => {
        const itemKey = String(item?.id || '');
        if (!itemKey) return;
        const productCode = String(selectionState?.[itemKey] || '').trim();
        if (!productCode) return;
        const matchOptions = groupedMatches[itemKey] || [];
        const selectedMatch =
          matchOptions.find((entry) => String(entry?.product_code || '').trim() === productCode) ||
          matchOptions[0] ||
          null;
        const itemNotes = getItemNotesValue(item);
        checkoutItems.push({
          item_id: item.id,
          product_code: productCode,
          quantity: Number(qtyState?.[itemKey] ?? item?.normalized_parts?.qty ?? 1) || 1,
          itemNotes,
          source_item: item?.normalized_parts || {},
          match: selectedMatch,
        });
      });
      return {
        requestId: sourceRequestId,
        userId: userId.trim(),
        customerIdAsInErp: customerIdAsInErp.trim(),
        shipToIdAsInErp: shipToIdAsInErp.trim(),
        summary: {
          ...summaryData,
          totalItems: Array.isArray(sourceItems) ? sourceItems.length : 0,
          selectedMatches: checkoutItems.length,
        },
        items: checkoutItems,
      };
    },
    [customerIdAsInErp, getItemNotesValue, getProjectSummary, shipToIdAsInErp, userId]
  );

  const handleProceedToCheckout = useCallback(
    async (sourceItems, sourceMatches, sourceRequestId, sourceUserId, sourceSummary, selectionState, qtyState) => {
      if (!sourceRequestId || !sourceUserId) return;
      const checkoutPayload = buildCheckoutPayload(
        sourceItems,
        sourceMatches,
        sourceRequestId,
        sourceSummary,
        selectionState,
        qtyState
      );
      const summaryPayload = {
        ...(sourceSummary && typeof sourceSummary === 'object' && !Array.isArray(sourceSummary) ? sourceSummary : {}),
        ...checkoutPayload.summary,
      };
      try {
        await updateOrderGenieSummary(sourceRequestId, {
          user_id: sourceUserId,
          summary: summaryPayload,
        });
        setRequestSummaryById((prev) => ({ ...prev, [sourceRequestId]: summaryPayload }));

        await updateOrderGenieCart(sourceRequestId, {
          user_id: sourceUserId,
          action: 'clear',
          items: [],
        });
        if (checkoutPayload.items.length) {
          await updateOrderGenieCart(sourceRequestId, {
            user_id: sourceUserId,
            action: 'add',
            items: checkoutPayload.items.map((entry) => ({
              item_id: entry.item_id,
              product_code: entry.product_code,
              quantity: entry.quantity,
              meta: {
                ...(entry.match?.match || {}),
                itemNotes: entry.itemNotes || '',
              },
            })),
          });
        }

        localStorage.setItem(
          scopedKey('order_genie_checkout_payload', getActiveAccountId()),
          JSON.stringify({
            ...checkoutPayload,
            createdAt: new Date().toISOString(),
          })
        );
        window.dispatchEvent(
          new CustomEvent('order_genie_checkout_ready', {
            detail: checkoutPayload,
          })
        );
        pushNotification(
          'Checkout payload ready',
          `Saved ${checkoutPayload.items.length} selected line item${checkoutPayload.items.length === 1 ? '' : 's'}.`
        );
      } catch (err) {
        pushNotification('Checkout save failed', err.message || 'Unable to prepare checkout payload.');
      }
    },
    [buildCheckoutPayload, pushNotification]
  );

  const openCurrentRequestResults = useCallback(async () => {
    const targetRequestId = String(requestId || '').trim();
    const tabUserId = String(
      (quoteTabs || []).find((tab) => String(tab.id) === String(targetRequestId))?.userId || ''
    ).trim();
    const activeProcessUserId = String(
      (activeProcesses || []).find((entry) => String(entry.requestId) === String(targetRequestId))?.userId || ''
    ).trim();
    const reviewUserId = normalizeRequestUserId(userId, tabUserId, activeProcessUserId, statusUserId);
    if (!targetRequestId || !reviewUserId) {
      setOverlayActive(false);
      setQuoteReadyNotice(null);
      setStep('results');
      return;
    }
    try {
      const expectedFromRowsByPage = processStatus?.extraction_rows_by_page
        && typeof processStatus.extraction_rows_by_page === 'object'
        ? Object.values(processStatus.extraction_rows_by_page).reduce((sum, value) => sum + (Number(value) || 0), 0)
        : 0;
      const expectedItemCount = Math.max(
        Number(processStatus?.line_items || 0),
        Number(expectedFromRowsByPage || 0)
      );
      let refreshed = await getOrderGenieRequest(targetRequestId, reviewUserId);
      let refreshedItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const hasNoItems = refreshedItems.length === 0;
        const belowExpected = expectedItemCount > 0 && refreshedItems.length < expectedItemCount;
        if (!hasNoItems && !belowExpected) break;
        await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
        refreshed = await getOrderGenieRequest(targetRequestId, reviewUserId);
        refreshedItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
      }
      const nextItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
      const nextMatches = mergeCartSelectionsIntoMatches(refreshed?.matches, refreshed?.cart);
      const nextSourceFiles = Array.isArray(refreshed?.request?.source_files)
        ? refreshed.request.source_files
        : [];
      const nextRequestSummary =
        refreshed?.request?.summary_json &&
        typeof refreshed.request.summary_json === 'object' &&
        !Array.isArray(refreshed.request.summary_json)
          ? refreshed.request.summary_json
          : {};
      const finalItems = nextItems.length ? nextItems : (Array.isArray(items) ? items : []);
      const finalMatches = nextMatches.length ? nextMatches : (Array.isArray(matches) ? matches : []);
      const finalAiRerankedRowKeys = new Set();
      const finalAiLowConfidenceRowKeys = new Set();
      (Array.isArray(finalMatches) ? finalMatches : []).forEach((entry) => {
        const rowKey = String(entry?.item_id ?? entry?.itemId ?? '').trim();
        if (!rowKey) return;
        if (entry?.ai_ranked) {
          finalAiRerankedRowKeys.add(rowKey);
        }
        if (entry?.ai_low_confidence || (entry?.ai_analyzed && !entry?.ai_ranked)) {
          finalAiLowConfidenceRowKeys.add(rowKey);
        }
      });
      const finalAiRerankedRows = finalAiRerankedRowKeys.size;
      const finalAiLowConfidenceRows = finalAiLowConfidenceRowKeys.size;
      setItems(finalItems);
      setMatches(finalMatches);
      setSourceFiles(nextSourceFiles);
      applyDefaultSelections(finalItems, finalMatches, setSelectedByItem);
      setRequestSummaryById((prev) => ({ ...prev, [targetRequestId]: nextRequestSummary }));
      setProcessMetaById((prev) => {
        const current = prev?.[targetRequestId] || {};
        const nextRerankCount = finalAiRerankedRows;
        const nextRerankTotal = Math.max(nextRerankCount, finalAiLowConfidenceRows);
        return {
          ...prev,
          [targetRequestId]: {
            ...current,
            rerankCount: nextRerankCount,
            rerankTotal: nextRerankTotal,
            aiLowConfidenceCount: finalAiLowConfidenceRows,
            aiLowConfidenceTotal: nextRerankTotal,
          },
        };
      });
      setProcessStatus((prev) => {
        if (!prev || String(prev?.request_id || requestId || '') !== String(targetRequestId)) return prev;
        const nextRerankCount = finalAiRerankedRows;
        const nextRerankTotal = Math.max(nextRerankCount, finalAiLowConfidenceRows);
        return {
          ...prev,
          rerank_count: nextRerankCount,
          rerank_total: nextRerankTotal,
          ai_low_confidence_count: finalAiLowConfidenceRows,
          ai_low_confidence_total: nextRerankTotal,
        };
      });
      setQuoteTabs((prev) =>
        prev.map((tab) =>
          String(tab.id) === String(targetRequestId)
            ? {
                ...tab,
                items: finalItems.length ? finalItems : tab.items,
                matches: finalMatches.length ? finalMatches : tab.matches,
                sourceFiles: nextSourceFiles.length ? nextSourceFiles : tab.sourceFiles,
                requestSummary: nextRequestSummary || tab.requestSummary,
              }
            : tab
        )
      );
    } catch (err) {
      // Keep current state if refresh fails.
    } finally {
      setOverlayActive(false);
      setQuoteReadyNotice(null);
      setStep('results');
    }
  }, [activeProcesses, items, matches, mergeCartSelectionsIntoMatches, normalizeRequestUserId, processStatus, quoteTabs, requestId, statusUserId, userId]);

  const markNotificationsRead = useCallback(() => {
    setNotifications((prev) => prev.map((note) => ({ ...note, read: true })));
  }, []);

  const handleRealtimeEvent = useCallback(
    (eventName, payload) => {
      const now = Date.now();
      const realtimeRequestId = String(payload?.request_id || payload?.requestId || '').trim();
      const realtimeMessage = String(payload?.message || '').trim();
      const realtimeDetail = String(payload?.detail || '').trim();
      const realtimeStatus = String(payload?.status || '').trim();
      const realtimeStep = String(payload?.step || '').trim();
      const realtimeMatchedCount = Number(payload?.matched_count || 0);
      const realtimeMatchedTotal = Number(payload?.matched_total || 0);
      const realtimeRerankCount = Number(payload?.rerank_count || 0);
      const realtimeRerankTotal = Number(payload?.rerank_total || 0);
      const realtimeAiLowConfidenceCount = Number(payload?.ai_low_confidence_count || 0);
      const realtimeAiLowConfidenceTotal = Number(payload?.ai_low_confidence_total || 0);
      const realtimeQueueStatus = String(payload?.queue_status || '').trim().toLowerCase();
      const realtimeQueuePriorityLabel = String(payload?.queue_priority_label || '').trim().toLowerCase();
      const realtimeQueuePriority = Number(payload?.queue_priority || 0);
      const realtimeQueuePosition = Number(payload?.queue_position || 0);
      const realtimeQueueDepth = Number(payload?.queue_depth || 0);
      const realtimeQueueEtaSeconds = Number(payload?.queue_eta_seconds || 0);
      const realtimeDocumentPageTotal = Number(payload?.document_page_total || 0);
      const realtimeExtractionPageTotal = Number(payload?.extraction_page_total || 0);
      const realtimeExtractionPageCompleted = Number(payload?.extraction_page_completed || 0);
      const realtimeExtractionRowsByPage =
        payload?.extraction_rows_by_page && typeof payload.extraction_rows_by_page === 'object'
          ? payload.extraction_rows_by_page
          : null;
      const realtimeExtractionComplete = Boolean(payload?.extraction_complete);
      const realtimeHeartbeatElapsedSeconds = Number(payload?.heartbeat_elapsed_seconds || 0);
      const realtimeHeartbeatAt = payload?.heartbeat_at || null;
      const realtimeParallelThreads = Number(payload?.parallel_match_threads || 1);
      const realtimeAiMarkdown = String(payload?.ai_stream_markdown || '');
      const realtimeAiAgent = String(payload?.ai_stream_agent || '');
      const realtimeAiEngine = String(payload?.ai_stream_engine || '');
      const realtimeAiModel = String(payload?.ai_stream_model || '');
      const realtimeAiPass = Number(payload?.ai_stream_pass || 0);
      const hasRealtimeAiDone =
        payload && Object.prototype.hasOwnProperty.call(payload, 'ai_stream_done');
      const realtimeAiDone = hasRealtimeAiDone ? Boolean(payload?.ai_stream_done) : undefined;

      if (eventName === 'order_genie.status_update' && realtimeRequestId) {
        setRequestStatusById((prev) => ({
          ...prev,
          [realtimeRequestId]: {
            ...(prev?.[realtimeRequestId] || {}),
            status: realtimeStatus || prev?.[realtimeRequestId]?.status || '',
            message: realtimeMessage || prev?.[realtimeRequestId]?.message || '',
            detail: realtimeDetail || prev?.[realtimeRequestId]?.detail || '',
            queue_status: realtimeQueueStatus || prev?.[realtimeRequestId]?.queue_status || '',
            queue_priority_label:
              realtimeQueuePriorityLabel || prev?.[realtimeRequestId]?.queue_priority_label || '',
            queue_priority:
              Number.isFinite(realtimeQueuePriority) && realtimeQueuePriority > 0
                ? realtimeQueuePriority
                : prev?.[realtimeRequestId]?.queue_priority || 0,
            queue_position:
              Number.isFinite(realtimeQueuePosition) && realtimeQueuePosition >= 0
                ? realtimeQueuePosition
                : prev?.[realtimeRequestId]?.queue_position || 0,
            queue_depth:
              Number.isFinite(realtimeQueueDepth) && realtimeQueueDepth >= 0
                ? realtimeQueueDepth
                : prev?.[realtimeRequestId]?.queue_depth || 0,
            queue_eta_seconds:
              Number.isFinite(realtimeQueueEtaSeconds) && realtimeQueueEtaSeconds >= 0
                ? realtimeQueueEtaSeconds
                : prev?.[realtimeRequestId]?.queue_eta_seconds || 0,
          },
        }));
        setProcessMetaById((prev) => ({
          ...prev,
          [realtimeRequestId]: {
            ...(prev[realtimeRequestId] || {}),
            lastUpdateAt: payload?.updated_at || Date.now(),
            lastStep: realtimeStep || (prev[realtimeRequestId] || {}).lastStep,
            lastStatus: realtimeStatus || (prev[realtimeRequestId] || {}).lastStatus,
            lastMessage: realtimeMessage || (prev[realtimeRequestId] || {}).lastMessage,
            lastDetail: realtimeDetail || (prev[realtimeRequestId] || {}).lastDetail,
            parallelMatchThreads: realtimeParallelThreads > 0 ? realtimeParallelThreads : (prev[realtimeRequestId] || {}).parallelMatchThreads || 1,
            matchedCount: Number.isFinite(realtimeMatchedCount) ? realtimeMatchedCount : (prev[realtimeRequestId] || {}).matchedCount || 0,
            matchedTotal: Number.isFinite(realtimeMatchedTotal) ? realtimeMatchedTotal : (prev[realtimeRequestId] || {}).matchedTotal || 0,
            rerankCount: Number.isFinite(realtimeRerankCount) ? realtimeRerankCount : (prev[realtimeRequestId] || {}).rerankCount || 0,
            rerankTotal: Number.isFinite(realtimeRerankTotal) ? realtimeRerankTotal : (prev[realtimeRequestId] || {}).rerankTotal || 0,
            aiLowConfidenceCount:
              Number.isFinite(realtimeAiLowConfidenceCount)
                ? realtimeAiLowConfidenceCount
                : (prev[realtimeRequestId] || {}).aiLowConfidenceCount || 0,
            aiLowConfidenceTotal:
              Number.isFinite(realtimeAiLowConfidenceTotal)
                ? realtimeAiLowConfidenceTotal
                : (prev[realtimeRequestId] || {}).aiLowConfidenceTotal || 0,
            queueStatus: realtimeQueueStatus || (prev[realtimeRequestId] || {}).queueStatus || '',
            queuePriorityLabel:
              realtimeQueuePriorityLabel || (prev[realtimeRequestId] || {}).queuePriorityLabel || '',
            queuePriority:
              Number.isFinite(realtimeQueuePriority) && realtimeQueuePriority > 0
                ? realtimeQueuePriority
                : (prev[realtimeRequestId] || {}).queuePriority || 0,
            queuePosition:
              Number.isFinite(realtimeQueuePosition) && realtimeQueuePosition >= 0
                ? realtimeQueuePosition
                : (prev[realtimeRequestId] || {}).queuePosition || 0,
            queueDepth:
              Number.isFinite(realtimeQueueDepth) && realtimeQueueDepth >= 0
                ? realtimeQueueDepth
                : (prev[realtimeRequestId] || {}).queueDepth || 0,
            queueEtaSeconds:
              Number.isFinite(realtimeQueueEtaSeconds) && realtimeQueueEtaSeconds >= 0
                ? realtimeQueueEtaSeconds
                : (prev[realtimeRequestId] || {}).queueEtaSeconds || 0,
            documentPageTotal:
              Number.isFinite(realtimeDocumentPageTotal) && realtimeDocumentPageTotal > 0
                ? realtimeDocumentPageTotal
                : (prev[realtimeRequestId] || {}).documentPageTotal || 0,
            extractionPageTotal:
              Number.isFinite(realtimeExtractionPageTotal) && realtimeExtractionPageTotal > 0
                ? realtimeExtractionPageTotal
                : (prev[realtimeRequestId] || {}).extractionPageTotal || 0,
            extractionPageCompleted:
              Number.isFinite(realtimeExtractionPageCompleted) && realtimeExtractionPageCompleted >= 0
                ? realtimeExtractionPageCompleted
                : (prev[realtimeRequestId] || {}).extractionPageCompleted || 0,
            extractionRowsByPage:
              realtimeExtractionRowsByPage || (prev[realtimeRequestId] || {}).extractionRowsByPage || {},
            extractionComplete:
              Boolean(realtimeExtractionComplete)
              || Boolean((prev[realtimeRequestId] || {}).extractionComplete),
            heartbeatElapsedSeconds:
              Number.isFinite(realtimeHeartbeatElapsedSeconds) && realtimeHeartbeatElapsedSeconds >= 0
                ? realtimeHeartbeatElapsedSeconds
                : (prev[realtimeRequestId] || {}).heartbeatElapsedSeconds || 0,
            heartbeatAt: realtimeHeartbeatAt || (prev[realtimeRequestId] || {}).heartbeatAt || null,
            aiMarkdown: realtimeAiMarkdown || (prev[realtimeRequestId] || {}).aiMarkdown || '',
            aiAgent: realtimeAiAgent || (prev[realtimeRequestId] || {}).aiAgent || '',
            aiEngine: realtimeAiEngine || (prev[realtimeRequestId] || {}).aiEngine || '',
            aiModel: realtimeAiModel || (prev[realtimeRequestId] || {}).aiModel || '',
            aiPass: Number.isFinite(realtimeAiPass) ? realtimeAiPass : (prev[realtimeRequestId] || {}).aiPass || 0,
            aiDone:
              typeof realtimeAiDone === 'boolean'
                ? realtimeAiDone
                : (prev[realtimeRequestId] || {}).aiDone,
          },
        }));
        if (String(requestId || '') === realtimeRequestId) {
          const currentRunType = processRunTypeById[realtimeRequestId];
          if ((step === 'processing' || currentRunType === 'rerun') && realtimeMessage) {
            setOverlay(realtimeMessage, realtimeDetail || '');
          }
          const nextStepValue = String(realtimeStep || '').toLowerCase();
          const clearAiStream = STREAM_PANEL_HIDDEN_STEPS.has(nextStepValue);
          setProcessStatus((prev) => ({
            ...(prev || {}),
            status: realtimeStatus || (prev || {}).status,
            step: realtimeStep || (prev || {}).step,
            message: realtimeMessage || (prev || {}).message,
            detail: realtimeDetail || (prev || {}).detail,
            matched_count: Number.isFinite(realtimeMatchedCount) ? realtimeMatchedCount : (prev || {}).matched_count,
            matched_total: Number.isFinite(realtimeMatchedTotal) ? realtimeMatchedTotal : (prev || {}).matched_total,
            rerank_count: Number.isFinite(realtimeRerankCount) ? realtimeRerankCount : (prev || {}).rerank_count,
            rerank_total: Number.isFinite(realtimeRerankTotal) ? realtimeRerankTotal : (prev || {}).rerank_total,
            ai_low_confidence_count:
              Number.isFinite(realtimeAiLowConfidenceCount)
                ? realtimeAiLowConfidenceCount
                : (prev || {}).ai_low_confidence_count,
            ai_low_confidence_total:
              Number.isFinite(realtimeAiLowConfidenceTotal)
                ? realtimeAiLowConfidenceTotal
                : (prev || {}).ai_low_confidence_total,
            queue_status: realtimeQueueStatus || (prev || {}).queue_status,
            queue_priority_label: realtimeQueuePriorityLabel || (prev || {}).queue_priority_label,
            queue_priority:
              Number.isFinite(realtimeQueuePriority) && realtimeQueuePriority > 0
                ? realtimeQueuePriority
                : (prev || {}).queue_priority,
            queue_position:
              Number.isFinite(realtimeQueuePosition) && realtimeQueuePosition >= 0
                ? realtimeQueuePosition
                : (prev || {}).queue_position,
            queue_depth:
              Number.isFinite(realtimeQueueDepth) && realtimeQueueDepth >= 0
                ? realtimeQueueDepth
                : (prev || {}).queue_depth,
            queue_eta_seconds:
              Number.isFinite(realtimeQueueEtaSeconds) && realtimeQueueEtaSeconds >= 0
                ? realtimeQueueEtaSeconds
                : (prev || {}).queue_eta_seconds,
            document_page_total:
              Number.isFinite(realtimeDocumentPageTotal) && realtimeDocumentPageTotal > 0
                ? realtimeDocumentPageTotal
                : (prev || {}).document_page_total,
            extraction_page_total:
              Number.isFinite(realtimeExtractionPageTotal) && realtimeExtractionPageTotal > 0
                ? realtimeExtractionPageTotal
                : (prev || {}).extraction_page_total,
            extraction_page_completed:
              Number.isFinite(realtimeExtractionPageCompleted) && realtimeExtractionPageCompleted >= 0
                ? realtimeExtractionPageCompleted
                : (prev || {}).extraction_page_completed,
            extraction_rows_by_page:
              realtimeExtractionRowsByPage || (prev || {}).extraction_rows_by_page,
            extraction_complete:
              Boolean(realtimeExtractionComplete) || Boolean((prev || {}).extraction_complete),
            heartbeat_elapsed_seconds:
              Number.isFinite(realtimeHeartbeatElapsedSeconds) && realtimeHeartbeatElapsedSeconds >= 0
                ? realtimeHeartbeatElapsedSeconds
                : (prev || {}).heartbeat_elapsed_seconds,
            heartbeat_at: realtimeHeartbeatAt || (prev || {}).heartbeat_at,
            updated_at: payload?.updated_at || (prev || {}).updated_at,
            parallel_match_threads:
              realtimeParallelThreads > 0 ? realtimeParallelThreads : (prev || {}).parallel_match_threads,
            ai_stream_markdown:
              clearAiStream ? '' : (realtimeAiMarkdown || (prev || {}).ai_stream_markdown),
            ai_stream_agent:
              clearAiStream ? '' : (realtimeAiAgent || (prev || {}).ai_stream_agent),
            ai_stream_engine:
              clearAiStream ? '' : (realtimeAiEngine || (prev || {}).ai_stream_engine),
            ai_stream_model:
              clearAiStream ? '' : (realtimeAiModel || (prev || {}).ai_stream_model),
            ai_stream_pass: Number.isFinite(realtimeAiPass) ? realtimeAiPass : (prev || {}).ai_stream_pass,
            ai_stream_done:
              clearAiStream
                ? true
                : typeof realtimeAiDone === 'boolean'
                ? realtimeAiDone
                : (prev || {}).ai_stream_done,
          }));
        }
      }

      const summary =
        payload?.message ||
        payload?.detail ||
        payload?.status ||
        (typeof payload === 'string' ? payload : '');
      setRealtimeFeed((prev) => [
        {
          id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
          eventName: String(eventName || 'Realtime update'),
          summary: String(summary || 'New websocket update received.').slice(0, 220),
          at: now,
        },
        ...prev,
      ].slice(0, 60));
      if (!realtimePanelOpenRef.current) {
        setRealtimeUnreadCount((prev) => Math.min(99, Number(prev || 0) + 1));
      }
      if (now - lastRealtimeNotificationAtRef.current < 3000) {
        return;
      }
      lastRealtimeNotificationAtRef.current = now;
      pushNotification(`Realtime: ${eventName}`, String(summary || 'New websocket update received.'));
    },
    [processRunTypeById, pushNotification, requestId, step]
  );

  const {
    status: websocketStatus,
    socketId: websocketSocketId,
    lastError: websocketError,
    isConnected: websocketConnected,
  } = useRealtimeSocket({
    enabled: true,
    room: 'order_genie',
    events: useMemo(() => ['order_genie.status_update'], []),
    clientName: 'order_genie_v2',
    onEvent: handleRealtimeEvent,
  });

  const resetNewRequestView = useCallback(() => {
    setRequestId(null);
    setProcessStatus(null);
    setProcessMetaById({});
    setOverlayActive(false);
    setOverlayMessage('');
    setOverlayDetail('');
    setQuoteReadyNotice(null);
    setItems([]);
    setMatches([]);
    setSourceFiles([]);
    setUserIdRequiredError('');
    setFileRequiredError('');
    setStep('upload');
    completionNotified.current.clear();
  }, []);

  const handleStartNewRequest = useCallback(async () => {
    const hasInFlightRequest = !!requestId;
    const hasData =
      hasInFlightRequest ||
      files.length > 0 ||
      items.length > 0 ||
      matches.length > 0 ||
      Object.keys(selectedByItem || {}).length > 0 ||
      Object.keys(qtyByItem || {}).length > 0 ||
      step !== 'upload' ||
      !!requestName.trim() ||
      !!userId.trim();

    if (hasData) {
      const ok = await confirmDialog({
        title: 'Start new request',
        content: 'Clear the current request and start a new one?',
      });
      if (!ok) return;
    }

    if (requestId) {
      setActiveProcesses((prev) => prev.filter((entry) => String(entry.requestId) !== String(requestId)));
      workflowFallbackNotified.current.delete(requestId);
      processStaleNotified.current.delete(requestId);
      completionNotified.current.delete(String(requestId));
    }
    setFiles([]);
    setUploadSheetNames([]);
    setUploadSelectedSheet('');
    setUploadPreviewTable([]);
    setUploadPreviewText('');
    setUploadPreviewHtml('');
    setRequestName(buildDefaultName());
    setSelectedByItem({});
    setQtyByItem({});
    setExpandedMatchKeys({});
    setManualSearchByRow({});
    setManualSearchLoadingByRow({});
    setManualSearchErrorByRow({});
    setManualSearchMetaByRow({});
    setManualSearchCategoryByRow({});
    setManualSearchSnapshotByRow({});
    setRowRerunLoadingByRow({});
    setRowRerunStatusByRow({});
    setPreviewModalFile(null);
    readingPageRef.current = 1;
    readingTotalRef.current = 1;
    setCameraModalOpen(false);
    resetNewRequestView();
  }, [files.length, items.length, matches.length, qtyByItem, requestId, requestName, resetNewRequestView, selectedByItem, step, userId]);

  const createEditableRow = useCallback((source = {}) => ({
    id: source.id ?? null,
    __draftKey: source.__draftKey || `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    parsed_json: source.parsed_json || {},
    part_number: source.part_number || '',
    manufacturer_part_number: source.manufacturer_part_number || '',
    customer_part_number: source.customer_part_number || '',
    manufacturer: source.manufacturer || '',
    description: source.description || '',
    qty: Number(source.qty) > 0 ? Number(source.qty) : 1,
    item_notes: source.item_notes || '',
  }), []);

  const serializeEditingItems = useCallback(
    (rows) => JSON.stringify(
      (rows || []).map((row) => ({
        id: row?.id ?? null,
        part_number: String(row?.part_number || '').trim(),
        manufacturer_part_number: String(row?.manufacturer_part_number || '').trim(),
        customer_part_number: String(row?.customer_part_number || '').trim(),
        manufacturer: String(row?.manufacturer || '').trim(),
        description: String(row?.description || '').trim(),
        qty: Number(row?.qty) > 0 ? Number(row?.qty) : 1,
        item_notes: String(row?.item_notes || '').trim(),
      }))
    ),
    []
  );

  const validateEditingRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return 'At least one row is required.';
    }
    for (let idx = 0; idx < rows.length; idx += 1) {
      const row = rows[idx] || {};
      const hasIdentity = String(row.part_number || '').trim() || String(row.description || '').trim();
      if (!hasIdentity) {
        return `Line ${idx + 1}: enter Part Number or Description before saving.`;
      }
      const qty = Number(row.qty || 0);
      if (!Number.isFinite(qty) || qty <= 0) {
        return `Line ${idx + 1}: quantity must be greater than 0.`;
      }
    }
    return '';
  }, []);

  const buildItemsPayloadForPersist = useCallback((rows) => (
    (rows || []).map((row, index) => {
      const itemNotes = String(row.item_notes || '').trim();
      const payloadRow = {
        row_index: index,
        parsed_json: {
          ...(row.parsed_json || {}),
          itemNotes: itemNotes || undefined,
          item_notes: itemNotes || undefined,
          notes: itemNotes || undefined,
        },
        normalized_parts: {
          part_number: row.part_number || '',
          manufacturer_part_number: row.manufacturer_part_number || '',
          customer_part_number: row.customer_part_number || '',
          manufacturer: row.manufacturer || '',
          description: row.description || '',
          qty: row.qty || 1,
          notes: itemNotes,
        },
      };
      if (row.id) payloadRow.id = row.id;
      return payloadRow;
    })
  ), []);

  const hasEditDrawerUnsavedChanges = useMemo(() => {
    if (!editDrawerOpen) return false;
    if (!editDrawerInitialSnapshot) return false;
    return serializeEditingItems(editingItems) !== editDrawerInitialSnapshot;
  }, [editDrawerInitialSnapshot, editDrawerOpen, editingItems, serializeEditingItems]);

  const updateEditingRow = useCallback((rowIndex, updater) => {
    setEditingItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== rowIndex) return item;
        const nextPatch = typeof updater === 'function' ? updater(item) : updater;
        return { ...item, ...(nextPatch || {}) };
      })
    );
  }, []);

  const addNewEditableRow = useCallback(() => {
    const nextRow = createEditableRow();
    setEditingItems((prev) => [...prev, nextRow]);
    setEditingHighlightKey(nextRow.__draftKey || '');
    setEditDrawerValidationError('');
  }, [createEditableRow]);

  const requestDeleteEditingRow = useCallback(async (rowIndex) => {
    const total = editingItems.length;
    if (total <= 1) {
      setEditDrawerValidationError('Cannot delete the last remaining row in this request.');
      return;
    }
    const ok = await confirmDialog({
      title: 'Delete Row',
      content: `This will permanently remove Line ${rowIndex + 1} and its matches.`,
      okText: 'Delete Row',
      cancelText: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    const nextRows = editingItems.filter((_, idx) => idx !== rowIndex);
    if (!editingRequestId || !editingUserId) {
      setEditingItems(nextRows);
      setEditDrawerInitialSnapshot(serializeEditingItems(nextRows));
      setEditDrawerValidationError('');
      return;
    }
    const rowValidationError = validateEditingRows(nextRows);
    if (rowValidationError) {
      setEditDrawerValidationError(rowValidationError);
      return;
    }
    setEditingSaving(true);
    try {
      await updateOrderGenieItems(editingRequestId, {
        user_id: editingUserId,
        items: buildItemsPayloadForPersist(nextRows),
      });
      const refreshed = await getOrderGenieRequest(editingRequestId, editingUserId);
      const nextItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
      const nextSourceFiles = Array.isArray(refreshed?.request?.source_files)
        ? refreshed.request.source_files
        : [];
      const nextMatches = mergeCartSelectionsIntoMatches(refreshed?.matches, refreshed?.cart);
      if (String(requestId || '') === String(editingRequestId)) {
        setItems(nextItems);
        setMatches(isV3 ? nextMatches : []);
        setSourceFiles(nextSourceFiles);
      }
      setQuoteTabs((prev) =>
        prev.map((tab) =>
          String(tab.id) === String(editingRequestId)
            ? { ...tab, items: nextItems, matches: isV3 ? nextMatches : [], sourceFiles: nextSourceFiles }
            : tab
        )
      );
      const refreshedRows = (nextItems || []).map((item) => {
        const normalized = item.normalized_parts || {};
        return createEditableRow({
          id: item.id,
          __draftKey: `existing-${String(item.id || '')}`,
          parsed_json: item.parsed_json || {},
          part_number: normalized.part_number || '',
          manufacturer_part_number: normalized.manufacturer_part_number || '',
          customer_part_number: normalized.customer_part_number || '',
          manufacturer: normalized.manufacturer || '',
          description: normalized.description || '',
          qty: normalized.qty || 1,
          item_notes: getItemNotesValue(item),
        });
      });
      setEditingItems(refreshedRows);
      setEditDrawerInitialSnapshot(serializeEditingItems(refreshedRows));
      setEditDrawerValidationError('');
    } catch (err) {
      setEditDrawerValidationError(err?.message || 'Unable to delete row. Please try again.');
    } finally {
      setEditingSaving(false);
    }
  }, [
    buildItemsPayloadForPersist,
    createEditableRow,
    editingItems,
    editingRequestId,
    editingUserId,
    getItemNotesValue,
    isV3,
    mergeCartSelectionsIntoMatches,
    requestId,
    serializeEditingItems,
    validateEditingRows,
  ]);

  const requestCloseEditDrawer = useCallback(async () => {
    if (hasEditDrawerUnsavedChanges) {
      const ok = await confirmDialog({
        title: 'Discard unsaved changes?',
        content: 'You have unsaved changes in Edit Input. Close without saving?',
        okText: 'Discard',
        cancelText: 'Keep editing',
        danger: true,
      });
      if (!ok) return;
    }
    setEditDrawerOpen(false);
    setEditingHighlightKey('');
    setEditDrawerValidationError('');
    setEditDrawerInitialSnapshot('');
  }, [hasEditDrawerUnsavedChanges]);

  useEffect(() => {
    if (!editDrawerOpen || !editingHighlightKey) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      const targetRow = editingRowRefs.current[editingHighlightKey];
      if (targetRow && typeof targetRow.scrollIntoView === 'function') {
        targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [editDrawerOpen, editingHighlightKey, editingItems]);

  const openEditDrawer = useCallback((sourceItems, sourceRequestId, sourceUserId, options = {}) => {
    const appendNewRow = Boolean(options?.appendNewRow);
    const focusItemId = String(options?.focusItemId || '').trim();
    setEditingRequestId(sourceRequestId || null);
    setEditingUserId(sourceUserId || '');
    const rows = (sourceItems || []).map((item) => {
      const normalized = item.normalized_parts || {};
      return createEditableRow({
        id: item.id,
        __draftKey: `existing-${String(item.id || '')}`,
        parsed_json: item.parsed_json || {},
        part_number: normalized.part_number || '',
        manufacturer_part_number: normalized.manufacturer_part_number || '',
        customer_part_number: normalized.customer_part_number || '',
        manufacturer: normalized.manufacturer || '',
        description: normalized.description || '',
        qty: normalized.qty || 1,
        item_notes: getItemNotesValue(item),
      });
    });
    if (appendNewRow) {
      rows.push(createEditableRow());
    }
    setEditingItems(rows);
    if (appendNewRow) {
      const lastRow = rows[rows.length - 1];
      setEditingHighlightKey(lastRow?.__draftKey || '');
    } else if (focusItemId) {
      const focused = rows.find((row) => String(row.id || '') === focusItemId);
      setEditingHighlightKey(focused?.__draftKey || '');
    } else {
      setEditingHighlightKey('');
    }
    setEditDrawerValidationError('');
    setEditDrawerInitialSnapshot(serializeEditingItems(rows));
    setEditDrawerOpen(true);
  }, [createEditableRow, getItemNotesValue, serializeEditingItems]);

  const startReadingAnimation = (totalPages) => {
    const normalizedTotal = Math.max(1, Number(totalPages || 1));
    readingTotalRef.current = normalizedTotal;
    readingPageRef.current = 1;
    setOverlay(
      normalizedTotal > 1 ? `Reading page 1 of ${normalizedTotal}…` : 'Scanning document…',
      'Preparing files'
    );
    let current = 1;
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += 1;
      if (normalizedTotal > 1) {
        current += 1;
        if (current > normalizedTotal) {
          clearInterval(timer);
          return;
        }
        readingPageRef.current = current;
        setOverlay(`Reading page ${current} of ${normalizedTotal}…`, `Scanning document • ${elapsed}s`);
      } else {
        setOverlay('Scanning document…', `Processing uploaded file • ${elapsed}s`);
      }
    }, 1200);
    return () => clearInterval(timer);
  };

  const handleSetupSecretsSubmit = async (secretValues) => {
    setSetupSecretsSaving(true);
    setSetupSecretsError('');
    try {
      const result = await saveOrderGenieSetupSecrets(secretValues);
      if (result?.setup_required) {
        setSetupSecretsMissing(result.missing_secrets || []);
        setSetupSecretsError('Some required credentials are still missing.');
        return;
      }
      setSetupSecretsOpen(false);
      setSetupSecretsMissing([]);
      // Continue the original action now that setup is complete.
      handleStart();
    } catch (err) {
      setSetupSecretsError(err?.message || 'Unable to save credentials. Please try again.');
    } finally {
      setSetupSecretsSaving(false);
    }
  };

  const handleSetupSecretsCancel = () => {
    if (setupSecretsSaving) return;
    setSetupSecretsOpen(false);
    setSetupSecretsError('');
  };

  const handleStart = async () => {
    if (!userId.trim()) {
      setUserIdRequiredError('User ID is required.');
      return;
    }
    setUserIdRequiredError('');
    if (!files.length) {
      setFileRequiredError('Please upload or capture at least one file.');
      return;
    }
    const hasExcelUpload = (files || []).some((file) => {
      const fileName = String(file?.name || file?.filename || '').toLowerCase();
      return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    });
    const uploadIsExcel = hasExcelUpload;
    if (hasExcelUpload && (!uploadSheetCheckReady || uploadSheetCheckLoading)) {
      setFileRequiredError('Please wait for sheet detection to complete before starting.');
      return;
    }
    if (uploadIsExcel && uploadSheetNames.length > 1 && !String(uploadSelectedSheet || '').trim()) {
      setFileRequiredError('This Excel file has multiple sheets. Select one sheet to process.');
      return;
    }
    setFileRequiredError('');
    // First-use setup gate: new accounts inherit all standard settings
    // automatically but must enter their own secrets once before processing.
    try {
      const setup = await getOrderGenieSetupSecrets();
      if (setup?.setup_required) {
        setSetupSecretsMissing(setup.missing_secrets || []);
        setSetupSecretsError('');
        setSetupSecretsOpen(true);
        return;
      }
    } catch (err) {
      // Never let the precheck block a fully-configured account from processing.
      // eslint-disable-next-line no-console
      console.warn('OrderX setup-secrets precheck failed:', err?.message || err);
    }
    setProcessing(true);
    setStep('processing');
    setProcessStatus(null);
    // Start from a clean UI state for a new request so no previous rows leak in.
    setItems([]);
    setMatches([]);
    setSelectedByItem({});
    setSourceFiles([]);
    const totalPages = Math.max(1, files.length);
    const stopReading = startReadingAnimation(totalPages);
    try {
      const requestedName = String(requestName || '').trim();
      const autoGeneratedNamePattern = /^(order genie|request)\b/i;
      const requestNameValue =
        requestedName && !autoGeneratedNamePattern.test(requestedName)
          ? requestedName
          : buildDefaultName(userId.trim());
      const requestResp = await createOrderGenieRequest({
        requestName: requestNameValue,
        userId: userId.trim(),
        customerIdAsInErp: customerIdAsInErp.trim(),
        selectedAgents: [],
        aiEngine: '',
        selectedSheet: uploadIsExcel ? String(uploadSelectedSheet || '').trim() : '',
        files,
      });
      const newRequestId = requestResp?.request?.id;
      const createdSourceFiles = Array.isArray(requestResp?.request?.source_files)
        ? requestResp.request.source_files
        : [];
      stopReading();
      if (!newRequestId) {
        setOverlay('Unable to start request');
        setProcessing(false);
        return;
      }
      const requestUserId = userId.trim();
      const requestTitle = `Request: ${requestNameValue || `Request ${newRequestId}`}`;
      setRequestId(newRequestId);
      setProcessStatus({
        status: 'queued',
        step: 'queued',
        message: 'Queued for processing',
        detail: 'Starting request...',
      });
      setQuoteTabs((prev) => {
        const existing = prev.find((tab) => String(tab.id) === String(newRequestId));
        if (existing) {
          return prev.map((tab) =>
            String(tab.id) === String(newRequestId)
              ? {
                  ...tab,
                  title: requestTitle,
                  items: [],
                  matches: [],
                  sourceFiles: createdSourceFiles,
                  userId: requestUserId,
                }
              : tab
          );
        }
        return [
          ...prev,
          {
            id: newRequestId,
            title: requestTitle,
            items: [],
            matches: [],
            sourceFiles: createdSourceFiles,
            requestSummary: null,
            userId: requestUserId,
          },
        ];
      });
      setSourceFiles(createdSourceFiles);
      setQuoteSelections((prev) => ({ ...prev, [newRequestId]: prev[newRequestId] || {} }));
      setQuoteQtys((prev) => ({ ...prev, [newRequestId]: prev[newRequestId] || {} }));
      setV3ReviewOpenedByRequest((prev) => ({ ...prev, [String(newRequestId)]: false }));
      setActiveTab(`quote-${newRequestId}`);
      // Keep "New Quote / Order Request" ready for the next request.
      setFiles([]);
      setUploadSheetNames([]);
      setUploadSelectedSheet('');
      setRequestName(buildDefaultName(requestUserId));
      setUserIdRequiredError('');
      setFileRequiredError('');
      setStep('upload');
      setProcessMetaById((prev) => ({
        ...prev,
        [newRequestId]: {
          ...(prev[newRequestId] || {}),
          lastUpdateAt: Date.now(),
          lastStatus: 'queued',
          lastMessage: 'Queued for processing',
        },
      }));
      setActiveProcesses((prev) => {
        const exists = prev.some((entry) => entry.requestId === newRequestId);
        if (exists) return prev;
        return [...prev, { requestId: newRequestId, userId: requestUserId }];
      });
      setProcessRunTypeById((prev) => ({ ...prev, [newRequestId]: 'initial' }));
      setOverlay('Reading complete', 'Queued for background processing');
      pushNotification('Request queued', 'We will notify you when the quote is ready.');
      try {
        await withTimeout(
          processOrderGenieRequestAsync(newRequestId, { user_id: requestUserId }),
          PROCESS_START_TIMEOUT_MS,
          'Background process is taking longer than expected'
        );
      } catch (err) {
        setOverlay('Request queued', 'Still processing in the background.');
        pushNotification('Background processing', err.message || 'We will keep checking for updates.');
      }
    } catch (err) {
      setOverlay('Unable to complete request', err.message || '');
    } finally {
      setProcessing(false);
    }
  };

  const loadProcessStatus = useCallback(
    async (targetId, targetUser, options = {}) => {
      const { updateUi = false, preloadedStatus = null } = options;
      const tabUserId = String(
        (quoteTabs || []).find((tab) => String(tab.id) === String(targetId))?.userId || ''
      ).trim();
      const activeProcessUserId = String(
        (activeProcesses || []).find((entry) => String(entry.requestId) === String(targetId))?.userId || ''
      ).trim();
      const statusUser = normalizeRequestUserId(targetUser, tabUserId, activeProcessUserId, userId, statusUserId);
      if (!targetId || !statusUser) return null;
      try {
        let status = preloadedStatus || (await getOrderGenieProcessStatus(targetId, statusUser))?.status || null;
        const isCurrentRequest = String(targetId) === String(requestId || '');
        const forceUi = isCurrentRequest && isTerminalStatus(status?.request_status || status?.status);
        const shouldUpdateUi = updateUi || forceUi;
        const isRerunRequest = processRunTypeById[targetId] === 'rerun';
        let isTerminalStatusUpdate = isTerminalStatus(status?.request_status || status?.status);
        const rerunKickoffAtMs = Number(rerunKickoffAtRef.current[String(targetId)] || 0);
        const statusUpdatedAtMs = Number(new Date(status?.updated_at || 0).getTime() || 0);
        const staleRerunTerminalStatus = Boolean(
          isRerunRequest
          && isTerminalStatusUpdate
          && rerunKickoffAtMs > 0
          && (statusUpdatedAtMs <= 0 || statusUpdatedAtMs < rerunKickoffAtMs)
        );
        if (staleRerunTerminalStatus) {
          status = {
            ...(status || {}),
            request_status: 'running',
            request_step: 'matching',
            message: 'Rerun in progress',
            detail: 'Waiting for fresh rerun status...',
            updated_at: new Date().toISOString(),
          };
          isTerminalStatusUpdate = false;
        }
        if (status) {
          const itemsCount = Number(
            status?.line_items
              || (Array.isArray(status?.items_preview) ? status.items_preview.length : 0)
              || 0
          );
          const matchesCount = status.matches_preview
            ? Object.values(status.matches_preview).flat().length
            : 0;
          const matchedCount = Number(status.matched_count || 0);
          const matchedTotal = Number(status.matched_total || 0);
          const rerankCount = Number(status.rerank_count || 0);
          const rerankTotal = Number(status.rerank_total || 0);
          const aiLowConfidenceCount = Number(status.ai_low_confidence_count || 0);
          const aiLowConfidenceTotal = Number(status.ai_low_confidence_total || 0);
          const documentPageTotal = Number(status.document_page_total || 0);
          const extractionPageTotal = Number(status.extraction_page_total || 0);
          const extractionPageCompleted = Number(status.extraction_page_completed || 0);
          const extractionRowsByPage =
            status?.extraction_rows_by_page && typeof status.extraction_rows_by_page === 'object'
              ? status.extraction_rows_by_page
              : {};
          const extractionComplete = Boolean(status.extraction_complete);
          const heartbeatElapsedSeconds = Number(status.heartbeat_elapsed_seconds || 0);
          const heartbeatAt = status.heartbeat_at || null;
          const statusAiMarkdown = String(status?.ai_stream_markdown || '');
          const statusAiAgent = String(status?.ai_stream_agent || '');
          const statusAiEngine = String(status?.ai_stream_engine || '');
          const statusAiModel = String(status?.ai_stream_model || '');
          const statusAiPass = Number(status?.ai_stream_pass || 0);
          const hasStatusAiDone = Object.prototype.hasOwnProperty.call(status || {}, 'ai_stream_done');
          const statusAiDone = hasStatusAiDone ? Boolean(status?.ai_stream_done) : undefined;
          const rowRerankStatus =
            status?.row_rerank_status && typeof status.row_rerank_status === 'object'
              ? status.row_rerank_status
              : {};
          const statusSummary =
            status?.request_summary && typeof status.request_summary === 'object' && !Array.isArray(status.request_summary)
              ? status.request_summary
              : null;
          const lastUpdateAt = status.updated_at || Date.now();
          setRequestStatusById((prev) => ({
            ...prev,
            [targetId]: {
              status: status.status || prev?.[targetId]?.status || '',
              step: status.step || prev?.[targetId]?.step || '',
              request_status: status.request_status || status.status || prev?.[targetId]?.request_status || '',
              request_step: status.request_step || status.step || prev?.[targetId]?.request_step || '',
              message: status.message || prev?.[targetId]?.message || '',
              detail: status.detail || prev?.[targetId]?.detail || '',
              row_rerun_status: status.row_rerun_status || prev?.[targetId]?.row_rerun_status || '',
              row_rerun_step: status.row_rerun_step || prev?.[targetId]?.row_rerun_step || '',
              row_rerun_item_id: status.row_rerun_item_id || prev?.[targetId]?.row_rerun_item_id || null,
            },
          }));
          setProcessMetaById((prev) => ({
            ...prev,
            [targetId]: {
              ...(prev[targetId] || {}),
              lastUpdateAt,
              lastStep: status.step || (prev[targetId] || {}).lastStep,
              lastStatus: status.status || (prev[targetId] || {}).lastStatus,
              requestStep: status.request_step || status.step || (prev[targetId] || {}).requestStep,
              requestStatus: status.request_status || status.status || (prev[targetId] || {}).requestStatus,
              rowRerunStatus: status.row_rerun_status || (prev[targetId] || {}).rowRerunStatus,
              rowRerunStep: status.row_rerun_step || (prev[targetId] || {}).rowRerunStep,
              lastMessage: status.message || (prev[targetId] || {}).lastMessage,
              lastDetail: status.detail || (prev[targetId] || {}).lastDetail,
              parallelMatchThreads: Number(
                status.parallel_match_threads
                || (prev[targetId] || {}).parallelMatchThreads
                || configuredParallelMatchThreads
                || 1
              ),
              itemsCount,
              matchesCount,
              matchedCount,
              matchedTotal,
              rerankCount,
              rerankTotal,
              aiLowConfidenceCount,
              aiLowConfidenceTotal,
              documentPageTotal,
              extractionPageTotal,
              extractionPageCompleted,
              extractionRowsByPage,
              extractionComplete,
              heartbeatElapsedSeconds:
                Number.isFinite(heartbeatElapsedSeconds) && heartbeatElapsedSeconds >= 0
                  ? heartbeatElapsedSeconds
                  : (prev[targetId] || {}).heartbeatElapsedSeconds || 0,
              heartbeatAt: heartbeatAt || (prev[targetId] || {}).heartbeatAt || null,
              aiMarkdown: statusAiMarkdown || (prev[targetId] || {}).aiMarkdown || '',
              aiAgent: statusAiAgent || (prev[targetId] || {}).aiAgent || '',
              aiEngine: statusAiEngine || (prev[targetId] || {}).aiEngine || '',
              aiModel: statusAiModel || (prev[targetId] || {}).aiModel || '',
              aiPass: Number.isFinite(statusAiPass) ? statusAiPass : (prev[targetId] || {}).aiPass || 0,
              aiDone:
                typeof statusAiDone === 'boolean'
                  ? statusAiDone
                  : (prev[targetId] || {}).aiDone,
              rowRerankStatus,
              errorCount: 0,
            },
          }));
          const previewItems = Array.isArray(status?.items_preview) ? status.items_preview : [];
          const previewMatches = status?.matches_preview && typeof status.matches_preview === 'object'
            ? Object.values(status.matches_preview).flat()
            : [];
          const previewSourceFiles = Array.isArray(status?.source_files) ? status.source_files : [];
          if (statusSummary) {
            setRequestSummaryById((prev) => ({
              ...prev,
              [targetId]: mergeRequestSummary(prev?.[targetId], statusSummary),
            }));
          }
          if (previewItems.length || previewMatches.length || previewSourceFiles.length) {
            setQuoteTabs((prev) =>
              prev.map((tab) => {
                if (String(tab.id) !== String(targetId)) return tab;
                const currentItems = Array.isArray(tab.items) ? tab.items : [];
                const statusLineItems = Number(status?.line_items || 0);
                const shouldApplyPreviewItems = (
                  !isTerminalStatusUpdate
                  && previewItems.length > 0
                  && (
                    currentItems.length === 0
                    || previewItems.length >= currentItems.length
                    || (statusLineItems > 0 && statusLineItems <= previewItems.length)
                  )
                );
                const nextItems = shouldApplyPreviewItems ? previewItems : currentItems;
                let nextMatches = Array.isArray(tab.matches) ? tab.matches : [];
                if (previewMatches.length) {
                  if (isRerunRequest) {
                    nextMatches = previewMatches;
                  } else {
                    const previewKeys = new Set(
                      previewMatches.map((entry) => String(entry.item_id ?? entry.itemId ?? ''))
                    );
                    nextMatches = [
                      ...nextMatches.filter(
                        (entry) => !previewKeys.has(String(entry.item_id ?? entry.itemId ?? ''))
                      ),
                      ...previewMatches,
                    ];
                  }
                }
                if (!isTerminalStatusUpdate && previewItems.length) {
                  const allowedItemIds = new Set(previewItems.map((item) => String(item?.id ?? '')));
                  nextMatches = nextMatches.filter((entry) =>
                    allowedItemIds.has(String(entry?.item_id ?? entry?.itemId ?? ''))
                  );
                }
                const nextSourceFiles = previewSourceFiles.length ? previewSourceFiles : (tab.sourceFiles || []);
                return {
                  ...tab,
                  items: nextItems,
                  matches: nextMatches,
                  sourceFiles: nextSourceFiles,
                  requestSummary: mergeRequestSummary(tab.requestSummary, statusSummary),
                };
              })
            );
          }
        }
        if (shouldUpdateUi) {
          setProcessStatus(status);
          const isCompletedStatus = isSuccessStatus(status?.request_status || status?.status);
          if (status?.message && !isCompletedStatus) {
            setOverlay(status.message, status.detail || '');
          }
          if (status?.workflow_fallback || status?.workflow_notice) {
            pushNotification(
              'Search flow not setup',
              status?.workflow_notice || 'Using fallback default workflow.'
            );
          }
          if (status?.workflow_fallback && !workflowFallbackNotified.current.has(targetId)) {
            workflowFallbackNotified.current.add(targetId);
            pushNotification('Search flow not setup', 'Using fallback default workflow for matching.');
          }
          if (!isTerminalStatusUpdate && Array.isArray(status?.items_preview)) {
            const previewItems = status.items_preview;
            const statusLineItems = Number(status?.line_items || 0);
            setItems((prev) => {
              const currentItems = Array.isArray(prev) ? prev : [];
              const shouldApplyPreviewItems = (
                previewItems.length > 0
                && (
                  currentItems.length === 0
                  || previewItems.length >= currentItems.length
                  || (statusLineItems > 0 && statusLineItems <= previewItems.length)
                )
              );
              return shouldApplyPreviewItems ? previewItems : currentItems;
            });
          }
          if (status?.matches_preview && typeof status.matches_preview === 'object') {
            const preview = status.matches_preview || {};
            if (isRerunRequest) {
              setMatches(Object.values(preview).flat());
            } else {
              setMatches((prev) => {
                const existing = Array.isArray(prev) ? prev : [];
                const filtered = existing.filter(
                  (entry) => !preview[String(entry.item_id ?? entry.itemId ?? '')]
                );
                const nextMatches = Object.values(preview).flat();
                return [...filtered, ...nextMatches];
              });
            }
            if (step !== 'results') {
              setStep('results');
            }
          }
          if (Array.isArray(status?.source_files) && !sourceFiles.length) {
            setSourceFiles(status.source_files);
          }
          // Once extraction is available during rerun, hydrate canonical rows from DB.
          // This avoids stale in-memory rows making rerun look stuck or over-counted.
          const extractionLooksReady =
            status?.status === 'running'
            && (
              String(status?.step || '').toLowerCase() === 'parsed'
              || String(status?.step || '').toLowerCase() === 'matching'
              || String(status?.message || '').toLowerCase().includes('ai extraction updated')
            )
            && Array.isArray(status?.items_preview)
            && status.items_preview.length > 0;
          if (isCurrentRequest && isRerunRequest && extractionLooksReady) {
            if (step !== 'results') {
              setStep('results');
            }
            const statusLineItems = Number(status?.line_items || 0);
            setItems((prev) => {
              const currentItems = Array.isArray(prev) ? prev : [];
              const shouldApplyPreviewItems = (
                Array.isArray(status?.items_preview)
                && status.items_preview.length > 0
                && (
                  currentItems.length === 0
                  || status.items_preview.length >= currentItems.length
                  || (statusLineItems > 0 && statusLineItems <= status.items_preview.length)
                )
              );
              return shouldApplyPreviewItems ? status.items_preview : currentItems;
            });
            const currentMeta = processMetaRef.current[targetId] || {};
            if (!currentMeta.extractionHydrated) {
              try {
                const hydrated = await getOrderGenieRequest(targetId, statusUser);
                const hydratedItems = Array.isArray(hydrated?.items) ? hydrated.items : [];
                const hydratedMatches = mergeCartSelectionsIntoMatches(hydrated?.matches, hydrated?.cart);
                const hydratedSourceFiles = Array.isArray(hydrated?.request?.source_files)
                  ? hydrated.request.source_files
                  : [];
                if (hydratedItems.length > 0) {
                  setItems(hydratedItems);
                  setMatches(hydratedMatches);
                  setSourceFiles(hydratedSourceFiles);
                  applyDefaultSelections(hydratedItems, hydratedMatches, setSelectedByItem);
                  if (step !== 'results') {
                    setStep('results');
                  }
                }
              } catch (err) {
                // keep current preview state when hydration is transiently unavailable
              } finally {
                setProcessMetaById((prev) => ({
                  ...prev,
                  [targetId]: {
                    ...(prev[targetId] || {}),
                    extractionHydrated: true,
                  },
                }));
              }
            }
          }
          if (isSuccessStatus(status?.request_status || status?.status)) {
            const completionMatchedCount = Number(status?.matched_count || 0);
            const completionMatchedTotal = Number(status?.matched_total || 0);
            const completionRerankDone = Number(status?.rerank_count || 0);
            const completionRerankTotal = Number(status?.rerank_total || 0);
            const completionStep = String(status?.request_step || status?.step || '').toLowerCase();
            const completionStillProcessing = ['reading', 'parsing', 'processing', 'matching', 'ai_rerank', 'rerank', 'reranking', 'queued'].includes(completionStep);
            const completionMatchingDone = completionMatchedTotal <= 0 || completionMatchedCount >= completionMatchedTotal;
            const completionRerankDoneFlag = completionRerankTotal <= 0 || completionRerankDone >= completionRerankTotal;
            const stagesReadyForReview = !completionStillProcessing && completionMatchingDone && completionRerankDoneFlag;
            if (stagesReadyForReview) {
              const completionKey = String(targetId);
              if (!completionNotified.current.has(completionKey)) {
                completionNotified.current.add(completionKey);
                setQuoteReadyNotice((prev) => (prev?.requestId === targetId ? prev : { requestId: targetId }));
                pushNotification('Request ready for review', 'Ready for review');
              }
              setOverlayActive(false);
              setProcessStatus((prev) => ({
                ...(prev || {}),
                status: 'completed',
                step: 'completed',
                message: 'Ready for review',
                detail: '',
              }));
              try {
              // Retry if items/matches come back empty or below expected extraction count.
              const expectedFromRowsByPage = status?.extraction_rows_by_page
                && typeof status.extraction_rows_by_page === 'object'
                ? Object.values(status.extraction_rows_by_page).reduce((sum, value) => sum + (Number(value) || 0), 0)
                : 0;
              const expectedItemCount = Math.max(
                Number(status?.line_items || 0),
                Number(expectedFromRowsByPage || 0)
              );
              let refreshed = await getOrderGenieRequest(targetId, statusUser);
              let refreshedItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
              for (let attempt = 0; attempt < 2; attempt += 1) {
                const hasNoItems = refreshedItems.length === 0;
                const belowExpected = expectedItemCount > 0 && refreshedItems.length < expectedItemCount;
                if (!hasNoItems && !belowExpected) break;
                await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
                refreshed = await getOrderGenieRequest(targetId, statusUser);
                refreshedItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
              }
              const nextItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
              const nextMatches = mergeCartSelectionsIntoMatches(refreshed?.matches, refreshed?.cart);
              const nextSourceFiles = Array.isArray(refreshed?.request?.source_files)
                ? refreshed.request.source_files
                : [];
              const nextRequestSummary =
                refreshed?.request?.summary_json &&
                typeof refreshed.request.summary_json === 'object' &&
                !Array.isArray(refreshed.request.summary_json)
                  ? refreshed.request.summary_json
                  : {};
              setRequestSummaryById((prev) => ({ ...prev, [targetId]: nextRequestSummary }));
              // Only overwrite items/matches if we got real data back
              const finalItems = nextItems.length ? nextItems : null;
              const finalMatches = nextMatches.length
                ? nextMatches
                : null;
              if (Array.isArray(finalItems)) {
                setItems(finalItems);
              }
              if (Array.isArray(finalMatches)) {
                setMatches(finalMatches);
              }
              setSourceFiles(nextSourceFiles);
              applyDefaultSelections(finalItems || [], finalMatches || [], setSelectedByItem);
              setQuoteTabs((prev) =>
                prev.map((tab) =>
                  String(tab.id) === String(targetId)
                    ? {
                        ...tab,
                        items: Array.isArray(finalItems) ? finalItems : (tab.items || []),
                        matches: Array.isArray(finalMatches) ? finalMatches : (tab.matches || []),
                        sourceFiles: nextSourceFiles,
                        requestSummary: nextRequestSummary,
                      }
                    : tab
                )
              );
              setQuoteSelections((prev) => {
                const current = prev[targetId] || {};
                let changed = false;
                const next = { ...prev };
                const grouped = {};
                (nextMatches || []).forEach((entry) => {
                  const key = String(entry.item_id ?? entry.itemId ?? '');
                  if (!key) return;
                  grouped[key] = grouped[key] || [];
                  grouped[key].push(entry);
                });
                Object.keys(grouped).forEach((key) => {
                  grouped[key] = [...grouped[key]].sort((left, right) => {
                    const leftAiRanked = Boolean(left?.ai_ranked);
                    const rightAiRanked = Boolean(right?.ai_ranked);
                    if (leftAiRanked !== rightAiRanked) return leftAiRanked ? -1 : 1;
                    const leftAiRank = Number(left?.ai_rank);
                    const rightAiRank = Number(right?.ai_rank);
                    const leftAiRankFinite = Number.isFinite(leftAiRank);
                    const rightAiRankFinite = Number.isFinite(rightAiRank);
                    if (leftAiRankFinite && rightAiRankFinite && leftAiRank !== rightAiRank) return leftAiRank - rightAiRank;
                    if (leftAiRankFinite !== rightAiRankFinite) return leftAiRankFinite ? -1 : 1;
                    const leftRank = Number(left?.rank);
                    const rightRank = Number(right?.rank);
                    const leftRankFinite = Number.isFinite(leftRank);
                    const rightRankFinite = Number.isFinite(rightRank);
                    if (leftRankFinite && rightRankFinite && leftRank !== rightRank) return leftRank - rightRank;
                    if (leftRankFinite !== rightRankFinite) return leftRankFinite ? -1 : 1;
                    const leftScore = Number(left?.score);
                    const rightScore = Number(right?.score);
                    const leftScoreFinite = Number.isFinite(leftScore);
                    const rightScoreFinite = Number.isFinite(rightScore);
                    if (leftScoreFinite && rightScoreFinite && leftScore !== rightScore) return rightScore - leftScore;
                    if (leftScoreFinite !== rightScoreFinite) return leftScoreFinite ? -1 : 1;
                    return 0;
                  });
                });
                (nextItems || []).forEach((item) => {
                  const itemKey = String(item.id || '');
                  if (!itemKey || current[itemKey]) return;
                  const options = grouped[itemKey] || [];
                  if (options.length) {
                    current[itemKey] = options[0].product_code;
                    changed = true;
                  }
                });
                if (!changed) return prev;
                next[targetId] = { ...current };
                return next;
              });
            } catch (err) {
              // Keep current rows/matches on transient fetch failures.
            }
            setStep('results');
            }
          }
          if ((status?.request_status || status?.status) === 'failed') {
            pushNotification('Request failed', status.detail || 'Unable to complete request');
            setOverlay('Request failed', status.detail || 'Unable to complete request');
          } else if ((status?.request_status || status?.status) === 'cancelled') {
            pushNotification('Request stopped', status.detail || 'Processing was stopped by user.');
            setOverlayActive(false);
          }
        } else if (isSuccessStatus(status?.request_status || status?.status)) {
          const completionKey = String(targetId);
          if (!completionNotified.current.has(completionKey)) {
            completionNotified.current.add(completionKey);
            setQuoteReadyNotice((prev) => (prev?.requestId === targetId ? prev : { requestId: targetId }));
            pushNotification('Request ready for review', 'Ready for review');
          }
          try {
            const refreshed = await getOrderGenieRequest(targetId, statusUser);
            const nextItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
            const nextMatches = mergeCartSelectionsIntoMatches(refreshed?.matches, refreshed?.cart);
            const nextSourceFiles = Array.isArray(refreshed?.request?.source_files)
              ? refreshed.request.source_files
              : [];
            const nextRequestSummary =
              refreshed?.request?.summary_json &&
              typeof refreshed.request.summary_json === 'object' &&
              !Array.isArray(refreshed.request.summary_json)
                ? refreshed.request.summary_json
                : {};
            setRequestSummaryById((prev) => ({ ...prev, [targetId]: nextRequestSummary }));
            setQuoteTabs((prev) =>
              prev.map((tab) =>
                String(tab.id) === String(targetId)
                  ? {
                      ...tab,
                      items: nextItems,
                      matches: nextMatches,
                      sourceFiles: nextSourceFiles,
                      requestSummary: nextRequestSummary,
                    }
                  : tab
              )
            );
            setQuoteSelections((prev) => {
              const current = prev[targetId] || {};
              let changed = false;
              const next = { ...prev };
              const grouped = {};
              (nextMatches || []).forEach((entry) => {
                const key = String(entry.item_id ?? entry.itemId ?? '');
                if (!key) return;
                grouped[key] = grouped[key] || [];
                grouped[key].push(entry);
              });
              (nextItems || []).forEach((item) => {
                const itemKey = String(item.id || '');
                if (!itemKey || current[itemKey]) return;
                const options = grouped[itemKey] || [];
                if (options.length) {
                  current[itemKey] = options[0].product_code;
                  changed = true;
                }
              });
              if (!changed) return prev;
              next[targetId] = { ...current };
              return next;
            });
          } catch (err) {
            // ignore refresh failures
          }
        } else if ((status?.request_status || status?.status) === 'failed') {
          pushNotification('Request failed', status.detail || 'Unable to complete request');
        } else if ((status?.request_status || status?.status) === 'cancelled') {
          pushNotification('Request stopped', status.detail || 'Processing was stopped by user.');
        }
        return status;
      } catch (err) {
        const meta = processMetaRef.current[targetId] || {};
        const nextErrorCount = (meta.errorCount || 0) + 1;
        setProcessMetaById((prev) => ({
          ...prev,
          [targetId]: {
            ...(prev[targetId] || {}),
            errorCount: nextErrorCount,
            lastErrorAt: Date.now(),
          },
        }));
        if (updateUi) {
          const lastUpdate = meta.lastUpdateAt;
          const lastMessage = meta.lastMessage ? ` • ${meta.lastMessage}` : '';
          setProcessStatus((prev) => ({
            ...(prev || {}),
            status: prev?.status || 'running',
            step: prev?.step || meta.lastStep,
            message: 'Waiting for server response',
            detail: lastUpdate
              ? `Last update ${formatTimestamp(lastUpdate)}${lastMessage}`
              : 'No updates received yet.',
          }));
        }
        return null;
      }
    },
    [
      activeProcesses,
      configuredParallelMatchThreads,
      mergeCartSelectionsIntoMatches,
      mergeRequestSummary,
      normalizeRequestUserId,
      processRunTypeById,
      pushNotification,
      quoteTabs,
      requestId,
      sourceFiles.length,
      statusUserId,
      step,
      userId,
    ]
  );

  const stopRunningRequest = useCallback(
    async (targetId, targetUser) => {
      const stopUser = String(targetUser || userId || '').trim();
      if (!targetId || !stopUser) return;
      const ok = await confirmDialog({
        title: 'Stop running request',
        content: 'Do you want to stop this running request?',
      });
      if (!ok) return;
      await stopOrderGenieProcess(targetId, { user_id: stopUser });
      setActiveProcesses((prev) =>
        prev.filter((entry) => String(entry.requestId) !== String(targetId))
      );
      setProcessRunTypeById((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      delete rerunKickoffAtRef.current[String(targetId)];
      setRequestStatusById((prev) => ({
        ...prev,
        [targetId]: {
          status: 'cancelled',
          message: 'Stopped',
          detail: 'Processing was stopped by user.',
        },
      }));
      if (String(targetId) === String(requestId || '')) {
        setProcessStatus((prev) => ({
          ...(prev || {}),
          status: 'cancelled',
          step: 'cancelled',
          message: 'Request stopped',
          detail: 'Processing was stopped by user.',
        }));
        setOverlayActive(false);
      }
      pushNotification('Request stopped', 'Processing was stopped by user.');
      await loadProcessStatus(targetId, stopUser, { updateUi: String(targetId) === String(requestId || '') });
    },
    [loadProcessStatus, pushNotification, requestId, userId]
  );

  const startRequestRerun = useCallback(
    async (targetRequestId, targetUserId) => {
      const rerunUser = String(targetUserId || '').trim();
      if (!targetRequestId || !rerunUser) return;
      rerunKickoffAtRef.current[String(targetRequestId)] = Date.now();
      setOverlay('Request rerun started', 'Streaming updates every 5 seconds');
      await processOrderGenieRequestAsync(targetRequestId, { user_id: rerunUser });
      setActiveProcesses((prev) => {
        const exists = prev.some((entry) => entry.requestId === targetRequestId);
        if (exists) return prev;
        return [...prev, { requestId: targetRequestId, userId: rerunUser }];
      });
      setProcessRunTypeById((prev) => ({ ...prev, [targetRequestId]: 'rerun' }));
      setProcessMetaById((prev) => ({
        ...prev,
        [targetRequestId]: {
          ...(prev[targetRequestId] || {}),
          extractionHydrated: false,
        },
      }));
      completionNotified.current.delete(String(targetRequestId));
      setQuoteTabs((prev) =>
        prev.map((tab) =>
          String(tab.id) === String(targetRequestId)
            ? { ...tab, items: [], matches: [] }
            : tab
        )
      );
      setV3ReviewOpenedByRequest((prev) => ({ ...prev, [String(targetRequestId)]: false }));
      setRequestStatusById((prev) => ({
        ...prev,
        [targetRequestId]: {
          status: 'running',
          message: 'Rerun in progress',
          detail: 'Streaming updates every 5 seconds...',
        },
      }));
      if (String(targetRequestId) === String(requestId || '')) {
        setItems([]);
        setMatches([]);
        setSelectedByItem({});
        setProcessStatus((prev) => ({
          ...(prev || {}),
          status: 'running',
          step: prev?.step || 'matching',
          message: 'Rerun in progress',
          detail: 'Streaming updates every 5 seconds...',
        }));
      }
      pushNotification('Request rerun started', 'We will notify you when the quote is ready.');
      await loadProcessStatus(targetRequestId, rerunUser, {
        updateUi: String(targetRequestId) === String(requestId || ''),
      });
    },
    [loadProcessStatus, pushNotification, requestId]
  );

  const probeRequestFromList = useCallback(
    async (targetRequestId, targetUserId) => {
      const probeUser = String(targetUserId || '').trim();
      if (!targetRequestId || !probeUser) return null;
      const resp = await probeOrderGenieRequest(targetRequestId, probeUser);
      return resp?.probe || null;
    },
    []
  );

  const openProbeModal = useCallback(
    async (req, targetUserId) => {
      const requestIdValue = Number(req?.id || 0);
      const requestUser = String(targetUserId || '').trim();
      if (!requestIdValue || !requestUser) return;
      setProbeModalRequest(req);
      setProbeModalData(null);
      setProbeModalDiagnose(null);
      setProbeModalError('');
      setProbeModalOpen(true);
      setProbeModalLoading(true);
      try {
        const probeUser = String(targetUserId || '').trim();
        const [probe, diagnose] = await Promise.all([
          probeRequestFromList(requestIdValue, probeUser),
          getReadinessDiagnoseRequest(requestIdValue, { user_id: probeUser }).catch(() => null),
        ]);
        setProbeModalData(probe);
        setProbeModalDiagnose(diagnose);
      } catch (err) {
        const message = err?.message || 'Unable to collect process diagnostics.';
        setProbeModalError(message);
        pushNotification('Probe failed', message);
      } finally {
        setProbeModalLoading(false);
      }
    },
    [probeRequestFromList, pushNotification]
  );

  const resumeRequestFromCheckpoint = useCallback(
    async (targetRequestId, targetUserId, options = {}) => {
      const resumeUser = String(targetUserId || '').trim();
      if (!targetRequestId || !resumeUser) return;
      const fromProbeModal = Boolean(options?.fromProbeModal);
      await processOrderGenieRequestAsync(targetRequestId, {
        user_id: resumeUser,
        resume_from_checkpoint: true,
      });
      setActiveProcesses((prev) => {
        const exists = prev.some((entry) => String(entry.requestId) === String(targetRequestId));
        if (exists) return prev;
        return [...prev, { requestId: targetRequestId, userId: resumeUser }];
      });
      setProcessRunTypeById((prev) => ({ ...prev, [targetRequestId]: 'rerun' }));
      pushNotification('Resume started', 'Continuing this request from the last saved checkpoint.');
      await loadProcessStatus(targetRequestId, resumeUser, {
        updateUi: String(targetRequestId) === String(requestId || ''),
      });
      if (fromProbeModal) {
        setProbeModalOpen(false);
        setProbeModalData(null);
        setProbeModalDiagnose(null);
        setProbeModalError('');
      }
    },
    [loadProcessStatus, pushNotification, requestId]
  );

  useEffect(() => {
    if (!activeProcesses.length) return;
    let cancelled = false;
    let inFlight = false;
    const poll = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const requestIds = activeProcesses
          .map((entry) => Number(entry?.requestId))
          .filter((value) => Number.isFinite(value) && value > 0);
        const requestUsers = activeProcesses.reduce((acc, entry) => {
          const id = Number(entry?.requestId);
          const processUser = String(entry?.userId || '').trim();
          if (Number.isFinite(id) && id > 0 && processUser) {
            acc[String(id)] = processUser;
          }
          return acc;
        }, {});
        let bulkStatuses = {};
        if (requestIds.length) {
          try {
            const bulkResp = await getOrderGenieProcessStatusBulk({
              request_ids: requestIds,
              request_users: requestUsers,
            });
            bulkStatuses =
              bulkResp?.statuses && typeof bulkResp.statuses === 'object'
                ? bulkResp.statuses
                : {};
          } catch (err) {
            bulkStatuses = {};
          }
        }
        const results = await Promise.all(
          activeProcesses.map(async (entry) => {
            const isCurrentRequest = String(entry.requestId) === String(requestId || '');
            const isCurrentRerun = isCurrentRequest && processRunTypeById[entry.requestId] === 'rerun';
            const preloadedStatus = bulkStatuses[String(entry.requestId)] || null;
            const status = await loadProcessStatus(entry.requestId, entry.userId, {
              // Keep current request processStatus hydrated even after UI leaves "processing".
              updateUi: isCurrentRequest || isCurrentRerun,
              preloadedStatus,
            });
            return { ...entry, status };
          })
        );
        const completed = results
          .filter((entry) => (
            isSuccessStatus(entry.status?.request_status || entry.status?.status) ||
            (entry.status?.request_status || entry.status?.status) === 'failed' ||
            (entry.status?.request_status || entry.status?.status) === 'cancelled'
          ))
          .map((entry) => entry.requestId);
        if (completed.length) {
          setActiveProcesses((prev) => prev.filter((entry) => !completed.includes(entry.requestId)));
          setProcessRunTypeById((prev) => {
            const next = { ...prev };
            completed.forEach((id) => {
              delete next[id];
              delete rerunKickoffAtRef.current[String(id)];
            });
            return next;
          });
        }
        if (requestId && step === 'processing') {
          const meta = processMetaRef.current[requestId] || {};
          if (meta.lastUpdateAt) {
            const lastUpdateMs = new Date(meta.lastUpdateAt).getTime();
            const age = Number.isNaN(lastUpdateMs) ? 0 : Date.now() - lastUpdateMs;
            if (age > PROCESS_POLL_STALE_MS) {
              if (!processStaleNotified.current.has(requestId)) {
                processStaleNotified.current.add(requestId);
                pushNotification(
                  'Processing delayed',
                  'No recent updates from the server. We will keep checking.'
                );
              }
              setProcessStatus((prev) => ({
                ...(prev || {}),
                status: prev?.status || 'running',
                step: prev?.step || meta.lastStep,
                message: 'Processing delayed',
                detail: `No updates for ${Math.round(age / 1000)}s. Last update ${formatTimestamp(
                  meta.lastUpdateAt
                )}.`,
              }));
            }
          }
        }
      } finally {
        inFlight = false;
      }
    };
    poll();
    const timer = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeProcesses, requestId, step, loadProcessStatus, processRunTypeById, pushNotification]);

  useEffect(() => {
    const tabRequestId = String(activeTab || '').startsWith('quote-')
      ? String(activeTab || '').replace('quote-', '').trim()
      : '';
    const targetRequestId = String(requestId || tabRequestId || '').trim();
    if (!targetRequestId) return;
    const currentStatus =
      String(
        getUnifiedRequestStatus(targetRequestId, {
          statusById: requestStatusById,
          fallback: processStatus,
          requestStatusFallback: requestStatusById[targetRequestId],
        })?.status || ''
      ).toLowerCase();
    const onRequestScreen = step === 'processing' || String(activeTab || '').startsWith('quote-');
    if (!onRequestScreen) return;
    if (isSuccessStatus(currentStatus) || currentStatus === 'failed' || currentStatus === 'cancelled') return;
    // This request is already covered by bulk polling in the activeProcesses loop.
    // Avoid duplicate per-request GET /process-status calls.
    const hasActiveBulkPoll = (activeProcesses || []).some(
      (entry) => String(entry?.requestId || '') === String(targetRequestId)
    );
    if (hasActiveBulkPoll) return;

    const tabUserId = String(
      (quoteTabs || []).find((tab) => String(tab.id) === String(targetRequestId))?.userId || ''
    ).trim();
    const activeProcessUserId = String(
      (activeProcesses || []).find((entry) => String(entry.requestId) === String(targetRequestId))?.userId || ''
    ).trim();
    const targetUserId = normalizeRequestUserId(userId, tabUserId, activeProcessUserId, statusUserId);
    if (!targetUserId) return;

    let cancelled = false;
    let inFlight = false;
    const pollCurrentRequest = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        await loadProcessStatus(targetRequestId, targetUserId, { updateUi: true });
      } finally {
        inFlight = false;
      }
    };

    pollCurrentRequest();
    const timer = setInterval(pollCurrentRequest, 8000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    activeProcesses,
    activeTab,
    getUnifiedRequestStatus,
    loadProcessStatus,
    normalizeRequestUserId,
    processStatus,
    quoteTabs,
    requestId,
    requestStatusById,
    statusUserId,
    step,
    userId,
  ]);

  useEffect(() => {
    const targetRequestId = String(requestId || '').trim();
    const tabUserId = String(
      (quoteTabs || []).find((tab) => String(tab.id) === String(targetRequestId))?.userId || ''
    ).trim();
    const activeProcessUserId = String(
      (activeProcesses || []).find((entry) => String(entry.requestId) === String(targetRequestId))?.userId || ''
    ).trim();
    const targetUserId = normalizeRequestUserId(userId, tabUserId, activeProcessUserId, statusUserId);
    const expectedCount = Number(processStatus?.line_items || 0);
    const currentCount = Array.isArray(items) ? items.length : 0;
    if (!targetRequestId || !targetUserId) return;
    const onReviewScreen = step === 'results' || String(activeTab || '').startsWith('quote-');
    if (!onReviewScreen) return;
    if (expectedCount <= 0) return;
    if (currentCount >= expectedCount) return;
    if (fullHydrationInFlightRef.current[targetRequestId]) return;
    fullHydrationInFlightRef.current[targetRequestId] = true;
    (async () => {
      try {
        let refreshed = await getOrderGenieRequest(targetRequestId, targetUserId);
        let refreshedItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
        for (let attempt = 0; attempt < 2; attempt += 1) {
          if (refreshedItems.length >= expectedCount) break;
          await new Promise((res) => setTimeout(res, 800 * (attempt + 1)));
          refreshed = await getOrderGenieRequest(targetRequestId, targetUserId);
          refreshedItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
        }
        if (refreshedItems.length > currentCount) {
          const refreshedMatches = mergeCartSelectionsIntoMatches(refreshed?.matches, refreshed?.cart);
          const refreshedSourceFiles = Array.isArray(refreshed?.request?.source_files)
            ? refreshed.request.source_files
            : [];
          setItems(refreshedItems);
          setMatches(refreshedMatches);
          setSourceFiles(refreshedSourceFiles);
          applyDefaultSelections(refreshedItems, refreshedMatches, setSelectedByItem);
          setQuoteTabs((prev) =>
            prev.map((tab) =>
              String(tab.id) === String(targetRequestId)
                ? {
                    ...tab,
                    items: refreshedItems,
                    matches: refreshedMatches,
                    sourceFiles: refreshedSourceFiles,
                  }
                : tab
            )
          );
        }
      } catch (err) {
        // ignore transient hydration errors
      } finally {
        fullHydrationInFlightRef.current[targetRequestId] = false;
      }
    })();
  }, [activeProcesses, activeTab, items, mergeCartSelectionsIntoMatches, normalizeRequestUserId, processStatus?.line_items, quoteTabs, requestId, statusUserId, step, userId]);

  const handleCameraClick = () => {
    setCameraError('');
    setCameraModalOpen(true);
  };

  const handleCameraChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setFiles((prev) => (isV3 ? [file] : [...prev, file]));
      setFileRequiredError('');
      setUploadSheetNames([]);
      setUploadSelectedSheet('');
      setUploadSheetCheckLoading(/\.(xlsx|xls)$/i.test(String(file?.name || '').trim()));
      setUploadSheetCheckReady(!/\.(xlsx|xls)$/i.test(String(file?.name || '').trim()));
      event.target.value = '';
    }
  };

  const addPastedFiles = useCallback((incomingFiles) => {
    const normalized = (Array.isArray(incomingFiles) ? incomingFiles : [])
      .filter(Boolean)
      .map((file, index) => {
        if (file?.uid) return file;
        return Object.assign(file, {
          uid: `pasted-${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
        });
      });
    if (!normalized.length) return false;
    setFiles((prev) => (isV3 ? [normalized[normalized.length - 1]] : [...prev, ...normalized]));
    setFileRequiredError('');
    setUploadPreviewHtml('');
    setUploadSheetNames([]);
    setUploadSelectedSheet('');
    setUploadSheetCheckLoading(false);
    setUploadSheetCheckReady(false);
    return true;
  }, [isV3]);

  const handlePasteFromClipboard = useCallback((clipboardData) => {
    const items = Array.from(clipboardData?.items || []);
    const imageItem = items.find((item) => String(item?.type || '').toLowerCase().startsWith('image/'));
    if (imageItem && typeof imageItem.getAsFile === 'function') {
      const rawImage = imageItem.getAsFile();
      if (rawImage) {
        const extension = String(rawImage.type || 'image/png').split('/')[1] || 'png';
        const pastedImage = new File([rawImage], `pasted-image-${Date.now()}.${extension}`, {
          type: rawImage.type || 'image/png',
        });
        addPastedFiles([pastedImage]);
        return true;
      }
    }
    const pastedHtml = String(clipboardData?.getData?.('text/html') || '').trim();
    const pastedTextPlain = String(clipboardData?.getData?.('text/plain') || '').trim();
    const pastedTextRaw = pastedTextPlain || String(clipboardData?.getData?.('text') || '').trim();
    if (pastedHtml) {
      const cleanHtml = sanitizeRichHtml(pastedHtml);
      const htmlAsText = normalizeHtmlAsText(cleanHtml);
      if (cleanHtml || htmlAsText) {
        const pastedTextFile = new File([htmlAsText || cleanHtml], `pasted-text-${Date.now()}.txt`, { type: 'text/plain' });
        addPastedFiles([pastedTextFile]);
        setUploadPreviewHtml(cleanHtml);
        setUploadPreviewText(htmlAsText);
        return true;
      }
    }
    const pastedText = pastedTextRaw || (pastedHtml ? normalizeHtmlAsText(pastedHtml) : '');
    if (pastedText) {
      const pastedTextFile = new File([pastedText], `pasted-text-${Date.now()}.txt`, { type: 'text/plain' });
      addPastedFiles([pastedTextFile]);
      setUploadPreviewHtml('');
      setUploadPreviewText(pastedText);
      return true;
    }
    return false;
  }, [addPastedFiles, normalizeHtmlAsText, sanitizeRichHtml]);

  const activatePasteCapture = useCallback(() => {
    const input = pasteInputRef.current;
    if (!input) return;
    input.style.pointerEvents = 'auto';
    input.focus();
  }, []);

  const releasePasteCapture = useCallback(() => {
    const input = pasteInputRef.current;
    if (!input) return;
    input.value = '';
    input.blur();
    input.style.pointerEvents = 'none';
  }, []);

  const handlePasteTileClick = useCallback(async () => {
    try {
      if (navigator?.clipboard?.read) {
        const entries = await navigator.clipboard.read();
        for (const entry of entries) {
          const imageType = (entry.types || []).find((type) => String(type || '').toLowerCase().startsWith('image/'));
          if (imageType) {
            const blob = await entry.getType(imageType);
            if (blob) {
              const extension = String(blob.type || 'image/png').split('/')[1] || 'png';
              const pastedImage = new File([blob], `pasted-image-${Date.now()}.${extension}`, {
                type: blob.type || 'image/png',
              });
              addPastedFiles([pastedImage]);
              return;
            }
          }
        }
      }
      if (navigator?.clipboard?.readText) {
        const text = String(await navigator.clipboard.readText() || '').trim();
        if (text) {
          const pastedTextFile = new File([text], `pasted-text-${Date.now()}.txt`, { type: 'text/plain' });
          addPastedFiles([pastedTextFile]);
          setUploadPreviewHtml('');
          setUploadPreviewText(text);
          return;
        }
      }
    } catch (_) {
      // Fallback to manual paste below.
    }
    setFileRequiredError('Press Ctrl/Cmd+V, right-click Paste, or long-press paste area on touch devices.');
    activatePasteCapture();
  }, [activatePasteCapture, addPastedFiles]);

  const handlePasteTilePaste = useCallback((event) => {
    event.preventDefault();
    const accepted = handlePasteFromClipboard(event.clipboardData);
    if (!accepted) {
      setFileRequiredError('Clipboard does not contain supported text or image content.');
    }
    releasePasteCapture();
  }, [handlePasteFromClipboard, releasePasteCapture]);

  useEffect(() => {
    if (!cameraModalOpen) return;
    let mounted = true;
    let localStream = null;
    const openCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (!mounted) return;
        localStream = stream;
        setCameraStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        if (!mounted) return;
        setCameraError('Camera access is not available. Please allow permissions or use upload.');
      }
    };
    openCamera();
    return () => {
      mounted = false;
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
      setCameraStream(null);
    };
  }, [cameraModalOpen]);

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFiles((prev) => (isV3 ? [file] : [...prev, file]));
      setFileRequiredError('');
      setUploadSheetNames([]);
      setUploadSelectedSheet('');
      setUploadSheetCheckLoading(false);
      setUploadSheetCheckReady(false);
      setCameraModalOpen(false);
    }, 'image/jpeg');
  };

  const handleClearUploadForm = useCallback(async () => {
    const defaultRequestName = buildDefaultName(userId.trim());
    const hasSelectedFiles = files.length > 0;
    const hasCustomRequestName = String(requestName || '').trim() !== String(defaultRequestName || '').trim();
    if (!hasSelectedFiles && !hasCustomRequestName) return;

    const ok = await confirmDialog({
      title: 'Clear form',
      content: 'Clear selected files and reset this new request form?',
    });
    if (!ok) return;

    setFiles([]);
    setRequestName(defaultRequestName);
    setFileRequiredError('');
    setUserIdRequiredError('');
    setPreviewModalFile(null);
    setUploadSheetNames([]);
    setUploadSelectedSheet('');
    setUploadSheetCheckLoading(false);
    setUploadSheetCheckReady(false);
    setCameraModalOpen(false);
    setCameraError('');
  }, [files.length, requestName, userId]);

  const loadErpCustomers = useCallback(async (query) => {
    const seq = ++erpCustomerSearchSeqRef.current;
    setErpCustomerLoading(true);
    try {
      const resp = await searchOrderGenieErpCustomers(query);
      if (seq !== erpCustomerSearchSeqRef.current) return;
      setErpCustomerOptions(Array.isArray(resp?.items) ? resp.items : []);
    } catch (err) {
      if (seq !== erpCustomerSearchSeqRef.current) return;
      setErpCustomerOptions([]);
    } finally {
      if (seq === erpCustomerSearchSeqRef.current) {
        setErpCustomerLoading(false);
      }
    }
  }, []);

  const searchErpCustomersDebounced = useCallback((query) => {
    if (erpCustomerSearchTimerRef.current) {
      clearTimeout(erpCustomerSearchTimerRef.current);
    }
    const trimmed = String(query || '').trim();
    if (trimmed.length < 2) {
      erpCustomerSearchSeqRef.current += 1;
      setErpCustomerOptions([]);
      setErpCustomerLoading(false);
      return;
    }
    setErpCustomerLoading(true);
    erpCustomerSearchTimerRef.current = setTimeout(() => {
      loadErpCustomers(trimmed);
    }, 2000);
  }, [loadErpCustomers]);

  const loadShipToAddresses = useCallback(async (customerId) => {
    const cid = String(customerId || '').trim();
    if (!cid) {
      setShipToOptions([]);
      setShipToLoading(false);
      return;
    }
    setShipToLoading(true);
    try {
      const resp = await searchOrderGenieErpShipToAddresses(cid);
      setShipToOptions(Array.isArray(resp?.items) ? resp.items : []);
    } catch (err) {
      setShipToOptions([]);
    } finally {
      setShipToLoading(false);
    }
  }, []);

  const openUserEditor = useCallback(() => {
    setUserIdDraft(userId);
    setCustomerIdAsInErpDraft(customerIdAsInErp);
    setShipToIdAsInErpDraft(shipToIdAsInErp);
    setUserEditorOpen(true);
    setErpCustomerOptions([]);
    setShipToOptions([]);
    if (String(customerIdAsInErp || '').trim()) {
      loadShipToAddresses(customerIdAsInErp);
    }
  }, [customerIdAsInErp, loadShipToAddresses, shipToIdAsInErp, userId]);

  const saveHeaderUserId = useCallback(() => {
    const nextUserId = String(userIdDraft || '').trim();
    if (!nextUserId) {
      setUserIdRequiredError('User ID is required before starting.');
      return;
    }
    const nextCustomerIdAsInErp = String(customerIdAsInErpDraft || '').trim();
    // The customer AutoComplete's onSearch/onSelect already clear shipToIdAsInErpDraft
    // whenever the customer draft changes, so the draft here is always consistent with it.
    const nextShipToIdAsInErp = String(shipToIdAsInErpDraft || '').trim();
    setUserId(nextUserId);
    setCustomerIdAsInErp(nextCustomerIdAsInErp);
    setShipToIdAsInErp(nextShipToIdAsInErp);
    setUserIdRequiredError('');
    setStatusUserId((prev) => {
      const next = prev && prev !== 'all' ? prev : nextUserId;
      setStatusUserIdDraft(next);
      return next;
    });
    setRequestName((prev) => {
      const current = String(prev || '').trim();
      if (!current || current === buildDefaultName() || current === buildDefaultName(userId)) {
        return buildDefaultName(nextUserId);
      }
      return prev;
    });
    setUserEditorOpen(false);
  }, [customerIdAsInErpDraft, shipToIdAsInErpDraft, userId, userIdDraft]);

  const renderUpload = () => {
    const hasRichUploadPreview = Boolean(String(uploadPreviewHtml || '').trim());
    return (
    <div className="row g-4">
      <div className={`col-12 ${isV3 ? 'col-lg-5' : 'col-lg-7'}`}>
        <Card className="shadow-sm border-0">
          <h4 className="mb-2">Start a new quote/order request</h4>
          <p className="text-muted mb-4">Upload a document, paste text/image, or snap a photo. We will extract line items and match products.</p>
          {(() => {
            const defaultRequestName = buildDefaultName(userId.trim());
            const hasExcelUpload = (files || []).some((file) => {
              const fileName = String(file?.name || file?.filename || '').toLowerCase();
              return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
            });
            const needsSheetSelection = hasExcelUpload && uploadSheetNames.length > 1;
            const canStartExcelProcessing = !hasExcelUpload
              || (
                uploadSheetCheckReady
                && !uploadSheetCheckLoading
                && (!needsSheetSelection || String(uploadSelectedSheet || '').trim().length > 0)
              );
            const isUploadFormDirty =
              files.length > 0
              || String(requestName || '').trim() !== String(defaultRequestName || '').trim();
            return (
              <>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Request Name</label>
              <Input value={requestName} onChange={(e) => setRequestName(e.target.value)} />
              {userIdRequiredError && (
                <div className="text-danger small mt-1">
                  {userIdRequiredError} Set it from the header (top-right).
                </div>
              )}
            </div>
          </div>
          <div className="row g-3 mt-4 align-items-stretch">
            <div className="col-12 col-md-4 d-flex">
              <Dragger
                multiple={!isV3}
                maxCount={isV3 ? 1 : undefined}
                showUploadList={false}
                className={`order-genie-v2-upload-tile ${isV3 ? 'order-genie-v2-upload-tile--compact' : ''} w-100 ${fileRequiredError ? 'border border-danger' : ''}`}
                beforeUpload={(file) => {
                  const isExcel = /\.(xlsx|xls)$/i.test(String(file?.name || '').trim());
                  setFiles((prev) => (isV3 ? [file] : [...prev, file]));
                  setFileRequiredError('');
                  setUploadSheetNames([]);
                  setUploadSelectedSheet('');
                  setUploadSheetCheckLoading(isExcel);
                  setUploadSheetCheckReady(!isExcel);
                  return false;
                }}
                onRemove={(file) => {
                  setFiles((prev) => prev.filter((item) => item.uid !== file.uid));
                  setUploadSheetNames([]);
                  setUploadSelectedSheet('');
                  setUploadSheetCheckLoading(false);
                  setUploadSheetCheckReady(false);
                }}
                fileList={files}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Upload files</p>
                <p className="ant-upload-hint text-muted">PDF, Excel, CSV, images supported.</p>
              </Dragger>
            </div>
            <div className="col-12 col-md-4 d-flex">
              <button
                type="button"
                className={`order-genie-v2-camera-tile ${isV3 ? 'order-genie-v2-camera-tile--compact' : ''} h-100 w-100`}
                onClick={handleCameraClick}
              >
                <CameraOutlined className="order-genie-v2-camera-icon" />
                <div className="fw-semibold">Use Camera</div>
                <div className="text-muted small">Capture a photo</div>
              </button>
            </div>
            <div className="col-12 col-md-4 d-flex">
              <div
                className={`order-genie-v2-camera-tile ${isV3 ? 'order-genie-v2-camera-tile--compact' : ''} h-100 w-100`}
                onClick={handlePasteTileClick}
                onContextMenu={() => {
                  activatePasteCapture();
                }}
                onTouchStart={() => {
                  activatePasteCapture();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handlePasteTileClick();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <MessageOutlined className="order-genie-v2-camera-icon" />
                <div className="fw-semibold">Paste (Beta)</div>
                <div className="text-muted small">Paste text or image</div>
                <textarea
                  ref={pasteInputRef}
                  className="order-genie-v2-paste-capture"
                  aria-label="Paste text or image"
                  placeholder=""
                  onPaste={handlePasteTilePaste}
                  onBlur={releasePasteCapture}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="text-muted small">Paste (Beta) tip: right-click or long-press anywhere on the Paste tile, then choose Paste.</div>
            </div>
            {files.length > 0 && (
              <div className="col-12">
                <div className="order-genie-v2-upload-selected-list">
                  {files.map((file, index) => {
                    const name = String(file?.name || '').trim() || `File ${index + 1}`;
                    const uid = String(file?.uid || `${index}`);
                    return (
                      <div key={uid} className="order-genie-v2-upload-selected-item">
                        <span className="order-genie-v2-upload-selected-name" title={name}>{name}</span>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 order-genie-v2-upload-selected-remove"
                          onClick={() => {
                            setFiles((prev) => prev.filter((entry) => String(entry?.uid || '') !== uid));
                            setUploadSheetNames([]);
                            setUploadSelectedSheet('');
                            setUploadSheetCheckLoading(false);
                            setUploadSheetCheckReady(false);
                          }}
                        >
                          Remove file
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
              {fileRequiredError && (
              <div className="col-12">
                <div
                  className="text-danger small text-start"
                  style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                  {fileRequiredError}
                </div>
              </div>
            )}
            {needsSheetSelection && (
              <div className="col-12">
                <label className="form-label">Select sheet to process</label>
                <select
                  className="form-select"
                  value={uploadSelectedSheet}
                  onChange={(event) => {
                    setUploadSelectedSheet(String(event.target.value || '').trim());
                    setFileRequiredError('');
                  }}
                >
                  <option value="">Select a sheet</option>
                  {uploadSheetNames.map((sheetName) => (
                    <option key={sheetName} value={sheetName}>
                      {sheetName}
                    </option>
                  ))}
                </select>
                <div className="text-muted small mt-1">Only the selected sheet will be processed.</div>
              </div>
            )}
            {hasExcelUpload && uploadSheetCheckLoading && (
              <div className="col-12">
                <div className="text-muted small">Detecting sheets in the uploaded Excel file...</div>
              </div>
            )}
          </div>
          <div className="mt-3 d-flex gap-2">
            <Button
              type="default"
              className="w-50"
              onClick={handleClearUploadForm}
              disabled={processing || !isUploadFormDirty}
            >
              Clear Form
            </Button>
            <Button
              type="primary"
              className="w-50"
              loading={processing}
              disabled={!canStartExcelProcessing}
              onClick={handleStart}
            >
              Start Processing
            </Button>
          </div>
              </>
            );
          })()}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="order-genie-v2-camera-input d-none"
            onChange={handleCameraChange}
          />
        </Card>
      </div>
      <div className={`col-12 ${isV3 ? 'col-lg-7' : 'col-lg-5'}`}>
        <div className="d-flex flex-column gap-3">
          <Card className="shadow-sm border-0">
            <h5 className="mb-3">Document preview</h5>
            {previewUrl && previewType === 'application/pdf' ? (
              <iframe title="Document preview" src={previewUrl} className="order-genie-v2-doc-frame" />
            ) : (
              <div
                className="order-genie-v2-doc-preview"
                style={{
                  backgroundImage: previewUrl && previewType.startsWith('image/') ? `url(${previewUrl})` : 'none',
                }}
              >
                {!previewUrl && <div className="text-muted">Select a file to see a preview.</div>}
                {hasRichUploadPreview && (
                  <iframe
                    title="Rich text preview"
                    sandbox=""
                    srcDoc={uploadPreviewHtml}
                    className="order-genie-v2-doc-frame order-genie-v2-doc-frame-rich"
                  />
                )}
                {previewUrl
                  && !hasRichUploadPreview
                  && !previewType.startsWith('image/')
                  && previewType !== 'application/pdf'
                  && !(uploadPreviewTable.length || uploadPreviewText) && (
                  <div className="order-genie-v2-doc-fallback">
                    <div className="fw-semibold">{previewFile?.name || 'Document uploaded'}</div>
                    <div className="text-muted small">
                      {isPlainTextPreviewFile(previewType, String(previewFile?.name || previewFileBlob?.name || ''))
                        ? 'Loading text preview...'
                        : 'Preview not available for this file type.'}
                    </div>
                  </div>
                )}
                {!hasRichUploadPreview && (uploadPreviewTable.length > 0 || uploadPreviewText) && (
                  <div className="order-genie-v2-doc-fallback w-100">
                    <div className="fw-semibold mb-2">{previewFile?.name || 'Document uploaded'}</div>
                    {uploadPreviewTable.length > 0 && (
                      <div className="table-responsive mb-2">
                        <table className="table table-sm mb-0">
                          <tbody>
                            {uploadPreviewTable.map((row, rowIdx) => (
                              <tr key={`upload-preview-row-${rowIdx}`}>
                                {(Array.isArray(row) ? row : []).map((cell, colIdx) => (
                                  <td key={`upload-preview-cell-${rowIdx}-${colIdx}`}>{String(cell || '')}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {uploadPreviewText && <div className="text-muted small">{uploadPreviewText}</div>}
                  </div>
                )}
              </div>
            )}
          </Card>
          <Card className="shadow-sm border-0">
            <h5 className="mb-3">What happens next</h5>
            <ul className="list-unstyled text-muted small mb-0">
              <li className="mb-2">1. We securely read and validate your uploaded document.</li>
              <li className="mb-2">2. We extract line items and quantities from each page.</li>
              <li className="mb-2">3. We match products from your catalog and improve accuracy with AI analysis.</li>
              <li>4. Your request is prepared and ready for review.</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
  };

  const renderResults = (
    sourceItems = items,
    sourceMatches = matches,
    sourceDocs = sourceFiles,
    sourceRequestId = requestId,
    sourceUserId = userId,
    sourceSummary = null,
    selectionState = selectedByItem,
    qtyState = qtyByItem,
    setQtyState = setQtyByItem
  ) => {
    const isCurrentRequestView = String(sourceRequestId || '') === String(requestId || '');
    const effectiveDocs =
      Array.isArray(sourceDocs) && sourceDocs.length
        ? sourceDocs
        : isCurrentRequestView && Array.isArray(processStatus?.source_files)
          ? processStatus.source_files
          : [];
    const previewDoc = Array.isArray(effectiveDocs) && effectiveDocs.length
      ? effectiveDocs[effectiveDocs.length - 1]
      : null;
    const previewDocLabel = (() => {
      if (!previewDoc) return 'No source document attached.';
      const direct = String(previewDoc?.filename || previewDoc?.name || '').trim();
      if (direct) return direct;
      const pathLike = String(previewDoc?.storage_path || previewDoc?.blob_path || previewDoc?.url || '').trim();
      if (!pathLike) return 'Unnamed source file';
      const normalized = pathLike.split('?')[0].split('#')[0];
      const parts = normalized.split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : 'Unnamed source file';
    })();
    // Keep upload blob preview scoped to current request only.
    const fallbackPreviewUrl = isCurrentRequestView ? (previewUrl || '') : '';
    const fallbackPreviewType = isCurrentRequestView ? (previewType || '') : '';
    const serverUrl = previewDoc?.url || previewDoc?.previewUrl || '';
    // If server URL is a relative path (local storage), it may not be browser-accessible; prefer blob URL
    const isAbsoluteUrl = serverUrl.startsWith('http://') || serverUrl.startsWith('https://');
    const previewUrlValue = fallbackPreviewUrl || (isAbsoluteUrl ? serverUrl : '') || serverUrl;
    const previewTypeValue = previewDoc?.content_type || previewDoc?.type || fallbackPreviewType;
    const previewStoragePath = String(previewDoc?.storage_path || previewDoc?.path || '').trim();
    const cachedStoredPreview = previewStoragePath ? (storedPreviewByPath[previewStoragePath] || {}) : {};
    const previewTableValue = isCurrentRequestView
      && Array.isArray(uploadPreviewTable)
      && uploadPreviewTable.length > 0
      ? uploadPreviewTable
      : (Array.isArray(cachedStoredPreview?.table) ? cachedStoredPreview.table : []);
    const previewTextValue = isCurrentRequestView
      && String(uploadPreviewText || '').trim()
      ? String(uploadPreviewText || '').trim()
      : String(cachedStoredPreview?.text || '').trim();
    const tabularPreviewActive = isTabularPreviewFile(previewTypeValue, previewDocLabel)
      || previewTableValue.length > 0
      || Boolean(previewTextValue);
    const processMeta = processMetaById[sourceRequestId] || {};
    const statusInfo = getUnifiedRequestStatus(sourceRequestId);
    const lineItemCount = getSummaryLineItemCount({
      sourceItemsLength: sourceItems.length,
      processMetaItemsCount: processMeta.itemsCount,
      processMetaMatchedTotal: processMeta.matchedTotal,
    });
    const shouldFloatSummary = viewportWidth >= 640 && Boolean(sourceRequestId);
    const shouldFloatMatchFilter = Boolean(sourceRequestId);
    const floatingMatchFilterBottomOffset = viewportWidth < 768 ? 84 : viewportWidth < 992 ? 72 : 16;
    const floatingMatchFilterSafeBuffer = Math.max(
      140,
      Number(floatingMatchFilterHeight || 0) + floatingMatchFilterBottomOffset + 28
    );
    const projectSummary = getProjectSummary(sourceSummary);
    const hasActiveProcess = activeProcesses.some((entry) => String(entry.requestId) === String(sourceRequestId));
    const effectiveStatusValue = String(statusInfo?.request_status || statusInfo?.status || '').toLowerCase();
    const statusStepValue = String(statusInfo?.request_step || statusInfo?.step || '').toLowerCase();
    const isStatusRunningLike =
      effectiveStatusValue === 'running'
      || effectiveStatusValue === 'queued'
      || ['reading', 'parsing', 'processing', 'matching', 'ai_rerank', 'queued'].includes(statusStepValue);
    const isRunning = (hasActiveProcess || isStatusRunningLike) && !hasTerminalReviewSignal(statusInfo);
    const isReadyForReview = isSuccessStatus(effectiveStatusValue);
    const reviewHeading = isRunning || !isReadyForReview ? 'Processing request' : 'Request ready for review';
    const reviewDetail = isRunning
      ? String(statusInfo?.message || processMeta?.lastMessage || 'Extracting and matching your request...')
      : '';
    const scanningActive = isRunning
      && ['reading', 'parsing', 'processing'].includes(String(statusInfo?.request_step || statusInfo?.step || '').toLowerCase());
    const parallelWorkers = Math.max(
      1,
      Math.min(4, Number(processMeta.parallelMatchThreads || configuredParallelMatchThreads || 1))
    );
    const isHydratingReviewData = Boolean(reviewHydrationLoadingByRequest[String(sourceRequestId || '')]);
    const shouldShowReviewProgressDetails = isRunning
      || (Object.keys(statusInfo || {}).length > 0 && !(isV3 && isReadyForReview));
    const requestFilterKey = String(sourceRequestId || 'new');
    const activeReviewFilter = String(reviewMatchFilterByRequest[requestFilterKey] || 'all');
    const sourceMatchesList = Array.isArray(sourceMatches) ? sourceMatches : [];
    const matchesByItem = sourceMatchesList.reduce((acc, entry) => {
      const key = String(entry?.item_id ?? entry?.itemId ?? '').trim();
      if (!key) return acc;
      acc[key] = acc[key] || [];
      acc[key].push(entry);
      return acc;
    }, {});
    const sourceItemsList = Array.isArray(sourceItems) ? sourceItems : [];
    const requestLineNumberByItemKey = sourceItemsList.reduce((acc, item, idx) => {
      const key = String(item?.id || '').trim();
      if (!key) return acc;
      const explicitRowIndex = Number(item?.row_index ?? item?.rowIndex);
      const requestLine = Number.isFinite(explicitRowIndex) ? explicitRowIndex + 1 : idx + 1;
      acc[key] = requestLine;
      return acc;
    }, {});
    const isItemAiReranked = (item) => {
      const key = String(item?.id || '').trim();
      const options = matchesByItem[key] || [];
      const optionAiRanked = options.some((entry) => Boolean(entry?.ai_ranked));
      if (optionAiRanked) return true;
      const parsed =
        item?.parsed_json && typeof item.parsed_json === 'object' && !Array.isArray(item.parsed_json)
          ? item.parsed_json
          : {};
      const debugInfo = parsed?.match_debug || parsed?.debug_info || {};
      return Boolean(debugInfo?.selected?.ai_ranked);
    };
    const isItemAiLowConfidence = (item) => {
      const key = String(item?.id || '').trim();
      const options = matchesByItem[key] || [];
      const hasAiLowConfidenceFlag = options.some((entry) => Boolean(entry?.ai_low_confidence));
      const hasAiAnalyzed = options.some((entry) => Boolean(entry?.ai_analyzed));
      const hasAiRanked = options.some((entry) => Boolean(entry?.ai_ranked));
      if (hasAiLowConfidenceFlag) return true;
      if (hasAiAnalyzed && !hasAiRanked) return true;
      const parsed =
        item?.parsed_json && typeof item.parsed_json === 'object' && !Array.isArray(item.parsed_json)
          ? item.parsed_json
          : {};
      const debugInfo = parsed?.match_debug || parsed?.debug_info || {};
      return Boolean(debugInfo?.selected?.ai_low_confidence)
        || (Boolean(debugInfo?.selected?.ai_analyzed) && !Boolean(debugInfo?.selected?.ai_ranked));
    };
    const baseCategoryByItemKey = sourceItemsList.reduce((acc, item) => {
      const key = String(item?.id || '').trim();
      if (!key) return acc;
      const options = matchesByItem[key] || [];
      if (isItemAiReranked(item)) {
        acc[key] = 'ai_reranked';
      } else if (isItemAiLowConfidence(item)) {
        acc[key] = 'ai_low_confidence';
      } else if (options.length === 0) {
        acc[key] = 'no_matches';
      } else if (options.length === 1) {
        acc[key] = 'direct';
      } else {
        acc[key] = 'all';
      }
      return acc;
    }, {});
    const getEffectiveCategoryForItem = (itemKey) => {
      const rowSearchKey = buildRowSearchKey(sourceRequestId, itemKey);
      return manualSearchCategoryByRow[rowSearchKey] || baseCategoryByItemKey[itemKey] || 'all';
    };
    const directItems = sourceItemsList.filter((item) => {
      const key = String(item?.id || '').trim();
      return key && getEffectiveCategoryForItem(key) === 'direct';
    });
    const aiRerankedItems = sourceItemsList.filter((item) => {
      const key = String(item?.id || '').trim();
      return key && getEffectiveCategoryForItem(key) === 'ai_reranked';
    });
    const aiLowConfidenceItems = sourceItemsList.filter((item) => {
      const key = String(item?.id || '').trim();
      return key && getEffectiveCategoryForItem(key) === 'ai_low_confidence';
    });
    const noMatchItems = sourceItemsList.filter((item) => {
      const key = String(item?.id || '').trim();
      const options = matchesByItem[key] || [];
      if (key && getEffectiveCategoryForItem(key) === 'no_matches') return true;
      return options.length === 0;
    });
    const filteredItems =
      activeReviewFilter === 'ai_reranked'
        ? aiRerankedItems
        : activeReviewFilter === 'ai_low_confidence'
          ? aiLowConfidenceItems
        : activeReviewFilter === 'direct'
          ? directItems
          : activeReviewFilter === 'no_matches'
            ? noMatchItems
            : sourceItemsList;
    const allMatchCount = sourceItemsList.length;
    const aiRerankedCount = aiRerankedItems.length;
    const aiLowConfidenceCount = aiLowConfidenceItems.length;
    const directMatchCount = directItems.length;
    const noMatchesCount = noMatchItems.length;
    const activeFilterLabel =
      activeReviewFilter === 'ai_reranked'
        ? (isV3 ? 'AI Analysis' : 'AI Reranked')
        : activeReviewFilter === 'ai_low_confidence'
          ? 'AI Low Confidence'
        : activeReviewFilter === 'direct'
          ? 'Direct Matches'
          : activeReviewFilter === 'no_matches'
            ? 'No Matches'
            : 'All';
    const streamStepValue = String(statusInfo?.step || '').toLowerCase();
    const streamDone =
      typeof statusInfo?.ai_stream_done === 'boolean'
        ? statusInfo.ai_stream_done
        : Boolean(processMeta?.aiDone);
    const streamActiveSteps = new Set(['reading', 'parsing', 'processing']);
    const showTabAiStream = isCurrentRequestView
      && streamActiveSteps.has(streamStepValue)
      && !STREAM_PANEL_HIDDEN_STEPS.has(streamStepValue);
    const tabAiStreamDone = Boolean(streamDone);
    return (
    <>
    <div className="row g-4">
      <div className="col-12 col-xl-8">
        <Card className="shadow-sm border-0">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-0">{reviewHeading}</h4>
              {reviewDetail && <div className="small text-muted mt-1">{reviewDetail}</div>}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Tag color="blue">{sourceItems.length} items</Tag>
              <Button size="small" onClick={() => openEditDrawer(sourceItems, sourceRequestId, sourceUserId)}>
                Edit inputs
              </Button>
              {isRunning && (
                <Button
                  size="small"
                  danger
                  onClick={() => stopRunningRequest(sourceRequestId, sourceUserId)}
                >
                  Stop
                </Button>
              )}
            </div>
          </div>
          {isHydratingReviewData && (
            <div className="order-genie-v2-review-loading mb-3" role="status" aria-live="polite">
              <Spin size="small" />
              <span>Loading latest request data...</span>
            </div>
          )}
          {showTabAiStream && (
            <div className="mb-3">
              <div className="fw-semibold small mb-1">Extraction activity</div>
              <div
                ref={aiStreamPanelRef}
                className="border rounded p-2"
                style={{ maxHeight: 240, overflowY: 'auto' }}
              >
                {renderExtractionActivity(!tabAiStreamDone)}
              </div>
            </div>
          )}
          <div
            ref={floatingMatchFilterRef}
            className={`order-genie-v2-match-filter-float mb-3 ${shouldFloatMatchFilter ? 'order-genie-v2-match-filter-float--floating' : ''}`}
            style={shouldFloatMatchFilter ? { bottom: `${floatingMatchFilterBottomOffset}px` } : undefined}
          >
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Button
                size="small"
                type={activeReviewFilter === 'direct' ? 'primary' : 'default'}
                onClick={() =>
                  setReviewMatchFilterByRequest((prev) => ({ ...prev, [requestFilterKey]: 'direct' }))
                }
              >
                See Direct Matches ({directMatchCount})
              </Button>
              <Button
                size="small"
                type={activeReviewFilter === 'ai_reranked' ? 'primary' : 'default'}
                onClick={() =>
                  setReviewMatchFilterByRequest((prev) => ({ ...prev, [requestFilterKey]: 'ai_reranked' }))
                }
              >
                {isV3 ? `See AI Analysis (${aiRerankedCount})` : `See AI Reranked (${aiRerankedCount})`}
              </Button>
              <Button
                size="small"
                type={activeReviewFilter === 'ai_low_confidence' ? 'primary' : 'default'}
                onClick={() =>
                  setReviewMatchFilterByRequest((prev) => ({ ...prev, [requestFilterKey]: 'ai_low_confidence' }))
                }
              >
                See AI Low Confidence ({aiLowConfidenceCount})
              </Button>
              <Button
                size="small"
                type={activeReviewFilter === 'all' ? 'primary' : 'default'}
                onClick={() =>
                  setReviewMatchFilterByRequest((prev) => ({ ...prev, [requestFilterKey]: 'all' }))
                }
              >
                See All ({allMatchCount})
              </Button>
              <Button
                size="small"
                type={activeReviewFilter === 'no_matches' ? 'primary' : 'default'}
                onClick={() =>
                  setReviewMatchFilterByRequest((prev) => ({ ...prev, [requestFilterKey]: 'no_matches' }))
                }
              >
                See No Matches ({noMatchesCount})
              </Button>
            </div>
            <div className="small text-muted">Seeing: {activeFilterLabel}</div>
          </div>
          {filteredItems.map((item, itemIndex) => {
            const itemKey = String(item.id || '');
            const requestLineNumber = Number(requestLineNumberByItemKey[itemKey] || (itemIndex + 1));
            const filterLineNumber = itemIndex + 1;
            const rowSearchKey = buildRowSearchKey(sourceRequestId, itemKey);
            const rowRerunLoading = !!rowRerunLoadingByRow[rowSearchKey];
            const rowRerunStatus = rowRerunStatusByRow[rowSearchKey] || null;
            const baseMatchesForItem = (matchesByItem[itemKey] || [])
              .sort((left, right) => {
                const leftAiRanked = Boolean(left?.ai_ranked);
                const rightAiRanked = Boolean(right?.ai_ranked);
                if (leftAiRanked !== rightAiRanked) return leftAiRanked ? -1 : 1;
                const leftAiRank = Number(left?.ai_rank);
                const rightAiRank = Number(right?.ai_rank);
                const leftAiRankFinite = Number.isFinite(leftAiRank);
                const rightAiRankFinite = Number.isFinite(rightAiRank);
                if (leftAiRankFinite && rightAiRankFinite && leftAiRank !== rightAiRank) return leftAiRank - rightAiRank;
                if (leftAiRankFinite !== rightAiRankFinite) return leftAiRankFinite ? -1 : 1;
                const leftRank = Number(left?.rank);
                const rightRank = Number(right?.rank);
                const leftRankFinite = Number.isFinite(leftRank);
                const rightRankFinite = Number.isFinite(rightRank);
                if (leftRankFinite && rightRankFinite && leftRank !== rightRank) return leftRank - rightRank;
                if (leftRankFinite !== rightRankFinite) return leftRankFinite ? -1 : 1;
                const leftScore = Number(left?.score);
                const rightScore = Number(right?.score);
                const leftScoreFinite = Number.isFinite(leftScore);
                const rightScoreFinite = Number.isFinite(rightScore);
                if (leftScoreFinite && rightScoreFinite && leftScore !== rightScore) return rightScore - leftScore;
                if (leftScoreFinite !== rightScoreFinite) return leftScoreFinite ? -1 : 1;
                return 0;
              });
            const manualMatchesForItem = Array.isArray(manualSearchResultsByRow[rowSearchKey])
              ? manualSearchResultsByRow[rowSearchKey]
              : [];
            const manualViewMode = String(
              manualSearchViewByRow[rowSearchKey] || (manualMatchesForItem.length ? 'search' : 'base')
            );
            const showingManualResults = manualViewMode === 'search' && manualMatchesForItem.length > 0;
            const matchesForItem = showingManualResults ? manualMatchesForItem : baseMatchesForItem;
            const allMatchesForItem = (() => {
              const merged = [...baseMatchesForItem];
              const seen = new Set(
                merged
                  .map((entry) => normalizeProductCodeKey(entry?.product_code))
                  .filter(Boolean)
              );
              manualMatchesForItem.forEach((entry) => {
                const key = normalizeProductCodeKey(entry?.product_code);
                if (!key || seen.has(key)) return;
                seen.add(key);
                merged.push(entry);
              });
              return merged;
            })();
            const aiEnhancedDesc = getAiEnhancedDescription(item);
            const parsedItemDebug =
              item?.parsed_json &&
              typeof item.parsed_json === 'object' &&
              !Array.isArray(item.parsed_json)
                ? item.parsed_json.match_debug || item.parsed_json.debug_info || null
                : null;
            const itemDebugInfo = matchesForItem.find((entry) => entry?.debug_info)?.debug_info || parsedItemDebug || null;
            const debugInput = itemDebugInfo?.input || {};
            const normalizedParts = item?.normalized_parts || {};
            const debugPartNumber = String(
              debugInput?.part_number ||
              normalizedParts?.part_number ||
              normalizedParts?.partNumber ||
              ''
            ).trim();
            const debugManufacturerPartNumber = String(
              debugInput?.manufacturer_part_number ||
              normalizedParts?.manufacturer_part_number ||
              normalizedParts?.mfr_part_number ||
              normalizedParts?.manufacturerPartNumber ||
              ''
            ).trim();
            const debugManufacturer = String(
              debugInput?.manufacturer ||
              normalizedParts?.manufacturer ||
              normalizedParts?.manufacturer_name ||
              normalizedParts?.mfr ||
              ''
            ).trim();
            const debugDescription = String(
              debugInput?.description ||
              normalizedParts?.description ||
              normalizedParts?.desc ||
              ''
            ).trim();
            const noMatchOutcome = String(itemDebugInfo?.outcome || '').toLowerCase() === 'no_matches';
            const debugReason = itemDebugInfo?.selected?.reason || (noMatchOutcome ? 'No products matched search workflow' : '');
            const debugRuleType = itemDebugInfo?.selected?.rule_type || (noMatchOutcome ? 'no_match' : '');
            const debugCandidateCount =
              itemDebugInfo?.selected?.candidate_count ??
              (noMatchOutcome ? 0 : null);
            const debugReturnedCount =
              itemDebugInfo?.selected?.returned_count ??
              (noMatchOutcome ? 0 : null);
            const debugAiRanked =
              itemDebugInfo?.selected?.ai_ranked ??
              (noMatchOutcome ? false : null);
            const hasDebugInfo = !!itemDebugInfo || !!aiEnhancedDesc;
            const debugKey = `${sourceRequestId || 'new'}-${itemKey}`;
            const debugExpanded = !!expandedDebugKeys[debugKey];
            const showNoMatchesMessage = !isRunning && !rowRerunLoading && matchesForItem.length === 0;
            const rowExpanded = !!expandedRowsByKey[rowSearchKey];
            const rowManualMeta = manualSearchMetaByRow[rowSearchKey] || null;
            const showManualMeta = !!rowManualMeta && Number(rowManualMeta.total || 0) > 0;
            const loadedResults = Math.min(
              Number(rowManualMeta?.loaded || 0),
              Number(rowManualMeta?.total || 0)
            );
            const hasMoreManualResults =
              !!rowManualMeta && loadedResults < Number(rowManualMeta.total || 0);
            const currentRowRerankStatus = String(
              processMeta?.rowRerankStatus?.[itemKey] ||
              processMeta?.rowRerankStatus?.[String(item?.id || '')] ||
              ''
            ).toLowerCase();
            const rowSearchAnchorId = `ogv2-row-search-${String(rowSearchKey).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
            const manufacturerPartNumber = String(
              item?.normalized_parts?.manufacturer_part_number ||
              item?.normalized_parts?.mfr_part_number ||
              ''
            ).trim();
            const customerPartNumber = String(
              item?.normalized_parts?.customer_part_number ||
              item?.normalized_parts?.customerPartNumber ||
              item?.parsed_json?.customer_part_number ||
              item?.parsed_json?.customerPartNumber ||
              ''
            ).trim();
            const itemUom = String(
              item?.normalized_parts?.uom ||
              item?.normalized_parts?.unit ||
              item?.normalized_parts?.unit_of_measure ||
              ''
            ).trim();
            const itemNotes = getItemNotesValue(item);
            const selectedCode = String(selectionState[itemKey] || '');
            const selectedMatch =
              allMatchesForItem.find(
                (entry) => normalizeProductCodeKey(entry?.product_code) === normalizeProductCodeKey(selectedCode)
              ) ||
              matchesForItem[0] ||
              null;
            const isAiReranked = isItemAiReranked(item);
            const isAiLowConfidence = isItemAiLowConfidence(item);
            const isAiRerankingNow = currentRowRerankStatus === 'running'
              || (
                String(processStatus?.step || '').toLowerCase() === 'row_rerun_ai_rerank'
                && String(processStatus?.rerun_item_id || '') === itemKey
              );
            const selectedProductImageUrl = resolveProductImageUrl(selectedMatch?.match?.image_url);
            const selectedProductImageVisible = !!selectedProductImageUrl && !isImageUrlBroken(selectedMatch?.match?.image_url);
            const selectedMatchTypeReason = String(selectedMatch?.reason || '').trim();
            const selectedMatchReason = String(selectedMatch?.reasoning || '').trim();
            const normalizedTypeReason = selectedMatchTypeReason.toLowerCase();
            const isDirectTypeReason = (
              normalizedTypeReason === 'part number match'
              || normalizedTypeReason === 'manufacturer part number match'
              || normalizedTypeReason === 'customer part number match'
            );
            const selectedHasAiAnalysis = Boolean(
              selectedMatch?.ai_analyzed
              || selectedMatch?.ai_ranked
              || selectedMatch?.ai_low_confidence
            );
            const shouldUseAiReason = Boolean(
              selectedHasAiAnalysis
              && !isDirectTypeReason
              && selectedMatchReason
            );
            const selectedDisplayReason = (
              shouldUseAiReason
                ? selectedMatchReason
                : (selectedMatchTypeReason || selectedMatchReason)
            ).trim();
            const selectedManualReason = String(
              selectedMatch?.manual_override_reason || manualSearchApplyReasonByRow[rowSearchKey] || ''
            ).trim();
            const isManualOverrideSelection = Boolean(
              selectedMatch?.manual_override === true
              || selectedMatch?.is_manual_override === true
              || selectedMatch?.selection_source === 'manual'
              || selectedMatch?.selectionSource === 'manual'
              || selectedManualReason
            );
            const showSelectedAiReason = Boolean(
              isV3
              && isAiReranked
              && !isManualOverrideSelection
              && selectedMatchReason
              && selectedDisplayReason !== selectedMatchReason
            );
            const showSelectedManualReason = Boolean(selectedManualReason);
            const compactInputDetails = [];
            if (
              manufacturerPartNumber &&
              manufacturerPartNumber !== String(item?.normalized_parts?.part_number || '').trim()
            ) {
              compactInputDetails.push(`Mfr Part #: ${manufacturerPartNumber}`);
            }
            if (
              customerPartNumber &&
              customerPartNumber !== String(item?.normalized_parts?.part_number || '').trim() &&
              customerPartNumber !== manufacturerPartNumber
            ) {
              compactInputDetails.push(`Customer Part #: ${customerPartNumber}`);
            }
            if (itemNotes) {
              compactInputDetails.push(`Item Notes: ${itemNotes}`);
            }
            return (
              <Card
                key={itemKey}
                size="small"
                className={`mb-3 order-genie-v2-item-row ${itemIndex % 2 === 0 ? 'order-genie-v2-item-row--even' : 'order-genie-v2-item-row--odd'}`}
              >
                <div className="order-genie-v2-row-top d-flex justify-content-between align-items-start gap-3 order-genie-v2-item-header">
                  <div className="flex-grow-1">
                    <div className="order-genie-v2-item-meta d-flex align-items-center gap-2 flex-wrap">
                      <div className="order-genie-v2-item-title mb-0">
                        Line {requestLineNumber}
                      </div>
                      <div className="order-genie-v2-item-filter-index">
                        Filter row {filterLineNumber}
                      </div>
                      <div className="fw-semibold order-genie-v2-item-part">
                        {item.normalized_parts?.part_number || 'Item'}
                      </div>
                      <div className="d-inline-flex align-items-center gap-1 order-genie-v2-inline-qty">
                        <span className="text-muted small">Qty</span>
                        <InputNumber
                          min={1}
                          size="small"
                          value={qtyState[itemKey] ?? item.normalized_parts?.qty ?? 1}
                          onChange={(value) => {
                            setQtyState((prev) => ({ ...prev, [itemKey]: value || 1 }));
                          }}
                        />
                        <span className="order-genie-v2-inline-uom">
                          {itemUom || 'EA'}
                        </span>
                      </div>
                      {rowRerunLoading ? (
                        <Tag color="processing" className="mb-0">Rerunning row...</Tag>
                      ) : isAiRerankingNow ? (
                        <Tag color="processing" className="mb-0">{isV3 ? 'AI analysis…' : 'AI reranking…'}</Tag>
                      ) : isAiReranked ? (
                        <Tag color="purple" className="mb-0">{isV3 ? 'AI analyzed' : 'AI reranked'}</Tag>
                      ) : isAiLowConfidence ? (
                        <Tag color="orange" className="mb-0">AI Low Confidence</Tag>
                      ) : isRunning ? (
                        matchesForItem.length > 0 ? (
                          <Tag color="green" className="mb-0">{`Matched ${matchesForItem.length} option(s)`}</Tag>
                        ) : (
                          <Tag color="processing" className="mb-0">Matching...</Tag>
                        )
                      ) : matchesForItem.length > 0 ? (
                        <Tag color="green" className="mb-0">{`Matched ${matchesForItem.length} option(s)`}</Tag>
                      ) : (
                        <></>
                      )}
                    </div>
                    <div className="text-muted small order-genie-v2-item-desc">
                      {decodeHtmlEntities(item.normalized_parts?.description || '')}
                    </div>
                    {rowRerunStatus && (
                      <div className="small text-muted mt-1">
                        {rowRerunStatus.message}
                        {rowRerunStatus.detail ? ` - ${rowRerunStatus.detail}` : ''}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-2 align-items-end order-genie-v2-item-actions">
                    <Button
                      size="small"
                      onClick={() =>
                        openEditDrawer(sourceItems, sourceRequestId, sourceUserId, { focusItemId: item.id })
                      }
                      disabled={!sourceRequestId || !sourceUserId}
                      icon={<EditOutlined />}
                    >
                      Edit row
                    </Button>
                    <Button
                      size="small"
                      shape="circle"
                      type={rowExpanded ? 'primary' : 'default'}
                      icon={rowExpanded ? <DownOutlined /> : <RightOutlined />}
                      aria-label={rowExpanded ? 'Collapse row details' : 'Expand row details'}
                      onClick={() => {
                        if (!rowExpanded) {
                          loadErpPricesForCodes(matchesForItem.map((m) => m.product_code));
                        }
                        setExpandedRowsByKey((prev) => ({
                          ...prev,
                          [rowSearchKey]: !prev[rowSearchKey],
                        }));
                      }}
                    />
                  </div>
                </div>
                <div className="order-genie-v2-row-summary mt-2">
                  {compactInputDetails.length > 0 && (
                    <div className="order-genie-v2-row-inputs small">
                      {compactInputDetails.map((detail, idx) => (
                        <div key={`${rowSearchKey}-detail-${idx}`}>{detail}</div>
                      ))}
                    </div>
                  )}
                  {selectedMatch ? (
                    <div className={`order-genie-v2-selected-match ${compactInputDetails.length > 0 ? 'mt-2' : ''}`}>
                      {selectedProductImageVisible ? (
                        <img
                          src={selectedProductImageUrl}
                          alt={selectedMatch?.match?.name || selectedMatch?.product_code || 'Selected product'}
                          className="order-genie-v2-thumb order-genie-v2-summary-thumb"
                          onError={() => markImageUrlBroken(selectedMatch?.match?.image_url)}
                        />
                      ) : (
                        <div className="order-genie-v2-thumb order-genie-v2-thumb--empty order-genie-v2-summary-thumb">
                          No image
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <div className="fw-semibold">
                          {selectedMatch.product_code}
                          {matchesForItem.length > 1 ? ` (${matchesForItem.length} options)` : ''}
                        </div>
                        <div className="text-muted small">
                          {decodeHtmlEntities(selectedMatch.match?.name || 'Matched product')}
                        </div>
                        {Number.isFinite(Number(erpPriceByProductCode[selectedMatch.product_code])) && (
                          <div className="order-genie-v2-match-price fw-semibold">
                            ${Number(erpPriceByProductCode[selectedMatch.product_code]).toFixed(2)}
                          </div>
                        )}
                        {selectedDisplayReason && (
                          <div className="order-genie-v2-reason-block mt-2 mb-0">
                            <div className="order-genie-v2-reason-label">Match reason</div>
                            <div className="order-genie-v2-reason-text">{decodeHtmlEntities(selectedDisplayReason)}</div>
                          </div>
                        )}
                        {showSelectedAiReason && (
                          <div className="order-genie-v2-reason-block mt-2 mb-0">
                            <div className="order-genie-v2-reason-label">AI analysis reason</div>
                            <div className="order-genie-v2-reason-text">{decodeHtmlEntities(selectedMatchReason)}</div>
                          </div>
                        )}
                        {showSelectedManualReason && (
                          <div className="order-genie-v2-reason-block mt-2 mb-0">
                            <div className="order-genie-v2-reason-label">Manual replacement reason</div>
                            <div className="order-genie-v2-reason-text">{decodeHtmlEntities(selectedManualReason)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    showNoMatchesMessage && <div className="order-genie-v2-no-match mt-2 mb-0">No matches found.</div>
                  )}
                </div>
                {rowExpanded && (
                <div className="mt-2">
                  <div className="d-flex justify-content-end mb-2">
                    <Button
                      size="small"
                      loading={rowRerunLoading}
                      disabled={!sourceRequestId || !sourceUserId}
                      onClick={() => runRowLevelRerunMatch(sourceRequestId, sourceUserId, item)}
                    >
                      Rerun Match
                    </Button>
                  </div>
                  <div className="mb-2 order-genie-v2-row-search" id={rowSearchAnchorId}>
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                      <div className="text-muted small">Search catalog for this row</div>
                      <Button
                        size="small"
                        type="link"
                        className="p-0"
                        onClick={() => setAdvancedSearchTipsOpen(true)}
                      >
                        Advanced Search Tips
                      </Button>
                    </div>
                    <Space.Compact block>
                      <Input
                        value={manualSearchByRow[rowSearchKey] ?? ''}
                        placeholder={
                          decodeHtmlEntities(item.normalized_parts?.part_number) ||
                          decodeHtmlEntities(item.normalized_parts?.description) ||
                          'Search by part number or description'
                        }
                        onChange={(e) =>
                          {
                            const nextValue = e.target.value;
                            setManualSearchByRow((prev) => ({ ...prev, [rowSearchKey]: nextValue }));
                            setManualSearchMetaByRow((prev) => {
                              const currentMeta = prev[rowSearchKey];
                              if (!currentMeta) return prev;
                              if (String(currentMeta.query || '').trim() === String(nextValue || '').trim()) return prev;
                              const next = { ...prev };
                              delete next[rowSearchKey];
                              return next;
                            });
                          }
                        }
                        onPressEnter={() => runManualRowSearch(sourceRequestId, item)}
                      />
                      <Button
                        type="default"
                        loading={!!manualSearchLoadingByRow[rowSearchKey]}
                        onClick={() => runManualRowSearch(sourceRequestId, item)}
                      >
                        Search
                      </Button>
                      <Button
                        type="default"
                        onClick={() => clearManualRowSearch(sourceRequestId, item)}
                        disabled={
                          !manualSearchSnapshotByRow[rowSearchKey]
                          && !manualSearchMetaByRow[rowSearchKey]
                          && !manualSearchResultsByRow[rowSearchKey]
                          && !String(manualSearchByRow[rowSearchKey] || '').trim()
                        }
                      >
                        Clear
                      </Button>
                    </Space.Compact>
                    {manualSearchErrorByRow[rowSearchKey] && (
                      <div className="text-danger small mt-1">{manualSearchErrorByRow[rowSearchKey]}</div>
                    )}
                    {showManualMeta && (
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <div className="text-muted small">
                          Showing {loadedResults} of {Number(rowManualMeta.total || 0)} results
                        </div>
                      </div>
                    )}
                    {(manualMatchesForItem.length > 0 || matchesForItem.length > 0) && (
                      <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                        {manualMatchesForItem.length > 0 && (
                          <>
                            <Button
                              size="small"
                              type={showingManualResults ? 'primary' : 'default'}
                              onClick={() =>
                                setManualSearchViewByRow((prev) => ({ ...prev, [rowSearchKey]: 'search' }))
                              }
                            >
                              Search results
                            </Button>
                            <Button
                              size="small"
                              type={!showingManualResults ? 'primary' : 'default'}
                              onClick={() =>
                                setManualSearchViewByRow((prev) => ({ ...prev, [rowSearchKey]: 'base' }))
                              }
                            >
                              AI/System matches
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {(matchesForItem.length > 0 || hasDebugInfo || showNoMatchesMessage) && (
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                      <div className={showNoMatchesMessage ? 'order-genie-v2-no-match mb-0' : 'text-muted small'}>
                        {matchesForItem.length > 0
                          ? (
                            showingManualResults
                              ? `Choose a search result and click "Select As Match" (${showManualMeta ? Number(rowManualMeta.total || matchesForItem.length) : matchesForItem.length} found)`
                              : `Use "Select As Match" on the best option (${showManualMeta ? Number(rowManualMeta.total || matchesForItem.length) : matchesForItem.length} found)`
                          )
                          : showNoMatchesMessage
                            ? 'No matches found.'
                            : ''}
                      </div>
                      {hasDebugInfo && !isV3 && (
                        <Button
                          type="link"
                          size="small"
                          className="p-0"
                          onClick={() =>
                            setExpandedDebugKeys((prev) => ({
                              ...prev,
                              [debugKey]: !prev[debugKey],
                            }))
                          }
                        >
                          {debugExpanded ? 'Hide debug info' : 'Debug info'}
                        </Button>
                      )}
                    </div>
                  )}
                  {hasDebugInfo && !isV3 && debugExpanded && (
                    <div className="order-genie-v2-debug-block mb-3">
                      <div className="order-genie-v2-debug-title">Match decision log</div>
                      <div className="order-genie-v2-debug-grid">
                        <div><strong>Part #:</strong> {formatDebugValue(debugPartNumber)}</div>
                        <div><strong>Mfr Part #:</strong> {formatDebugValue(debugManufacturerPartNumber)}</div>
                        <div><strong>Manufacturer:</strong> {formatDebugValue(debugManufacturer)}</div>
                        <div><strong>Description:</strong> {formatDebugValue(debugDescription)}</div>
                        <div><strong>AI Enhanced:</strong> {formatDebugValue(aiEnhancedDesc)}</div>
                        <div><strong>Rule Type:</strong> {formatDebugValue(debugRuleType)}</div>
                        <div><strong>Reason:</strong> {formatDebugValue(debugReason)}</div>
                        <div><strong>Candidates:</strong> {formatDebugValue(debugCandidateCount)}</div>
                        <div><strong>Returned:</strong> {formatDebugValue(debugReturnedCount)}</div>
                        <div><strong>AI Ranked:</strong> {formatDebugValue(debugAiRanked)}</div>
                        <div><strong>AI Agent:</strong> {formatDebugValue(itemDebugInfo?.selected?.ai_agent)}</div>
                      </div>
                      {Array.isArray(itemDebugInfo?.steps) && itemDebugInfo.steps.length > 0 && (
                        <div className="mt-2">
                          <div className="fw-semibold small mb-1">Steps</div>
                          <div className="d-flex flex-column gap-1">
                            {itemDebugInfo.steps.map((stepEntry, idx) => (
                              <div key={`${debugKey}-step-${idx}`} className="order-genie-v2-debug-step">
                                {formatDebugStep(stepEntry)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="row g-3">
                    {matchesForItem.map((match) => {
                      const currentMatchedCodeKey = normalizeProductCodeKey(
                        selectedMatch?.product_code || selectedCode
                      );
                      const isCurrentMatchedItem =
                        !!currentMatchedCodeKey
                        && currentMatchedCodeKey === normalizeProductCodeKey(match.product_code);
                      const matchKey = `${itemKey}-${match.product_code}`;
                      const isExpanded = !!expandedMatchKeys[matchKey];
                      const attributes =
                        match.match?.attributesJson ||
                        match.match?.attributes_json ||
                        match.match?.attributes;
                      const attributeEntries = normalizeMatchAttributes(attributes);
                      return (
                        <div className="col-12 col-md-6" key={`${itemKey}-${match.product_code}`}>
                          <Card
                            size="small"
                            className={`h-100 ${isCurrentMatchedItem ? 'order-genie-v2-match-card--current' : ''}`}
                          >
                            <div className="d-flex gap-3 order-genie-v2-match-card-row">
                              {resolveProductImageUrl(match.match?.image_url)
                              && !isImageUrlBroken(match.match?.image_url) ? (
                                <img
                                  src={resolveProductImageUrl(match.match?.image_url)}
                                  alt={match.match?.name || match.product_code}
                                  className="order-genie-v2-thumb"
                                  onError={() => markImageUrlBroken(match.match?.image_url)}
                                />
                              ) : (
                                <div className="order-genie-v2-thumb order-genie-v2-thumb--empty">No image</div>
                              )}
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start">
                                  <strong>{match.product_code}</strong>
                                  {isCurrentMatchedItem ? (
                                    <Tag color="green" className="mb-0">Current match</Tag>
                                  ) : (
                                    <Button
                                      size="small"
                                      type="primary"
                                      disabled={!sourceRequestId}
                                      onClick={() => {
                                        setManualReplacePending({
                                          rowKey: rowSearchKey,
                                          itemKey,
                                          sourceRequestId,
                                          sourceUserId,
                                          productCode: match.product_code,
                                          selectedMatchPayload: match,
                                        });
                                        setManualReplaceReasonDraft(String(
                                          match?.reasoning
                                          || match?.reason
                                          || manualSearchApplyReasonByRow[rowSearchKey]
                                          || ''
                                        ).trim());
                                        setManualReplaceReasonError('');
                                        setManualReplaceModalOpen(true);
                                      }}
                                    >
                                      Select As Match
                                    </Button>
                                  )}
                                </div>
                                <div className="text-muted small">{decodeHtmlEntities(match.match?.name || '')}</div>
                                {Number.isFinite(Number(erpPriceByProductCode[match.product_code])) && (
                                  <div className="order-genie-v2-match-price fw-semibold">
                                    ${Number(erpPriceByProductCode[match.product_code]).toFixed(2)}
                                  </div>
                                )}
                                {match.reasoning && (
                                  <div className="order-genie-v2-reason-block mt-2">
                                    <div className="order-genie-v2-reason-label">Match reason</div>
                                    <div className="order-genie-v2-reason-text">{decodeHtmlEntities(match.reasoning)}</div>
                                  </div>
                                )}
                                <Button
                                  type="link"
                                  size="small"
                                  className="p-0 mt-1"
                                  onClick={() =>
                                    setExpandedMatchKeys((prev) => ({
                                      ...prev,
                                      [matchKey]: !prev[matchKey],
                                    }))
                                  }
                                >
                                  {isExpanded ? 'Hide details' : 'View details'}
                                </Button>
                                {isExpanded && (
                                  <div className="order-genie-v2-match-details mt-2">
                                    {match.match?.manufacturer_name && (
                                      <div className="small">
                                        <strong>Manufacturer:</strong> {decodeHtmlEntities(match.match.manufacturer_name)}
                                      </div>
                                    )}
                                    {match.match?.manufacturer_part_number && (
                                      <div className="small">
                                        <strong>Mfr Part #:</strong> {match.match.manufacturer_part_number}
                                      </div>
                                    )}
                                    {match.match?.description && (
                                      <div className="small">
                                        <strong>Description:</strong> {decodeHtmlEntities(match.match.description)}
                                      </div>
                                    )}
                                    {attributeEntries.length > 0 && (
                                      <div className="small">
                                        <strong>Product Details:</strong>
                                        <div className="order-genie-v2-attr-block">
                                          {attributeEntries.map(([label, value]) => (
                                            <div key={label} className="order-genie-v2-attr-row">
                                              <span className="order-genie-v2-attr-label">{label}</span>
                                              <span className="order-genie-v2-attr-value">{String(value)}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                  {(showManualMeta || matchesForItem.length > 0) && (
                    <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                      {hasMoreManualResults && (
                        <Button
                          size="small"
                          loading={!!manualSearchLoadingByRow[rowSearchKey]}
                          onClick={() => runManualRowSearch(sourceRequestId, item, { append: true })}
                        >
                          Load more
                        </Button>
                      )}
                      <Button
                        size="small"
                        type="default"
                        onClick={() => {
                          const node = document.getElementById(rowSearchAnchorId);
                          if (node && typeof node.scrollIntoView === 'function') {
                            node.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                      >
                        Back to top
                      </Button>
                    </div>
                  )}
                </div>
                )}
              </Card>
            );
          })}
        </Card>
        {!editDrawerOpen && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="order-genie-v2-add-row-fab"
            onClick={() => openEditDrawer(sourceItems, sourceRequestId, sourceUserId, { appendNewRow: true })}
            disabled={!sourceRequestId || !sourceUserId}
          >
            Add row
          </Button>
        )}
      </div>
      <div className="col-12 col-xl-4">
        {shouldFloatSummary ? (
        <Affix
          key={`affix-${sourceRequestId || 'new'}-${activeTab}-${lineItemCount}`}
          offsetTop={84}
          target={getAffixTarget}
        >
        <Card
          ref={floatingSummaryCardRef}
          className="shadow-sm border-0 order-genie-v2-summary-card order-genie-v2-summary-card--floating"
          style={floatingSummaryMaxHeight ? { maxHeight: `${floatingSummaryMaxHeight}px` } : undefined}
          onWheelCapture={handleFloatingSummaryWheel}
        >
          <h5>Summary</h5>
          <div className="text-muted small mb-3">Review selections and proceed.</div>
          {(projectSummary.projectName || projectSummary.projectDescription) && (
            <div className="order-genie-v2-project-summary mb-3">
              {projectSummary.projectName && (
                <div className="order-genie-v2-project-summary-title">{projectSummary.projectName}</div>
              )}
              {projectSummary.projectDescription && (
                <div className="order-genie-v2-project-summary-desc">{projectSummary.projectDescription}</div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-between mb-2">
            <span>Line items</span>
            <strong>{lineItemCount}</strong>
          </div>
          <div className="d-flex justify-content-between mb-3">
            <span>Selected matches</span>
            <strong>{Object.keys(selectionState).length}</strong>
          </div>
          <div className="border-top pt-3 mt-3">
            <div className="fw-semibold mb-2">Order details</div>
            {(projectSummary.poNumber ||
              projectSummary.shipVia ||
              projectSummary.requiredDate ||
              projectSummary.shipToAddress ||
              projectSummary.shippingInstructions ||
              projectSummary.orderNotes) ? (
              <div className="order-genie-v2-order-summary">
                <div className="order-genie-v2-order-grid">
                  {projectSummary.poNumber && (
                    <div className="order-genie-v2-order-kv">
                      <div className="order-genie-v2-order-label">PO Number</div>
                      <div className="order-genie-v2-order-value">{projectSummary.poNumber}</div>
                    </div>
                  )}
                  {projectSummary.shipVia && (
                    <div className="order-genie-v2-order-kv">
                      <div className="order-genie-v2-order-label">Ship Via</div>
                      <div className="order-genie-v2-order-value">{projectSummary.shipVia}</div>
                    </div>
                  )}
                  {projectSummary.requiredDate && (
                    <div className="order-genie-v2-order-kv">
                      <div className="order-genie-v2-order-label">Required Date</div>
                      <div className="order-genie-v2-order-value">{projectSummary.requiredDate}</div>
                    </div>
                  )}
                </div>
                {projectSummary.shipToAddress && (
                  <div className="order-genie-v2-order-block">
                    <div className="order-genie-v2-order-label">Ship To Address</div>
                    <div className="order-genie-v2-order-value order-genie-v2-order-text">
                      {projectSummary.shipToAddress}
                    </div>
                  </div>
                )}
                {projectSummary.shippingInstructions && (
                  <div className="order-genie-v2-order-block">
                    <div className="order-genie-v2-order-label">Shipping Instructions</div>
                    <div className="order-genie-v2-order-value order-genie-v2-order-text">
                      {projectSummary.shippingInstructions}
                    </div>
                  </div>
                )}
                {projectSummary.orderNotes && (
                  <div className="order-genie-v2-order-block">
                    <div className="order-genie-v2-order-label">Order Notes</div>
                    <div className="order-genie-v2-order-value order-genie-v2-order-text">
                      {projectSummary.orderNotes}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted small">No order-level details detected.</div>
            )}
          </div>
          <div className="border-top pt-3 mt-3">
            <div className="fw-semibold mb-2">Source document</div>
            <div className="text-muted small mb-2">
              {previewDocLabel}
            </div>
            {(previewUrlValue || previewTableValue.length > 0 || previewTextValue) ? (
              <div className="order-genie-v2-source-preview">
                <div className="order-genie-v2-doc-scan-wrap">
                  {previewTypeValue === 'application/pdf' ? (
                    <iframe title="Source document" src={previewUrlValue} />
                  ) : previewTypeValue && String(previewTypeValue).startsWith('image/') ? (
                    <img src={previewUrlValue} alt={previewDoc?.filename || 'Uploaded document'} />
                  ) : (previewTableValue.length > 0 || previewTextValue) ? (
                    <div className="order-genie-v2-doc-fallback w-100">
                      {previewTableValue.length > 0 && (
                        <div className="table-responsive mb-2">
                          <table className="table table-sm mb-0">
                            <tbody>
                              {previewTableValue.map((row, rowIdx) => (
                                <tr key={`source-preview-row-${rowIdx}`}>
                                  {(Array.isArray(row) ? row : []).map((cell, colIdx) => (
                                    <td key={`source-preview-cell-${rowIdx}-${colIdx}`}>{String(cell || '')}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {previewTextValue && <div className="text-muted small">{previewTextValue}</div>}
                    </div>
                  ) : (
                    <div className="text-muted small">No document preview available.</div>
                  )}
                  {scanningActive && (
                    <div
                      className={`order-genie-v2-scanning-overlay ${tabularPreviewActive ? 'order-genie-v2-scanning-overlay--tabular' : ''}`}
                      aria-hidden="true"
                    >
                      <div className="order-genie-v2-scanning-line" />
                      <div className="order-genie-v2-scanning-label">
                        {tabularPreviewActive ? 'Reading rows...' : 'Scanning document...'}
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  size="small"
                  className="mt-2 w-100"
                  onClick={() =>
                    setPreviewModalFile({
                      ...(previewDoc || {}),
                      url: previewUrlValue || previewDoc?.url || '',
                      content_type: previewTypeValue || previewDoc?.content_type || '',
                    })
                  }
                >
                  Open large
                </Button>
              </div>
            ) : (
              <div className="text-muted small">No document preview available.</div>
            )}
          </div>
          <div className="border-top pt-3 mt-3">
            <div className="fw-semibold mb-2">Actions</div>
            <Button
              type="default"
              className="w-100 mb-2"
              onClick={() => openEditDrawer(sourceItems, sourceRequestId, sourceUserId)}
            >
              Edit inputs
            </Button>
            <Button type="default" className="w-100 mb-2" onClick={handleStartNewRequest}>
              Start New Request
            </Button>
            {!isV3 && (
              <Button
                type="default"
                className="w-100"
                onClick={async () => {
                  if (!sourceRequestId || !sourceUserId) return;
                  await startRequestRerun(sourceRequestId, sourceUserId);
                }}
              >
                Rerun request
              </Button>
            )}
          </div>
          <Button
            type="primary"
            className="w-100 mt-2"
            onClick={() =>
              handleProceedToCheckout(
                sourceItems,
                sourceMatches,
                sourceRequestId,
                sourceUserId,
                sourceSummary,
                selectionState,
                qtyState
              )
            }
          >
            Proceed to checkout
          </Button>
          {shouldShowReviewProgressDetails && (
            <div className="mt-3">
              {renderRequestProgressCard(statusInfo, processMeta, { showReadyTitle: false })}
              <div className="text-muted small mt-1">
                Configured workers: {parallelWorkers} parallel worker{parallelWorkers > 1 ? 's' : ''}
              </div>
            </div>
          )}
        </Card>
        </Affix>
        ) : (
        <Card className="shadow-sm border-0 order-genie-v2-summary-card">
          <h5>Summary</h5>
          <div className="text-muted small mb-3">Review selections and proceed.</div>
          {(projectSummary.projectName || projectSummary.projectDescription) && (
            <div className="order-genie-v2-project-summary mb-3">
              {projectSummary.projectName && (
                <div className="order-genie-v2-project-summary-title">{projectSummary.projectName}</div>
              )}
              {projectSummary.projectDescription && (
                <div className="order-genie-v2-project-summary-desc">{projectSummary.projectDescription}</div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-between mb-2">
            <span>Line items</span>
            <strong>{lineItemCount}</strong>
          </div>
          <div className="d-flex justify-content-between mb-3">
            <span>Selected matches</span>
            <strong>{Object.keys(selectionState).length}</strong>
          </div>
          <div className="border-top pt-3 mt-3">
            <div className="fw-semibold mb-2">Order details</div>
            {(projectSummary.poNumber ||
              projectSummary.shipVia ||
              projectSummary.requiredDate ||
              projectSummary.shipToAddress ||
              projectSummary.shippingInstructions ||
              projectSummary.orderNotes) ? (
              <div className="order-genie-v2-order-summary">
                <div className="order-genie-v2-order-grid">
                  {projectSummary.poNumber && (
                    <div className="order-genie-v2-order-kv">
                      <div className="order-genie-v2-order-label">PO Number</div>
                      <div className="order-genie-v2-order-value">{projectSummary.poNumber}</div>
                    </div>
                  )}
                  {projectSummary.shipVia && (
                    <div className="order-genie-v2-order-kv">
                      <div className="order-genie-v2-order-label">Ship Via</div>
                      <div className="order-genie-v2-order-value">{projectSummary.shipVia}</div>
                    </div>
                  )}
                  {projectSummary.requiredDate && (
                    <div className="order-genie-v2-order-kv">
                      <div className="order-genie-v2-order-label">Required Date</div>
                      <div className="order-genie-v2-order-value">{projectSummary.requiredDate}</div>
                    </div>
                  )}
                </div>
                {projectSummary.shipToAddress && (
                  <div className="order-genie-v2-order-block">
                    <div className="order-genie-v2-order-label">Ship To Address</div>
                    <div className="order-genie-v2-order-value order-genie-v2-order-text">
                      {projectSummary.shipToAddress}
                    </div>
                  </div>
                )}
                {projectSummary.shippingInstructions && (
                  <div className="order-genie-v2-order-block">
                    <div className="order-genie-v2-order-label">Shipping Instructions</div>
                    <div className="order-genie-v2-order-value order-genie-v2-order-text">
                      {projectSummary.shippingInstructions}
                    </div>
                  </div>
                )}
                {projectSummary.orderNotes && (
                  <div className="order-genie-v2-order-block">
                    <div className="order-genie-v2-order-label">Order Notes</div>
                    <div className="order-genie-v2-order-value order-genie-v2-order-text">
                      {projectSummary.orderNotes}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted small">No order-level details detected.</div>
            )}
          </div>
          <div className="border-top pt-3 mt-3">
            <div className="fw-semibold mb-2">Source document</div>
            <div className="text-muted small mb-2">
              {previewDocLabel}
            </div>
            {(previewUrlValue || previewTableValue.length > 0 || previewTextValue) ? (
              <div className="order-genie-v2-source-preview">
                <div className="order-genie-v2-doc-scan-wrap">
                  {previewTypeValue === 'application/pdf' ? (
                    <iframe title="Source document" src={previewUrlValue} />
                  ) : previewTypeValue && String(previewTypeValue).startsWith('image/') ? (
                    <img src={previewUrlValue} alt={previewDoc?.filename || 'Uploaded document'} />
                  ) : (previewTableValue.length > 0 || previewTextValue) ? (
                    <div className="order-genie-v2-doc-fallback w-100">
                      {previewTableValue.length > 0 && (
                        <div className="table-responsive mb-2">
                          <table className="table table-sm mb-0">
                            <tbody>
                              {previewTableValue.map((row, rowIdx) => (
                                <tr key={`source-preview-compact-row-${rowIdx}`}>
                                  {(Array.isArray(row) ? row : []).map((cell, colIdx) => (
                                    <td key={`source-preview-compact-cell-${rowIdx}-${colIdx}`}>{String(cell || '')}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {previewTextValue && <div className="text-muted small">{previewTextValue}</div>}
                    </div>
                  ) : (
                    <div className="text-muted small">No document preview available.</div>
                  )}
                  {scanningActive && (
                    <div
                      className={`order-genie-v2-scanning-overlay ${tabularPreviewActive ? 'order-genie-v2-scanning-overlay--tabular' : ''}`}
                      aria-hidden="true"
                    >
                      <div className="order-genie-v2-scanning-line" />
                      <div className="order-genie-v2-scanning-label">
                        {tabularPreviewActive ? 'Reading rows...' : 'Scanning document...'}
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  size="small"
                  className="mt-2 w-100"
                  onClick={() =>
                    setPreviewModalFile({
                      ...(previewDoc || {}),
                      url: previewUrlValue || previewDoc?.url || '',
                      content_type: previewTypeValue || previewDoc?.content_type || '',
                    })
                  }
                >
                  Open large
                </Button>
              </div>
            ) : (
              <div className="text-muted small">No document preview available.</div>
            )}
          </div>
          <div className="border-top pt-3 mt-3">
            <div className="fw-semibold mb-2">Actions</div>
            <Button
              type="default"
              className="w-100 mb-2"
              onClick={() => openEditDrawer(sourceItems, sourceRequestId, sourceUserId)}
            >
              Edit inputs
            </Button>
            <Button type="default" className="w-100 mb-2" onClick={handleStartNewRequest}>
              Start New Request
            </Button>
            {!isV3 && (
              <Button
                type="default"
                className="w-100"
                onClick={async () => {
                  if (!sourceRequestId || !sourceUserId) return;
                  await startRequestRerun(sourceRequestId, sourceUserId);
                }}
              >
                Rerun request
              </Button>
            )}
          </div>
          <Button
            type="primary"
            className="w-100 mt-2"
            onClick={() =>
              handleProceedToCheckout(
                sourceItems,
                sourceMatches,
                sourceRequestId,
                sourceUserId,
                sourceSummary,
                selectionState,
                qtyState
              )
            }
          >
            Proceed to checkout
          </Button>
          {shouldShowReviewProgressDetails && (
            <div className="mt-3">
              {renderRequestProgressCard(statusInfo, processMeta, { showReadyTitle: false })}
              <div className="text-muted small mt-1">
                Configured workers: {parallelWorkers} parallel worker{parallelWorkers > 1 ? 's' : ''}
              </div>
            </div>
          )}
        </Card>
        )}
      </div>
    </div>
    {shouldFloatMatchFilter && (
      <div
        className="order-genie-v2-match-filter-page-buffer"
        style={{ height: `${floatingMatchFilterSafeBuffer}px` }}
        aria-hidden="true"
      />
    )}
    </>
  );
  };

  const markRequestRead = (requestIdValue) => {
    try {
      const key = `order_genie_v2_read_${statusUserId || userId || 'default'}`;
      const raw = localStorage.getItem(key);
      const list = raw ? JSON.parse(raw) : [];
      const next = Array.isArray(list) ? list : [];
      if (!next.includes(requestIdValue)) {
        next.push(requestIdValue);
      }
      localStorage.setItem(key, JSON.stringify(next));
    } catch (err) {
      // ignore
    }
  };

  const isRequestRead = (requestIdValue) => {
    try {
      const key = `order_genie_v2_read_${statusUserId || userId || 'default'}`;
      const raw = localStorage.getItem(key);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) && list.includes(requestIdValue);
    } catch (err) {
      return false;
    }
  };

  const loadStatusRequests = useCallback(async (userOverride, pageOverride, pageSizeOverride, dateRangeOverride) => {
    const targetUserRaw = String((userOverride ?? statusUserIdRef.current) || '').trim();
    const targetUser = targetUserRaw || 'all';
    if (!targetUser) return;
    const page = pageOverride ?? statusPage;
    const pageSize = pageSizeOverride ?? statusPageSize;
    const range = dateRangeOverride ?? statusDateRange;
    if (statusListLoadInFlightRef.current) {
      statusListLoadPendingRef.current = {
        userOverride,
        pageOverride,
        pageSizeOverride,
        dateRangeOverride,
      };
      return;
    }
    statusListLoadInFlightRef.current = true;
    const dateFrom = range?.[0]?.format ? range[0].format('YYYY-MM-DD') : undefined;
    const dateTo = range?.[1]?.format ? range[1].format('YYYY-MM-DD') : undefined;
    const queryUser = targetUser.toLowerCase() === 'all' ? undefined : targetUser;
    setStatusLoading(true);
    setQueuePanelLoading(true);
    try {
      const queueBundlePromise = Promise.all([
        listOrderGenieQueue({
          user_id: queryUser || 'all',
          statuses: 'queued,running',
          page: 1,
          page_size: 50,
        }).catch(() => ({ items: [], total: 0 })),
        getOrderGenieQueueMetrics().catch(() => ({ metrics: null })),
        listOrderGenieQueue({
          user_id: queryUser || 'all',
          statuses: 'dead_letter',
          page: 1,
          page_size: 50,
        }).catch(() => ({ items: [], total: 0 })),
      ]);
      const reqResp = await listOrderGenieRequests(queryUser, {
        page,
        pageSize,
        dateFrom,
        dateTo,
        lite: true,
        minimal: isV3,
      });
      const quoteResp = isV3
        ? { items: [] }
        : await (queryUser
          ? listOrderGenieQuotes(queryUser, { page, pageSize, dateFrom, dateTo, lite: true })
          : Promise.resolve({}));
      const requests = Array.isArray(reqResp?.items) ? reqResp.items : [];
      const quotes = Array.isArray(quoteResp?.items) ? quoteResp.items : [];
      setStatusRequests(requests);
      setStatusQuotes(quotes);
      setStatusTotal(Number(reqResp?.total || 0));
      const [queueResp, queueMetricsResp, queueDeadLetterResp] = await queueBundlePromise;
      setQueuePanelItems(Array.isArray(queueResp?.items) ? queueResp.items : []);
      setQueuePanelTotal(Number(queueResp?.total || 0));
      setQueueMetrics(queueMetricsResp?.metrics || null);
      setQueueDeadLetterItems(Array.isArray(queueDeadLetterResp?.items) ? queueDeadLetterResp.items : []);
      if (isV3) {
        setV3ProcessDetailsLoadingByRequest({});
        setV3ProcessDetailsModalOpen(false);
        setV3ProcessDetailsModalRequest(null);
        setV3ProcessDetailsModalStatus(null);
      }
      setStatusUserOptions((prev) => {
        const nextSet = new Set(
          (Array.isArray(prev) ? prev : [])
            .map((entry) => String(entry || '').trim())
            .filter(Boolean)
        );
        requests.forEach((req) => {
          const requestUser = String(req?.user_id || '').trim();
          if (requestUser) nextSet.add(requestUser);
        });
        return Array.from(nextSet).sort((left, right) => left.localeCompare(right));
      });
      setProcessMetaById((prev) => {
        const next = { ...(prev || {}) };
        (requests || []).forEach((req) => {
          const key = String(req?.id || '').trim();
          if (!key) return;
          const current = next[key] || {};
          next[key] = {
            ...current,
            queueStatus: String(req?.queue_status || current?.queueStatus || '').toLowerCase(),
            queuePriorityLabel: String(req?.queue_priority_label || current?.queuePriorityLabel || '').toLowerCase(),
            queuePriority: Math.max(0, Number(req?.queue_priority || current?.queuePriority || 0)),
            queuePosition: Math.max(0, Number(req?.queue_position || current?.queuePosition || 0)),
            queueDepth: Math.max(0, Number(req?.queue_depth || current?.queueDepth || 0)),
            queueEtaSeconds: Math.max(0, Number(req?.queue_eta_seconds || current?.queueEtaSeconds || 0)),
          };
        });
        return next;
      });
      if (!isV3) {
        setProcessMetaById((prev) => {
          const next = { ...(prev || {}) };
          (requests || []).forEach((req) => {
            const key = String(req?.id || '').trim();
            if (!key) return;
            const current = next[key] || {};
            const dbAiRerankedItems = Number(req?.ai_reranked_items || 0);
            const dbAiLowConfidenceItems = Number(req?.ai_low_confidence_items || 0);
            if (
              (!Number.isFinite(dbAiRerankedItems) || dbAiRerankedItems < 0)
              && (!Number.isFinite(dbAiLowConfidenceItems) || dbAiLowConfidenceItems < 0)
            ) return;
            const nextRerankCount = Math.max(Number(current?.rerankCount || 0), dbAiRerankedItems);
            const nextRerankTotal = Math.max(Number(current?.rerankTotal || 0), nextRerankCount);
            const nextLowConfidenceCount = Math.max(
              Number(current?.aiLowConfidenceCount || 0),
              Number.isFinite(dbAiLowConfidenceItems) ? Math.max(0, dbAiLowConfidenceItems) : 0
            );
            const nextLowConfidenceTotal = Math.max(Number(current?.aiLowConfidenceTotal || 0), nextRerankTotal);
            next[key] = {
              ...current,
              rerankCount: nextRerankCount,
              rerankTotal: nextRerankTotal,
              aiLowConfidenceCount: nextLowConfidenceCount,
              aiLowConfidenceTotal: nextLowConfidenceTotal,
            };
          });
          return next;
        });
      }
      const statusMap = {};
      const sample = requests.slice(0, 20).filter((req) => req?.id);
      if (!isV3) {
        const requestIds = sample.map((req) => req.id);
        const requestUsers = sample.reduce((acc, req) => {
          const statusUser = String(queryUser || req.user_id || '').trim();
          if (statusUser) {
            acc[String(req.id)] = statusUser;
          }
          return acc;
        }, {});
        if (requestIds.length) {
          try {
            const resp = await getOrderGenieProcessStatusBulk({
              request_ids: requestIds,
              request_users: requestUsers,
            });
            const statuses = resp?.statuses && typeof resp.statuses === 'object' ? resp.statuses : {};
            sample.forEach((req) => {
              statusMap[req.id] = statuses[String(req.id)] || null;
            });
          } catch (err) {
            sample.forEach((req) => {
              statusMap[req.id] = null;
            });
          }
        }
      }
      setRequestStatusById(statusMap);
      const terminalByRequest = requests.reduce((acc, req) => {
        const requestIdValue = String(req?.id || '');
        if (!requestIdValue) return acc;
        const runtimeStatus = String(statusMap[requestIdValue]?.status || '').toLowerCase();
        const persistedStatus = String(
          (req?.status && typeof req.status === 'object')
            ? (req.status.request_status || req.status.status || '')
            : (req?.status || '')
        ).toLowerCase();
        acc[requestIdValue] = isTerminalStatus(runtimeStatus) || isTerminalStatus(persistedStatus);
        return acc;
      }, {});
      setActiveProcesses((prev) =>
        prev.filter((entry) => !terminalByRequest[String(entry.requestId || '')])
      );
      setProcessRunTypeById((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.keys(next).forEach((requestIdValue) => {
          if (terminalByRequest[String(requestIdValue || '')]) {
            delete next[requestIdValue];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } catch (err) {
      // ignore
    } finally {
      statusListLoadInFlightRef.current = false;
      setStatusLoading(false);
      setQueuePanelLoading(false);
      const pendingLoad = statusListLoadPendingRef.current;
      if (pendingLoad) {
        statusListLoadPendingRef.current = null;
        setTimeout(() => {
          loadStatusRequests(
            pendingLoad.userOverride,
            pendingLoad.pageOverride,
            pendingLoad.pageSizeOverride,
            pendingLoad.dateRangeOverride
          );
        }, 0);
      }
    }
  }, [isV3, statusPage, statusPageSize, statusDateRange]);

  const loadUsageEvents = useCallback(async () => {
    const targetUserRaw = String((statusUserIdRef.current || userId || '')).trim();
    const targetUser = targetUserRaw || 'all';
    const range = usageDateRange;
    const dateFrom = range?.[0]?.format ? range[0].format('YYYY-MM-DD') : undefined;
    const dateTo = range?.[1]?.format ? range[1].format('YYYY-MM-DD') : undefined;
    const queryUser = targetUser.toLowerCase() === 'all' ? undefined : targetUser;
    setUsageLoading(true);
    setUsageError('');
    try {
      const resp = await listOrderGenieUsageEvents({
        user_id: queryUser,
        customer_id_erp: String(customerIdAsInErp || '').trim() || undefined,
        date_from: dateFrom,
        date_to: dateTo,
        limit: 5000,
      });
      setUsageEvents(Array.isArray(resp?.items) ? resp.items : []);
    } catch (err) {
      setUsageEvents([]);
      setUsageError(String(err?.message || 'Failed to load usage dashboard.'));
    } finally {
      setUsageLoading(false);
    }
  }, [usageDateRange, userId, customerIdAsInErp]);

  useEffect(() => {
    if (activeTab !== 'status') return;
    loadStatusRequests();
  }, [activeTab, statusPage, statusPageSize, statusDateRange, loadStatusRequests]);

  useEffect(() => {
    if (activeTab !== 'usage' || !isV3) return;
    loadUsageEvents();
  }, [activeTab, isV3, loadUsageEvents]);

  useEffect(() => {
    if (activeTab !== 'status' || !isV3) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const resp = await getOrderGenieQueueMetrics();
        if (cancelled) return;
        const metrics = resp?.metrics || {};
        setQueueMetrics(metrics);
        setQueueMetricsTrend((prev) => {
          const next = [
            ...prev,
            {
              at: Date.now(),
              queued: Number(metrics?.queued_total || 0),
              running: Number(metrics?.running_total || 0),
              deadLetter: Number(metrics?.dead_letter_total || 0),
              waitAvgSec: Number(metrics?.queued_wait_avg_seconds || 0),
            },
          ];
          return next.slice(-24);
        });
      } catch (err) {
        // ignore
      }
    };
    tick();
    const timer = setInterval(tick, 20000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeTab, isV3]);

  useEffect(() => {
    if (activeTab !== 'status') return;
    if (statusUserOptions.length > 0 || statusUserLoading) return;
    loadStatusUsers();
  }, [activeTab, statusUserOptions.length, statusUserLoading, loadStatusUsers]);

  const renderStatus = () => {
    const quoteByRequest = (isV3 ? [] : statusQuotes).reduce((acc, entry) => {
      acc[entry.request_id] = entry;
      return acc;
    }, {});
    const buildInitialSelections = (nextItems, nextMatches) => {
      const selectionMap = {};
      const grouped = {};
      (nextMatches || []).forEach((entry) => {
        const key = String(entry.item_id ?? entry.itemId ?? '');
        if (!key) return;
        grouped[key] = grouped[key] || [];
        grouped[key].push(entry);
      });
      (nextItems || []).forEach((item) => {
        const key = String(item.id || '');
        if (!key) return;
        const options = grouped[key] || [];
        if (options.length) {
          selectionMap[key] = options[0].product_code;
        }
      });
      return selectionMap;
    };
    return (
      <div className="row g-4">
        <div className="col-12 col-xl-9">
          <Card className="shadow-sm border-0">
            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
              <div className="flex-grow-1">
                <label className="form-label">User ID</label>
                <Space.Compact block>
                  <Select
                    ref={statusUserSelectRef}
                    showSearch
                    allowClear
                    placeholder="Select or type a user"
                    value={statusUserIdDraft || undefined}
                    searchValue={statusUserSearch}
                    open={statusUserDropdownOpen}
                    onChange={(value) => {
                      const nextUser = value || '';
                      setStatusUserIdDraft(nextUser);
                      setStatusUserSearch('');
                    }}
                    onSearch={(value) => setStatusUserSearch(value)}
                    onClear={() => {
                      setStatusUserSearch('');
                    }}
                    onOpenChange={(open) => {
                      setStatusUserDropdownOpen(open);
                      if (open && statusUserOptions.length === 0) {
                        loadStatusUsers();
                      }
                    }}
                    filterOption={(input, option) =>
                      String(option?.value || '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={[
                      { value: 'all', label: 'All' },
                      ...statusUserOptions.map((entry) => ({ value: entry, label: entry })),
                    ]}
                    loading={statusUserLoading}
                    style={{ width: '100%' }}
                  />
                  <Button
                    type="default"
                    onClick={() => {
                      setStatusUserSearch('');
                      setStatusUserDropdownOpen(true);
                      if (statusUserOptions.length === 0) {
                        loadStatusUsers();
                      }
                      if (statusUserSelectRef.current?.focus) {
                        statusUserSelectRef.current.focus();
                      }
                    }}
                  >
                    Change user
                  </Button>
                </Space.Compact>
              </div>
              <div>
                <label className="form-label">Date</label>
                <RangePicker
                  value={statusDateRange || null}
                  onChange={(nextRange) => {
                    setStatusDateRange(nextRange);
                    setStatusPage(1);
                  }}
                  allowClear
                  style={{ width: '100%' }}
                />
              </div>
              <Button
                type="primary"
                loading={statusLoading}
                onClick={async () => {
                  const nextUser = String(statusUserIdDraft || '').trim();
                  const nextPage = 1;
                  setStatusUserId(nextUser);
                  setStatusPage(nextPage);
                  await loadStatusRequests(nextUser, nextPage, statusPageSize, statusDateRange);
                }}
              >
                Load
              </Button>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 text-muted small">
              <div>Total: {statusTotal}</div>
              <div>
                {statusTotal > 0
                  ? `Showing ${(statusPage - 1) * statusPageSize + 1}-${Math.min(
                      statusPage * statusPageSize,
                      statusTotal
                    )}`
                  : 'Showing 0'}
              </div>
            </div>
            {isV3 && isQueueAdmin && (
              <div className="d-flex justify-content-end mb-2">
                <Button size="small" onClick={() => setQueueDrawerOpen(true)}>
                  Queue Admin
                </Button>
              </div>
            )}
            <div className="border rounded p-2 mb-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <div className="fw-semibold">Account Queue</div>
                <div className="small text-muted">
                  {queueMetrics
                    ? `Queued ${Number(queueMetrics.queued_total || 0)} · Running ${Number(queueMetrics.running_total || 0)} · DLQ ${Number(queueMetrics.dead_letter_total || 0)}`
                    : `Queued ${queuePanelTotal}`}
                </div>
              </div>
              {queuePanelLoading ? (
                <div className="small text-muted">Loading queue...</div>
              ) : queuePanelItems.length === 0 ? (
                <div className="small text-muted">No queued or running requests.</div>
              ) : (
                <div className="d-flex flex-column gap-1">
                  {queuePanelItems.slice(0, 10).map((queueItem) => {
                    const queueRequestId = String(queueItem?.request_id || '');
                    const queuePriority = String(queueItem?.priority_label || 'normal').toLowerCase();
                    const queuePosition = Math.max(0, Number(queueItem?.queue_position || queueItem?.position_snapshot || 0));
                    const queueDepth = Math.max(0, Number(queueItem?.queue_depth || queueItem?.queue_depth_snapshot || 0));
                    const queueEtaSeconds = Math.max(0, Number(queueItem?.eta_seconds_snapshot || 0));
                    const queueStatus = String(queueItem?.queue_status || '').toLowerCase();
                    return (
                      <div key={`queue-${queueRequestId}`} className="d-flex flex-wrap align-items-center justify-content-between gap-2 small border-top pt-1">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <Tag color={queueStatus === 'running' ? 'processing' : 'gold'} className="mb-0">
                            {queueStatus || 'queued'}
                          </Tag>
                          <span className="text-muted">{`Request #${queueRequestId || '-'}`}</span>
                          {queueStatus === 'queued' && (
                            <span className="text-muted">{`#${Math.max(1, queuePosition)} of ${Math.max(queueDepth, queuePosition || 1)}`}</span>
                          )}
                          {queueEtaSeconds > 0 && <span className="text-muted">{`ETA ~${Math.max(1, Math.round(queueEtaSeconds / 60))} min`}</span>}
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-1">
                          {['urgent', 'normal', 'low'].map((label) => {
                            const savingKey = `${queueRequestId}:${label}`;
                            const isSaving = Boolean(queuePrioritySavingByRequest[savingKey]);
                            const urgentBlocked = label === 'urgent' && !isQueueAdmin;
                            return (
                              <Button
                                key={savingKey}
                                size="small"
                                type={queuePriority === label ? 'primary' : 'default'}
                                loading={isSaving}
                                disabled={!queueRequestId || urgentBlocked || !queuePriorityEnabled}
                                onClick={async () => {
                                  if (!queueRequestId) return;
                                  if (urgentBlocked) return;
                                  const actorUser = String(statusUserId || userId || '').trim();
                                  if (!actorUser) return;
                                  setQueuePrioritySavingByRequest((prev) => ({ ...prev, [savingKey]: true }));
                                  try {
                                    await updateOrderGenieRequestPriority(queueRequestId, {
                                      user_id: actorUser,
                                      priority: label,
                                    });
                                    await loadStatusRequests(statusUserId.trim());
                                  } finally {
                                    setQueuePrioritySavingByRequest((prev) => ({ ...prev, [savingKey]: false }));
                                  }
                                }}
                              >
                                {label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {statusRequests.length === 0 && (
              <div className="text-muted small">No requests loaded yet.</div>
            )}
            {statusRequests.length > 0 && (
              <div className="order-genie-v2-list-header text-muted small">
                <div className="row align-items-center">
                  <div className="col-12 col-lg-3">Request</div>
                  <div className="col-6 col-lg-2">User</div>
                  <div className="col-6 col-lg-2">Customer ID</div>
                  <div className="col-6 col-lg-2">Updated</div>
                  <div className="col-6 col-lg-1">Status</div>
                  <div className="col-12 col-lg-2 text-lg-end">Actions</div>
                </div>
              </div>
            )}
            {statusRequests.map((req) => {
              const persistedStatusText = getStatusValueText(req?.status);
              const persistedStatusMessage = getStatusMessageText(req?.status);
              const rawStatus = getUnifiedRequestStatus(req.id, {
                status: persistedStatusText,
                message: persistedStatusMessage || persistedStatusText,
                detail: '',
              });
              const hasActiveProcess = activeProcesses.some((entry) => String(entry.requestId) === String(req.id));
              const persistedStatusValue = String(persistedStatusText || '').toLowerCase();
              const status = (
                isV3
                && isTerminalStatus(persistedStatusValue)
                && !hasActiveProcess
              )
                ? {
                    ...rawStatus,
                    status: persistedStatusValue === 'matched' ? 'completed' : persistedStatusValue,
                    step: persistedStatusValue === 'matched' ? 'completed' : persistedStatusValue,
                    message:
                      persistedStatusValue === 'matched' || persistedStatusValue === 'completed'
                        ? 'Ready for review'
                        : persistedStatusValue === 'cancelled'
                          ? 'Stopped'
                          : persistedStatusValue === 'failed'
                            ? 'Failed'
                            : String(rawStatus?.message || persistedStatusMessage || persistedStatusText || ''),
                    detail:
                      persistedStatusValue === 'matched' || persistedStatusValue === 'completed'
                        ? 'Recovered from database terminal status.'
                        : String(rawStatus?.detail || ''),
                  }
                : rawStatus;
              const listDetailText = getRequestListDetailText(status?.detail);
              const requestMeta = processMetaById[req.id] || {};
              const queueStatusValue = String(
                status?.queue_status
                || req?.queue_status
                || requestMeta?.queueStatus
                || ''
              ).toLowerCase();
              const terminalFromQueue = ['completed', 'failed', 'cancelled'].includes(queueStatusValue);
              const canonicalStatusValue = String(status?.status || '').toLowerCase();
              let effectiveStatusValue = (
                terminalFromQueue
                  ? (queueStatusValue === 'completed' ? 'completed' : queueStatusValue)
                  : String(status?.request_status || status?.status || persistedStatusText || '').toLowerCase()
              );
              if (isTerminalStatus(canonicalStatusValue) && !isTerminalStatus(effectiveStatusValue)) {
                effectiveStatusValue = canonicalStatusValue;
              }
              const canResumeFromCheckpoint = effectiveStatusValue === 'failed' || effectiveStatusValue === 'cancelled';
              const quote = quoteByRequest[req.id];
              const isRead = isRequestRead(req.id);
              const isReady = isV3
                ? (persistedStatusValue === 'matched' || isSuccessStatus(status?.request_status || status?.status))
                : (quote || persistedStatusValue === 'matched' || isSuccessStatus(status?.request_status || status?.status));
              const v3StatusLabel = (() => {
                if (!isV3) return String(status?.message || persistedStatusMessage || persistedStatusText || '');
                if (terminalFromQueue) {
                  if (queueStatusValue === 'completed') return 'Ready for review';
                  if (queueStatusValue === 'cancelled') return 'Stopped';
                  if (queueStatusValue === 'failed') return 'Failed';
                }
                if (isSuccessStatus(status?.request_status || effectiveStatusValue)) return 'Ready for review';
                if (
                  String(status?.row_rerun_status || '').toLowerCase() === 'running'
                  || (String(status?.row_rerun_step || '').toLowerCase().startsWith('row_rerun')
                    && !['row_rerun_completed', 'row_rerun_failed'].includes(String(status?.row_rerun_step || '').toLowerCase()))
                ) {
                  return 'Updating row matches';
                }
                if (effectiveStatusValue === 'failed') return 'Failed';
                if (effectiveStatusValue === 'cancelled') return 'Stopped';
                if (effectiveStatusValue === 'running' || effectiveStatusValue === 'queued') return 'In progress';
                return persistedStatusMessage || persistedStatusText || 'In progress';
              })();
              const effectiveStepValue = String(status?.request_step || status?.step || '').toLowerCase();
              const isStatusRunningLike =
                !terminalFromQueue
                && (
                  effectiveStatusValue === 'running'
                  || effectiveStatusValue === 'queued'
                  || ['reading', 'parsing', 'processing', 'matching', 'ai_rerank', 'queued'].includes(effectiveStepValue)
                );
              const isRunning = (
                (hasActiveProcess || isStatusRunningLike)
                && !isTerminalStatus(effectiveStatusValue)
                && !hasTerminalReviewSignal(status)
              );
              const isProgressExpanded = Object.prototype.hasOwnProperty.call(expandedProgressByRequest, req.id)
                ? Boolean(expandedProgressByRequest[req.id])
                : isRunning;
              const statusLineItems = Number(status?.line_items ?? NaN);
              const metaLineItems = Number(requestMeta?.itemsCount ?? NaN);
              const persistedLineItems = Number(req?.total_items ?? NaN);
              const extractionRows = Math.max(
                0,
                Number.isFinite(statusLineItems) && statusLineItems > 0 ? statusLineItems : 0,
                Number.isFinite(metaLineItems) && metaLineItems > 0 ? metaLineItems : 0,
                Number.isFinite(persistedLineItems) && persistedLineItems > 0 ? persistedLineItems : 0
              );
              const matchingDone = Number(status?.matched_count || requestMeta.matchedCount || 0);
              const matchingTotal = Number(status?.matched_total || requestMeta.matchedTotal || extractionRows || 0);
              const rerankDone = Math.max(
                Number(status?.rerank_count || 0),
                Number(requestMeta?.rerankCount || 0)
              );
              const rerankTotal = Math.max(
                Number(status?.rerank_total || 0),
                Number(requestMeta?.rerankTotal || 0),
                rerankDone
              );
              const lowConfidenceRows = Math.max(
                Number(status?.ai_low_confidence_count || 0),
                Number(requestMeta?.aiLowConfidenceCount || 0)
              );
              const queuePosition = Math.max(
                0,
                Number(status?.queue_position || 0),
                Number(req?.queue_position || 0),
                Number(requestMeta?.queuePosition || 0)
              );
              const queueDepth = Math.max(
                0,
                Number(status?.queue_depth || 0),
                Number(req?.queue_depth || 0),
                Number(requestMeta?.queueDepth || 0)
              );
              const queueEtaSeconds = Math.max(
                0,
                Number(status?.queue_eta_seconds || 0),
                Number(req?.queue_eta_seconds || 0),
                Number(requestMeta?.queueEtaSeconds || 0)
              );
              const queuePriorityLabel = String(
                status?.queue_priority_label
                || req?.queue_priority_label
                || requestMeta?.queuePriorityLabel
                || ''
              ).toLowerCase();
              const requestKey = String(req.id || '');
              const customerIdText = String(
                req?.customer_id_erp
                || req?.customerIdErp
                || req?.customer_id
                || req?.customerId
                || req?.summary_json?.customer_id_erp
                || req?.summary_json?.customerIdErp
                || ''
              ).trim();
              const v3DetailsLoading = Boolean(v3ProcessDetailsLoadingByRequest[requestKey]);
              const openRequestReview = async () => {
                const requestUser = normalizeRequestUserId(req.user_id, userId, statusUserId);
                if (!requestUser) return;
                const requestUserId = requestUser.trim();
                const requestKey = String(req.id || '');
                setReviewHydrationLoadingByRequest((prev) => ({ ...prev, [requestKey]: true }));
                const existingTab = (quoteTabs || []).find((tab) => String(tab.id) === requestKey);
                const cachedItems = Array.isArray(existingTab?.items) ? existingTab.items : [];
                const cachedMatches = Array.isArray(existingTab?.matches) ? existingTab.matches : [];
                const cachedSourceFiles = Array.isArray(existingTab?.sourceFiles) ? existingTab.sourceFiles : [];
                const cachedSummary =
                  existingTab?.requestSummary &&
                  typeof existingTab.requestSummary === 'object' &&
                  !Array.isArray(existingTab.requestSummary)
                    ? existingTab.requestSummary
                    : {};
                const seededCustomerSummary = customerIdText
                  ? { customer_id_erp: customerIdText, customerIdErp: customerIdText }
                  : {};
                const summarySeed = mergeRequestSummary(cachedSummary, seededCustomerSummary) || cachedSummary;
                const reqStatusText = getStatusValueText(req?.status);
                const reqStatusMessage = getStatusMessageText(req?.status);
                const seedStatus = {
                  status: reqStatusText,
                  step: reqStatusText,
                  message: reqStatusMessage || reqStatusText,
                  detail: '',
                  ...(status && typeof status === 'object' ? status : {}),
                };
                const title = `Request: ${req.request_name || req.id}`;
                const seedTerminalStatusValue = String(
                  seedStatus?.request_status || seedStatus?.status || reqStatusText || ''
                ).toLowerCase();
                const isSeedTerminal = !hasActiveProcess && isTerminalStatus(seedTerminalStatusValue);

                // Open tab immediately to avoid blocking on network fetch.
                setRequestStatusById((prev) => ({
                  ...(prev || {}),
                  [req.id]: {
                    ...((prev || {})[req.id] || {}),
                      status: seedStatus?.status || reqStatusText,
                      step: seedStatus?.step || reqStatusText,
                      message: seedStatus?.message || reqStatusMessage || reqStatusText,
                    detail: seedStatus?.detail || '',
                  },
                }));
                setQuoteTabs((prev) => {
                  const existing = prev.find((tab) => String(tab.id) === requestKey);
                  if (existing) {
                    return prev.map((tab) =>
                      String(tab.id) === requestKey
                        ? {
                            ...tab,
                            title,
                            userId: requestUserId,
                            statusSnapshot: seedStatus,
                            items: Array.isArray(tab.items) ? tab.items : cachedItems,
                            matches: Array.isArray(tab.matches) ? tab.matches : cachedMatches,
                            sourceFiles: Array.isArray(tab.sourceFiles) ? tab.sourceFiles : cachedSourceFiles,
                            requestSummary:
                              tab.requestSummary && typeof tab.requestSummary === 'object' && !Array.isArray(tab.requestSummary)
                                ? mergeRequestSummary(tab.requestSummary, seededCustomerSummary)
                                : summarySeed,
                          }
                        : tab
                    );
                  }
                  return [
                    ...prev,
                    {
                      id: req.id,
                      title,
                      items: cachedItems,
                      matches: cachedMatches,
                      sourceFiles: cachedSourceFiles,
                      requestSummary: summarySeed,
                      userId: requestUserId,
                      statusSnapshot: seedStatus,
                    },
                  ];
                });
                setRequestSummaryById((prev) => ({
                  ...prev,
                  [req.id]: mergeRequestSummary(prev?.[req.id], summarySeed) || summarySeed,
                }));
                if (cachedItems.length > 0 || cachedMatches.length > 0) {
                  setQuoteSelections((prev) => ({
                    ...prev,
                    [req.id]: prev[req.id] || buildInitialSelections(cachedItems, cachedMatches),
                  }));
                  setQuoteQtys((prev) => ({
                    ...prev,
                    [req.id]:
                      prev[req.id] ||
                      cachedItems.reduce((acc, item) => {
                        const key = String(item.id || '');
                        if (key) {
                          acc[key] = item.normalized_parts?.qty || 1;
                        }
                        return acc;
                      }, {}),
                  }));
                }

                setRequestId(req.id);
                setProcessStatus(seedStatus);
                setStep(isSeedTerminal ? 'results' : 'processing');
                if (isV3 && isSeedTerminal) {
                  setV3ReviewOpenedByRequest((prev) => ({ ...prev, [requestKey]: true }));
                }
                if (!isSeedTerminal) {
                  setActiveProcesses((prev) => {
                    const exists = prev.some((entry) => String(entry.requestId) === requestKey);
                    if (exists) return prev;
                    return [...prev, { requestId: req.id, userId: requestUserId }];
                  });
                  setProcessRunTypeById((prev) => ({ ...prev, [req.id]: prev?.[req.id] || 'initial' }));
                }
                markRequestRead(req.id);
                setActiveTab(`quote-${req.id}`);

                // Refresh with latest canonical data in background.
                (async () => {
                  const liveStatusResp = await getOrderGenieProcessStatus(req.id, requestUserId).catch(() => null);
                  const liveStatus = liveStatusResp?.status || null;
                  const resolvedStatus = liveStatus && typeof liveStatus === 'object' ? liveStatus : seedStatus;
                  const liveTerminalStatusValue = String(
                    resolvedStatus?.request_status || resolvedStatus?.status || reqStatusText || ''
                  ).toLowerCase();
                  const isLiveTerminal = !hasActiveProcess && isTerminalStatus(liveTerminalStatusValue);
                  setRequestStatusById((prev) => ({
                    ...(prev || {}),
                    [req.id]: {
                      ...((prev || {})[req.id] || {}),
                      status: resolvedStatus?.status || reqStatusText,
                      step: resolvedStatus?.step || reqStatusText,
                      message: resolvedStatus?.message || reqStatusMessage || reqStatusText,
                      detail: resolvedStatus?.detail || '',
                    },
                  }));
                  if (String(requestId || '') === requestKey) {
                    setProcessStatus((prev) => ({ ...(prev || {}), ...resolvedStatus }));
                    setStep(isLiveTerminal ? 'results' : 'processing');
                  }
                  if (isV3 && isLiveTerminal) {
                    setV3ReviewOpenedByRequest((prev) => ({ ...prev, [requestKey]: true }));
                  }
                  if (!isLiveTerminal) {
                    setActiveProcesses((prev) => {
                      const exists = prev.some((entry) => String(entry.requestId) === requestKey);
                      if (exists) return prev;
                      return [...prev, { requestId: req.id, userId: requestUserId }];
                    });
                  }

                  const expectedFromRowsByPage = resolvedStatus?.extraction_rows_by_page
                    && typeof resolvedStatus.extraction_rows_by_page === 'object'
                    ? Object.values(resolvedStatus.extraction_rows_by_page).reduce((sum, value) => sum + (Number(value) || 0), 0)
                    : 0;
                  const expectedItemCount = Math.max(
                    Number(resolvedStatus?.line_items || 0),
                    Number(expectedFromRowsByPage || 0)
                  );

                  let refreshed = await getOrderGenieRequest(req.id, requestUserId).catch(() => null);
                  let refreshedItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
                  if (!isV3) {
                    for (let attempt = 0; attempt < 2; attempt += 1) {
                      const hasNoItems = refreshedItems.length === 0;
                      const belowExpected = expectedItemCount > 0 && refreshedItems.length < expectedItemCount;
                      if (!hasNoItems && !belowExpected) break;
                      await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
                      try {
                        refreshed = await getOrderGenieRequest(req.id, requestUserId);
                      } catch {
                        // keep last successful payload for retry loop
                      }
                      refreshedItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
                    }
                  }
                  if (!refreshed || typeof refreshed !== 'object') return;

                  const nextItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
                  const nextMatches = mergeCartSelectionsIntoMatches(refreshed?.matches, refreshed?.cart);
                  const nextAiRerankedRowKeys = new Set();
                  const nextAiLowConfidenceRowKeys = new Set();
                  (Array.isArray(nextMatches) ? nextMatches : []).forEach((entry) => {
                    const rowKey = String(entry?.item_id ?? entry?.itemId ?? '').trim();
                    if (!rowKey) return;
                    if (entry?.ai_ranked) {
                      nextAiRerankedRowKeys.add(rowKey);
                    }
                    if (entry?.ai_low_confidence || (entry?.ai_analyzed && !entry?.ai_ranked)) {
                      nextAiLowConfidenceRowKeys.add(rowKey);
                    }
                  });
                  const nextAiRerankedRows = nextAiRerankedRowKeys.size;
                  const nextAiLowConfidenceRows = nextAiLowConfidenceRowKeys.size;
                  const nextSourceFiles = Array.isArray(refreshed?.request?.source_files)
                    ? refreshed.request.source_files
                    : [];
                  const nextRequestSummary =
                    refreshed?.request?.summary_json &&
                    typeof refreshed.request.summary_json === 'object' &&
                    !Array.isArray(refreshed.request.summary_json)
                      ? refreshed.request.summary_json
                      : {};

                  setRequestSummaryById((prev) => ({ ...prev, [req.id]: nextRequestSummary }));
                  setProcessMetaById((prev) => {
                    const current = prev?.[req.id] || {};
                    const nextRerankCount = nextAiRerankedRows;
                    const nextRerankTotal = Math.max(nextRerankCount, nextAiLowConfidenceRows);
                    return {
                      ...prev,
                      [req.id]: {
                        ...current,
                        rerankCount: nextRerankCount,
                        rerankTotal: nextRerankTotal,
                        aiLowConfidenceCount: nextAiLowConfidenceRows,
                        aiLowConfidenceTotal: nextRerankTotal,
                      },
                    };
                  });
                  setRequestStatusById((prev) => {
                    const current = prev?.[req.id] || {};
                    const nextRerankCount = nextAiRerankedRows;
                    const nextRerankTotal = Math.max(nextRerankCount, nextAiLowConfidenceRows);
                    return {
                      ...prev,
                      [req.id]: {
                        ...current,
                        rerank_count: nextRerankCount,
                        rerank_total: nextRerankTotal,
                        ai_low_confidence_count: nextAiLowConfidenceRows,
                        ai_low_confidence_total: nextRerankTotal,
                      },
                    };
                  });
                  setQuoteTabs((prev) =>
                    prev.map((tab) =>
                      String(tab.id) === requestKey
                        ? {
                            ...tab,
                            title,
                            items: nextItems,
                            matches: nextMatches,
                            sourceFiles: nextSourceFiles,
                            requestSummary: nextRequestSummary,
                            userId: requestUserId,
                            statusSnapshot: resolvedStatus,
                          }
                        : tab
                    )
                  );
                  setQuoteSelections((prev) => ({
                    ...prev,
                    [req.id]: prev[req.id] || buildInitialSelections(nextItems, nextMatches),
                  }));
                  setQuoteQtys((prev) => ({
                    ...prev,
                    [req.id]:
                      prev[req.id] ||
                      nextItems.reduce((acc, item) => {
                        const key = String(item.id || '');
                        if (key) {
                          acc[key] = item.normalized_parts?.qty || 1;
                        }
                        return acc;
                      }, {}),
                  }));
                })().finally(() => {
                  setReviewHydrationLoadingByRequest((prev) => {
                    const next = { ...(prev || {}) };
                    delete next[requestKey];
                    return next;
                  });
                });
              };
              const deleteRequest = async () => {
                const ok = await confirmDialog({
                  title: 'Delete request',
                  content: 'Delete this request and all associated data?',
                });
                if (!ok) return;
                const requestUser = normalizeRequestUserId(req.user_id, userId, statusUserId);
                if (!requestUser) return;
                const requestKey = String(req.id);
                await deleteOrderGenieRequest(req.id, requestUser.trim());
                setQuoteTabs((prev) => prev.filter((tab) => String(tab.id) !== requestKey));
                setQuoteSelections((prev) => {
                  const next = { ...prev };
                  delete next[requestKey];
                  return next;
                });
                setQuoteQtys((prev) => {
                  const next = { ...prev };
                  delete next[requestKey];
                  return next;
                });
                setRequestSummaryById((prev) => {
                  const next = { ...prev };
                  delete next[requestKey];
                  return next;
                });
                setRequestStatusById((prev) => {
                  const next = { ...prev };
                  delete next[requestKey];
                  return next;
                });
                setProcessMetaById((prev) => {
                  const next = { ...prev };
                  delete next[requestKey];
                  return next;
                });
                setProcessRunTypeById((prev) => {
                  const next = { ...prev };
                  delete next[requestKey];
                  return next;
                });
                setReviewHydrationLoadingByRequest((prev) => {
                  const next = { ...prev };
                  delete next[requestKey];
                  return next;
                });
                setActiveProcesses((prev) =>
                  prev.filter((entry) => String(entry.requestId) !== requestKey)
                );
                if (String(requestId || '') === requestKey) {
                  resetNewRequestView();
                }
                if (activeTab === `quote-${requestKey}`) {
                  setActiveTab('status');
                }
                await loadStatusRequests(statusUserId.trim());
              };
              const openV3ProcessDetailsModal = async () => {
                if (!isV3) return;
                const requestUser = normalizeRequestUserId(req.user_id, userId, statusUserId);
                if (!requestUser) return;
                setV3ProcessDetailsModalRequest(req);
                setV3ProcessDetailsModalStatus(null);
                setV3ProcessDetailsModalOpen(true);
                setV3ProcessDetailsLoadingByRequest((prev) => ({ ...prev, [requestKey]: true }));
                try {
                  const liveStatusResp = await getOrderGenieProcessStatus(req.id, requestUser.trim()).catch(() => null);
                  const liveStatus = liveStatusResp?.status || null;
                  if (liveStatus && typeof liveStatus === 'object') {
                    setRequestStatusById((prev) => ({
                      ...(prev || {}),
                      [req.id]: liveStatus,
                    }));
                    setV3ProcessDetailsModalStatus(liveStatus);
                  }
                } finally {
                  setV3ProcessDetailsLoadingByRequest((prev) => ({ ...prev, [requestKey]: false }));
                }
              };
              const openRunLogModal = async () => {
                if (isV3) return;
                const requestUser = normalizeRequestUserId(req.user_id, userId, statusUserId);
                if (!requestUser) return;
                setRunDetailsOpen(true);
                setRunDetailsLoading(true);
                setRunDetailsData(null);
                try {
                  const details = await getOrderGenieRunDetails(req.id, requestUser.trim(), 200);
                  setRunDetailsData(details || null);
                } catch (err) {
                  setRunDetailsData({
                    request: { request_name: req.request_name || `Request ${req.id}` },
                    ai_runtime: [],
                    ai_errors: [err?.message || 'Unable to load run details.'],
                    db_events: [],
                    ai_events: [],
                  });
                } finally {
                  setRunDetailsLoading(false);
                }
              };
              return (
                <Card
                  key={req.id}
                  size="small"
                  className={`mb-2 ${isV3 ? 'order-genie-v2-status-card--v3' : ''}`}
                >
                  <div className={`row align-items-start ${isV3 ? 'gy-1' : 'gy-2'}`}>
                    <div className="col-12 col-lg-3">
                      <div className="fw-semibold">{req.request_name}</div>
                      <div className="text-muted small">
                        Total lines: {extractionRows}
                        {req?.customer ? ` | Customer: ${req.customer}` : ''}
                      </div>
                      {queueStatusValue === 'queued' && (
                        <div className="text-muted small">
                          {`Queued #${Math.max(1, queuePosition)} of ${Math.max(queueDepth, queuePosition || 1)}`}
                          {queueEtaSeconds > 0 ? ` · ETA ~${Math.max(1, Math.round(queueEtaSeconds / 60))} min` : ''}
                        </div>
                      )}
                      {isReady && !isRead && <Tag color="blue">New</Tag>}
                      {req.source === 'email_inbox' && req.source_email_id && (
                        <div className="mt-1">
                          <a
                            href={`/order-x/email-inbox?item=${req.source_email_id}`}
                            className="badge bg-info-subtle text-info border border-info-subtle text-decoration-none d-inline-flex align-items-center gap-1"
                            style={{ fontSize: 10 }}
                            title="View the originating email in the inbox"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="bi bi-envelope" style={{ fontSize: 10 }} />
                            From email
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="col-6 col-lg-2">
                      <div className="text-muted small">{req.user_id || statusUserId}</div>
                    </div>
                    <div className="col-6 col-lg-2">
                      <div className="text-muted small">{customerIdText || '-'}</div>
                    </div>
                    <div className="col-6 col-lg-2">
                      <div className="text-muted small">{req.updated_at}</div>
                    </div>
                    <div className="col-6 col-lg-1 mt-1 mt-lg-0">
                      {isRunning ? (
                        <Tag color="processing" className="order-genie-v2-status-tag">
                          Running
                        </Tag>
                      ) : (
                        <Tag color="geekblue" className="order-genie-v2-status-tag">
                          {v3StatusLabel}
                        </Tag>
                      )}
                      {queueStatusValue === 'queued' && (
                        <Tag color="gold" className="order-genie-v2-status-tag mt-1">
                          {`Queue #${Math.max(1, queuePosition)}`}
                        </Tag>
                      )}
                      {queuePriorityLabel && (
                        <Tag color={queuePriorityLabel === 'urgent' ? 'red' : queuePriorityLabel === 'low' ? 'default' : 'blue'} className="order-genie-v2-status-tag mt-1">
                          {`Priority: ${queuePriorityLabel}`}
                        </Tag>
                      )}
                    </div>
                    <div className={`col-12 col-lg-2 ${isV3 ? 'mt-0' : 'mt-1 mt-lg-0'}`}>
                      <div className={`d-grid ${isV3 ? 'gap-1' : 'gap-2'}`}>
                        <Button
                          size="small"
                          className="w-100"
                          loading={Boolean(reviewHydrationLoadingByRequest[requestKey])}
                          onClick={openRequestReview}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          className="w-100"
                          onClick={async () => {
                            const requestUser = normalizeRequestUserId(req.user_id, userId, statusUserId);
                            if (!requestUser) return;
                            await openProbeModal(req, requestUser.trim());
                          }}
                        >
                          Probe
                        </Button>
                        {canResumeFromCheckpoint && (
                          <Button
                            size="small"
                            className="w-100"
                            onClick={async () => {
                              const requestUser = normalizeRequestUserId(req.user_id, userId, statusUserId);
                              if (!requestUser) return;
                              await resumeRequestFromCheckpoint(req.id, requestUser.trim());
                              await loadStatusRequests(statusUserId.trim());
                            }}
                          >
                            Resume
                          </Button>
                        )}
                        {!isV3 && (
                          <Button
                            size="small"
                            className="w-100"
                            disabled={isRunning}
                            onClick={async () => {
                              const requestUser = normalizeRequestUserId(req.user_id, userId, statusUserId);
                              if (!requestUser) return;
                              await startRequestRerun(req.id, requestUser.trim());
                            }}
                          >
                            Rerun
                          </Button>
                        )}
                        {!isV3 && (
                          <Button size="small" className="w-100" onClick={openRunLogModal}>
                            Run Log
                          </Button>
                        )}
                        {isRunning && (
                          <Button
                            size="small"
                            className="w-100"
                            danger
                            onClick={async () => {
                              const requestUser = normalizeRequestUserId(req.user_id, userId, statusUserId);
                              if (!requestUser) return;
                              await stopRunningRequest(req.id, requestUser.trim());
                            }}
                          >
                            Stop
                          </Button>
                        )}
                        <Button size="small" className="w-100" danger onClick={deleteRequest}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className={`row ${isV3 ? 'g-1 mt-0' : 'g-2 mt-1'} align-items-start`}>
                    <div className="col-12">
                      {listDetailText && (
                        <div className="text-muted small mb-2">{listDetailText}</div>
                      )}
                      {status && !isV3 && (
                        <div className="order-genie-v2-list-progress-shell">
                          <div className="order-genie-v2-list-progress-header">
                            <div className="small fw-semibold">
                              {isSuccessStatus(status?.request_status || status?.status)
                                ? `Process Details: Time taken ${(() => {
                                  const totalSec = Number(
                                    status?.timing_total_sec || status?.step_timings_sec?.total || 0
                                  );
                                  const safe = Number.isFinite(totalSec) ? Math.max(0, Math.round(totalSec)) : 0;
                                  const mins = Math.floor(safe / 60);
                                  const rem = safe % 60;
                                  return `${mins} min ${rem} secs`;
                                })()}`
                                : 'Progress details'}
                            </div>
                            <Button
                              type="default"
                              size="small"
                              shape="circle"
                              className="order-genie-v2-list-progress-toggle"
                              icon={isProgressExpanded ? <DownOutlined /> : <RightOutlined />}
                              aria-label={isProgressExpanded ? 'Collapse progress details' : 'Expand progress details'}
                              title={isProgressExpanded ? 'Collapse progress details' : 'Expand progress details'}
                              onClick={() =>
                                setExpandedProgressByRequest((prev) => ({
                                  ...prev,
                                  [req.id]: !isProgressExpanded,
                                }))
                              }
                            />
                          </div>
                          {isProgressExpanded ? (
                            <div className="order-genie-v2-list-progress">
                              {renderRequestProgressCard(
                                status,
                                requestMeta,
                                { onReview: isSuccessStatus(status?.request_status || status?.status) ? openRequestReview : null }
                              )}
                            </div>
                          ) : (
                            <div className="order-genie-v2-list-progress-collapsed">
                              <div className="order-genie-v2-list-progress-chip">
                                Extraction: {extractionRows} rows
                              </div>
                              <div className="order-genie-v2-list-progress-chip">
                                Product matching: {matchingDone}/{matchingTotal || 0}
                              </div>
                              <div className="order-genie-v2-list-progress-chip">
                                AI analysis: {rerankTotal > 0 ? `${rerankDone}/${rerankTotal} rows` : '0/0 rows'}
                              </div>
                              {lowConfidenceRows > 0 && (
                                <div className="order-genie-v2-list-progress-chip">
                                  AI low confidence: {lowConfidenceRows} row(s)
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {isV3 && (
                    <div className="row mt-1">
                      <div className="col-12 col-lg-2 ms-lg-auto">
                        <div className="d-grid gap-1">
                          <Button size="small" className="w-100" loading={v3DetailsLoading} onClick={openV3ProcessDetailsModal}>
                            Process Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
            {statusTotal > 0 && (
              <div className="d-flex justify-content-end mt-3">
                <Pagination
                  current={statusPage}
                  pageSize={statusPageSize}
                  total={statusTotal}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '30', '40', '50']}
                  onChange={(page, pageSize) => {
                    setStatusPage(page);
                    setStatusPageSize(pageSize);
                  }}
                />
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  };

  const renderV3ProcessingOnly = (tab) => {
    const tabId = String(tab?.id || '');
    const status = getUnifiedRequestStatus(tabId, {});
    const meta = processMetaById[tabId] || {};
    const stepValue = String(status?.request_step || status?.step || '').toLowerCase();
    const statusValue = String(status?.request_status || status?.status || '').toLowerCase();
    const activelyProcessingStep = ['reading', 'parsing', 'processing', 'matching', 'ai_rerank', 'rerank', 'reranking', 'queued'].includes(stepValue);
    const rowsByPageTotal = (
      status?.extraction_rows_by_page && typeof status.extraction_rows_by_page === 'object'
        ? Object.values(status.extraction_rows_by_page).reduce((sum, value) => sum + (Number(value) || 0), 0)
        : 0
    );
    const rowsExtracted = Math.max(
      0,
      Number(status?.line_items || 0),
      Number(meta?.itemsCount || 0),
      Number(rowsByPageTotal || 0)
    );
    const matchedCount = Number(status?.matched_count || meta?.matchedCount || 0);
    const matchedTotal = Number(status?.matched_total || meta?.matchedTotal || rowsExtracted || 0);
    const rerankDone = Number(status?.rerank_count || meta?.rerankCount || 0);
    const rerankTotal = Number(status?.rerank_total || meta?.rerankTotal || 0);
    const statusHasLowConfidenceRows = Object.prototype.hasOwnProperty.call(status || {}, 'ai_low_confidence_count');
    const lowConfidenceRowsRaw = statusHasLowConfidenceRows
      ? Number(status?.ai_low_confidence_count || 0)
      : Number(meta?.aiLowConfidenceCount || 0);
    const lowConfidenceRows = Math.max(
      0,
      Math.min(lowConfidenceRowsRaw, rerankTotal > 0 ? rerankTotal : lowConfidenceRowsRaw)
    );
    const tabularExpectedRows = Math.max(0, Number(status?.tabular_expected_rows || 0));
    const tabularExtractedRows = Math.max(0, Number(status?.tabular_extracted_rows || 0));
    const tabularSkippedRows = Math.max(0, Number(status?.tabular_skipped_rows || 0));
    const tabularAiMappingUsed = Boolean(status?.tabular_ai_mapping_used);
    const tabularFallbackReason = String(status?.tabular_fallback_reason || '').trim();
    const rowAiEnrichmentDone = Math.max(0, Number(status?.row_ai_enrichment_done || 0));
    const rowAiEnrichmentTotal = Math.max(rowAiEnrichmentDone, Number(status?.row_ai_enrichment_total || 0));
    const rowAiEnrichmentFailed = Math.max(0, Number(status?.row_ai_enrichment_failed || 0));
    const stageName = String(status?.stage || '').trim();
    const stageDone = Math.max(0, Number(status?.stage_done || 0));
    const stageTotal = Math.max(stageDone, Number(status?.stage_total || 0));
    const stageFailed = Math.max(0, Number(status?.stage_failed || 0));
    const chunkIndex = Math.max(0, Number(status?.chunk_index || 0));
    const chunkTotal = Math.max(chunkIndex, Number(status?.chunk_total || 0));
    const throughput = Math.max(0, Number(status?.throughput_rows_per_min || 0));
    const etaSeconds = Math.max(0, Number(status?.eta_seconds || 0));
    const lastProgressAtRaw = Number(status?.last_progress_at || 0);
    const lastProgressAgeSec = lastProgressAtRaw > 0 ? Math.max(0, Math.round((Date.now() / 1000) - lastProgressAtRaw)) : 0;
    const degradedProgress = stageFailed > 0 || String(status?.detail || '').toLowerCase().includes('retry');
    const matchingStarted = ['matching', 'ai_rerank', 'matched', 'completed'].includes(stepValue) || matchedCount > 0;
    const matchingCountsComplete = matchedTotal <= 0 || matchedCount >= matchedTotal;
    const matchingCompleted = (
      (['ai_rerank', 'matched', 'completed'].includes(stepValue) && matchingCountsComplete)
      || (matchedTotal > 0 && matchedCount >= matchedTotal)
    );
    const rerankingStarted = matchingCompleted && (['ai_rerank', 'matched', 'completed'].includes(stepValue) || rerankTotal > 0 || rerankDone > 0);
    const rerankingCountsComplete = rerankTotal <= 0 || rerankDone >= rerankTotal;
    const rerankingCompleted = (
      (['matched', 'completed'].includes(stepValue) && rerankingCountsComplete)
      || (rerankingStarted && rerankingCountsComplete)
    );
    // Do not show Ready for Review until matching and AI analysis counts are done,
    // even if a stale/premature terminal status arrives from the API.
    const isCompleted = (
      isSuccessStatus(statusValue)
      && !activelyProcessingStep
      && matchingCountsComplete
      && rerankingCountsComplete
    );
    const scanningActive = !isCompleted;
    const extractionStarted = ['reading', 'parsing', 'processing', 'parsed', 'matching', 'ai_rerank', 'matched', 'completed'].includes(stepValue)
      || isCompleted;
    const extractionCompleted = Boolean(
      status?.extraction_complete
      || meta?.extractionComplete
      || ['parsed', 'matching', 'ai_rerank', 'matched', 'completed'].includes(stepValue)
    );

    const sourceDocs = Array.isArray(tab?.sourceFiles) ? tab.sourceFiles : [];
    const previewDoc = sourceDocs.length ? sourceDocs[sourceDocs.length - 1] : null;
    const serverUrl = String(previewDoc?.url || previewDoc?.previewUrl || '').trim();
    const isAbsoluteUrl = serverUrl.startsWith('http://') || serverUrl.startsWith('https://');
    const fallbackPreviewUrl = String(tabId) === String(requestId || '') ? (previewUrl || '') : '';
    const fallbackPreviewType = String(tabId) === String(requestId || '') ? (previewType || '') : '';
    const previewUrlValue = fallbackPreviewUrl || (isAbsoluteUrl ? serverUrl : '');
    const previewTypeValue = String(previewDoc?.content_type || previewDoc?.type || fallbackPreviewType || '').trim().toLowerCase();
    const previewStoragePath = String(previewDoc?.storage_path || previewDoc?.path || '').trim();
    const processingStoredPreview = previewStoragePath ? (storedPreviewByPath[previewStoragePath] || {}) : {};
    const processingPreviewTable = String(tabId) === String(requestId || '') && uploadPreviewTable.length > 0
      ? uploadPreviewTable
      : (Array.isArray(processingStoredPreview?.table) ? processingStoredPreview.table : []);
    const processingPreviewText = String(tabId) === String(requestId || '') && String(uploadPreviewText || '').trim()
      ? String(uploadPreviewText || '').trim()
      : String(processingStoredPreview?.text || '').trim();
    const processingPreviewHtml = String(tabId) === String(requestId || '') ? String(uploadPreviewHtml || '').trim() : '';
    const processingTabularActive = isTabularPreviewFile(previewTypeValue, String(previewDoc?.filename || previewDoc?.name || ''))
      || processingPreviewTable.length > 0
      || Boolean(processingPreviewText)
      || Boolean(processingPreviewHtml);
    const openReviewForRequest = async () => {
      setV3ReviewOpenedByRequest((prev) => ({ ...prev, [tabId]: true }));
      pushNotification('Request ready for review', 'Click review to finalize match selections.');
    };

    return (
      <div className={`row g-4 ${isV3 ? 'order-genie-v2-processing-layout--v3' : ''}`}>
        <div className={isV3 ? 'col-12 col-xl-10 mx-xl-auto' : 'col-12 col-xl-7'}>
          <Card className="shadow-sm border-0">
            <div className="order-genie-v2-doc-scan-wrap">
              {previewUrlValue && previewTypeValue === 'application/pdf' ? (
                <iframe title="Document preview" src={previewUrlValue} className="order-genie-v2-doc-frame" />
              ) : (
                <div
                  className="order-genie-v2-doc-preview"
                  style={{
                    backgroundImage: previewUrlValue && previewTypeValue.startsWith('image/') ? `url(${previewUrlValue})` : 'none',
                  }}
                >
                  {!previewUrlValue && !processingPreviewHtml && !(processingPreviewTable.length > 0 || processingPreviewText) && (
                    <div className="text-muted">Document preview will appear here.</div>
                  )}
                  {processingPreviewHtml && (
                    <iframe
                      title="Rich text preview"
                      sandbox=""
                      srcDoc={processingPreviewHtml}
                      className="order-genie-v2-doc-frame order-genie-v2-doc-frame-rich"
                    />
                  )}
                  {!processingPreviewHtml && (processingPreviewTable.length > 0 || processingPreviewText) && (
                    <div className="order-genie-v2-doc-fallback w-100">
                      {processingPreviewTable.length > 0 && (
                        <div className="table-responsive mb-2">
                          <table className="table table-sm mb-0">
                            <tbody>
                              {processingPreviewTable.map((row, rowIdx) => (
                                <tr key={`processing-tabular-row-${rowIdx}`}>
                                  {(Array.isArray(row) ? row : []).map((cell, colIdx) => (
                                    <td key={`processing-tabular-cell-${rowIdx}-${colIdx}`}>{String(cell || '')}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {processingPreviewText && <div className="text-muted small">{processingPreviewText}</div>}
                    </div>
                  )}
                </div>
              )}
              {scanningActive && (
                <div
                  className={`order-genie-v2-scanning-overlay ${processingTabularActive ? 'order-genie-v2-scanning-overlay--tabular' : ''}`}
                  aria-hidden="true"
                >
                  <div className="order-genie-v2-scanning-line" />
                  <div className="order-genie-v2-scanning-label">
                    {processingTabularActive ? 'Reading rows...' : 'Scanning document...'}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
        <div className={isV3 ? 'col-12 col-xl-10 mx-xl-auto' : 'col-12 col-xl-5'}>
          <Card className="shadow-sm border-0">
            {isCompleted ? (
              <div className="order-genie-v2-v3-ready-panel">
                <Button
                  type="primary"
                  size="large"
                  className="order-genie-v2-v3-ready-btn"
                  onClick={openReviewForRequest}
                >
                  Ready for Review
                </Button>
              </div>
            ) : (
              <>
                <div className="fw-semibold mb-2">Request Progress</div>
                <div className="text-muted small mb-3">{getStatusMessageText(status?.message) || 'Processing request...'}</div>
                <div className="d-flex flex-column gap-2">
                  <div className="small">
                    <strong>Data extraction:</strong>{' '}
                    {!extractionStarted ? 'pending' : extractionCompleted ? `completed (${rowsExtracted} rows extracted)` : 'started'}
                  </div>
                  {(tabularExpectedRows > 0 || tabularExtractedRows > 0 || tabularSkippedRows > 0) && (
                    <div className="small text-muted">
                      <strong>Tabular completeness:</strong>{' '}
                      {`${Math.max(tabularExtractedRows, rowsExtracted)}/${Math.max(tabularExpectedRows, tabularExtractedRows, rowsExtracted)} extracted`}
                      {tabularSkippedRows > 0 ? `, ${tabularSkippedRows} skipped` : ''}
                    </div>
                  )}
                  {(tabularAiMappingUsed || tabularFallbackReason) && (
                    <div className="small text-muted">
                      <strong>Column mapping:</strong>{' '}
                      {tabularAiMappingUsed ? 'AI mapping used' : 'Deterministic mapping'}
                      {tabularFallbackReason ? ` (${tabularFallbackReason.replace(/_/g, ' ')})` : ''}
                    </div>
                  )}
                  <div className="small">
                    <strong>Product matching:</strong>{' '}
                    {!matchingStarted ? 'pending' : matchingCompleted ? 'completed' : 'started'}
                  </div>
                  {rowAiEnrichmentTotal > 0 && (
                    <div className="small text-muted">
                      <strong>Row enrichment:</strong>{' '}
                      {`${rowAiEnrichmentDone}/${rowAiEnrichmentTotal}`}
                      {rowAiEnrichmentFailed > 0 ? `, failed ${rowAiEnrichmentFailed}` : ''}
                    </div>
                  )}
                  {matchingCompleted && (
                    <div className="small text-muted">
                      {isV3 ? 'Records requiring AI analysis' : 'Records requiring AI ranking'}: {Math.max(0, rerankTotal)}
                    </div>
                  )}
                  {lowConfidenceRows > 0 && (
                    <div className="small text-warning">
                      <strong>AI Low Confidence:</strong> {Math.max(0, lowConfidenceRows)} row(s)
                    </div>
                  )}
                  <div className="small">
                    <strong>{isV3 ? 'AI Analysis' : 'AI reranking'}:</strong>{' '}
                    {!rerankingStarted ? 'pending' : rerankingCompleted ? 'completed' : 'started'}
                  </div>
                  {stageTotal > 0 && (
                    <div className="small text-muted">
                      <strong>Work progress:</strong> {stageDone} / {stageTotal}
                      {stageName ? ` (${stageName})` : ''}
                    </div>
                  )}
                  {chunkTotal > 0 && (
                    <div className="small text-muted">
                      <strong>Chunk:</strong> {chunkIndex} / {chunkTotal}
                    </div>
                  )}
                  {(throughput > 0 || etaSeconds > 0 || lastProgressAgeSec > 0) && (
                    <div className="small text-muted">
                      <strong>Speed:</strong> {throughput > 0 ? `${throughput} rows/min` : 'calculating'}
                      {etaSeconds > 0 ? `, ETA ~${Math.ceil(etaSeconds / 60)} min` : ''}
                      {lastProgressAgeSec > 0 ? `, last update ${lastProgressAgeSec}s ago` : ''}
                    </div>
                  )}
                  {degradedProgress && (
                    <div className="small text-warning">
                      <strong>Notice:</strong> Recovering from slow/failed rows ({stageFailed}).
                    </div>
                  )}
                </div>
                <div className="mt-3 text-muted small">
                  Review will be enabled when all processing stages complete.
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    );
  };

  const renderUsageDashboard = () => {
    const rows = Array.isArray(usageEvents) ? usageEvents : [];
    const normalizeText = (value) => String(value || '').trim().toLowerCase();
    const isBillableRunEvent = (row) => {
      const eventType = normalizeText(row?.event_type);
      return eventType === 'process_completed' || eventType === 'row_rerun_completed';
    };
    const billableRows = rows.filter(isBillableRunEvent);
    const toNumber = (value) => {
      const n = Number(value || 0);
      return Number.isFinite(n) ? n : 0;
    };
    const sumBy = (key, sourceRows = rows) => sourceRows.reduce((acc, row) => acc + toNumber(row?.[key]), 0);
    const usageEventCount = rows.length;
    const uniqueRequests = new Set(rows.map((row) => String(row?.request_id || '')).filter(Boolean)).size;
    const uniqueUsers = new Set(rows.map((row) => String(row?.request_user_id || '').trim().toLowerCase()).filter(Boolean)).size;
    const uniqueCustomers = new Set(rows.map((row) => String(row?.customer_id_erp || '').trim().toLowerCase()).filter(Boolean)).size;
    const billableRuns = billableRows.length;
    const rowsExtracted = sumBy('rows_extracted', billableRows);
    const itemsMatched = sumBy('items_matched', billableRows);
    const totalMatches = sumBy('total_matches', billableRows);
    const aiMatches = sumBy('matches_through_ai', billableRows);
    const directPart = sumBy('direct_part_number_matches', billableRows);
    const directMfr = sumBy('direct_mfr_part_number_matches', billableRows);
    const directCustomer = sumBy('direct_customer_part_number_matches', billableRows);
    const docBytes = sumBy('document_size_bytes', billableRows);
    const processCompletedRows = billableRows.filter((row) => normalizeText(row?.event_type) === 'process_completed');
    const rowReruns = billableRows.filter((row) => normalizeText(row?.event_type) === 'row_rerun_completed').length;
    const processCompletedByRequest = processCompletedRows.reduce((acc, row) => {
      const requestKey = String(row?.request_id || '').trim();
      if (!requestKey) return acc;
      acc[requestKey] = (acc[requestKey] || 0) + 1;
      return acc;
    }, {});
    const wholeReruns = Object.values(processCompletedByRequest).reduce(
      (acc, count) => acc + Math.max(0, Number(count || 0) - 1),
      0
    );
    const processCompletedOrdered = [...processCompletedRows].sort((left, right) => {
      const leftAt = Date.parse(String(left?.created_at || '')) || 0;
      const rightAt = Date.parse(String(right?.created_at || '')) || 0;
      if (leftAt !== rightAt) return leftAt - rightAt;
      return toNumber(left?.id) - toNumber(right?.id);
    });
    const processRunIndexByEventId = {};
    const processRunCountByRequest = {};
    processCompletedOrdered.forEach((row) => {
      const requestKey = String(row?.request_id || '').trim();
      if (!requestKey) return;
      const next = (processRunCountByRequest[requestKey] || 0) + 1;
      processRunCountByRequest[requestKey] = next;
      processRunIndexByEventId[String(row?.id || '')] = next;
    });
    const groupedRunMetrics = {
      initial_run: { label: 'Initial runs', runs: 0, rowsExtracted: 0, itemsMatched: 0, totalMatches: 0, aiMatches: 0, directMatches: 0, docBytes: 0 },
      whole_rerun: { label: 'Whole reruns', runs: 0, rowsExtracted: 0, itemsMatched: 0, totalMatches: 0, aiMatches: 0, directMatches: 0, docBytes: 0 },
      row_rerun: { label: 'Row reruns', runs: 0, rowsExtracted: 0, itemsMatched: 0, totalMatches: 0, aiMatches: 0, directMatches: 0, docBytes: 0 },
    };
    billableRows.forEach((row) => {
      const eventType = normalizeText(row?.event_type);
      let groupKey = 'initial_run';
      if (eventType === 'row_rerun_completed') {
        groupKey = 'row_rerun';
      } else if (eventType === 'process_completed') {
        const runIndex = processRunIndexByEventId[String(row?.id || '')] || 1;
        groupKey = runIndex > 1 ? 'whole_rerun' : 'initial_run';
      }
      const target = groupedRunMetrics[groupKey];
      target.runs += 1;
      target.rowsExtracted += toNumber(row?.rows_extracted);
      target.itemsMatched += toNumber(row?.items_matched);
      target.totalMatches += toNumber(row?.total_matches);
      target.aiMatches += toNumber(row?.matches_through_ai);
      target.directMatches += (
        toNumber(row?.direct_part_number_matches)
        + toNumber(row?.direct_mfr_part_number_matches)
        + toNumber(row?.direct_customer_part_number_matches)
      );
      target.docBytes += toNumber(row?.document_size_bytes);
    });
    const groupedRunRows = ['initial_run', 'whole_rerun', 'row_rerun'].map((key) => ({
      key,
      ...groupedRunMetrics[key],
    }));
    const aiShare = totalMatches > 0 ? Math.round((aiMatches / totalMatches) * 100) : 0;

    const formatBytes = (value) => {
      const b = Math.max(0, toNumber(value));
      if (b < 1024) return `${Math.round(b)} B`;
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
      if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
      return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };

    const eventTypeMap = rows.reduce((acc, row) => {
      const key = String(row?.event_type || 'unknown').trim() || 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const eventTypeRows = Object.entries(eventTypeMap)
      .map(([eventType, count]) => ({ eventType, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
    const eventTypeTotal = Math.max(1, eventTypeRows.reduce((acc, row) => acc + row.count, 0));

    return (
      <div className="row g-4">
        <div className="col-12">
          <Card className="shadow-sm border-0">
            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
              <div>
                <label className="form-label">Date</label>
                <RangePicker
                  value={usageDateRange || null}
                  onChange={(nextRange) => setUsageDateRange(nextRange)}
                  allowClear
                  style={{ width: '100%' }}
                />
              </div>
              <Button type="primary" loading={usageLoading} onClick={loadUsageEvents}>
                Load Usage Report
              </Button>
            </div>
            {usageError ? (
              <div className="text-danger small mb-2">{usageError}</div>
            ) : (
              <div className="text-muted small mb-3">
                Reporting points: request volume, extraction throughput, match quality, AI impact, direct-match mix, rerun behavior, user/customer activity.
              </div>
            )}
            <div className="row g-3">
              {[
                { label: 'Billable runs', value: billableRuns, sub: `Requests: ${uniqueRequests} · Raw events: ${usageEventCount}` },
                { label: 'Rows extracted', value: rowsExtracted, sub: `Items matched: ${itemsMatched}` },
                { label: 'Total matches', value: totalMatches, sub: `AI-assisted: ${aiShare}%` },
                { label: 'Document volume', value: formatBytes(docBytes), sub: `Users: ${uniqueUsers} · Customers: ${uniqueCustomers}` },
                { label: 'Reruns', value: wholeReruns + rowReruns, sub: `Whole: ${wholeReruns} · Row: ${rowReruns}` },
                { label: 'Direct matches', value: directPart + directMfr + directCustomer, sub: `Part: ${directPart} · Mfr: ${directMfr} · Customer: ${directCustomer}` },
              ].map((entry) => (
                <div key={entry.label} className="col-12 col-md-6 col-xl-4">
                  <div className="border rounded p-3 bg-light h-100">
                    <div className="text-muted small">{entry.label}</div>
                    <div className="fw-bold fs-4">{entry.value}</div>
                    <div className="text-muted small">{entry.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="col-12">
          <Card className="shadow-sm border-0">
            <div className="fw-semibold mb-3">Billable metrics grouped by reruns</div>
            <div className="row g-3">
              {groupedRunRows.map((group) => (
                <div key={group.key} className="col-12 col-md-6 col-xl-4">
                  <div className="border rounded p-3 h-100 bg-light">
                    <div className="fw-semibold mb-2">{group.label}</div>
                    <div className="small text-muted mb-1">Runs: {group.runs}</div>
                    <div className="small text-muted mb-1">Rows extracted: {group.rowsExtracted}</div>
                    <div className="small text-muted mb-1">Items matched: {group.itemsMatched}</div>
                    <div className="small text-muted mb-1">Total matches: {group.totalMatches}</div>
                    <div className="small text-muted mb-1">AI matches: {group.aiMatches}</div>
                    <div className="small text-muted mb-1">Direct matches: {group.directMatches}</div>
                    <div className="small text-muted">Document volume: {formatBytes(group.docBytes)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="col-12 col-xl-6">
          <Card className="shadow-sm border-0 h-100">
            <div className="fw-semibold mb-3">Event type distribution</div>
            {eventTypeRows.length === 0 ? (
              <div className="text-muted small">No usage events in selected range.</div>
            ) : (
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                {eventTypeRows.map((entry) => (
                  <div key={entry.eventType}>
                    <div className="d-flex justify-content-between small mb-1">
                      <span>{entry.eventType}</span>
                      <span>{entry.count}</span>
                    </div>
                    <Progress
                      percent={Math.round((entry.count / eventTypeTotal) * 100)}
                      showInfo={false}
                      strokeColor="#1677ff"
                    />
                  </div>
                ))}
              </Space>
            )}
          </Card>
        </div>

        <div className="col-12 col-xl-6">
          <Card className="shadow-sm border-0 h-100">
            <div className="fw-semibold mb-3">Match source mix</div>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <div className="d-flex justify-content-between small">
                <span>AI assisted</span>
                <span>{aiMatches}</span>
              </div>
              <Progress percent={totalMatches > 0 ? Math.round((aiMatches / totalMatches) * 100) : 0} />
              <div className="d-flex justify-content-between small">
                <span>Direct part number</span>
                <span>{directPart}</span>
              </div>
              <Progress percent={totalMatches > 0 ? Math.round((directPart / totalMatches) * 100) : 0} strokeColor="#52c41a" />
              <div className="d-flex justify-content-between small">
                <span>Direct manufacturer part number</span>
                <span>{directMfr}</span>
              </div>
              <Progress percent={totalMatches > 0 ? Math.round((directMfr / totalMatches) * 100) : 0} strokeColor="#13c2c2" />
              <div className="d-flex justify-content-between small">
                <span>Direct customer part number</span>
                <span>{directCustomer}</span>
              </div>
              <Progress percent={totalMatches > 0 ? Math.round((directCustomer / totalMatches) * 100) : 0} strokeColor="#722ed1" />
            </Space>
          </Card>
        </div>
      </div>
    );
  };

  const renderSettings = () => (
    <div className="order-genie-v2-settings">
      <iframe
        title="OrderX Settings"
        src="/order-x/settings-embed"
        className="order-genie-v2-settings-frame"
      />
    </div>
  );

  const closeQuoteTab = useCallback((targetKey) => {
    const key = String(targetKey || '');
    if (!key.startsWith('quote-')) return;
    confirmDialog({ title: 'Close quote', content: 'Close this quote tab?' }).then((shouldClose) => {
      if (!shouldClose) return;
      const id = key.replace('quote-', '');
      setQuoteTabs((prev) => prev.filter((tab) => String(tab.id) !== id));
      setQuoteSelections((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setQuoteQtys((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setV3ReviewOpenedByRequest((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (activeTab === key) {
        resetNewRequestView();
        setActiveTab('new');
      }
    });
  }, [activeTab, resetNewRequestView]);

  const switchToTab = useCallback((nextKeyRaw) => {
    const nextKey = String(nextKeyRaw || '');
    if (nextKey === 'new' && nextKey !== String(activeTab || '')) {
      // Keep the New Request pane clean whenever returning to New tab.
      // This prevents stale review/process data from leaking into New Request.
      resetNewRequestView();
    }
    setActiveTab(nextKey);
  }, [activeTab, resetNewRequestView]);

  return (
    <div className={`container-fluid order-genie-v2-page ${isV3 ? 'order-genie-v2-page--v3' : ''}`}>
      <Drawer
        title="Queue Admin"
        placement="right"
        width={Math.min(640, Math.max(380, Math.round(viewportWidth * 0.5)))}
        open={queueDrawerOpen && isV3 && isQueueAdmin}
        onClose={() => setQueueDrawerOpen(false)}
      >
        <div className="d-flex flex-column gap-3">
          <div className="row g-2">
            {(() => {
              const latest = queueMetricsTrend[queueMetricsTrend.length - 1] || {};
              const previous = queueMetricsTrend[queueMetricsTrend.length - 2] || {};
              const cards = [
                {
                  key: 'queued',
                  label: 'Queued',
                  value: Number(queueMetrics?.queued_total ?? latest?.queued ?? 0),
                  delta: Number(latest?.queued ?? 0) - Number(previous?.queued ?? 0),
                },
                {
                  key: 'running',
                  label: 'Running',
                  value: Number(queueMetrics?.running_total ?? latest?.running ?? 0),
                  delta: Number(latest?.running ?? 0) - Number(previous?.running ?? 0),
                },
                {
                  key: 'deadLetter',
                  label: 'Dead Letter',
                  value: Number(queueMetrics?.dead_letter_total ?? latest?.deadLetter ?? 0),
                  delta: Number(latest?.deadLetter ?? 0) - Number(previous?.deadLetter ?? 0),
                },
                {
                  key: 'waitAvg',
                  label: 'Avg Wait (min)',
                  value: Math.max(0, Math.round(Number(queueMetrics?.queued_wait_avg_seconds ?? latest?.waitAvgSec ?? 0) / 60)),
                  delta: Math.round((Number(latest?.waitAvgSec ?? 0) - Number(previous?.waitAvgSec ?? 0)) / 60),
                },
              ];
              return cards.map((card) => (
                <div className="col-6" key={card.key}>
                  <Card size="small" className="h-100">
                    <div className="small text-muted">{card.label}</div>
                    <div className="fw-semibold">{card.value}</div>
                    <div className={`small ${card.delta > 0 ? 'text-danger' : card.delta < 0 ? 'text-success' : 'text-muted'}`}>
                      {card.delta === 0 ? 'No change' : `${card.delta > 0 ? '+' : ''}${card.delta} vs prev`}
                    </div>
                  </Card>
                </div>
              ));
            })()}
          </div>
          <Card size="small" title={`Dead-letter Queue (${queueDeadLetterItems.length})`}>
            {queueDeadLetterItems.length === 0 ? (
              <div className="text-muted small">No dead-letter requests.</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {queueDeadLetterItems.map((entry) => {
                  const deadRequestId = String(entry?.request_id || '').trim();
                  const replayKey = deadRequestId;
                  const replayLoading = Boolean(queueReplayLoadingByRequest[replayKey]);
                  return (
                    <div key={`dlq-${deadRequestId}`} className="border rounded p-2">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                        <div>
                          <div className="fw-semibold small">{`Request #${deadRequestId || '-'}`}</div>
                          <div className="small text-muted">
                            {String(entry?.last_error || 'No error details').slice(0, 200)}
                          </div>
                        </div>
                        <Button
                          size="small"
                          loading={replayLoading}
                          onClick={async () => {
                            if (!deadRequestId) return;
                            const actorUser = String(statusUserId || userId || '').trim();
                            if (!actorUser) return;
                            setQueueReplayLoadingByRequest((prev) => ({ ...prev, [replayKey]: true }));
                            try {
                              await replayOrderGenieQueueRequest(deadRequestId, { user_id: actorUser });
                              await loadStatusRequests(statusUserId.trim());
                            } finally {
                              setQueueReplayLoadingByRequest((prev) => ({ ...prev, [replayKey]: false }));
                            }
                          }}
                        >
                          Replay
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </Drawer>
      <Modal
        title={`Run Details${runDetailsData?.request?.request_name ? ` - ${runDetailsData.request.request_name}` : ''}`}
        open={runDetailsOpen}
        onCancel={() => setRunDetailsOpen(false)}
        footer={null}
        width={1100}
      >
        {runDetailsLoading ? (
          <div className="d-flex align-items-center gap-2">
            <Spin size="small" />
            <span>Loading run details...</span>
          </div>
        ) : (
          <div className="row g-3">
            <div className="col-12">
              <Card size="small" title="AI Runtime">
                {Array.isArray(runDetailsData?.ai_runtime) && runDetailsData.ai_runtime.length > 0 ? (
                  runDetailsData.ai_runtime.map((entry, idx) => (
                    <div key={`runtime-${idx}`} className="small mb-1">
                      {entry?.agent || 'agent'}: {entry?.engine || 'auto'} / {entry?.model || 'auto'} ({entry?.mode || 'text'})
                    </div>
                  ))
                ) : (
                  <div className="text-muted small">No AI runtime data captured.</div>
                )}
              </Card>
            </div>
            <div className="col-12">
              <Card size="small" title={`AI Errors (${(runDetailsData?.ai_errors || []).length})`}>
                {Array.isArray(runDetailsData?.ai_errors) && runDetailsData.ai_errors.length > 0 ? (
                  <pre className="small mb-0" style={{ maxHeight: 220, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                    {runDetailsData.ai_errors.join('\n')}
                  </pre>
                ) : (
                  <div className="text-muted small">No AI errors found for this run.</div>
                )}
              </Card>
            </div>
            <div className="col-12">
              <Card size="small" title={`DB Actions (${(runDetailsData?.db_events || []).length})`}>
                {Array.isArray(runDetailsData?.db_events) && runDetailsData.db_events.length > 0 ? (
                  <pre className="small mb-0" style={{ maxHeight: 220, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                    {runDetailsData.db_events.join('\n')}
                  </pre>
                ) : (
                  <div className="text-muted small">No DB actions found for this run.</div>
                )}
              </Card>
            </div>
            <div className="col-12">
              <Card size="small" title={`AI Events (${(runDetailsData?.ai_events || []).length})`}>
                {Array.isArray(runDetailsData?.ai_events) && runDetailsData.ai_events.length > 0 ? (
                  <pre className="small mb-0" style={{ maxHeight: 260, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                    {runDetailsData.ai_events.join('\n')}
                  </pre>
                ) : (
                  <div className="text-muted small">No AI events found for this run.</div>
                )}
              </Card>
            </div>
            <div className="col-12">
              <Card size="small" title={`AI Rerank Debug (${rerankDebugEvents.length})`}>
                  {rerankDebugEvents.length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {rerankDebugEvents.map((entry) => (
                        <div key={entry.id} className="border rounded p-2">
                          <div className="small mb-1">
                            <strong>Engine:</strong> {String(entry.engine || 'auto')} | <strong>Model:</strong> {String(entry.model || 'auto')}
                          </div>
                          <div className="small mb-1"><strong>URL:</strong> {String(entry.url || 'N/A')}</div>
                          <div className="small fw-semibold">Request</div>
                          <pre className="small mb-2" style={{ maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(entry.request || {}, null, 2)}
                          </pre>
                          <div className="small fw-semibold">Response</div>
                          <pre className="small mb-0" style={{ maxHeight: 220, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(entry.response || {}, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted small">No AI rerank debug events found for this run.</div>
                  )}
              </Card>
            </div>
          </div>
        )}
      </Modal>
      <Modal
        title={`Process Details${v3ProcessDetailsModalRequest?.request_name ? ` - ${v3ProcessDetailsModalRequest.request_name}` : ''}`}
        open={isV3 && v3ProcessDetailsModalOpen}
        onCancel={() => {
          setV3ProcessDetailsModalOpen(false);
          setV3ProcessDetailsModalRequest(null);
          setV3ProcessDetailsModalStatus(null);
        }}
        footer={null}
        width={980}
      >
        {(() => {
          const requestIdValue = v3ProcessDetailsModalRequest?.id;
          const requestKey = String(requestIdValue || '');
          const loading = Boolean(v3ProcessDetailsLoadingByRequest[requestKey]);
          const status = v3ProcessDetailsModalStatus || {};
          const meta = processMetaById[requestIdValue] || {};
          if (loading) {
            return (
              <div className="d-flex align-items-center gap-2">
                <Spin size="small" />
                <span>Loading process details...</span>
              </div>
            );
          }
          if (!status || Object.keys(status).length === 0) {
            return <div className="text-muted small">No process details found for this request.</div>;
          }
          return (
            <div className="order-genie-v2-list-progress">
              {renderRequestProgressCard(status, meta)}
            </div>
          );
        })()}
      </Modal>
      <Modal
        title={`Probe details${probeModalRequest?.request_name ? ` - ${probeModalRequest.request_name}` : ''}`}
        open={probeModalOpen}
        onCancel={() => {
          if (probeModalResumeLoading) return;
          setProbeModalOpen(false);
          setProbeModalData(null);
          setProbeModalDiagnose(null);
          setProbeModalError('');
          setProbeModalRequest(null);
        }}
        width={860}
        footer={[
          <Button
            key="close"
            onClick={() => {
              if (probeModalResumeLoading) return;
              setProbeModalOpen(false);
              setProbeModalData(null);
              setProbeModalDiagnose(null);
              setProbeModalError('');
              setProbeModalRequest(null);
            }}
            disabled={probeModalResumeLoading}
          >
            Close
          </Button>,
          ...(Boolean(probeModalData?.can_resume)
            ? [
                <Button
                  key="resume"
                  type="primary"
                  loading={probeModalResumeLoading}
                  onClick={async () => {
                    const req = probeModalRequest;
                    const requestUser = normalizeRequestUserId(req?.user_id, userId, statusUserId);
                    if (!req?.id || !requestUser) return;
                    try {
                      setProbeModalResumeLoading(true);
                      await resumeRequestFromCheckpoint(req.id, requestUser.trim(), { fromProbeModal: true });
                      await loadStatusRequests(statusUserId.trim());
                    } finally {
                      setProbeModalResumeLoading(false);
                    }
                  }}
                >
                  Resume from checkpoint
                </Button>,
              ]
            : []),
        ]}
      >
        {probeModalLoading ? (
          <div className="d-flex align-items-center gap-2">
            <Spin size="small" />
            <span>Collecting process diagnostics...</span>
          </div>
        ) : probeModalError ? (
          <div className="small">
            <div className="text-danger mb-2">{probeModalError}</div>
            <div className="text-muted">Unable to load probe details right now.</div>
          </div>
        ) : (
          (() => {
            const probe = probeModalData || {};
            const status = probe?.status || {};
            const checkpoint = probe?.checkpoint || {};
            const queue = probe?.queue || {};
            const statusValue = String(status?.request_status || status?.status || 'unknown');
            const stepValue = String(status?.request_step || status?.step || 'unknown');
            return (
              <div className="small">
                <div className="mb-2 text-muted">{String(probe?.summary_message || 'Probe completed.')}</div>
                <div className="mb-2">
                  <strong>Status:</strong> {statusValue} | <strong>Step:</strong> {stepValue}
                </div>
                <div className="mb-2">
                  <strong>Task active:</strong> {probe?.active_task ? 'Yes' : 'No'} | <strong>Stale:</strong> {probe?.stale ? 'Yes' : 'No'}
                </div>
                <div className="mb-2">
                  <strong>Checkpoint:</strong> Rows {Number(checkpoint?.line_items || 0)}, matched {Number(checkpoint?.rows_with_matches || 0)}, AI ranked {Number(checkpoint?.rows_ai_ranked || 0)}, AI low confidence {Number(checkpoint?.rows_ai_low_confidence || 0)}
                </div>
                {queue && Object.keys(queue).length > 0 && (
                  <div className="mb-2">
                    <strong>Queue:</strong>{' '}
                    {String(queue?.queue_status || 'queued')}
                    {Number(queue?.queue_position || 0) > 0
                      ? ` #${Number(queue?.queue_position || 0)} of ${Math.max(Number(queue?.queue_depth || 0), Number(queue?.queue_position || 0))}`
                      : ''}
                    {Number(queue?.eta_seconds_snapshot || 0) > 0
                      ? ` · ETA ~${Math.max(1, Math.round(Number(queue?.eta_seconds_snapshot || 0) / 60))} min`
                      : ''}
                    {String(queue?.priority_label || '').trim()
                      ? ` · Priority ${String(queue?.priority_label || '').trim()}`
                      : ''}
                  </div>
                )}
                {probe?.seconds_since_progress != null && (
                  <div className="text-muted">
                    Last progress update {Number(probe.seconds_since_progress)} seconds ago.
                  </div>
                )}
                {probeModalDiagnose?.primary_blocker ? (
                  <div className="mt-3 pt-3 border-top">
                    <div className="fw-semibold mb-1">Readiness primary blocker</div>
                    <div className="text-danger mb-1">
                      {probeModalDiagnose.primary_blocker.message || probeModalDiagnose.primary_blocker.id}
                    </div>
                    {Array.isArray(probeModalDiagnose.layers) && probeModalDiagnose.layers.length ? (
                      <div className="text-muted">
                        Diagnosed layers: {probeModalDiagnose.layers.join(', ')}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })()
        )}
      </Modal>
      <Modal
        title="Reason for replacing match"
        open={manualReplaceModalOpen}
        okText="Apply replacement"
        cancelText="Cancel"
        okButtonProps={{ disabled: !String(manualReplaceReasonDraft || '').trim() }}
        onCancel={() => {
          setManualReplaceModalOpen(false);
          setManualReplacePending(null);
          setManualReplaceReasonDraft('');
          setManualReplaceReasonError('');
        }}
        onOk={async () => {
          const reason = String(manualReplaceReasonDraft || '').trim();
          if (!reason) {
            setManualReplaceReasonError('Reason is required.');
            return;
          }
          const applied = await applyManualSearchSelection(manualReplacePending, reason);
          if (!applied) {
            setManualReplaceReasonError('Unable to apply selected search result. Please re-run search.');
            return;
          }
          setManualReplaceModalOpen(false);
          setManualReplacePending(null);
          setManualReplaceReasonDraft('');
          setManualReplaceReasonError('');
        }}
      >
        <div className="small text-muted mb-2">
          Enter why this manual search result should replace the current system/AI match.
        </div>
        <Input.TextArea
          value={manualReplaceReasonDraft}
          onChange={(event) => {
            setManualReplaceReasonDraft(event.target.value);
            if (manualReplaceReasonError) setManualReplaceReasonError('');
          }}
          rows={4}
          maxLength={500}
          placeholder="Required reason (e.g., Exact customer part number match)"
        />
        {manualReplaceReasonError && (
          <div className="text-danger small mt-2">{manualReplaceReasonError}</div>
        )}
      </Modal>
      {showCompletedTopBar && (
        <div className="alert alert-success order-genie-v2-complete-bar d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 px-3 mb-2">
          <div className="d-flex align-items-start gap-2">
            <CheckCircleTwoTone twoToneColor="#16a34a" style={{ fontSize: 20, marginTop: 2 }} />
            <div>
              <div className="fw-semibold">{overlayMessage || 'Request ready for review'}</div>
              {overlayDetail && <div className="small text-success-emphasis">{overlayDetail}</div>}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {step !== 'results' && (
              <Button
                type="primary"
                size="small"
                onClick={openCurrentRequestResults}
              >
                Review matches
              </Button>
            )}
            <Button size="small" onClick={() => setOverlayActive(false)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}
      {quoteReadyNotice && step !== 'results' && (
        <div className="order-genie-v2-ready">
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div className="flex-grow-1">
              <div className="fw-semibold">Request ready for review</div>
              <div className="text-muted small">Go to results to finalize your request.</div>
            </div>
            <div className="d-flex flex-column gap-1">
              <Button
                type="primary"
                size="small"
                onClick={openCurrentRequestResults}
              >
                Review
              </Button>
              <Button type="link" size="small" onClick={() => setQuoteReadyNotice(null)}>
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className={`order-genie-v2-header mb-2 ${isV3 ? 'order-genie-v2-header--v3' : ''}`}>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <h2 className="mb-0">{isV3 ? 'OrderX v3' : 'OrderX'}</h2>
            <div className="text-muted order-genie-v2-header-subtitle">
              {isV3 ? 'Order Processing, Reimagined with AI.' : 'Simple, modern AI driven quote builder'}
            </div>
          </div>
          <div className="position-relative d-flex align-items-center flex-wrap justify-content-end gap-2">
            <div className="order-genie-v2-user-pill" title="Tenant specific user id">
              <UserOutlined />
              <span className="order-genie-v2-user-pill-label">
                {userId ? `User ID: ${userId}` : 'User ID not set'}
                {customerIdAsInErp ? ` • ERP Customer ID: ${customerIdAsInErp}` : ''}
                {shipToIdAsInErp ? ` • Ship To: ${shipToIdAsInErp}` : ''}
              </span>
              <Button size="small" type="link" className="p-0 order-genie-v2-user-pill-action" onClick={openUserEditor}>
                Change
              </Button>
            </div>
            <button
              type="button"
              className={`order-genie-v2-settings-fab ${activeTab === 'settings' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('settings')}
              aria-label="Open settings"
              title="Settings"
            >
              <SettingOutlined />
            </button>
            <button
              type="button"
              className={`order-genie-v2-realtime-fab ${websocketConnected ? 'is-connected' : 'is-disconnected'}`}
              onClick={() => {
                setRealtimePanelOpen((prev) => {
                  const next = !prev;
                  if (next) setRealtimeUnreadCount(0);
                  return next;
                });
              }}
              aria-label="Realtime websocket updates"
              title={websocketConnected ? 'Realtime connected' : `Realtime status: ${websocketStatus}`}
            >
              <Badge count={realtimeUnreadCount} size="small" overflowCount={99}>
                <MessageOutlined />
              </Badge>
              <span className="order-genie-v2-realtime-dot" />
            </button>
            <button
              type="button"
              className="order-genie-v2-bell"
              onClick={() => {
                setNotificationsOpen((prev) => !prev);
                markNotificationsRead();
              }}
              aria-label="Notifications"
            >
              <Badge count={unreadCount} size="small" overflowCount={9}>
                <BellOutlined />
              </Badge>
            </button>
            {realtimePanelOpen && (
              <div className="order-genie-v2-realtime-panel">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div>
                    <div className="fw-semibold">Realtime updates</div>
                    <div className="small text-muted">
                      {websocketConnected ? 'Connected' : `Status: ${websocketStatus}`}
                      {websocketSocketId ? ` • Socket: ${websocketSocketId}` : ''}
                    </div>
                  </div>
                  <Button size="small" onClick={() => setRealtimePanelOpen(false)}>
                    Close
                  </Button>
                </div>
                {websocketError && <div className="small text-danger mb-2">{websocketError}</div>}
                {realtimeFeed.length === 0 ? (
                  <div className="text-muted small">No realtime updates yet.</div>
                ) : (
                  <div className="order-genie-v2-notify-list">
                    {realtimeFeed.map((entry) => (
                      <div key={entry.id} className="order-genie-v2-notify-item">
                        <div className="fw-semibold small">{entry.eventName}</div>
                        <div className="text-muted small">{entry.summary}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {notificationsOpen && (
              <div className="order-genie-v2-notify-panel">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold">Notifications</span>
                  <Button size="small" onClick={() => setNotificationsOpen(false)}>
                    Close
                  </Button>
                </div>
                {notifications.length === 0 && (
                  <div className="text-muted small">No notifications yet.</div>
                )}
                <div className="order-genie-v2-notify-list">
                  {notifications.map((note) => (
                    <div key={note.id} className="order-genie-v2-notify-item">
                      <div className="fw-semibold">{note.title}</div>
                      {note.detail && <div className="text-muted small">{note.detail}</div>}
                    </div>
                  ))}
                </div>
                {quoteReadyNotice && (
                  <Button
                    type="primary"
                    size="small"
                    className="w-100 mt-2"
                    onClick={openCurrentRequestResults}
                  >
                    Review quote
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="order-genie-v2-tabs">
        <Tabs
          activeKey={activeTab}
          onChange={switchToTab}
          type="editable-card"
          hideAdd
          tabPosition="top"
          onEdit={(targetKey, action) => {
            if (action !== 'remove') return;
            closeQuoteTab(targetKey);
          }}
          items={[
            { key: 'new', label: isV3 ? 'New Request' : 'New Quote / Order Request', closable: false },
            { key: 'status', label: isV3 ? 'Request List' : 'My Quoter/Order Request List', closable: false },
            ...(isV3 ? [{ key: 'usage', label: 'Usage Dashboard', closable: false }] : []),
            ...(isV3 ? [{ key: 'product-search', label: 'Product Search', closable: false }] : []),
            ...quoteTabs.map((tab) => ({
              key: `quote-${tab.id}`,
              label: (
                <span className="order-genie-v2-review-tab-label">
                  {isV3 ? (
                    <span className="order-genie-v2-review-tab-label-v3">
                      <span className="order-genie-v2-review-tab-label-v3-title">{String(tab.title || '').replace(/^Reuquest:/i, 'Request:')}</span>
                      <span className="order-genie-v2-review-tab-label-v3-status">{getV3TabStatusText(tab.id)}</span>
                    </span>
                  ) : (
                    tab.title
                  )}
                </span>
              ),
              children: (() => {
                const status = getUnifiedRequestStatus(tab.id, tab.statusSnapshot || {});
                const isTabComplete = isSuccessStatus(status?.request_status || status?.status);
                const isReviewOpened = Boolean(v3ReviewOpenedByRequest[String(tab.id)]);
                const hasActiveProcessForTab = activeProcesses.some(
                  (entry) => String(entry.requestId) === String(tab.id)
                );
                const tabRequestStepValue = String(status?.request_step || status?.step || '').toLowerCase();
                const shouldShowV3ProcessingScreen = isV3
                  && (
                    !isReviewOpened
                    || !isTabComplete
                    || hasActiveProcessForTab
                    || ['reading', 'parsing', 'processing', 'matching', 'ai_rerank', 'queued'].includes(tabRequestStepValue)
                  );
                if (shouldShowV3ProcessingScreen) {
                  return renderV3ProcessingOnly(tab);
                }
                return renderResults(
                  tab.items,
                  tab.matches,
                  tab.sourceFiles || [],
                  tab.id,
                  tab.userId,
                  mergeRequestSummary(tab.requestSummary, requestSummaryById[tab.id]),
                  quoteSelections[tab.id] || {},
                  quoteQtys[tab.id] || {},
                  (updater) =>
                    setQuoteSelections((prev) => ({
                      ...prev,
                      [tab.id]: typeof updater === 'function' ? updater(prev[tab.id] || {}) : updater,
                    })),
                  (updater) =>
                    setQuoteQtys((prev) => ({
                      ...prev,
                      [tab.id]: typeof updater === 'function' ? updater(prev[tab.id] || {}) : updater,
                    }))
                );
              })(),
            })),
          ]}
        />
      </div>

      {activeTab === 'new' && (
        <>
          {step === 'upload' && renderUpload()}
          {step === 'processing' && (
            (() => {
              const processingStep = String(processStatus?.step || '').toLowerCase();
              const scanningActive = ['reading', 'parsing', 'processing'].includes(processingStep)
                && !isSuccessStatus(processStatus?.request_status || processStatus?.status);
              const processingSourceDocs =
                Array.isArray(sourceFiles) && sourceFiles.length
                  ? sourceFiles
                  : (Array.isArray(processStatus?.source_files) ? processStatus.source_files : []);
              const processingPreviewDoc = Array.isArray(processingSourceDocs) && processingSourceDocs.length
                ? processingSourceDocs[processingSourceDocs.length - 1]
                : null;
              const processingPreviewLabel = (() => {
                const direct = String(processingPreviewDoc?.filename || processingPreviewDoc?.name || '').trim();
                if (direct) return direct;
                const fallback = String(previewFile?.name || previewFileBlob?.name || '').trim();
                return fallback || 'Document uploaded';
              })();
              const processingPreviewStoragePath = String(
                processingPreviewDoc?.storage_path || processingPreviewDoc?.path || ''
              ).trim();
              const processingStoredPreview =
                processingPreviewStoragePath && storedPreviewByPath[processingPreviewStoragePath]
                  ? storedPreviewByPath[processingPreviewStoragePath]
                  : {};
              const processingPreviewTable =
                Array.isArray(uploadPreviewTable) && uploadPreviewTable.length > 0
                  ? uploadPreviewTable
                  : (Array.isArray(processingStoredPreview?.table) ? processingStoredPreview.table : []);
              const processingPreviewText =
                String(uploadPreviewText || '').trim()
                  || String(processingStoredPreview?.text || '').trim();
              const processingPreviewUrl =
                String(
                  previewUrl
                  || processingPreviewDoc?.url
                  || processingPreviewDoc?.previewUrl
                  || ''
                ).trim();
              const processingPreviewType =
                String(
                  previewType
                  || processingPreviewDoc?.content_type
                  || processingPreviewDoc?.type
                  || ''
                ).trim();
              const tabularUploadActive = isTabularPreviewFile(
                processingPreviewType,
                processingPreviewLabel
              ) || processingPreviewTable.length > 0 || Boolean(processingPreviewText) || Boolean(String(uploadPreviewHtml || '').trim());
              const hasRichProcessingPreview = Boolean(String(uploadPreviewHtml || '').trim());
              return (
            <div className={`row g-4 ${isV3 ? 'order-genie-v2-processing-layout--v3' : ''}`}>
            <div className={isV3 ? 'col-12 col-xl-10 mx-xl-auto' : 'col-12 col-xl-7'}>
              <Card className="shadow-sm border-0">
                <div className="order-genie-v2-doc-scan-wrap">
                  {processingPreviewUrl && processingPreviewType === 'application/pdf' ? (
                    <iframe
                      title="Document preview"
                      src={processingPreviewUrl}
                      className="order-genie-v2-doc-frame"
                    />
                  ) : (
                    <div
                      className="order-genie-v2-doc-preview"
                      style={{
                        backgroundImage:
                          processingPreviewUrl && processingPreviewType.startsWith('image/')
                            ? `url(${processingPreviewUrl})`
                            : 'none',
                      }}
                    >
                      {!processingPreviewUrl && <div className="text-muted">Document preview will appear here.</div>}
                      {hasRichProcessingPreview && (
                        <iframe
                          title="Rich text preview"
                          sandbox=""
                          srcDoc={uploadPreviewHtml}
                          className="order-genie-v2-doc-frame order-genie-v2-doc-frame-rich"
                        />
                      )}
                      {processingPreviewUrl
                        && !hasRichProcessingPreview
                        && !processingPreviewType.startsWith('image/')
                        && processingPreviewType !== 'application/pdf'
                        && !(processingPreviewTable.length || processingPreviewText) && (
                        <div className="order-genie-v2-doc-fallback">
                          <div className="fw-semibold">{processingPreviewLabel}</div>
                          <div className="text-muted small">
                            {isPlainTextPreviewFile(processingPreviewType, processingPreviewLabel)
                              ? 'Loading text preview...'
                              : 'Preview not available for this file type.'}
                          </div>
                        </div>
                      )}
                      {!hasRichProcessingPreview && (processingPreviewTable.length > 0 || processingPreviewText) && (
                        <div className="order-genie-v2-doc-fallback w-100">
                          <div className="fw-semibold mb-2">{processingPreviewLabel}</div>
                          {processingPreviewTable.length > 0 && (
                            <div className="table-responsive mb-2">
                              <table className="table table-sm mb-0">
                                <tbody>
                                  {processingPreviewTable.map((row, rowIdx) => (
                                    <tr key={`process-upload-preview-row-${rowIdx}`}>
                                      {(Array.isArray(row) ? row : []).map((cell, colIdx) => (
                                        <td key={`process-upload-preview-cell-${rowIdx}-${colIdx}`}>{String(cell || '')}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {processingPreviewText && <div className="text-muted small">{processingPreviewText}</div>}
                        </div>
                      )}
                    </div>
                  )}
                  {scanningActive && (
                    <div
                      className={`order-genie-v2-scanning-overlay ${tabularUploadActive ? 'order-genie-v2-scanning-overlay--tabular' : ''}`}
                      aria-hidden="true"
                    >
                      <div className="order-genie-v2-scanning-line" />
                      <div className="order-genie-v2-scanning-label">
                        {tabularUploadActive ? 'Reading rows...' : 'Scanning document...'}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
              <div className={isV3 ? 'col-12 col-xl-10 mx-xl-auto' : 'col-12 col-xl-5'}>
                <Card className="shadow-sm border-0">
                  <div className="fw-semibold mb-2">Progress</div>
                  <div className="text-muted small mb-2">{getStatusMessageText(processStatus?.message) || 'Processing...'}</div>
                  {processStatus?.detail && <div className="text-muted small">{processStatus.detail}</div>}
                  {scanningActive && (
                    <div className="mt-3">
                      <div className="fw-semibold small mb-1">Extraction activity</div>
                      <div
                        ref={aiStreamPanelRef}
                        className="border rounded p-2"
                        style={{ maxHeight: 240, overflowY: 'auto' }}
                      >
                        {renderExtractionActivity(true)}
                      </div>
                    </div>
                  )}
                  {(() => {
                    const meta = requestId ? processMetaById[requestId] || {} : {};
                    const summary = buildProcessSummary(processStatus, meta);
                    if (!summary.length) return null;
                    return (
                      <div className="mt-3">
                        <div className="fw-semibold small mb-2">Completion summary</div>
                        <div className="d-flex flex-column gap-1">
                          {summary.map((line, idx) => (
                            <div key={`${line}-${idx}`} className="text-muted small">
                              {line}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {(() => {
                    const dbActions = Array.isArray(processStatus?.db_actions) ? processStatus.db_actions : [];
                    const dbActionTotal = Number(processStatus?.db_action_total || 0);
                    if (!dbActions.length && dbActionTotal <= 0) return null;
                    const recentActions = [...dbActions].slice(-20).reverse();
                    return (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="fw-semibold small">DB actions</div>
                          <div className="text-muted small">
                            {dbActionTotal > 0 ? `Total ${dbActionTotal}` : `Recent ${recentActions.length}`}
                          </div>
                        </div>
                        <div
                          className="border rounded p-2 bg-light"
                          style={{ maxHeight: 180, overflowY: 'auto' }}
                        >
                          {recentActions.map((entry, idx) => {
                            const table = String(entry?.table || '').trim() || 'table';
                            const action = String(entry?.action || '').trim() || 'action';
                            const rowText = Number.isFinite(Number(entry?.rows))
                              ? ` · rows ${Number(entry.rows)}`
                              : '';
                            const detailText = String(entry?.detail || '').trim();
                            const atText = formatTimestamp(entry?.at) || String(entry?.at || '');
                            return (
                              <div key={`${entry?.at || 'db'}-${table}-${action}-${idx}`} className="small mb-2">
                                <div className="fw-semibold">
                                  {table} · {action}
                                  {rowText}
                                </div>
                                {detailText && <div className="text-muted">{detailText}</div>}
                                {atText && <div className="text-muted">{atText}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  {(() => {
                    const meta = requestId ? processMetaById[requestId] || {} : {};
                    return renderRequestProgressCard(processStatus || {}, meta, {
                      onReview: openCurrentRequestResults,
                    });
                  })()}
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <Button type="default" onClick={handleStartNewRequest}>
                      Start New Request
                    </Button>
                    <Button type="link" onClick={() => setActiveTab('status')}>
                      Go to request list
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
              );
            })()
          )}
          {step === 'results' &&
            renderResults(
              items,
              matches,
              sourceFiles,
              requestId,
              userId,
              requestSummaryById[requestId] || null
            )}
        </>
      )}
      {activeTab === 'status' && renderStatus()}
      {isV3 && activeTab === 'usage' && renderUsageDashboard()}
      {isV3 && activeTab === 'product-search' && (
        <OrderGenieV3ProductSearch headerCustomerId={customerIdAsInErp} />
      )}
      {activeTab === 'settings' && renderSettings()}

      <Modal
        open={!!previewModalFile}
        footer={null}
        onCancel={() => setPreviewModalFile(null)}
        width={viewportWidth < 768 ? '96vw' : '80vw'}
        style={{ maxWidth: 960 }}
        styles={{ body: { padding: 0 } }}
      >
        {previewModalFile?.url ? (
          previewModalFile.content_type === 'application/pdf' ? (
            <iframe title="Document preview" src={previewModalFile.url} className="order-genie-v2-modal-frame" />
          ) : (
            <img
              src={previewModalFile.url}
              alt={previewModalFile.filename || 'Document preview'}
              className="order-genie-v2-modal-image"
            />
          )
        ) : (
          <div className="p-4 text-muted">No document preview available.</div>
        )}
      </Modal>

      <OrderGenieSetupSecretsDrawer
        open={setupSecretsOpen}
        missing={setupSecretsMissing}
        saving={setupSecretsSaving}
        error={setupSecretsError}
        onSubmit={handleSetupSecretsSubmit}
        onCancel={handleSetupSecretsCancel}
      />
      <Drawer
        open={editDrawerOpen}
        onClose={requestCloseEditDrawer}
        title="Edit request inputs"
        width={editDrawerWidth}
      >
        <div className="d-flex flex-column gap-3 order-genie-v2-edit-drawer-content">
          <div className="text-muted small">
            Edit line inputs and add missing rows before rerunning matches.
          </div>
          {editingItems.length === 0 ? (
            <div className="text-muted">No items to edit yet. Add a row to continue.</div>
          ) : (
            editingItems.map((row, idx) => (
              <div
                key={row.__draftKey || row.id || idx}
                className={`border rounded p-3 ${editingHighlightKey === (row.__draftKey || '') ? 'order-genie-v2-edit-row-highlight' : ''}`}
                ref={(node) => {
                  const rowKey = row.__draftKey || '';
                  if (!rowKey) return;
                  if (node) {
                    editingRowRefs.current[rowKey] = node;
                  } else {
                    delete editingRowRefs.current[rowKey];
                  }
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold mb-0">Line {idx + 1}</div>
                  <Button
                    size="small"
                    danger
                    onClick={() => requestDeleteEditingRow(idx)}
                    disabled={editingItems.length <= 1 || editingSaving}
                  >
                    Delete Row
                  </Button>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Part Number</label>
                    <Input
                      value={row.part_number}
                      onFocus={() => setEditingHighlightKey(row.__draftKey || '')}
                      onChange={(e) => updateEditingRow(idx, { part_number: e.target.value })}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Manufacturer Part #</label>
                    <Input
                      value={row.manufacturer_part_number}
                      onFocus={() => setEditingHighlightKey(row.__draftKey || '')}
                      onChange={(e) => updateEditingRow(idx, { manufacturer_part_number: e.target.value })}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Customer Part #</label>
                    <Input
                      value={row.customer_part_number || ''}
                      onFocus={() => setEditingHighlightKey(row.__draftKey || '')}
                      onChange={(e) => updateEditingRow(idx, { customer_part_number: e.target.value })}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Manufacturer</label>
                    <Input
                      value={row.manufacturer}
                      onFocus={() => setEditingHighlightKey(row.__draftKey || '')}
                      onChange={(e) => updateEditingRow(idx, { manufacturer: e.target.value })}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Qty</label>
                    <InputNumber
                      min={1}
                      value={row.qty}
                      onFocus={() => setEditingHighlightKey(row.__draftKey || '')}
                      onChange={(value) => updateEditingRow(idx, { qty: value || 1 })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <Input.TextArea
                      rows={2}
                      value={row.description}
                      onFocus={() => setEditingHighlightKey(row.__draftKey || '')}
                      onChange={(e) => updateEditingRow(idx, { description: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Item Comments</label>
                    <Input.TextArea
                      rows={2}
                      value={row.item_notes || ''}
                      onFocus={() => setEditingHighlightKey(row.__draftKey || '')}
                      onChange={(e) => updateEditingRow(idx, { item_notes: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
          {editDrawerValidationError && (
            <div className="text-danger small">{editDrawerValidationError}</div>
          )}
          <div className="d-flex justify-content-end gap-2 flex-wrap order-genie-v2-edit-drawer-actions">
            <Button type="default" icon={<PlusOutlined />} onClick={addNewEditableRow}>
              Add row
            </Button>
            <Button onClick={requestCloseEditDrawer}>Cancel</Button>
            <Button
              type="primary"
              loading={editingSaving}
              onClick={async () => {
                if (!editingRequestId || !editingUserId) return;
                const validationError = validateEditingRows(editingItems);
                if (validationError) {
                  setEditDrawerValidationError(validationError);
                  return;
                }
                setEditDrawerValidationError('');
                setEditingSaving(true);
                try {
                  const payloadItems = editingItems.map((row, index) => {
                    const itemNotes = String(row.item_notes || '').trim();
                    const payloadRow = {
                      row_index: index,
                      parsed_json: {
                        ...(row.parsed_json || {}),
                        itemNotes: itemNotes || undefined,
                        item_notes: itemNotes || undefined,
                        notes: itemNotes || undefined,
                      },
                      normalized_parts: {
                        part_number: row.part_number || '',
                        manufacturer_part_number: row.manufacturer_part_number || '',
                        customer_part_number: row.customer_part_number || '',
                        manufacturer: row.manufacturer || '',
                        description: row.description || '',
                        qty: row.qty || 1,
                        notes: itemNotes,
                      },
                    };
                    if (row.id) {
                      payloadRow.id = row.id;
                    }
                    return payloadRow;
                  });
                  await updateOrderGenieItems(editingRequestId, {
                    user_id: editingUserId,
                    items: payloadItems,
                  });
                  const refreshed = await getOrderGenieRequest(editingRequestId, editingUserId);
                  const nextItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
                  const nextSourceFiles = Array.isArray(refreshed?.request?.source_files)
                    ? refreshed.request.source_files
                    : [];
                  const nextMatches = mergeCartSelectionsIntoMatches(refreshed?.matches, refreshed?.cart);
                  if (String(requestId || '') === String(editingRequestId)) {
                    setItems(nextItems);
                    setMatches(isV3 ? nextMatches : []);
                    setSourceFiles(nextSourceFiles);
                  }
                  setQuoteTabs((prev) =>
                    prev.map((tab) =>
                      String(tab.id) === String(editingRequestId)
                        ? { ...tab, items: nextItems, matches: isV3 ? nextMatches : [], sourceFiles: nextSourceFiles }
                        : tab
                    )
                  );
                  if (!isV3) {
                    setRequestStatusById((prev) => ({
                      ...prev,
                      [editingRequestId]: {
                        status: 'running',
                        message: 'Running',
                        detail: 'Matching in progress...',
                      },
                    }));
                    await startRequestRerun(editingRequestId, editingUserId);
                  }
                  setEditDrawerOpen(false);
                  setEditingHighlightKey('');
                  setEditDrawerInitialSnapshot('');
                  setEditDrawerValidationError('');
                } finally {
                  setEditingSaving(false);
                }
              }}
            >
              {isV3 ? 'Save' : 'Save & Rerun'}
            </Button>
          </div>
        </div>
      </Drawer>

      <Modal
        open={cameraModalOpen}
        onCancel={() => setCameraModalOpen(false)}
        footer={null}
        width={480}
        title="Capture photo"
      >
        {cameraError ? (
          <div className="text-muted">{cameraError}</div>
        ) : (
          <div className="order-genie-v2-camera-modal">
            <video ref={videoRef} autoPlay playsInline muted />
            <canvas ref={canvasRef} className="d-none" />
            <div className="d-flex gap-2 mt-3">
              <Button type="primary" onClick={handleCapturePhoto} disabled={!cameraStream}>
                Capture
              </Button>
              <Button onClick={() => setCameraModalOpen(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={advancedSearchTipsOpen}
        onCancel={() => setAdvancedSearchTipsOpen(false)}
        footer={null}
        width={viewportWidth < 768 ? '96vw' : 760}
        title="Advanced Smart Deep Search Tips"
      >
        <div className="small">
          <div className="mb-2">
            Use the app's Advanced Smart Deep Search syntax for precise matching. You can combine field filters,
            boolean logic, ranges, and wildcards.
          </div>
          <div className="mb-2 fw-semibold">Common patterns</div>
          <ul className="mb-3 ps-3">
            <li><code>compressor*</code> - wildcard prefix match.</li>
            <li><code>c?mpressor</code> - single-character wildcard.</li>
            <li><code>"heat pump"</code> - exact phrase.</li>
            <li><code>\"R-410A\"~2</code> - fuzzy/proximity style search.</li>
            <li><code>manufacturerName:Carrier</code> - search specific field.</li>
            <li><code>productCode:(ABC123 OR ABC124)</code> - field with OR list.</li>
            <li><code>(compressor OR condenser) AND inverter</code> - grouped boolean logic.</li>
            <li><code>category:HVAC AND NOT manufacturerName:Generic</code> - exclude terms.</li>
            <li><code>createdAt:[2024-01-01T00:00:00Z TO *]</code> - date range.</li>
            <li><code>attr_s_tonnage:[2 TO 5]</code> - numeric/range style query.</li>
            <li><code>name:mini\\-split</code> - escaped special character.</li>
          </ul>
          <div className="mb-2 fw-semibold">Supported operators</div>
          <div className="mb-3">
            <code>AND</code>, <code>OR</code>, <code>NOT</code>, parentheses <code>()</code>, ranges{' '}
            <code>[a TO b]</code>/<code>{'{a TO b}'}</code>, wildcard <code>*</code>/<code>?</code>, fuzzy/proximity{' '}
            <code>~</code>, boost <code>^</code>, field syntax <code>field:value</code>.
          </div>
          <div className="mb-2 fw-semibold">Best practices</div>
          <ul className="mb-0 ps-3">
            <li>Start broad, then narrow with field filters.</li>
            <li>Group mixed AND/OR clauses with parentheses to avoid ambiguity.</li>
            <li>If a query fails, simplify first and add conditions incrementally.</li>
            <li>Escape special characters when searching them literally: <code>+ - && || ! ( ) {'{'} {'}'} [ ] ^ " ~ * ? : \\ /</code></li>
          </ul>
        </div>
      </Modal>

      <Modal
        title="Set User ID"
        open={userEditorOpen}
        onCancel={() => setUserEditorOpen(false)}
        onOk={saveHeaderUserId}
        okText="Save"
        maskClosable={false}
      >
        <div className="mb-2 text-muted small">
          These values are tenant specific and will be reused until changed.
        </div>
        <Input
          value={userIdDraft}
          autoFocus
          placeholder="Enter user id"
          onPressEnter={saveHeaderUserId}
          onChange={(e) => setUserIdDraft(e.target.value)}
        />
        <div className="mt-3">
          <label className="form-label mb-1">Customer ID as in ERP</label>
          <AutoComplete
            className="order-genie-erp-customer-autocomplete"
            value={customerIdAsInErpDraft}
            style={{ width: '100%' }}
            backfill
            filterOption={false}
            virtual={false}
            popupMatchSelectWidth
            notFoundContent={
              erpCustomerLoading
                ? <Spin size="small" />
                : String(customerIdAsInErpDraft || '').trim().length < 2
                  ? 'Type at least 2 characters to search'
                  : 'No matching customers'
            }
            options={erpCustomerOptions.map((c) => ({
              value: c.erpCustomerId,
              label: (
                <div className="order-genie-erp-customer-option">
                  <div className="order-genie-erp-customer-option-row">
                    <span className="order-genie-erp-customer-option-id">{c.erpCustomerId}</span>
                    <span className="order-genie-erp-customer-option-name">{c.customerName}</span>
                  </div>
                  <div className="order-genie-erp-customer-option-address">{c.billingAddress}</div>
                </div>
              ),
            }))}
            onSearch={(text) => {
              setCustomerIdAsInErpDraft(text);
              searchErpCustomersDebounced(text);
              if (text !== customerIdAsInErpDraft) {
                setShipToIdAsInErpDraft('');
                setShipToOptions([]);
              }
            }}
            onSelect={(value) => {
              setCustomerIdAsInErpDraft(value);
              setShipToIdAsInErpDraft('');
              setShipToOptions([]);
              loadShipToAddresses(value);
            }}
          >
            <Input
              placeholder="Search by ERP customer ID, name, or billing address"
              onPressEnter={saveHeaderUserId}
            />
          </AutoComplete>
          {(() => {
            const matchedCustomer = erpCustomerOptions.find(
              (c) => c.erpCustomerId === String(customerIdAsInErpDraft || '').trim()
            );
            if (!matchedCustomer) return null;
            return (
              <div className="order-genie-erp-customer-selected">
                <div className="order-genie-erp-customer-selected-name">{matchedCustomer.customerName}</div>
                <div className="order-genie-erp-customer-selected-address">{matchedCustomer.billingAddress}</div>
              </div>
            );
          })()}
        </div>
        <div className="mt-3">
          <label className="form-label mb-1">Ship To</label>
          <AutoComplete
            className="order-genie-erp-customer-autocomplete"
            value={shipToIdAsInErpDraft}
            style={{ width: '100%' }}
            backfill
            filterOption={(inputValue, option) => {
              const q = String(inputValue || '').trim().toLowerCase();
              if (!q) return true;
              const shipToId = String(option?.value || '').toLowerCase();
              const matched = shipToOptions.find((s) => s.shipToId === option?.value);
              const address = String(matched?.address || '').toLowerCase();
              return shipToId.includes(q) || address.includes(q);
            }}
            virtual={false}
            popupMatchSelectWidth
            disabled={!String(customerIdAsInErpDraft || '').trim()}
            notFoundContent={
              shipToLoading
                ? <Spin size="small" />
                : !String(customerIdAsInErpDraft || '').trim()
                  ? 'Select a customer first'
                  : 'No ship-to addresses'
            }
            options={shipToOptions.map((s) => ({
              value: s.shipToId,
              label: (
                <div className="order-genie-erp-customer-option">
                  <div className="order-genie-erp-customer-option-row">
                    <span className="order-genie-erp-customer-option-id">{s.shipToId}</span>
                  </div>
                  <div className="order-genie-erp-customer-option-address">{s.address}</div>
                </div>
              ),
            }))}
            onSearch={(text) => setShipToIdAsInErpDraft(text)}
            onSelect={(value) => setShipToIdAsInErpDraft(value)}
          >
            <Input
              placeholder={
                String(customerIdAsInErpDraft || '').trim()
                  ? 'Select or filter ship-to'
                  : 'Select a customer first'
              }
              onPressEnter={saveHeaderUserId}
            />
          </AutoComplete>
          {(() => {
            const matchedShipTo = shipToOptions.find(
              (s) => s.shipToId === String(shipToIdAsInErpDraft || '').trim()
            );
            if (!matchedShipTo) return null;
            return (
              <div className="order-genie-erp-customer-selected">
                <div className="order-genie-erp-customer-selected-address">{matchedShipTo.address}</div>
              </div>
            );
          })()}
        </div>
      </Modal>

      <style>{`
        .order-genie-erp-customer-option {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 2px 0;
          white-space: normal;
        }
        .order-genie-erp-customer-option-row {
          display: flex;
          align-items: baseline;
          gap: 8px;
          flex-wrap: wrap;
        }
        .order-genie-erp-customer-option-id {
          font-weight: 600;
          font-family: 'SFMono-Regular', Consolas, monospace;
          font-size: 12.5px;
          color: #1677ff;
        }
        .order-genie-erp-customer-option-name {
          font-weight: 500;
          font-size: 13px;
          color: rgba(0, 0, 0, 0.85);
        }
        .order-genie-erp-customer-option-address {
          font-size: 12px;
          color: rgba(0, 0, 0, 0.45);
          line-height: 1.3;
        }
        .order-genie-erp-customer-selected {
          margin-top: 6px;
          padding: 6px 10px;
          border-radius: 6px;
          background: #f6faff;
          border: 1px solid #e6f0ff;
        }
        .order-genie-erp-customer-selected-name {
          font-weight: 600;
          font-size: 12.5px;
          color: rgba(0, 0, 0, 0.85);
        }
        .order-genie-erp-customer-selected-address {
          font-size: 12px;
          color: rgba(0, 0, 0, 0.55);
          line-height: 1.3;
        }
        .order-genie-v2-page {
          min-height: 100%;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }
        .order-genie-v2-page .row {
          margin-left: 0;
          margin-right: 0;
        }
        .order-genie-v2-realtime-bar {
          min-height: 30px;
          border-radius: 10px;
          margin-bottom: 10px !important;
        }
        .order-genie-v2-realtime-bar .small {
          line-height: 1.15;
        }
        .order-genie-v2-stream-text {
          white-space: pre-wrap;
          word-break: break-word;
        }
        .order-genie-v2-stream-cursor {
          display: inline-block;
          margin-left: 1px;
          color: #0f172a;
          animation: ogv2blink 1s steps(1, end) infinite;
        }
        .order-genie-v2-extract-anim {
          position: relative;
          overflow: hidden;
        }
        .order-genie-v2-extract-anim-lines {
          display: grid;
          gap: 8px;
          filter: blur(1.3px);
          opacity: 0.9;
        }
        .order-genie-v2-extract-line {
          height: 10px;
          border-radius: 999px;
          background: linear-gradient(90deg, rgba(148, 163, 184, 0.28), rgba(99, 102, 241, 0.35), rgba(148, 163, 184, 0.28));
          background-size: 220% 100%;
          animation: ogv2ExtractSweep 1.1s linear infinite;
        }
        .order-genie-v2-extract-line.w-95 { width: 95%; }
        .order-genie-v2-extract-line.w-90 { width: 90%; }
        .order-genie-v2-extract-line.w-70 { width: 70%; }
        .order-genie-v2-extract-line.w-60 { width: 60%; }
        @keyframes ogv2ExtractSweep {
          0% { background-position: 0% 0; }
          100% { background-position: 220% 0; }
        }
        @keyframes ogv2blink {
          0%, 45% { opacity: 1; }
          46%, 100% { opacity: 0; }
        }
        .order-genie-v2-complete-bar {
          position: sticky;
          top: 8px;
          z-index: 1250;
          border-radius: 10px;
          border: 1px solid rgba(34, 197, 94, 0.35);
          box-shadow: 0 12px 26px rgba(21, 128, 61, 0.16);
        }
        .order-genie-v2-header {
          margin-bottom: 10px !important;
        }
        .order-genie-v2-header--v3 {
          padding: 12px 14px;
          border-radius: 14px;
          border: 1px solid rgba(59, 130, 246, 0.2);
          background: linear-gradient(135deg, rgba(219, 234, 254, 0.65) 0%, rgba(239, 246, 255, 0.85) 40%, rgba(255, 255, 255, 0.95) 100%);
          box-shadow: 0 10px 24px rgba(29, 78, 216, 0.08);
        }
        .order-genie-v2-header h2 {
          font-weight: 600;
          line-height: 1.1;
          margin: 0;
        }
        .order-genie-v2-header--v3 h2 {
          padding: 6px 12px;
          border-radius: 999px;
          border: 1px solid rgba(37, 99, 235, 0.18);
          background: rgba(255, 255, 255, 0.9);
          color: #1e3a8a;
          letter-spacing: 0.2px;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
        }
        .order-genie-v2-header-subtitle {
          font-size: 13px;
          line-height: 1.2;
          margin: 0;
          word-break: break-word;
        }
        .order-genie-v2-header--v3 .order-genie-v2-header-subtitle {
          color: #334155 !important;
          font-size: 13px;
          font-weight: 500;
        }
        .order-genie-v2-user-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid rgba(148, 163, 184, 0.45);
          background: #f8fafc;
          max-width: 100%;
        }
        .order-genie-v2-user-pill-label {
          max-width: 280px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 12px;
          color: #1f2937;
        }
        .order-genie-v2-user-pill-action {
          font-size: 12px;
          line-height: 1;
        }
        .order-genie-v2-tabs .ant-tabs-nav {
          margin-bottom: 20px;
        }
        .order-genie-v2-tabs .ant-tabs-nav-wrap {
          overflow-x: auto;
        }
        .order-genie-v2-tabs .ant-tabs-tab {
          flex-shrink: 0;
        }
        .order-genie-v2-tabs .ant-tabs-tab[data-node-key^="quote-"] {
          border-color: rgba(59, 130, 246, 0.28) !important;
          background: linear-gradient(180deg, rgba(239, 246, 255, 0.92) 0%, rgba(255, 255, 255, 0.95) 100%);
        }
        .order-genie-v2-tabs .ant-tabs-tab[data-node-key^="quote-"] .ant-tabs-tab-btn {
          color: #1d4ed8;
          font-weight: 600;
        }
        .order-genie-v2-tabs-left .ant-tabs-nav {
          min-width: 240px;
        }
        .order-genie-v2-tabs-left .ant-tabs-content-holder {
          min-width: 0;
        }
        .order-genie-v2-tabs-left .ant-tabs-tab {
          justify-content: space-between;
        }
        .order-genie-v2-tabs .ant-tabs-tab-active[data-node-key^="quote-"] {
          border-color: rgba(37, 99, 235, 0.48) !important;
          background: linear-gradient(180deg, rgba(219, 234, 254, 0.96) 0%, rgba(239, 246, 255, 0.98) 100%);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.16);
        }
        .order-genie-v2-tabs .ant-tabs-tab-active[data-node-key^="quote-"] .ant-tabs-tab-btn {
          color: #1e3a8a;
        }
        .order-genie-v2-review-tab-label {
          color: inherit;
        }
        .order-genie-v2-review-tab-label-v3 {
          display: inline-flex;
          flex-direction: column;
          gap: 2px;
          line-height: 1.2;
          max-width: 210px;
        }
        .order-genie-v2-review-tab-label-v3-title {
          font-weight: 600;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .order-genie-v2-review-tab-label-v3-status {
          font-size: 11px;
          color: #334155;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .order-genie-v2-doc-preview {
          min-height: 380px;
          border-radius: 16px;
          background-size: cover;
          background-position: center;
          border: 1px solid rgba(148, 163, 184, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .order-genie-v2-doc-scan-wrap {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
        }
        .order-genie-v2-scanning-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(to bottom, rgba(15, 23, 42, 0.14), rgba(15, 23, 42, 0.2));
        }
        .order-genie-v2-scanning-overlay--tabular {
          background: transparent;
        }
        .order-genie-v2-scanning-line {
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, rgba(56, 189, 248, 0), rgba(56, 189, 248, 0.95), rgba(56, 189, 248, 0));
          box-shadow: 0 0 14px rgba(56, 189, 248, 0.75);
          animation: ogv2ScanMove 2.1s ease-in-out infinite;
        }
        .order-genie-v2-scanning-overlay--tabular .order-genie-v2-scanning-line {
          animation: ogv2RowLineUp 1.5s linear infinite;
        }
        .order-genie-v2-scanning-label {
          position: absolute;
          right: 10px;
          bottom: 10px;
          font-size: 11px;
          color: #e2e8f0;
          background: rgba(15, 23, 42, 0.6);
          padding: 4px 8px;
          border-radius: 999px;
          letter-spacing: 0.2px;
        }
        @keyframes ogv2ScanMove {
          0% { top: 6%; opacity: 0.55; }
          50% { top: 52%; opacity: 1; }
          100% { top: 93%; opacity: 0.55; }
        }
        @keyframes ogv2RowLineUp {
          0% { top: 93%; opacity: 0.4; }
          50% { top: 48%; opacity: 1; }
          100% { top: 6%; opacity: 0.4; }
        }
        .order-genie-v2-upload-tile .ant-upload {
          min-height: 180px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .order-genie-v2-upload-tile--compact .ant-upload {
          min-height: 130px;
        }
        .order-genie-v2-upload-tile .ant-upload-list {
          margin-top: 8px;
        }
        .order-genie-v2-upload-tile .ant-upload-list-item {
          height: auto !important;
          min-height: 32px;
          align-items: flex-start;
        }
        .order-genie-v2-upload-tile .ant-upload-list-item-name {
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
          line-height: 1.3;
          margin-right: 8px;
          padding-right: 0;
        }
        .order-genie-v2-upload-tile .ant-upload-list-item-actions {
          position: static !important;
          margin-top: 2px;
          align-self: flex-start;
        }
        .order-genie-v2-upload-selected-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 2px;
        }
        .order-genie-v2-upload-selected-item {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          padding: 6px 10px;
          border: 1px solid rgba(148, 163, 184, 0.35);
          border-radius: 8px;
          background: #f8fafc;
        }
        .order-genie-v2-upload-selected-name {
          font-size: 0.9rem;
          color: #0f172a;
          overflow-wrap: anywhere;
          word-break: break-word;
          line-height: 1.35;
        }
        .order-genie-v2-upload-selected-remove {
          white-space: nowrap;
        }
        .order-genie-v2-camera-tile {
          width: 100%;
          min-height: 180px;
          border: 1px dashed rgba(148, 163, 184, 0.6);
          border-radius: 12px;
          background: #f8fafc;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 16px;
          cursor: pointer;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          position: relative;
        }
        .order-genie-v2-camera-tile--compact {
          min-height: 130px;
        }
        .order-genie-v2-camera-tile:hover {
          border-color: #2563eb;
          box-shadow: 0 6px 18px rgba(37, 99, 235, 0.12);
        }
        .order-genie-v2-camera-icon {
          font-size: 28px;
          color: #2563eb;
        }
        .order-genie-v2-camera-modal video {
          width: 100%;
          border-radius: 12px;
          border: 1px solid rgba(148, 163, 184, 0.3);
          background: #0f172a;
        }
        .order-genie-v2-paste-capture {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          pointer-events: none;
          border: 0;
          padding: 0;
          margin: 0;
          resize: none;
          cursor: text;
        }
        .order-genie-v2-paste-capture:focus {
          outline: none;
        }
        .order-genie-v2-doc-frame-rich {
          background: #fff;
        }
        .order-genie-v2-source-preview iframe,
        .order-genie-v2-source-preview img {
          width: 100%;
          height: 180px;
          border-radius: 12px;
          border: 1px solid rgba(148, 163, 184, 0.3);
          object-fit: cover;
          background: #f8fafc;
        }
        /* Prevent embedded PDF/image preview from trapping wheel/trackpad scroll inside Summary. */
        .order-genie-v2-summary-card .order-genie-v2-source-preview iframe {
          pointer-events: none;
        }
        .order-genie-v2-modal-frame {
          width: 100%;
          height: 80vh;
          border: none;
          display: block;
        }
        .order-genie-v2-modal-image {
          width: 100%;
          max-height: 80vh;
          object-fit: contain;
          display: block;
        }
        .order-genie-v2-list-header {
          padding: 8px 12px;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          background: #f8fafc;
          margin-bottom: 8px;
        }
        .order-genie-v2-status-card--v3 .ant-card-body {
          padding: 10px 12px;
        }
        .order-genie-v2-status-card--low-confidence {
          border: 1px solid rgba(245, 158, 11, 0.45) !important;
          box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.18), 0 8px 20px rgba(245, 158, 11, 0.12);
          background: linear-gradient(180deg, rgba(255, 251, 235, 0.9) 0%, #ffffff 100%);
        }
        .order-genie-v2-status-card--v3 .fw-semibold {
          line-height: 1.2;
        }
        .order-genie-v2-status-card--v3 .text-muted.small {
          line-height: 1.2;
          margin-bottom: 0;
        }
        .order-genie-v2-review-loading {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 999px;
          border: 1px solid rgba(148, 163, 184, 0.4);
          background: #f8fafc;
          color: #334155;
          font-size: 12px;
          font-weight: 500;
        }
        .order-genie-v2-status-tag {
          white-space: normal;
          line-height: 1.2;
        }
        .order-genie-v2-match-details {
          background: #f8fafc;
          border: 1px solid rgba(148, 163, 184, 0.3);
          border-radius: 8px;
          padding: 8px;
        }
        .order-genie-v2-attr-block {
          margin: 6px 0 0;
          padding: 8px;
          background: #fff;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 6px;
          max-height: 160px;
          overflow: auto;
          font-size: 12px;
          line-height: 1.3;
        }
        .order-genie-v2-attr-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          padding: 6px 0;
          border-bottom: 1px solid rgba(148, 163, 184, 0.2);
        }
        .order-genie-v2-attr-row:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        .order-genie-v2-attr-label {
          color: #475569;
          font-weight: 500;
          flex: 0 0 42%;
        }
        .order-genie-v2-attr-value {
          color: #0f172a;
          text-align: right;
          flex: 1 1 auto;
          word-break: break-word;
        }
        .order-genie-v2-order-summary {
          border: 1px solid rgba(148, 163, 184, 0.28);
          border-radius: 10px;
          background: #f8fafc;
          padding: 10px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .order-genie-v2-order-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }
        .order-genie-v2-order-kv {
          border: 1px solid rgba(148, 163, 184, 0.25);
          border-radius: 8px;
          background: #fff;
          padding: 8px;
          min-height: 58px;
        }
        .order-genie-v2-order-block {
          border: 1px solid rgba(148, 163, 184, 0.25);
          border-radius: 8px;
          background: #fff;
          padding: 8px;
        }
        .order-genie-v2-order-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #64748b;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .order-genie-v2-order-value {
          font-size: 13px;
          color: #0f172a;
          line-height: 1.35;
          word-break: break-word;
        }
        .order-genie-v2-order-text {
          white-space: pre-wrap;
        }
        .order-genie-v2-summary-card--floating {
          max-height: calc(100vh - 96px);
          overflow: hidden;
          padding-right: 6px;
        }
        .order-genie-v2-summary-card--floating .ant-card-body {
          max-height: inherit;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          touch-action: pan-y;
          padding-bottom: 20px;
        }
        .order-genie-v2-debug-block {
          border: 1px solid rgba(148, 163, 184, 0.25);
          border-radius: 10px;
          background: #f8fafc;
          padding: 10px;
        }
        .order-genie-v2-debug-title {
          font-weight: 600;
          font-size: 12px;
          margin-bottom: 6px;
        }
        .order-genie-v2-debug-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 6px;
          font-size: 12px;
          color: #334155;
        }
        .order-genie-v2-debug-step {
          font-size: 12px;
          color: #475569;
          background: #fff;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 8px;
          padding: 6px 8px;
        }
        .order-genie-v2-progress-card {
          border: 1px solid rgba(96, 165, 250, 0.35);
          border-radius: 10px;
          background: #f8fbff;
          padding: 10px 12px;
        }
        .order-genie-v2-list-progress .order-genie-v2-progress-card-v2 {
          border: 1px solid rgba(59, 130, 246, 0.24) !important;
          border-radius: 14px !important;
          background: linear-gradient(160deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.96)) !important;
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9) !important;
          overflow: hidden;
        }
        .order-genie-v2-list-progress .order-genie-v2-progress-card-v2 .ant-card-body {
          padding: 14px 14px 12px;
        }
        .order-genie-v2-list-progress .order-genie-v2-progress-card-v2 .fw-semibold {
          color: #0f172a;
          letter-spacing: 0.2px;
        }
        .order-genie-v2-list-progress .order-genie-v2-progress-card-v2 .text-muted {
          color: #475569 !important;
        }
        .order-genie-v2-list-progress .order-genie-v2-progress-card-v2 .ant-progress-inner {
          background: rgba(148, 163, 184, 0.2);
          border-radius: 999px;
        }
        .order-genie-v2-list-progress .order-genie-v2-progress-card-v2 .ant-progress-bg {
          box-shadow: 0 1px 0 rgba(255, 255, 255, 0.7) inset;
          border-radius: 999px;
        }
        .order-genie-v2-list-progress-shell {
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 14px;
          background: linear-gradient(160deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.96));
          box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.85);
          padding: 10px;
        }
        .order-genie-v2-list-progress-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 8px;
          color: #334155;
        }
        .order-genie-v2-list-progress-toggle.ant-btn {
          border-color: rgba(59, 130, 246, 0.3);
          background: linear-gradient(180deg, #ffffff, #f1f5f9);
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.9);
          color: #1d4ed8;
        }
        .order-genie-v2-list-progress-toggle.ant-btn:hover {
          border-color: rgba(37, 99, 235, 0.45);
          color: #1e3a8a;
          transform: translateY(-1px);
        }
        .order-genie-v2-list-progress-toggle.ant-btn:active {
          transform: translateY(0);
        }
        .order-genie-v2-list-progress-collapsed {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .order-genie-v2-list-progress-chip {
          font-size: 12px;
          color: #334155;
          border: 1px solid rgba(148, 163, 184, 0.28);
          background: rgba(255, 255, 255, 0.82);
          border-radius: 999px;
          padding: 4px 10px;
          line-height: 1.35;
          white-space: nowrap;
        }
        .order-genie-v2-v3-ready-panel {
          min-height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .order-genie-v2-v3-ready-btn.ant-btn {
          min-width: 280px;
          height: 56px;
          font-size: 18px;
          font-weight: 700;
          border-radius: 12px;
          box-shadow: 0 10px 24px rgba(37, 99, 235, 0.28);
        }
        .order-genie-v2-match-price {
          color: #0f6b3f;
          font-size: 14px;
          margin-top: 2px;
        }
        .order-genie-v2-reason-block {
          border: 1px solid rgba(125, 163, 217, 0.38);
          border-left: 3px solid #7aa2d6;
          border-radius: 8px;
          background: #f5f9ff;
          padding: 8px 10px;
        }
        .order-genie-v2-reason-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #4f78ad;
          font-weight: 600;
          margin-bottom: 2px;
        }
        .order-genie-v2-reason-text {
          font-size: 12px;
          color: #4b5f78;
          line-height: 1.35;
        }
        .order-genie-v2-item-row {
          border-radius: 12px;
          border: 1px solid rgba(148, 163, 184, 0.35);
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
          overflow: hidden;
        }
        .order-genie-v2-item-row--even {
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
        }
        .order-genie-v2-item-row--odd {
          background: linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%);
        }
        .order-genie-v2-item-title {
          display: inline-block;
          margin-bottom: 4px;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #1d4ed8;
          background: #dbeafe;
        }
        .order-genie-v2-item-filter-index {
          font-size: 11px;
          font-weight: 600;
          color: #475569;
          background: #eef2f7;
          border: 1px solid rgba(148, 163, 184, 0.35);
          border-radius: 999px;
          padding: 2px 8px;
          line-height: 1.25;
        }
        .order-genie-v2-item-meta {
          min-height: 28px;
        }
        .order-genie-v2-item-part {
          font-size: 13px;
          line-height: 1.25;
        }
        .order-genie-v2-match-filter-float {
          position: relative;
          z-index: 8;
          padding: 6px 8px;
          border: 1px solid rgba(148, 163, 184, 0.28);
          border-radius: 10px;
          background: rgba(248, 250, 252, 0.95);
          backdrop-filter: blur(4px);
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
          width: fit-content;
          max-width: min(96vw, 560px);
          margin-left: auto;
        }
        .order-genie-v2-match-filter-float--floating {
          position: fixed;
          left: 50%;
          transform: translateX(-50%);
          bottom: 16px;
          z-index: 1060;
          max-width: min(96vw, 720px);
          width: auto;
        }
        .order-genie-v2-match-filter-float .ant-btn.ant-btn-sm {
          font-size: 11px;
          padding-inline: 7px;
          height: 24px;
        }
        .order-genie-v2-match-filter-float .small {
          font-size: 11px;
        }
        .order-genie-v2-match-filter-buffer {
          height: 112px;
        }
        .order-genie-v2-inline-qty .ant-input-number {
          width: 78px;
        }
        .order-genie-v2-inline-uom {
          margin-left: 4px;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.03em;
          color: #0f172a;
          background: #e2e8f0;
          border: 1px solid rgba(100, 116, 139, 0.45);
          text-transform: uppercase;
          line-height: 1.2;
        }
        .order-genie-v2-item-desc {
          margin-top: 4px;
          line-height: 1.25;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .order-genie-v2-item-extra {
          margin-top: 2px;
          line-height: 1.25;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .order-genie-v2-row-summary {
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          background: #ffffff;
          padding: 8px 10px;
        }
        .order-genie-v2-row-inputs {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 4px 10px;
          color: #334155;
        }
        .order-genie-v2-selected-match {
          display: flex;
          gap: 10px;
          align-items: center;
          border-top: 1px dashed rgba(148, 163, 184, 0.45);
          padding-top: 8px;
        }
        .order-genie-v2-summary-thumb {
          width: 56px;
          height: 56px;
          border-radius: 10px;
        }
        .order-genie-v2-no-match {
          border: 1px dashed rgba(148, 163, 184, 0.55);
          border-radius: 8px;
          background: rgba(248, 250, 252, 0.85);
          color: #475569;
          font-size: 12px;
          line-height: 1.25;
          padding: 6px 8px;
        }
        .order-genie-v2-row-top {
          border-bottom: 1px solid rgba(148, 163, 184, 0.22);
          padding-bottom: 8px;
        }
        .order-genie-v2-row-search {
          border: 1px solid rgba(148, 163, 184, 0.24);
          border-radius: 10px;
          background: #f8fafc;
          padding: 8px;
        }
        .order-genie-v2-add-row-fab.ant-btn {
          position: fixed;
          right: 24px;
          bottom: 24px;
          z-index: 1150;
          border-radius: 999px;
          box-shadow: 0 16px 34px rgba(37, 99, 235, 0.28);
          padding-inline: 16px;
          height: 42px;
        }
        .order-genie-v2-edit-row-highlight {
          border-color: rgba(37, 99, 235, 0.45) !important;
          box-shadow: 0 0 0 1px rgba(147, 197, 253, 0.7), 0 10px 20px rgba(37, 99, 235, 0.12);
        }
        .order-genie-v2-edit-drawer-content {
          padding-bottom: 86px;
        }
        .order-genie-v2-edit-drawer-actions {
          position: sticky;
          bottom: 0;
          z-index: 9;
          margin-top: 8px;
          margin-bottom: -8px;
          padding: 10px 0 4px;
          border-top: 1px solid rgba(148, 163, 184, 0.28);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.82) 0%, #ffffff 32%);
          backdrop-filter: blur(3px);
        }
        .order-genie-v2-project-summary {
          border: 1px solid rgba(99, 102, 241, 0.25);
          border-radius: 10px;
          background: #f8faff;
          padding: 10px 12px;
        }
        .order-genie-v2-project-summary-title {
          font-weight: 600;
          color: #1e3a8a;
          margin-bottom: 2px;
        }
        .order-genie-v2-project-summary-desc {
          color: #475569;
          font-size: 13px;
          line-height: 1.35;
        }
        .order-genie-v2-settings {
          width: 100%;
        }
        .order-genie-v2-settings-frame {
          width: 100%;
          min-height: 70vh;
          border: none;
          background: #fff;
        }
        .order-genie-v2-toast {
          position: fixed;
          top: 100px;
          right: 24px;
          z-index: 1200;
          background: #fff;
          border-radius: 16px;
          padding: 14px 16px;
          width: min(420px, 92vw);
          border: 1px solid rgba(148, 163, 184, 0.35);
          box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
        }
        .order-genie-v2-toast--completed {
          border: 1px solid rgba(34, 197, 94, 0.45);
          background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 72%);
        }
        .order-genie-v2-ready {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 1200;
          background: #fff;
          border-radius: 14px;
          padding: 12px 16px;
          width: min(380px, 90vw);
          border: 1px solid rgba(148, 163, 184, 0.35);
          box-shadow: 0 18px 36px rgba(15, 23, 42, 0.18);
        }
        .order-genie-v2-scanner {
          height: 6px;
          width: 100%;
          border-radius: 999px;
          margin-bottom: 16px;
          background: linear-gradient(90deg, transparent, #38bdf8, transparent);
          animation: ogv2scan 1.6s infinite;
        }
        @keyframes ogv2scan {
          0% { transform: translateX(-20%); opacity: 0.4; }
          50% { transform: translateX(20%); opacity: 1; }
          100% { transform: translateX(120%); opacity: 0.4; }
        }
        .order-genie-v2-thumb {
          width: 72px;
          height: 72px;
          border-radius: 12px;
          object-fit: cover;
          border: 1px solid rgba(148, 163, 184, 0.3);
        }
        .order-genie-v2-thumb--empty {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #e2e8f0;
          color: #64748b;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          text-align: center;
          padding: 6px;
        }
        .order-genie-v2-match-card--current {
          border-color: rgba(34, 197, 94, 0.45);
          background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 82%);
          box-shadow: 0 0 0 1px rgba(134, 239, 172, 0.6);
        }
        .order-genie-v2-doc-preview {
          min-height: 420px;
          border-radius: 16px;
          background-size: contain;
          background-position: center;
          background-repeat: no-repeat;
          border: 1px solid rgba(148, 163, 184, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
        }
        .order-genie-v2-doc-frame {
          width: 100%;
          min-height: 420px;
          border: none;
          border-radius: 16px;
          background: #f8fafc;
        }
        .order-genie-v2-page--v3 .order-genie-v2-doc-preview,
        .order-genie-v2-page--v3 .order-genie-v2-doc-frame {
          min-height: 520px;
        }
        .order-genie-v2-page--v3 .order-genie-v2-processing-layout--v3 .order-genie-v2-scanning-label {
          right: auto;
          left: 50%;
          bottom: auto;
          top: 10px;
          transform: translateX(-50%);
        }
        .order-genie-v2-doc-fallback {
          padding: 20px;
          text-align: center;
        }
        .order-genie-v2-line-item {
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid rgba(148, 163, 184, 0.2);
          background: #f8fafc;
        }
        .order-genie-v2-bell {
          border: none;
          background: #fff;
          color: #0f172a;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
        }
        .order-genie-v2-bell:hover {
          background: #f8fafc;
        }
        .order-genie-v2-settings-fab {
          border: 1px solid rgba(59, 130, 246, 0.3);
          background: linear-gradient(180deg, #eff6ff 0%, #ffffff 92%);
          color: #1d4ed8;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
        }
        .order-genie-v2-settings-fab:hover {
          border-color: rgba(37, 99, 235, 0.45);
          color: #1e3a8a;
          background: linear-gradient(180deg, #dbeafe 0%, #eff6ff 95%);
        }
        .order-genie-v2-settings-fab.is-active {
          border-color: rgba(37, 99, 235, 0.52);
          color: #1e3a8a;
          background: linear-gradient(180deg, #dbeafe 0%, #dbeafe 100%);
          box-shadow: 0 0 0 2px rgba(147, 197, 253, 0.38), 0 10px 20px rgba(15, 23, 42, 0.14);
        }
        .order-genie-v2-realtime-fab {
          position: relative;
          border: 1px solid rgba(16, 185, 129, 0.35);
          background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 85%);
          color: #047857;
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 20px rgba(15, 23, 42, 0.14);
        }
        .order-genie-v2-realtime-fab.is-connected {
          box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45), 0 10px 20px rgba(15, 23, 42, 0.14);
          animation: ogv2pulse 1.8s infinite;
        }
        .order-genie-v2-realtime-fab.is-disconnected {
          color: #b45309;
          border-color: rgba(217, 119, 6, 0.4);
          background: linear-gradient(180deg, #fffbeb 0%, #ffffff 85%);
        }
        .order-genie-v2-realtime-dot {
          position: absolute;
          right: 3px;
          bottom: 3px;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          border: 1px solid #fff;
          background: #22c55e;
          box-shadow: 0 0 8px rgba(34, 197, 94, 0.85);
        }
        .order-genie-v2-realtime-fab.is-disconnected .order-genie-v2-realtime-dot {
          background: #f59e0b;
          box-shadow: 0 0 8px rgba(245, 158, 11, 0.75);
        }
        @keyframes ogv2pulse {
          0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45), 0 10px 20px rgba(15, 23, 42, 0.14); }
          70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0), 0 10px 20px rgba(15, 23, 42, 0.14); }
          100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0), 0 10px 20px rgba(15, 23, 42, 0.14); }
        }
        .order-genie-v2-realtime-panel {
          position: absolute;
          right: 52px;
          top: 48px;
          width: min(360px, 88vw);
          background: #fff;
          border: 1px solid rgba(16, 185, 129, 0.28);
          border-radius: 12px;
          padding: 12px;
          box-shadow: 0 16px 32px rgba(15, 23, 42, 0.16);
          z-index: 21;
        }
        .order-genie-v2-notify-panel {
          position: absolute;
          right: 0;
          top: 48px;
          width: min(320px, 85vw);
          background: #fff;
          border: 1px solid rgba(148, 163, 184, 0.35);
          border-radius: 12px;
          padding: 12px;
          box-shadow: 0 16px 32px rgba(15, 23, 42, 0.16);
          z-index: 20;
        }
        .order-genie-v2-notify-list {
          max-height: 240px;
          overflow: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .order-genie-v2-notify-item {
          padding: 8px 10px;
          border-radius: 10px;
          background: #f8fafc;
          border: 1px solid rgba(148, 163, 184, 0.2);
        }
        @media (max-width: 768px) {
          .order-genie-v2-toast {
            top: 88px;
            right: 16px;
            left: 16px;
            width: auto;
          }
          .order-genie-v2-ready {
            right: 16px;
            left: 16px;
            width: auto;
          }
          .order-genie-v2-thumb {
            width: 60px;
            height: 60px;
          }
          .order-genie-v2-item-header {
            flex-direction: column;
            align-items: stretch !important;
          }
          .order-genie-v2-item-meta {
            gap: 6px !important;
          }
          .order-genie-v2-item-desc {
            -webkit-line-clamp: 2;
          }
          .order-genie-v2-item-extra {
            -webkit-line-clamp: 3;
          }
          .order-genie-v2-row-inputs {
            grid-template-columns: 1fr;
          }
          .order-genie-v2-row-top {
            padding-bottom: 6px;
          }
          .order-genie-v2-item-header .ant-input-number {
            width: 100%;
          }
          .order-genie-v2-inline-qty .ant-input-number {
            width: 72px;
          }
          .order-genie-v2-item-actions {
            width: 100%;
            align-items: stretch !important;
          }
          .order-genie-v2-item-actions .ant-btn:not(.ant-btn-circle) {
            width: 100%;
          }
          .order-genie-v2-item-actions .ant-btn.ant-btn-circle {
            width: 32px;
            min-width: 32px;
            height: 32px;
            align-self: center;
          }
          .order-genie-v2-match-card-row {
            flex-direction: column;
          }
          .order-genie-v2-order-grid {
            grid-template-columns: 1fr;
          }
          .order-genie-v2-doc-preview,
          .order-genie-v2-doc-frame {
            min-height: 300px;
          }
        }
        .order-genie-v2-match-filter-page-buffer {
          height: 0;
        }
        @media (max-width: 991px) {
          .order-genie-v2-match-filter-float--floating {
            max-width: calc(100vw - 24px);
          }
          .order-genie-v2-match-filter-float .ant-btn.ant-btn-sm {
            height: 28px;
          }
        }
        @media (max-width: 768px) {
          .order-genie-v2-match-filter-float--floating {
            left: 12px;
            right: 12px;
            transform: none;
            width: auto;
            max-width: none;
          }
        }
        @media (max-width: 576px) {
          .order-genie-v2-summary-card--floating {
            height: auto;
            overflow: visible;
            padding-right: 0;
          }
          .order-genie-v2-summary-card--floating .ant-card-body {
            height: auto;
            overflow: visible;
            padding-right: 24px;
          }
          .order-genie-v2-header h2 {
            font-size: 22px;
          }
          .order-genie-v2-header--v3 {
            padding: 10px 12px;
          }
          .order-genie-v2-header-subtitle {
            font-size: 12px;
          }
          .order-genie-v2-doc-preview,
          .order-genie-v2-doc-frame {
            min-height: 240px;
          }
          .order-genie-v2-source-preview iframe,
          .order-genie-v2-source-preview img {
            height: 140px;
          }
          .order-genie-v2-add-row-fab.ant-btn {
            right: 16px;
            bottom: 16px;
            padding-inline: 14px;
          }
          .order-genie-v2-edit-drawer-actions .ant-btn {
            flex: 1 1 auto;
          }
        }
      `}</style>
    </div>
  );
};

export default OrderGenieV2;
